import { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import * as fabric from 'fabric';
import type {
  EditorTool,
  BrushSettings,
  ShapeSettings,
  ShapeFillValue,
  TextSettings,
  ShapeType,
  MarqueeShape,
  LayerMask,
  EditorDocument,
  RetouchSettings,
  RetouchVariant,
  PenSettings,
  VectorAnchor,
  VectorPath,
} from '../types';
import { computeMarqueeBounds } from '../lib/marquee-bounds';
import { contoursToSvgPath } from '../lib/marquee-path';
import { traverseDescendants } from '../lib/layer-tree';
import { linearAngleToUnitVector, unitVectorToLinearAngle } from '../lib/gradient-angle';
import { PixelStrokeFilter, PIXEL_STROKE_TYPE } from '../lib/pixel-stroke-filter';
import { PixelGlowFilter, PIXEL_GLOW_TYPE } from '../lib/pixel-glow-filter';
import {
  binaryMaskToCanvas,
  canvasToBinaryMask,
  dilateMask,
  erodeMask,
  polygonPointsToMask,
} from '../lib/mask-morphology';
import { selectionToBinaryMask, invertMaskInPlace, isMaskNonEmpty } from '../lib/selection-mask';
import {
  ensureCanvasElement,
  resolveOrRegisterPaintSurface,
  type PaintSurface,
} from '../lib/paint-surface';
import {
  maskMaterializationConfig,
  materializeEffectiveMaskInto,
} from '../lib/mask-materialization';
import { rasterizeProjective, type Quad as ProjectiveQuad } from '../lib/projective-transform';
import {
  pathToSvgData,
  recomputeCurvatureHandles,
  populateSmoothAnchorHandles,
  hitTestAnchor,
  hitTestHandle,
  bakeVectorPath,
} from '../lib/vector-path';
import {
  rasterizeWarp,
  defaultWarpGrid,
  applyWarpPreset,
  type WarpGrid,
  type WarpPreset,
} from '../lib/warp-mesh';
import {
  buildCloneBrushMask,
  applyHealToneShift,
  applyMaskedPixelMix,
  buildEdgeExtendedPaddedSource,
  dodgeBurnRangeWeight,
  DODGE_BURN_SPACING_RATIO,
  DODGE_BURN_PER_DAB_SCALE,
  MAX_RETOUCH_LAYER_PIXELS,
} from '../lib/retouch-helpers';

const MARQUEE_STROKE_SCREEN_PX = 1;
const MARQUEE_DASH_SCREEN_PX = [5, 3];
const MARQUEE_FILL_ALPHA = 0.1;
const MARQUEE_DEFAULT_COLOR = '#0078d7';

// Path Edit overlay sizing — Tier 2 Group A PR-B. All values in
// SCENE pixels; combined with `strokeUniform: true` on the Fabric
// objects they stay visually consistent across zoom levels.
const PATH_EDIT_ANCHOR_SIZE_SCENE = 8;
const PATH_EDIT_ANCHOR_HIT_RADIUS_SCENE = 6;
const PATH_EDIT_HANDLE_RADIUS_SCENE = 4;
const PATH_EDIT_HANDLE_HIT_RADIUS_SCENE = 6;
const PATH_EDIT_OVERLAY_COLOR = '#0078d7';

// Paint-bucket SVG (Lucide-style) used as the fill tool's cursor. Encoded
// inline so it ships with the bundle without an extra asset round-trip.
// White fill + black stroke keep it visible on any canvas color. Hotspot is
// at the bucket's lower-left tip where paint conceptually drips.
const FILL_CURSOR_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">` +
  `<path fill="white" d="m19 11-8-8-8.6 8.6a2 2 0 0 0 0 2.8l5.2 5.2c.8.8 2 .8 2.8 0L19 11Z"/>` +
  `<path d="m5 2 5 5"/>` +
  `<path d="M2 13h15"/>` +
  `<path fill="white" d="M22 20a2 2 0 1 1-4 0c0-1.6 1.7-2.4 2-4 .3 1.6 2 2.4 2 4Z"/>` +
  `</svg>`;
const FILL_CURSOR = `url("data:image/svg+xml;utf8,${encodeURIComponent(FILL_CURSOR_SVG)}") 4 20, crosshair`;

const MARQUEE_STYLE: Partial<fabric.FabricObjectProps> = {
  strokeWidth: MARQUEE_STROKE_SCREEN_PX,
  strokeDashArray: [...MARQUEE_DASH_SCREEN_PX],
  strokeUniform: true,
  selectable: false,
  evented: false,
  excludeFromExport: true,
  // Fabric v7 changed the default origin from 'left'/'top' to
  // 'center'/'center'. Our marquee drag math sets `left/top` to the
  // top-left corner via `Math.min(startX, pointer.x)`, then sets
  // `width/height` from the drag delta — that's all top-left
  // semantics. Without this, rect/ellipse marquees would render
  // shifted by (width/2, height/2) from the user's actual drag.
  originX: 'left',
  originY: 'top',
};

/** Build a Fabric Control wired to a skew-only action handler.
 *  Used by Free Transform's Skew sub-mode to override the default
 *  mid-edge controls (which scale-or-skew based on Alt modifier)
 *  with controls that skew unconditionally on bare drag.
 *
 *  `position` encodes the edge: 'x-' = ml, 'x+' = mr, 'y-' = mt,
 *  'y+' = mb. The axis MAPPING matches Fabric's own
 *  `scaleOrSkewActionName` convention (commonControls.ts +
 *  scaleSkew.ts):
 *    - control.y === 0 (left/right edges, ml/mr) → skew Y axis
 *    - control.x === 0 (top/bottom edges, mt/mb) → skew X axis
 *  Geometrically intuitive: dragging the LEFT edge up/down shears
 *  the rect vertically (Y skew); dragging the TOP edge left/right
 *  shears horizontally (X skew). My initial implementation had
 *  this mapping inverted — handlers ran but bailed out of the
 *  lockSkewing check on the wrong axis, producing the "do
 *  nothing" symptom on mid-edge drag.
 *
 *  The pre-transform control is passed in so we inherit visual
 *  sizing — only the action handler changes. */
function buildSkewMidEdgeControl(
  position: 'x-' | 'x+' | 'y-' | 'y+',
  base: fabric.Control,
): fabric.Control {
  const x = position === 'x-' ? -0.5 : position === 'x+' ? 0.5 : 0;
  const y = position === 'y-' ? -0.5 : position === 'y+' ? 0.5 : 0;
  const isHorizontalEdge = position === 'x-' || position === 'x+';
  return new fabric.Control({
    x,
    y,
    actionHandler: isHorizontalEdge
      ? fabric.controlsUtils.skewHandlerY
      : fabric.controlsUtils.skewHandlerX,
    cursorStyleHandler: fabric.controlsUtils.skewCursorStyleHandler,
    actionName: 'skewing',
    // Inherit visual styling from the default control so the
    // user-facing handle size stays consistent across modes.
    sizeX: base.sizeX,
    sizeY: base.sizeY,
    touchSizeX: base.touchSizeX,
    touchSizeY: base.touchSizeY,
  });
}

/** Build the dashed Fabric overlay objects that visually connect
 *  the non-affine Transform handles. For Distort/Perspective: a
 *  closed dashed quad through the 4 corners. For Warp: dashed
 *  rows + columns of the 3×3 grid showing the mesh structure.
 *
 *  Outlines render UNDER the handles so users can grab handles
 *  cleanly even when they overlap an outline edge. */
function buildNonAffineOutlines(
  mode: 'distort' | 'perspective' | 'warp',
  points: readonly { x: number; y: number }[],
): fabric.FabricObject[] {
  const lineStyle: Partial<fabric.FabricObjectProps> = {
    stroke: '#0078d7',
    strokeWidth: 1,
    strokeUniform: true,
    strokeDashArray: [4, 3],
    fill: 'transparent',
    selectable: false,
    evented: false,
    hoverCursor: 'default',
    objectCaching: false,
    excludeFromExport: true,
    originX: 'left',
    originY: 'top',
  };
  if (mode !== 'warp') {
    const polyline = new fabric.Polyline(
      [
        { x: points[0].x, y: points[0].y },
        { x: points[1].x, y: points[1].y },
        { x: points[2].x, y: points[2].y },
        { x: points[3].x, y: points[3].y },
        { x: points[0].x, y: points[0].y },
      ],
      lineStyle,
    );
    (polyline as unknown as { _isTransformOutline: boolean })._isTransformOutline = true;
    return [polyline];
  }
  // Warp: 3 rows + 3 columns. Each rendered as its own Polyline so
  // the mesh visualization is clear. Indices in 3×3 grid:
  //   0  1  2
  //   3  4  5
  //   6  7  8
  const rows = [
    [points[0], points[1], points[2]],
    [points[3], points[4], points[5]],
    [points[6], points[7], points[8]],
  ];
  const cols = [
    [points[0], points[3], points[6]],
    [points[1], points[4], points[7]],
    [points[2], points[5], points[8]],
  ];
  const lines: fabric.FabricObject[] = [];
  for (const r of rows) {
    const pl = new fabric.Polyline(
      r.map((p) => ({ x: p.x, y: p.y })),
      lineStyle,
    );
    (pl as unknown as { _isTransformOutline: boolean })._isTransformOutline = true;
    lines.push(pl);
  }
  for (const c of cols) {
    const pl = new fabric.Polyline(
      c.map((p) => ({ x: p.x, y: p.y })),
      lineStyle,
    );
    (pl as unknown as { _isTransformOutline: boolean })._isTransformOutline = true;
    lines.push(pl);
  }
  return lines;
}

/** Build the identity-warp 3×3 grid spanning the given scene-space
 *  bounding box. Wraps `defaultWarpGrid` from the warp-mesh lib by
 *  offsetting from the bbox origin. */
function defaultWarpGridForBbox(bbox: {
  left: number;
  top: number;
  width: number;
  height: number;
}): WarpGrid {
  const base = defaultWarpGrid(bbox.width, bbox.height);
  return base.map((p) => ({ x: p.x + bbox.left, y: p.y + bbox.top })) as unknown as WarpGrid;
}

/** Inputs the rasterize-and-commit path reads from a non-affine
 *  transform session — the subset shared by the live-preview tick
 *  and the Apply-time bake. Both call sites build their density
 *  from a session-stored source canvas (preview-downscaled for
 *  live, full-resolution for bake), so this type only captures
 *  the deformation state itself. */
export interface NonAffineDeformation {
  mode: 'distort' | 'perspective' | 'warp';
  distortQuad: ProjectiveQuad;
  warpGrid: WarpGrid;
  /** Original (pre-deformation) bbox of the layer in scene coords.
   *  Used solely to compute the destination canvas's pixel density
   *  (source.width / bbox.width). */
  bbox: { left: number; top: number; width: number; height: number };
}

/** Rasterize the current deformation into a fresh destination
 *  canvas and commit it to the target FabricImage. Used by both
 *  the live-preview tick (with `source = sourcePreview`) and the
 *  Apply-time bake (with `source = sourceFull`). Differences
 *  between the two callers are encoded entirely in the `source`
 *  and `subdivisions` arguments — the AABB-fitting math, the
 *  density math, the `setElement`/matrix commit are identical.
 *
 *  The matrix commit is a deliberate full reset: scale to map
 *  destination-canvas pixels back to scene units of the AABB,
 *  and zero the rotation/skew/flip terms. This is what makes
 *  successive ticks composable — each tick replaces the target's
 *  element and matrix from scratch rather than accumulating
 *  transform on top of the previous tick. */
export function commitNonAffineDeformation(
  target: fabric.FabricImage,
  source: HTMLCanvasElement,
  deformation: NonAffineDeformation,
  subdivisions: { projective: number; warp: number },
): void {
  const handlesPts: { x: number; y: number }[] =
    deformation.mode === 'warp' ? [...deformation.warpGrid] : [...deformation.distortQuad];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of handlesPts) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const dW = Math.max(1, maxX - minX);
  const dH = Math.max(1, maxY - minY);
  const density = source.width / deformation.bbox.width;
  const dest = document.createElement('canvas');
  dest.width = Math.max(1, Math.round(dW * density));
  dest.height = Math.max(1, Math.round(dH * density));
  const sx = dest.width / dW;
  const sy = dest.height / dH;
  const toLocal = (p: { x: number; y: number }) => ({
    x: (p.x - minX) * sx,
    y: (p.y - minY) * sy,
  });
  if (deformation.mode === 'distort' || deformation.mode === 'perspective') {
    const q: ProjectiveQuad = [
      toLocal(deformation.distortQuad[0]),
      toLocal(deformation.distortQuad[1]),
      toLocal(deformation.distortQuad[2]),
      toLocal(deformation.distortQuad[3]),
    ];
    rasterizeProjective(source, q, dest, { subdivisions: subdivisions.projective });
  } else {
    const g = deformation.warpGrid.map(toLocal) as unknown as WarpGrid;
    rasterizeWarp(source, g, dest, { subdivisions: subdivisions.warp });
  }
  target.setElement(dest);
  target.set({
    left: minX,
    top: minY,
    scaleX: dW / dest.width,
    scaleY: dH / dest.height,
    angle: 0,
    skewX: 0,
    skewY: 0,
    flipX: false,
    flipY: false,
  });
  target.setCoords();
  target.dirty = true;
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return hex;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function applyMarqueeColor(obj: fabric.Object, color: string): void {
  obj.set({
    stroke: color,
    fill: hexToRgba(color, MARQUEE_FILL_ALPHA),
  });
}

const CROP_RECT_STYLE: Partial<fabric.FabricObjectProps> = {
  fill: 'rgba(0, 120, 215, 0.08)',
  stroke: '#0078d7',
  strokeUniform: true,
  hasControls: true,
  hasBorders: true,
  borderColor: '#0078d7',
  borderDashArray: [5, 3],
  lockRotation: true,
  lockScalingFlip: true,
  selectable: true,
  evented: true,
  excludeFromExport: true,
  objectCaching: false,
  // v7 default origin is center; the crop rect is positioned by its
  // top-left corner via `left: 0, top: 0` + explicit width/height.
  originX: 'left',
  originY: 'top',
  cornerColor: '#ffffff',
  cornerStrokeColor: '#0078d7',
  cornerSize: 12,
  transparentCorners: false,
  cornerStyle: 'rect',
};

/**
 * Build a binary selection mask using either flood-fill (contiguous) or a global
 * threshold scan (non-contiguous). The tolerance is the maximum allowed
 * per-channel difference (0–255, Photoshop-style).
 */
function computeWandMask(
  imgData: ImageData,
  startX: number,
  startY: number,
  tolerance: number,
  contiguous: boolean,
): Uint8Array {
  const { data, width, height } = imgData;
  const mask = new Uint8Array(width * height);

  if (startX < 0 || startX >= width || startY < 0 || startY >= height) return mask;

  const startIdx = (startY * width + startX) * 4;
  const sr = data[startIdx];
  const sg = data[startIdx + 1];
  const sb = data[startIdx + 2];

  const matches = (i: number): boolean => {
    const di = i * 4;
    return (
      Math.abs(data[di] - sr) <= tolerance &&
      Math.abs(data[di + 1] - sg) <= tolerance &&
      Math.abs(data[di + 2] - sb) <= tolerance
    );
  };

  if (contiguous) {
    const queue: number[] = [startY * width + startX];
    mask[startY * width + startX] = 1;
    let head = 0;
    while (head < queue.length) {
      const idx = queue[head++];
      const x = idx % width;
      const y = (idx - x) / width;
      const neighbors: [number, number][] = [
        [x + 1, y],
        [x - 1, y],
        [x, y + 1],
        [x, y - 1],
      ];
      for (const [nx, ny] of neighbors) {
        if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
        const nidx = ny * width + nx;
        if (mask[nidx]) continue;
        if (matches(nidx)) {
          mask[nidx] = 1;
          queue.push(nidx);
        }
      }
    }
  } else {
    for (let i = 0; i < width * height; i++) {
      if (matches(i)) mask[i] = 1;
    }
  }

  return mask;
}

/**
 * Trace the boundary contours of a binary mask. Walks pixel-corner edges
 * between selected and unselected cells, then stitches edges into closed
 * polygons. Returns one polygon per disconnected region (and one per hole).
 */
function traceContours(mask: Uint8Array, w: number, h: number): { x: number; y: number }[][] {
  const isSelected = (x: number, y: number): boolean =>
    x >= 0 && x < w && y >= 0 && y < h && mask[y * w + x] === 1;

  type Pt = { x: number; y: number };
  const ptKey = (p: Pt) => `${p.x},${p.y}`;
  const edgeKey = (a: Pt, b: Pt) =>
    a.x < b.x || (a.x === b.x && a.y < b.y)
      ? `${a.x},${a.y}-${b.x},${b.y}`
      : `${b.x},${b.y}-${a.x},${a.y}`;

  const edges: [Pt, Pt][] = [];
  const adj = new Map<string, [Pt, Pt][]>();

  const addEdge = (a: Pt, b: Pt) => {
    const e: [Pt, Pt] = [a, b];
    edges.push(e);
    const ka = ptKey(a);
    const kb = ptKey(b);
    if (!adj.has(ka)) adj.set(ka, []);
    if (!adj.has(kb)) adj.set(kb, []);
    adj.get(ka)!.push(e);
    adj.get(kb)!.push(e);
  };

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!isSelected(x, y)) continue;
      if (!isSelected(x, y - 1)) addEdge({ x, y }, { x: x + 1, y });
      if (!isSelected(x + 1, y)) addEdge({ x: x + 1, y }, { x: x + 1, y: y + 1 });
      if (!isSelected(x, y + 1)) addEdge({ x, y: y + 1 }, { x: x + 1, y: y + 1 });
      if (!isSelected(x - 1, y)) addEdge({ x, y }, { x, y: y + 1 });
    }
  }

  const visited = new Set<string>();
  const contours: Pt[][] = [];

  for (const startEdge of edges) {
    if (visited.has(edgeKey(startEdge[0], startEdge[1]))) continue;
    const polygon: Pt[] = [startEdge[0]];
    let currentEdge = startEdge;
    let currentPoint = startEdge[0];
    while (true) {
      visited.add(edgeKey(currentEdge[0], currentEdge[1]));
      const nextPoint =
        currentEdge[0].x === currentPoint.x && currentEdge[0].y === currentPoint.y
          ? currentEdge[1]
          : currentEdge[0];
      polygon.push(nextPoint);
      const candidates = adj.get(ptKey(nextPoint)) || [];
      const nextEdge = candidates.find((e) => !visited.has(edgeKey(e[0], e[1])));
      if (!nextEdge) break;
      currentEdge = nextEdge;
      currentPoint = nextPoint;
    }
    if (polygon.length >= 3) contours.push(polygon);
  }

  return contours;
}

/** Translate a `ShapeSettings.fill` value into a Fabric-compatible
 *  fill — string for solid colors, `fabric.Gradient` for linear /
 *  radial. Plain hex strings (the legacy/most-common shape) pass
 *  through unchanged. The gradient is built in normalized coords
 *  (0..1) over the shape's bounding box via `gradientUnits:
 *  'percentage'`, so the same value renders consistently across
 *  rect/circle/triangle without us having to plumb width/height
 *  through every call site. */
type FabricShapeFill = string | fabric.Gradient<'linear'> | fabric.Gradient<'radial'>;

function resolveShapeFill(fill: string | ShapeFillValue): FabricShapeFill {
  if (typeof fill === 'string') return fill;
  if (fill.type === 'solid') return fill.color;

  const colorStops = [...fill.stops]
    .sort((a, b) => a.offset - b.offset)
    .map((s) => ({ offset: s.offset, color: s.color }));

  if (fill.type === 'linear') {
    const { dx, dy } = linearAngleToUnitVector(fill.angle);
    return new fabric.Gradient<'linear'>({
      type: 'linear',
      gradientUnits: 'percentage',
      coords: { x1: 0.5 - dx, y1: 0.5 - dy, x2: 0.5 + dx, y2: 0.5 + dy },
      colorStops,
    });
  }

  // Radial — center at 0.5,0.5; outer radius 0.5 (so it just fills
  // the shape's bounding box). Inner radius 0 = a single point.
  return new fabric.Gradient<'radial'>({
    type: 'radial',
    gradientUnits: 'percentage',
    coords: { x1: 0.5, y1: 0.5, r1: 0, x2: 0.5, y2: 0.5, r2: 0.5 },
    colorStops,
  });
}

/** Read a Fabric fill back into a `ShapeFillValue`. Round-trips
 *  whatever `resolveShapeFill` produced (within rounding error on
 *  the angle). Falls back to `'transparent'` for non-string,
 *  non-Gradient fills (e.g., Pattern), which the panel can render
 *  but not edit. */
function readShapeFill(fill: fabric.FabricObject['fill']): string | ShapeFillValue {
  if (typeof fill === 'string') return fill;
  if (fill instanceof fabric.Gradient) {
    const stops = ((fill.colorStops ?? []) as Array<{ offset: number; color: string }>).map(
      (s) => ({ offset: s.offset, color: s.color }),
    );
    if (fill.type === 'linear') {
      const c = fill.coords as { x1: number; y1: number; x2: number; y2: number };
      const angle = unitVectorToLinearAngle(c.x2 - c.x1, c.y2 - c.y1);
      return { type: 'linear', angle: Math.round(angle), stops };
    }
    return { type: 'radial', stops };
  }
  return 'transparent';
}

/** Convert a list of contour vertex arrays into a single fabric.Path
 *  with `fillRule: 'evenodd'` so nested contours render as holes
 *  rather than overlapping fills. Returns null when there are no
 *  drawable contours.
 *
 *  Why a single Path: traceContours can return an outer perimeter
 *  plus one or more inner silhouettes (e.g., after invert, or for
 *  masks with holes like a person standing with arm akimbo).
 *  Rendering each contour as its own fabric.Polygon with a
 *  translucent fill caused the holes to fill IN ON TOP of the outer
 *  fill — the user reads that as "everything is selected" rather
 *  than "the donut between contours is selected." A Path with
 *  evenodd fill correctly subtracts inner contours from outer ones,
 *  matching what Photoshop / Affinity show. */
function buildMarqueePath(contours: { x: number; y: number }[][]): fabric.Path | null {
  const d = contoursToSvgPath(contours);
  if (!d) return null;
  const path = new fabric.Path(d, {
    ...MARQUEE_STYLE,
    fillRule: 'evenodd',
    objectCaching: false,
  });
  return path;
}

function buildCropRect(canvas: fabric.Canvas, width: number, height: number): fabric.Rect {
  const zoom = canvas.getZoom() || 1;
  const rect = new fabric.Rect({
    ...CROP_RECT_STYLE,
    left: 0,
    top: 0,
    width,
    height,
    strokeWidth: 1 / zoom,
    strokeDashArray: [5 / zoom, 3 / zoom],
  });
  (rect as unknown as { _isCropRect: boolean })._isCropRect = true;
  return rect;
}

function tagAsMarquee(obj: fabric.Object): void {
  (obj as unknown as { _isMarquee: boolean })._isMarquee = true;
}

function isMarqueeObject(obj: fabric.Object): boolean {
  return (obj as unknown as { _isMarquee?: boolean })._isMarquee === true;
}

function setObjectLayerId(obj: fabric.Object, layerId: string): void {
  (obj as unknown as { layerId: string }).layerId = layerId;
}

function getObjectLayerId(obj: fabric.Object): string | undefined {
  return (obj as unknown as { layerId?: string }).layerId;
}

/**
 * Build a curved Fabric Path that text can follow. The slider value (-100..100)
 * scales an apex offset proportional to the text width; positive arches up
 * (smile), negative arches down (frown). Returns null for a flat (0) value or
 * non-positive width.
 */
function buildTextArchPath(width: number, arch: number): fabric.Path | null {
  if (arch === 0 || width <= 0) return null;
  // Apex height ≈ 25% of text width at full slider; control point is twice
  // the apex offset from the chord (quadratic-bezier midpoint property).
  const apex = (arch / 100) * (width * 0.25);
  const controlY = -apex * 2;
  const pathData = `M 0 0 Q ${width / 2} ${controlY} ${width} 0`;
  const path = new fabric.Path(pathData, {
    fill: '',
    stroke: '',
    objectCaching: false,
    visible: false,
  });
  return path;
}

function tagAsBackground(obj: fabric.Object): void {
  (obj as unknown as { _isBackground: boolean })._isBackground = true;
}

function isBackgroundImage(obj: fabric.Object): boolean {
  return (obj as unknown as { _isBackground?: boolean })._isBackground === true;
}

/** True when `obj` is a paint-surface OVERLAY created by
 *  `ensurePaintSurface` for a non-bg image layer (tagged with
 *  `id: 'paint_<layerId>'`). These overlays share the underlying
 *  layer's id-tag so brush/retouch handlers can find their write
 *  target by layerId, but they are doc-sized transparent
 *  FabricImages — NOT user-selectable layer content. The bg
 *  paint surface is the bg image itself (no separate overlay)
 *  so this helper returns false for it. */
function isPaintOverlay(obj: fabric.Object): boolean {
  const id = (obj as fabric.Object & { id?: string }).id;
  return typeof id === 'string' && id.startsWith('paint_');
}

/** Custom property name used to attach the user-painted RAW alpha
 *  canvas to a mask FabricImage. The clipPath FabricImage's element
 *  is the EFFECTIVE alpha (post feather/invert/density); the RAW
 *  canvas is the source of truth that brush strokes paint into and
 *  that materialization derives the effective from.
 *
 *  Lazy allocation: sessions saved before the materialization split
 *  shipped only have the effective canvas (which equalled raw at the
 *  time, since slow-path features were no-ops). On first paint /
 *  config change after load, `ensureRawMaskCanvas` copies the
 *  effective into a fresh raw — perfect in the common case where
 *  config hasn't changed, lossy only when the user adjusts feather/
 *  invert/density after a reload of a session that had non-default
 *  config baked in. Acceptable v1 trade-off; the alternative is a
 *  parallel persisted FabricImage which complicates every layer-walk
 *  call site (see `_isQuickMaskOverlay` filtering pattern). */
const RAW_MASK_CANVAS_KEY = '_fiasRawMaskCanvas';

type MaskFabricImageWithRaw = fabric.FabricImage & {
  [RAW_MASK_CANVAS_KEY]?: HTMLCanvasElement;
};

function readRawMaskCanvas(cp: fabric.Object | undefined): HTMLCanvasElement | null {
  if (!(cp instanceof fabric.FabricImage)) return null;
  const raw = (cp as MaskFabricImageWithRaw)[RAW_MASK_CANVAS_KEY];
  return raw instanceof HTMLCanvasElement ? raw : null;
}

function attachRawMaskCanvas(cp: fabric.FabricImage, raw: HTMLCanvasElement): void {
  (cp as MaskFabricImageWithRaw)[RAW_MASK_CANVAS_KEY] = raw;
}

/** Defensive fallback for paths that race ahead of
 *  `applyLayerOrder`'s rehydration pass — under normal flow every
 *  mask gets raw attached at creation (`addLayerMask` /
 *  `applyLayerMaskFromCanvas`) or at rehydration (post-load /
 *  post-undo). When this lazy path fires, it copies the current
 *  effective into raw; the resulting raw is lossy when the saved
 *  effective had non-default config baked in (user can't recover
 *  crisp edges from a feathered-then-toggled-back state). Logged
 *  once per process in dev so the brittle path surfaces rather
 *  than silently degrading. */
let warnedAboutLazyRawAllocation = false;
function ensureRawMaskCanvas(cp: fabric.FabricImage): HTMLCanvasElement | null {
  const existing = readRawMaskCanvas(cp);
  if (existing) return existing;
  const el = cp.getElement();
  if (!(el instanceof HTMLCanvasElement)) return null;
  const raw = document.createElement('canvas');
  raw.width = el.width;
  raw.height = el.height;
  const ctx = raw.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(el, 0, 0);
  attachRawMaskCanvas(cp, raw);
  if (import.meta.env.DEV && !warnedAboutLazyRawAllocation) {
    warnedAboutLazyRawAllocation = true;
    console.warn(
      '[image-editor] mask clipPath had no raw canvas at first access — ' +
        'lazily allocated from effective. Soft failure (mask still renders) ' +
        'but indicates `applyLayerOrder` rehydration ran late or was bypassed.',
    );
  }
  return raw;
}

/** Override the clipPath FabricImage's `toObject` so it serializes
 *  RAW instead of EFFECTIVE. Critical for lossless session save/load
 *  and undo/redo: without this, every snapshot bakes the current
 *  feather/invert/density into the canvas pixels and toggling those
 *  fields back to default after restore can't recover the original
 *  edges. The swap is in-place + reverted before return, so live
 *  rendering during a save (rare but possible) sees effective the
 *  whole time. Idempotent — flag prevents double-install on
 *  applyLayerOrder re-entries.
 *
 *  Per-instance override rather than a Fabric class registration:
 *  loadFromJSON rebuilds objects from base classes, so the override
 *  must be reinstalled on each fresh FabricImage that comes out of
 *  rehydration — done by the rehydration loop alongside raw
 *  reattachment. */
type MaskClipPathWithSerializer = fabric.FabricImage & {
  __fiasMaskSerializer?: boolean;
};
function installMaskClipPathSerializer(cp: fabric.FabricImage): void {
  const tagged = cp as MaskClipPathWithSerializer;
  if (tagged.__fiasMaskSerializer) return;
  const baseToObject = cp.toObject.bind(cp) as (
    propertiesToInclude?: string[],
  ) => Record<string, unknown>;
  cp.toObject = function (
    this: fabric.FabricImage,
    propertiesToInclude?: string[],
  ): Record<string, unknown> {
    const raw = readRawMaskCanvas(this);
    if (!raw) return baseToObject(propertiesToInclude);
    const savedElement = this.getElement();
    this.setElement(raw);
    try {
      return baseToObject(propertiesToInclude);
    } finally {
      this.setElement(savedElement as HTMLImageElement | HTMLCanvasElement);
    }
  } as typeof cp.toObject;
  tagged.__fiasMaskSerializer = true;
}

// `ensureCanvasElement` + `resolveOrRegisterPaintSurface` live in
// `../lib/paint-surface` so they're unit-testable without the
// Fabric runtime — see that file for the rationale.

/**
 * Convert a stroke render (where any non-transparent pixel is part of the
 * stroke shape) into an alpha mask whose every stroke pixel is opaque at
 * `opacity` (0..1). Used by the eraser bake to decouple the *preview*
 * color's alpha (a fixed semi-transparent gray, picked for visibility on
 * any background) from the bake's actual erase strength.
 *
 * `opacity = 1` produces a fully-opaque mask → destination-out fully
 * erases. `opacity = 0.5` halves the alpha removed at each pixel. The
 * input stroke shape is preserved; only the alpha values are normalized.
 */
function buildEraserMask(strokeEl: HTMLCanvasElement, opacity: number): HTMLCanvasElement {
  const w = strokeEl.width;
  const h = strokeEl.height;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) return out;
  ctx.drawImage(strokeEl, 0, 0);
  const data = ctx.getImageData(0, 0, w, h);
  const targetAlpha = Math.round(Math.max(0, Math.min(1, opacity)) * 255);
  const px = data.data;
  for (let i = 3; i < px.length; i += 4) {
    if (px[i] > 0) px[i] = targetAlpha;
  }
  ctx.putImageData(data, 0, 0);
  return out;
}

/**
 * Apply the active marquee's geometry as a clip on the given 2D context.
 * Wand selections use their cached binary mask via destination-in compositing
 * (caller must do that on a temp canvas — see `bakeStrokeOntoSurface`).
 *
 * Returns true if a clip was applied; false if no marquee is active or its
 * geometry isn't usable.
 */
function applyMarqueeClipToContext(
  ctx: CanvasRenderingContext2D,
  m: {
    shape: MarqueeShape;
    obj: fabric.Object | null;
    points: { x: number; y: number }[];
    isDrawing: boolean;
  },
  /** Scene-space offset of the surface canvas's (0, 0). When the
   *  surface is the bg image or a doc-aligned paint overlay this
   *  is (0, 0) and the marquee geometry is used unmodified. For
   *  a non-bg image-backed paint surface (paste / rasterize), the
   *  surface canvas is at the layer's scene `(left, top)` — the
   *  marquee path needs to shift by `-offset` so the clip lands
   *  on the right canvas-local pixels. */
  offsetX: number = 0,
  offsetY: number = 0,
): boolean {
  if (!m.obj || m.isDrawing) return false;
  ctx.beginPath();
  if (m.shape === 'rect') {
    const r = m.obj as fabric.Rect;
    ctx.rect((r.left ?? 0) - offsetX, (r.top ?? 0) - offsetY, r.width ?? 0, r.height ?? 0);
  } else if (m.shape === 'ellipse') {
    const e = m.obj as fabric.Ellipse;
    const rx = e.rx ?? 0;
    const ry = e.ry ?? 0;
    ctx.ellipse(
      (e.left ?? 0) + rx - offsetX,
      (e.top ?? 0) + ry - offsetY,
      rx,
      ry,
      0,
      0,
      Math.PI * 2,
    );
  } else if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 3) {
    ctx.moveTo(m.points[0].x - offsetX, m.points[0].y - offsetY);
    for (let i = 1; i < m.points.length; i++) {
      ctx.lineTo(m.points[i].x - offsetX, m.points[i].y - offsetY);
    }
    ctx.closePath();
  } else {
    // wand handled via caller's destination-in path; fallthrough returns false
    return false;
  }
  ctx.clip();
  return true;
}

/**
 * Rasterize a finalized marquee selection to a binary mask of size
 * `width × height`. `mask[y*width+x] === 1` iff scene pixel (x,y) is inside
 * the marquee. Returns null if the marquee isn't finalized or its geometry
 * isn't usable. Used by the fill tool to constrain flood-fill results to
 * the marquee region.
 */
function buildMarqueeMask(
  m: {
    shape: MarqueeShape;
    obj: fabric.Object | null;
    extraObjs: fabric.Object[];
    points: { x: number; y: number }[];
    isDrawing: boolean;
    wandMaskCanvas: HTMLCanvasElement | null;
  },
  width: number,
  height: number,
): Uint8Array | null {
  if (!m.obj || m.isDrawing) return null;

  // Wand: alpha channel of the cached mask canvas IS the binary mask.
  if (m.shape === 'wand' && m.wandMaskCanvas) {
    const ctx = m.wandMaskCanvas.getContext('2d');
    if (!ctx) return null;
    const data = ctx.getImageData(0, 0, width, height).data;
    const mask = new Uint8Array(width * height);
    for (let i = 0; i < mask.length; i++) {
      mask[i] = data[i * 4 + 3] > 0 ? 1 : 0;
    }
    return mask;
  }

  // Other shapes (rect / ellipse / lasso / polygon): rasterize by filling
  // the clipped region on a fresh canvas, then read its alpha channel.
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  const clipped = applyMarqueeClipToContext(ctx, m);
  if (!clipped) return null;
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, width, height);
  const data = ctx.getImageData(0, 0, width, height).data;
  const mask = new Uint8Array(width * height);
  for (let i = 0; i < mask.length; i++) {
    mask[i] = data[i * 4 + 3] > 0 ? 1 : 0;
  }
  return mask;
}

/** Tools that should preserve the active marquee when switched to. */
const MARQUEE_PRESERVING_TOOLS: ReadonlySet<EditorTool> = new Set<EditorTool>([
  'marquee',
  'move',
  'draw',
  'eraser',
  'fill',
  'retouch',
  'ai',
]);

/** Tools that route ALL mouse events through their own canvas-
 *  level handlers and need every layer locked (selectable=false,
 *  evented=false) so Fabric's hit-test doesn't run Move-tool drag
 *  in parallel. Consulted by `selectLayerObjects` /
 *  `selectLayersObjects` so panel-side layer clicks don't undo
 *  the lock that `setTool` installed. */
const TOOLS_REQUIRING_LOCKED_LAYERS: ReadonlySet<EditorTool> = new Set<EditorTool>([
  'marquee',
  'retouch',
]);

/**
 * Counter-scale stroke width and dash pattern by the current canvas zoom so the
 * marquee outline stays at a constant screen-pixel size regardless of zoom level.
 * Without this, dashed strokes shrink to fractions of a pixel when zoomed out.
 */
function applyMarqueeStroke(canvas: fabric.Canvas, obj: fabric.Object): void {
  const zoom = canvas.getZoom() || 1;
  obj.set({
    strokeWidth: MARQUEE_STROKE_SCREEN_PX / zoom,
    strokeDashArray: MARQUEE_DASH_SCREEN_PX.map((v) => v / zoom),
  });
}

const POLYGON_CLOSE_THRESHOLD_PX = 10;

interface UseFabricCanvasOptions {
  canvasElRef: React.RefObject<HTMLCanvasElement | null>;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  onObjectModified?: () => void;
  onSelectionChanged?: (objectIds: string[]) => void;
  /** Fired when an object on the canvas becomes the active object — receives
   *  the layerId of that object (or null if it has none). Not fired on clears. */
  onLayerSelected?: (layerId: string | null) => void;
  /** Fired when the user clicks an empty area while the Shape tool is active.
   *  EditorPage handles by creating a layer and adding a new shape at the
   *  click position. */
  onShapeToolClick?: (point: { x: number; y: number }) => void;
  /** Same idea for the Text tool — fired on empty-area click. */
  onTextToolClick?: (point: { x: number; y: number }) => void;
  /** Fired when the pen tool finalizes a path — the user pressed
   *  Enter, double-clicked, or clicked back on the first anchor to
   *  close. EditorPage creates a vector LayerNode + mounts the
   *  Fabric.Path on the canvas. The hook discards its preview
   *  state immediately before invoking; callers should treat the
   *  passed-in path as immutable. */
  onPenToolFinalize?: (path: VectorPath, options: { closed: boolean }) => void;
  /** Fired when the Path Edit overlay's selected anchor changes
   *  (canvas-side click selects / deselects). EditorPage mirrors
   *  the new index into `state.pathEditSelectedAnchorIndex` so the
   *  EditPathPanel can render the type radio for the right anchor. */
  onPathEditSelectionChanged?: (anchorIndex: number | null) => void;
  onCanvasDimensions?: (width: number, height: number) => void;
  onPathCreated?: () => void;
  onZoomChanged?: (zoom: number) => void;
  /** Fired whenever the Clone Stamp source point changes (set or
   *  cleared). EditorPage mirrors this into React state so the
   *  RetouchPanel can show "Source: (x, y)" or "Source: not set." */
  onCloneSourceChanged?: (point: { x: number; y: number } | null) => void;
  /** Fired when the user attempts a Clone stroke without a source
   *  set — gives the panel a chance to surface a hint. */
  onCloneStrokeRejected?: () => void;
}

function closePolygonMarquee(
  canvas: fabric.Canvas,
  m: {
    obj: fabric.Object | null;
    points: { x: number; y: number }[];
    isDrawing: boolean;
  },
  color: string,
): void {
  if (m.obj) canvas.remove(m.obj);
  const closed = new fabric.Polygon(m.points, MARQUEE_STYLE);
  tagAsMarquee(closed);
  applyMarqueeColor(closed, color);
  applyMarqueeStroke(canvas, closed);
  canvas.add(closed);
  m.obj = closed;
  m.isDrawing = false;
}

/**
 * Recreate the live-drawing polyline preview with the latest points. Mutating
 * `points` on an existing Polyline doesn't recalculate its bounding box, so
 * new points outside the original bbox get clipped on render. Replacing the
 * object outright keeps the preview in sync with the captured path.
 */
function replaceMarqueePolyline(
  canvas: fabric.Canvas,
  m: {
    obj: fabric.Object | null;
    points: { x: number; y: number }[];
  },
  color: string,
): void {
  if (m.obj) canvas.remove(m.obj);
  const poly = new fabric.Polyline(m.points, MARQUEE_STYLE);
  tagAsMarquee(poly);
  applyMarqueeColor(poly, color);
  applyMarqueeStroke(canvas, poly);
  canvas.add(poly);
  m.obj = poly;
}

function clientPointFromEvent(e: Event): { x: number; y: number } {
  if ('touches' in e && (e as TouchEvent).touches.length > 0) {
    const t = (e as TouchEvent).touches[0];
    return { x: t.clientX, y: t.clientY };
  }
  if ('changedTouches' in e && (e as TouchEvent).changedTouches.length > 0) {
    const t = (e as TouchEvent).changedTouches[0];
    return { x: t.clientX, y: t.clientY };
  }
  const me = e as MouseEvent;
  return { x: me.clientX ?? 0, y: me.clientY ?? 0 };
}

/** Viewport info for scrollbar indicators */
export interface ViewportInfo {
  thumbWidthRatio: number;
  thumbHeightRatio: number;
  scrollXRatio: number;
  scrollYRatio: number;
  showH: boolean;
  showV: boolean;
}

export function useFabricCanvas({
  canvasElRef,
  wrapperRef,
  onObjectModified,
  onSelectionChanged,
  onLayerSelected,
  onShapeToolClick,
  onTextToolClick,
  onPenToolFinalize,
  onPathEditSelectionChanged,
  onCanvasDimensions,
  onPathCreated,
  onZoomChanged,
  onCloneSourceChanged,
  onCloneStrokeRejected,
}: UseFabricCanvasOptions) {
  const canvasRef = useRef<fabric.Canvas | null>(null);
  const backgroundImageRef = useRef<fabric.FabricImage | null>(null);
  const imageDimsRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });
  const activeToolRef = useRef<EditorTool>('move');
  const marqueeRef = useRef<{
    shape: MarqueeShape;
    obj: fabric.Object | null;
    /** Additional contour polygons for shapes (like wand) that produce multiple regions. */
    extraObjs: fabric.Object[];
    /** Cached binary mask canvas for wand selections — used by the eraser path. */
    wandMaskCanvas: HTMLCanvasElement | null;
    /** Wand-mode tolerance and contiguous flag, latched at click time. */
    wandTolerance: number;
    wandContiguous: boolean;
    points: { x: number; y: number }[];
    startX: number;
    startY: number;
    isDrawing: boolean;
  }>({
    shape: 'rect',
    obj: null,
    extraObjs: [],
    wandMaskCanvas: null,
    wandTolerance: 32,
    wandContiguous: true,
    points: [],
    startX: 0,
    startY: 0,
    isDrawing: false,
  });
  const panRef = useRef<{ isPanning: boolean; lastX: number; lastY: number }>({
    isPanning: false,
    lastX: 0,
    lastY: 0,
  });
  const pinchRef = useRef<{ isPinching: boolean; startDist: number; startZoom: number }>({
    isPinching: false,
    startDist: 0,
    startZoom: 1,
  });
  const forceSquareRef = useRef(false);
  const marqueeColorRef = useRef(MARQUEE_DEFAULT_COLOR);
  const wandToleranceRef = useRef(32);
  const wandContiguousRef = useRef(true);
  const cropRectRef = useRef<fabric.Rect | null>(null);
  const activeLayerIdRef = useRef<string | null>(null);
  /** Edit-target snapshot kept in sync from React state via a
   *  setter helper. Read inside the brush stroke listener (which
   *  fires via Fabric event, outside the React render lifecycle).
   *  Default 'layer' for any layer not in the map. */
  const editTargetsRef = useRef<Record<string, 'layer' | 'mask'>>({});
  /** Latest mask config per layer, mirrored from `state.document` by
   *  `EditorPage` via `setMaskConfig`. `bakeStrokeOntoMask` reads this
   *  to materialize the post-stroke effective alpha with the current
   *  feather/invert/density baked in — without this mirror, in-stroke
   *  feedback would lose those features (the stroke would only
   *  appear after the next document-change tick). Empty map means
   *  every layer is treated as identity (the addLayerMask defaults).
   *  Map deletion happens in `removeLayerMask`. */
  const maskConfigsRef = useRef<Map<string, LayerMask>>(new Map());
  /** Set of text layer ids currently bound to a vector layer's
   *  path (Tier 2 Group A PR-E). Mirrored from `state.document` by
   *  EditorPage's text-path-sync effect via `setTextPathBinding`.
   *  `updateActiveText` reads this so font-size / style edits don't
   *  clobber the bound `fabric.IText.path` with an arch-derived
   *  path on the same dispatch — when the layer is bound, the
   *  `.path` slot is managed exclusively by the sync effect. */
  const textPathBoundLayerIdsRef = useRef<Set<string>>(new Set());

  // Clone Stamp tool state. Source point lives in scene coords
  // (so it survives viewport pan/zoom). `armed` flips true when
  // the user clicks the "Set Source" button — the next canvas
  // tap then records the source and disarms. The stroke-time
  // state (offset, source snapshot, brush mask) is rebuilt on
  // each mouse:down inside the stroke handler.
  const cloneSourceRef = useRef<{ x: number; y: number } | null>(null);
  const cloneArmedRef = useRef(false);
  /** Aligned-mode offset persisted across strokes. Set on the
   *  first stroke after Set Source; cleared on next Set Source.
   *  When non-null AND `clone.aligned === true` at stroke start,
   *  the offset takes precedence over the freshly-computed
   *  `source - paintStart` so subsequent strokes resume sampling
   *  at the offset the user last left off at — Photoshop's
   *  Aligned behavior. */
  const cloneAlignedOffsetRef = useRef<{ x: number; y: number } | null>(null);
  /** Per-stroke clone state. Built on mouse:down when a stroke
   *  begins, cleared on mouse:up. The offset is `source - paintStart`,
   *  so each subsequent paint sample at P pulls source pixels from
   *  `P + offset`. The source snapshot is captured at stroke start
   *  so subsequent paint sampling is fast (no compositing per
   *  stamp). */
  const cloneStrokeRef = useRef<{
    offset: { x: number; y: number };
    sourceSnapshot: HTMLCanvasElement;
    brushMask: HTMLCanvasElement;
    /** Cached ImageData of the brush mask. The Heal post-process
     *  reads it to weight per-pixel averages — getImageData per
     *  stamp would be wasteful since the mask is constant for
     *  the whole stroke. Cached lazily on first need. */
    brushMaskImageData: ImageData | null;
    radius: number;
    /** When true (Aligned mode), the offset persists across
     *  release+restroke. When false, each new stroke restarts
     *  from `source` as if the paintStart were fresh. */
    aligned: boolean;
    /** Per-stroke alpha: opacity × flow, both 0–100, normalized
     *  to 0–1. */
    strokeAlpha: number;
    /** Which Retouch variant owns this stroke. Both Clone and
     *  Heal share the source-set + stamp pipeline; Heal layers a
     *  per-stamp tone correction on top so the cloned texture
     *  blends with the destination's lighting. */
    variant: 'clone' | 'heal';
    /** Heal mode captured at stroke start so a mid-stroke
     *  toggle doesn't produce inconsistent dabs. `normal` runs
     *  the additive tone shift; `replace` skips it (becomes
     *  Clone-equivalent stamp invoked from the Heal variant).
     *  Ignored for `variant === 'clone'`. */
    healMode: 'normal' | 'replace';
  } | null>(null);
  /** Per-stroke Smudge state. Built on mouse:down and cleared on
   *  mouse:up. The sampleBuffer is the trailing pixel patch that
   *  the smudge "finger" carries from one dab to the next — at
   *  each dab we mix it with the destination patch, write the
   *  mixed result onto the surface, then overwrite the sample
   *  buffer with that result so the next dab drags the freshly-
   *  smeared content forward. Smudge has no source-set state, so
   *  none of the clone-namespace refs apply.
   *
   *  Strength = 0 → no effect. Strength = 1 → sample fully
   *  overwrites at the brush center (mask-weighted toward the
   *  periphery). Stroke alpha (opacity × flow) further scales the
   *  per-dab change at the destination level. */
  const smudgeStrokeRef = useRef<{
    sampleBuffer: HTMLCanvasElement;
    brushMask: HTMLCanvasElement;
    /** Cached ImageData of the brush mask — read once per stroke
     *  and reused for every dab's per-pixel mix. */
    brushMaskImageData: ImageData | null;
    radius: number;
    /** 0–1, copy of `retouchSettings.smudge.strength / 100` taken
     *  at stroke start. */
    strength: number;
    /** opacity × flow, both 0–100, normalized to 0–1. */
    strokeAlpha: number;
  } | null>(null);
  /** Per-stroke Blur state. Built on mouse:down and cleared on
   *  mouse:up. Unlike Smudge there's no carry-forward sample
   *  buffer — each dab re-reads the dest patch, blurs it fresh,
   *  and mixes the blur into the dest by Strength. Repeated dabs
   *  on the same area accumulate (the just-blurred pixels become
   *  the source of the NEXT blur), so the user can build up
   *  smoothness by dragging back and forth. Strength is captured
   *  at stroke start so a mid-stroke slider drag doesn't change
   *  the per-dab feel. */
  const blurStrokeRef = useRef<{
    brushMask: HTMLCanvasElement;
    /** Cached ImageData of the brush mask — read once per stroke. */
    brushMaskImageData: ImageData | null;
    radius: number;
    /** Gaussian sigma for the per-dab blur, in pixels. Derived
     *  from brush radius at stroke start (kernel size scales with
     *  brush size, matching Photoshop semantics — Strength
     *  controls mix amount, not kernel size). */
    sigma: number;
    /** 0–1, copy of `retouchSettings.blur.strength / 100`. */
    strength: number;
    /** opacity × flow, both 0–100, normalized to 0–1. */
    strokeAlpha: number;
  } | null>(null);
  /** Per-stroke state for the Sharpen variant. Same shape as
   *  `blurStrokeRef` — sharpen is unsharp-mask, which requires
   *  the same Gaussian-of-the-dest-patch that Blur computes, so
   *  it inherits radius/sigma/strength/strokeAlpha verbatim. The
   *  difference is only what we mix BACK into the surface: Blur
   *  uses the blurred patch directly, Sharpen uses
   *  `dest + (dest - blurred)` (the high-frequency detail
   *  amplified by 1×). Repeated dabs accumulate detail because
   *  each dab re-reads the (slightly sharpened) dest. */
  const sharpenStrokeRef = useRef<{
    brushMask: HTMLCanvasElement;
    brushMaskImageData: ImageData | null;
    radius: number;
    sigma: number;
    /** 0–1, copy of `retouchSettings.sharpen.strength / 100`. */
    strength: number;
    strokeAlpha: number;
    /** Per-stroke cumulative brush-mask-alpha applied at each
     *  pixel of the active layer surface (Uint8, 0–255 = no
     *  capacity used → fully consumed). Sharpen is unbounded
     *  under recursion (`2*dest - blurred` amplifies high-freq
     *  content on each pass), so without a cap a normal drag
     *  blows out within ~5 overlapping dabs. We cap at one full
     *  mask-weighted dose per pixel per stroke — Photoshop's
     *  Sharpen-tool behavior: a drag sharpens each region once;
     *  to push further the user releases and re-drags. Sized
     *  lazily on the first dab to match `target.width × height`,
     *  cleared on mouse:up. */
    appliedMask: Uint8Array | null;
    appliedMaskW: number;
    appliedMaskH: number;
  } | null>(null);
  /** Per-stroke state for the Dodge variant. Lightens pixels under
   *  the brush by `(255 - orig) * exposure * rangeWeight`, where
   *  rangeWeight is a luminance-band curve (shadows / midtones /
   *  highlights). Two limiters apply:
   *   - **Brush spacing** (radius × DODGE_BURN_SPACING_RATIO):
   *     skips zero-effect dabs on hold-still and reduces compute
   *     during slow drags.
   *   - **Per-stroke cap** (`appliedMask`): each pixel can absorb
   *     at most one full brush-mask-alpha dose per stroke, even
   *     if the cursor passes over it many times. Without this,
   *     a single drag at exposure=50% in shadows mode pushes
   *     dark pixels (L=0.1) all the way to ~107 (mid-gray) over
   *     8 overlapping dabs — drag became "gray draw tool", not
   *     "lift shadows a bit". Same Uint8Array pattern as
   *     Sharpen; allocated lazily on first dab, freed by nulling
   *     the ref on mouse:up. To layer more dodge on a region the
   *     user releases and re-strokes — additive across strokes,
   *     bounded within one stroke. */
  const dodgeStrokeRef = useRef<{
    brushMask: HTMLCanvasElement;
    brushMaskImageData: ImageData | null;
    radius: number;
    range: 'shadows' | 'midtones' | 'highlights';
    /** 0–1, copy of `retouchSettings.dodge.exposure / 100`. */
    exposure: number;
    strokeAlpha: number;
    /** Last point at which a dab was fired (scene coords); the
     *  next dab fires only if the cursor has moved at least
     *  `radius * DODGE_BURN_SPACING_RATIO` since this point. */
    lastDabX: number;
    lastDabY: number;
    /** Per-stroke cumulative brush-mask-alpha applied at each
     *  pixel of the active layer surface (Uint8, 255 = fully
     *  consumed). See type doc above for rationale. */
    appliedMask: Uint8Array | null;
    appliedMaskW: number;
    appliedMaskH: number;
  } | null>(null);
  /** Per-stroke state for the Burn variant. Same shape as
   *  `dodgeStrokeRef` — Burn is the multiplicative inverse of
   *  Dodge: `pixel * (1 - m)` per channel pushes toward 0
   *  instead of toward 255. Range curves, brush spacing, and
   *  per-stroke cap all behave identically to Dodge — see the
   *  Dodge type doc for the full rationale on why the cap is
   *  necessary even for "naturally bounded" math. */
  const burnStrokeRef = useRef<{
    brushMask: HTMLCanvasElement;
    brushMaskImageData: ImageData | null;
    radius: number;
    range: 'shadows' | 'midtones' | 'highlights';
    /** 0–1, copy of `retouchSettings.burn.exposure / 100`. */
    exposure: number;
    strokeAlpha: number;
    lastDabX: number;
    lastDabY: number;
    appliedMask: Uint8Array | null;
    appliedMaskW: number;
    appliedMaskH: number;
  } | null>(null);
  /** Per-stroke state for the Sponge variant. Shifts saturation
   *  (toward gray of same luminance for desaturate, away from it
   *  for saturate) without changing perceived brightness. Same
   *  brush-spacing + per-stroke cap pattern as Dodge/Burn — see
   *  the dodgeStrokeRef type doc for the full rationale. The
   *  cap matters most for *saturate* mode: the per-pixel formula
   *  `pixel + (pixel - L) * m` amplifies distance from the
   *  pixel's own luminance gray, so under recursion (overlapping
   *  dabs) it grows colors past 0/255 quickly and clips. */
  const spongeStrokeRef = useRef<{
    brushMask: HTMLCanvasElement;
    brushMaskImageData: ImageData | null;
    radius: number;
    mode: 'saturate' | 'desaturate';
    /** 0–1, copy of `retouchSettings.sponge.flow / 100` — the
     *  variant-specific Flow slider. Distinct from the tool-
     *  level `flow` (which combines with `opacity` into
     *  `strokeAlpha`). */
    flow: number;
    strokeAlpha: number;
    lastDabX: number;
    lastDabY: number;
    appliedMask: Uint8Array | null;
    appliedMaskW: number;
    appliedMaskH: number;
  } | null>(null);
  /** Mirror of `state.retouchSettings` from React, set via
   *  `setRetouchSettings`. Read inside Fabric mouse handlers. */
  const retouchSettingsRef = useRef<RetouchSettings | null>(null);
  /** Reticle Fabric object showing the current Clone source on
   *  the canvas. null when no source set or Clone variant not
   *  active. Stored as a flat array of `fabric.Rect` bars (no
   *  Group wrapper) plus the current center, so we can move all
   *  bars as a unit by tracking the center separately rather
   *  than reading each Rect's `.left` back to compute deltas. */
  const cloneReticleRef = useRef<{
    bars: fabric.Rect[];
    left: number;
    top: number;
  } | null>(null);

  /** Pen tool working state (Tier 2 Group A). The tool accumulates
   *  anchors across multiple mouse events before the user finalizes
   *  (Enter / double-click / click-on-first-anchor). The preview
   *  Fabric.Path is recreated whenever the path changes; on
   *  finalize, EditorPage creates a real vector LayerNode + mounts
   *  a persistent Fabric.Path via `addVectorObject`, and this ref
   *  is cleared.
   *
   *  Tag on the preview Fabric.Path: `_isPenPreview: true` — same
   *  pattern as `_isMarquee` / `_isCloneReticle` / etc. so the
   *  layer-order helper can keep it on top and so the cleanup step
   *  can identify orphans. */
  const penStateRef = useRef<{
    anchors: VectorAnchor[];
    previewObj: fabric.Path | null;
    /** Pen variant only: when mouse is held down with the just-
     *  placed anchor still being "dragged" — handles are being
     *  set by the drag vector. */
    draggingAnchorIndex: number | null;
    /** Freeform variant only: true while the user holds the mouse
     *  down sampling points. */
    freeformActive: boolean;
  } | null>(null);
  // The active variant is read fresh from `penSettingsRef.current`
  // at every event — caching it on session start would make
  // mid-draw chip-row changes silently no-op until the next path.
  /** Mirror of `state.penSettings` from EditorPage so the canvas-
   *  side mouse handlers see the latest variant + stroke + fill
   *  without re-binding the listeners. EditorPage calls
   *  `setPenSettings` whenever the panel changes. */
  const penSettingsRef = useRef<PenSettings>({
    variant: 'curvature',
    autoAddDelete: true,
    strokeColor: '#000000',
    strokeWidth: 2,
    fill: 'transparent',
    freeformMagnetic: false,
    freeformWidth: 50,
  });

  /** Quick Mask working state (Tier 1 Group D). The QM canvas is
   *  a full-image alpha mask — opaque-white where selected,
   *  transparent elsewhere. The overlay is a separate FabricImage
   *  tagged `_isQuickMaskOverlay` that renders on top of every
   *  layer, showing the un-selected area as a 50%-opaque red
   *  matte. Refs hold canvas-side state; React state holds only
   *  the boolean `quickMaskActive` (atomically toggled with the
   *  active tool — see useImageEditorState's reducer). */
  const quickMaskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const quickMaskOverlayRef = useRef<fabric.FabricImage | null>(null);
  /** Per-stroke state for Quick Mask painting. `mode='add'` paints
   *  white into the alpha mask (grows the selection); `mode='subtract'`
   *  paints alpha=0 (shrinks). Alt-held mid-stroke is detected on
   *  mouse:down and locked for the duration of the stroke — toggling
   *  add/subtract mid-stroke produces unpredictable results in
   *  Photoshop too, so we match that. */
  const quickMaskStrokeRef = useRef<{
    mode: 'add' | 'subtract';
    radius: number;
    hardness: number;
    opacity: number;
    lastX: number;
    lastY: number;
  } | null>(null);
  /** Default mode for new QM strokes, mirrored from React state via
   *  `setQuickMaskMode`. Alt held at mouse-down inverts whichever
   *  mode this carries (Photoshop convention). */
  const quickMaskModeRef = useRef<'add' | 'subtract'>('add');
  /** Forward-reference holders so the QM brush handlers (registered
   *  in the mount-time canvas-events effect) can call helpers that
   *  are defined later as `useCallback`s. Same pattern as
   *  `ensurePaintSurfaceRef`. */
  const paintQuickMaskDabRef = useRef<((x: number, y: number) => void) | null>(null);
  const refreshQuickMaskOverlayRef = useRef<(() => void) | null>(null);

  const brushColorRef = useRef('#000000');
  /** Width + opacity snapshot of the current brush settings,
   *  mirrored from React state. The Draw tool routes through
   *  Fabric's `freeDrawingBrush` (which has its own width/color)
   *  so reading-back from there worked; Quick Mask paints onto a
   *  separate alpha mask without involving Fabric's brush, so we
   *  need a direct reference. Updated by `updateBrush`. */
  const brushSizeRef = useRef(4);
  const brushOpacityRef = useRef(1);
  // Eraser strength as a fraction in [0, 1]. Decoupled from the eraser
  // brush's *preview* color (which is a fixed semi-transparent gray for
  // visibility) — the bake uses this value to set the alpha of the
  // destination-out mask, so 1.0 fully erases and 0 leaves pixels alone.
  // Mirrors brushSettings.opacity so the user has one stroke-strength
  // slider for both Draw and Eraser tools.
  const eraserOpacityRef = useRef(1);
  // Paint surfaces — one HTMLCanvasElement per paint-capable layer, wrapped
  // in a Fabric Image. New strokes render onto the active layer's surface.
  //
  // `offsetX` / `offsetY` is the scene-space position of the canvas's (0,0)
  // pixel. For the bg image and for empty/paint layers (added via the +
  // button), the surface canvas spans the document at identity transform —
  // offset is (0, 0) and scene coords == canvas pixel coords. For non-bg
  // image-backed layers (paste, image), the surface canvas IS the image's
  // intrinsic canvas, positioned at the image's scene (left, top); writes
  // at scene (sx, sy) translate to canvas pixel (sx − offsetX, sy − offsetY).
  // Layers with non-identity scale, rotation or skew are auto-rasterized to
  // an axis-aligned identity-scale FabricImage at first paint so this simple
  // translate is sufficient — no inverse matrix needed.
  const paintSurfacesRef = useRef(
    new Map<
      string,
      {
        canvas: HTMLCanvasElement;
        fabricImg: fabric.FabricImage;
        offsetX: number;
        offsetY: number;
      }
    >(),
  );
  // Forward-reference holder for ensurePaintSurface so callbacks defined
  // earlier in the file (e.g. deleteSelected) can call it without triggering
  // a use-before-declaration error.
  const ensurePaintSurfaceRef = useRef<
    | ((layerId: string) => {
        canvas: HTMLCanvasElement;
        fabricImg: fabric.FabricImage;
        offsetX: number;
        offsetY: number;
      } | null)
    | null
  >(null);
  // Mirror every parent-supplied callback into a ref so the init-only
  // useEffect (which sets up Fabric event handlers exactly once) always
  // dispatches to the latest closure. Without this, callers would have to
  // wrap each callback in a stable useCallback or risk stale state inside
  // the handler.
  const onZoomChangedRef = useRef(onZoomChanged);
  onZoomChangedRef.current = onZoomChanged;
  const onLayerSelectedRef = useRef(onLayerSelected);
  onLayerSelectedRef.current = onLayerSelected;
  const onShapeToolClickRef = useRef(onShapeToolClick);
  onShapeToolClickRef.current = onShapeToolClick;
  const onTextToolClickRef = useRef(onTextToolClick);
  onTextToolClickRef.current = onTextToolClick;
  const onPenToolFinalizeRef = useRef(onPenToolFinalize);
  onPenToolFinalizeRef.current = onPenToolFinalize;
  const onPathEditSelectionChangedRef = useRef(onPathEditSelectionChanged);
  onPathEditSelectionChangedRef.current = onPathEditSelectionChanged;
  const onObjectModifiedRef = useRef(onObjectModified);
  onObjectModifiedRef.current = onObjectModified;
  const onSelectionChangedRef = useRef(onSelectionChanged);
  onSelectionChangedRef.current = onSelectionChanged;
  /** Subscribers to cursor scene-coords updates. The Fabric
   *  `mouse:move` handler iterates this Set on every event;
   *  consumers (StatusBar) attach via `subscribeToCursorMove`
   *  and manage their own local state. Keeping the cursor out
   *  of the reducer avoids re-rendering the whole EditorPage
   *  subtree at 60+ Hz just from hover. */
  const cursorListenersRef = useRef<Set<(x: number, y: number) => void>>(new Set());
  const onPathCreatedRef = useRef(onPathCreated);
  onPathCreatedRef.current = onPathCreated;
  const onCanvasDimensionsRef = useRef(onCanvasDimensions);
  onCanvasDimensionsRef.current = onCanvasDimensions;
  // Clone Stamp callbacks — surfaced to EditorPage so the panel
  // can mirror source state and warn the user on rejected
  // strokes (no source set).
  const onCloneSourceChangedRef = useRef<
    ((p: { x: number; y: number } | null) => void) | undefined
  >(undefined);
  onCloneSourceChangedRef.current = onCloneSourceChanged;
  const onCloneStrokeRejectedRef = useRef<(() => void) | undefined>(undefined);
  onCloneStrokeRejectedRef.current = onCloneStrokeRejected;
  // Forward-reference holder for `renderCloneReticle` so the
  // mouse handlers above can invoke it before the function is
  // declared further down in the hook body.
  const renderCloneReticleRef = useRef<((overridePos?: { x: number; y: number }) => void) | null>(
    null,
  );

  /**
   * Resolve the background image. Returns the cached ref if it's still on the
   * canvas; otherwise scans the canvas for a non-selectable FabricImage,
   * rebinds the ref + dims, and returns it. Returns null if the canvas has no
   * image at all.
   *
   * Use this rather than reading `backgroundImageRef.current` directly — it
   * tolerates strict-mode double-mounts and any other path that drops the ref
   * out of sync with the actual canvas state.
   */
  const getBackgroundImage = useCallback((): fabric.FabricImage | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const current = backgroundImageRef.current;
    if (current && canvas.contains(current)) return current;

    // Prefer the explicit `_isBackground` tag; fall back to the legacy
    // "non-selectable FabricImage" check for sessions saved before tagging.
    const found = canvas
      .getObjects()
      .find(
        (o) =>
          o instanceof fabric.FabricImage &&
          (isBackgroundImage(o) || !(o as fabric.FabricImage & { selectable: boolean }).selectable),
      ) as fabric.FabricImage | undefined;

    if (found) {
      tagAsBackground(found);
      backgroundImageRef.current = found;
      imageDimsRef.current = {
        width: found.width || 1,
        height: found.height || 1,
      };
      return found;
    }

    backgroundImageRef.current = null;
    return null;
  }, []);
  const updateViewportInfoRef = useRef<() => void>(() => {});

  const [viewport, setViewport] = useState<ViewportInfo>({
    thumbWidthRatio: 1,
    thumbHeightRatio: 1,
    scrollXRatio: 0,
    scrollYRatio: 0,
    showH: false,
    showV: false,
  });

  // Reactive flags that drive Cut / Copy / Paste toolbar button enable
  // states. `hasMarqueeSelection` flips on every marquee finalize / clear;
  // `hasClipboard` flips when the in-memory clipboard ref is populated by
  // copy or cut. The clipboard ref itself is non-reactive so the heavy
  // bitmap doesn't trigger re-renders.
  const [hasMarqueeSelection, setHasMarqueeSelection] = useState(false);
  const [hasClipboard, setHasClipboard] = useState(false);
  const clipboardRef = useRef<{
    canvas: HTMLCanvasElement;
    left: number;
    top: number;
  } | null>(null);
  // Helper: re-evaluate `hasMarqueeSelection` from the current marquee
  // state. Called at every finalize and clear point so React state stays
  // in sync with the marqueeRef object identity.
  const refreshMarqueeSelectionState = useCallback(() => {
    const m = marqueeRef.current;
    setHasMarqueeSelection(!!m.obj && !m.isDrawing);
  }, []);

  /** Compute scrollbar indicator state from current viewport
   *  transform. Called by every pan / zoom / wheel handler — at
   *  60+ Hz on a continuous pan. The shallow-compare guard at
   *  the bottom is important: without it `setViewport({...})`
   *  allocates a fresh object identity even when none of the
   *  six ratios changed (e.g. wheel-zoom that lands on the same
   *  rounded ratios). The fresh identity rolls into the
   *  `useFabricCanvas` API memo's deps and invalidates `canvas`
   *  identity downstream, which refires every effect in
   *  EditorPage that lists `canvas` in its deps. Cap the churn
   *  at the source. */
  const updateViewportInfo = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const vt = canvas.viewportTransform;
    if (!vt) return;

    const zoom = vt[0];
    const panX = vt[4];
    const panY = vt[5];
    const vpW = wrapper.clientWidth;
    const vpH = wrapper.clientHeight;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;

    let next: ViewportInfo;
    if (!imgW || !imgH) {
      next = {
        thumbWidthRatio: 1,
        thumbHeightRatio: 1,
        scrollXRatio: 0,
        scrollYRatio: 0,
        showH: false,
        showV: false,
      };
    } else {
      const contentW = imgW * zoom;
      const contentH = imgH * zoom;

      const thumbWR = Math.min(1, vpW / contentW);
      const thumbHR = Math.min(1, vpH / contentH);

      const maxPanX = 0;
      const minPanX = vpW - contentW;
      const scrollXR = minPanX < maxPanX ? (maxPanX - panX) / (maxPanX - minPanX) : 0;

      const maxPanY = 0;
      const minPanY = vpH - contentH;
      const scrollYR = minPanY < maxPanY ? (maxPanY - panY) / (maxPanY - minPanY) : 0;

      next = {
        thumbWidthRatio: thumbWR,
        thumbHeightRatio: thumbHR,
        scrollXRatio: Math.max(0, Math.min(1, scrollXR)),
        scrollYRatio: Math.max(0, Math.min(1, scrollYR)),
        showH: thumbWR < 0.99,
        showV: thumbHR < 0.99,
      };
    }

    setViewport((prev) => {
      if (
        prev.thumbWidthRatio === next.thumbWidthRatio &&
        prev.thumbHeightRatio === next.thumbHeightRatio &&
        prev.scrollXRatio === next.scrollXRatio &&
        prev.scrollYRatio === next.scrollYRatio &&
        prev.showH === next.showH &&
        prev.showV === next.showV
      ) {
        return prev;
      }
      return next;
    });
  }, [wrapperRef]);

  // Keep ref in sync so event handlers in useEffect can call the latest version
  updateViewportInfoRef.current = updateViewportInfo;

  // Initialize canvas
  useEffect(() => {
    const canvasEl = canvasElRef.current;
    const wrapper = wrapperRef.current;
    if (!canvasEl || !wrapper) return;

    const canvas = new fabric.Canvas(canvasEl, {
      backgroundColor: 'transparent',
      selection: true,
      preserveObjectStacking: true,
    });

    canvasRef.current = canvas;
    // Expose to window for ad-hoc DevTools debugging.
    if (typeof window !== 'undefined') {
      (window as unknown as { __fiasCanvas?: fabric.Canvas }).__fiasCanvas = canvas;
    }

    // Reusable per-dab offscreen canvases. The Retouch variants
    // each allocated 1–2 fresh canvases per dab via
    // `document.createElement('canvas')`, totalling ~240
    // allocations/sec during a fast drag and a steady stream of
    // GC churn. These caches outlive individual strokes and are
    // resized lazily by the helper as the brush radius changes.
    // The width-setter clears both the bitmap and the context
    // state per the canvas spec, so callers don't need to
    // clearRect first. The hint passed on the first
    // `getContext` call sticks for the lifetime of the canvas;
    // we keep stamp / destSample uses willReadFrequently-
    // consistent so the hint matches the dominant access
    // pattern for each cache.
    const stampCanvasCache: { canvas: HTMLCanvasElement | null } = { canvas: null };
    const destSampleCanvasCache: { canvas: HTMLCanvasElement | null } = { canvas: null };
    const acquireDabCanvas = (
      cache: { canvas: HTMLCanvasElement | null },
      size: number,
    ): HTMLCanvasElement => {
      if (!cache.canvas) cache.canvas = document.createElement('canvas');
      cache.canvas.width = size;
      cache.canvas.height = size;
      return cache.canvas;
    };

    // Force Fabric's auto-created wrapper to fill our container
    if (canvas.wrapperEl) {
      canvas.wrapperEl.style.position = 'absolute';
      canvas.wrapperEl.style.inset = '0';
      canvas.wrapperEl.style.width = '100%';
      canvas.wrapperEl.style.height = '100%';
    }

    // Canvas element always matches wrapper size
    const resize = () => {
      const w = wrapper.clientWidth;
      const h = wrapper.clientHeight;
      if (w > 0 && h > 0) {
        canvas.setDimensions({ width: w, height: h });
        canvas.renderAll();
      }
    };
    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(wrapper);

    /**
     * Composite the visible rendering of every Fabric object
     * tagged with `layerId` into `ctx` at scene-space coords.
     * Used by retouch tools to snapshot the layer's actual
     * appearance as the clone/heal source — the paint surface
     * alone is empty for a non-bg layer (separate overlay) so
     * sampling from it would produce blank stamps.
     *
     * Iterates in z-order (`getObjects()` is bottom-to-top), so
     * the result mirrors what the user sees on screen. Skips
     * invisible objects. Each object's `toCanvasElement()` is
     * positioned at its scene-space bbox.
     */
    const snapshotLayerToContext = (ctx: CanvasRenderingContext2D, layerId: string): void => {
      for (const obj of canvas.getObjects()) {
        if (getObjectLayerId(obj) !== layerId) continue;
        if (obj.visible === false) continue;
        const objWithToCanvas = obj as fabric.FabricObject & {
          toCanvasElement?: () => HTMLCanvasElement;
        };
        if (!objWithToCanvas.toCanvasElement) continue;
        const rendered = objWithToCanvas.toCanvasElement();
        if (!rendered || rendered.width === 0 || rendered.height === 0) continue;
        const br = obj.getBoundingRect();
        ctx.drawImage(rendered, br.left, br.top);
      }
    };

    // Event listeners
    canvas.on('object:modified', () => onObjectModifiedRef.current?.());
    canvas.on('path:created', (e) => {
      const path = (e as { path?: fabric.Object }).path;
      if (!path) {
        onPathCreatedRef.current?.();
        return;
      }
      // Always remove the auto-added Fabric Path from the main canvas first;
      // bake takes the unbound path and renders it onto the active layer's
      // surface via a temporary StaticCanvas.
      canvas.remove(path);
      const layerId = activeLayerIdRef.current;
      if (layerId) {
        const isEraser = activeToolRef.current === 'eraser';
        // Route to mask alpha when the active edit target is the
        // mask AND the layer actually has a mask. Otherwise paint
        // into the layer's pixel surface as before. Tier 0
        // documents always hit the surface branch.
        const target = editTargetsRef.current[layerId] ?? 'layer';
        const routedToMask = target === 'mask' && bakeStrokeOntoMask(path, layerId, isEraser);
        if (!routedToMask) {
          bakeStrokeOntoSurface(path, layerId, isEraser);
        }
      }
      // Synchronous render so the user sees the baked stroke in the same
      // frame the live preview was cleared.
      canvas.renderAll();
      onPathCreatedRef.current?.();
    });

    const fireSelection = (selected: fabric.FabricObject[] | undefined) => {
      const objs = selected ?? [];
      const ids = objs.map((o) => (o as fabric.FabricObject & { id?: string }).id || '');
      onSelectionChangedRef.current?.(ids);
      // Pull the layerId from the first selected object — sufficient for the
      // common single-object case; for multi-select, fall back to that object's
      // layer to drive the panel highlight.
      const layerId = objs.length > 0 ? (getObjectLayerId(objs[0]) ?? null) : null;
      onLayerSelectedRef.current?.(layerId);
    };

    // When a single non-bg object is clicked with the move tool, expand the
    // selection to include every other object on the same layer (e.g. the
    // layer's paint surface) so they translate together. Without this, drawing
    // baked into a shape layer's paint surface would stay behind when the
    // shape is moved.
    let isExpanding = false;
    const expandSelectionToLayer = (selected: fabric.FabricObject[] | undefined) => {
      if (isExpanding) return;
      if (activeToolRef.current !== 'move') return;
      if (!selected || selected.length !== 1) return;
      const target = selected[0];
      if (isBackgroundImage(target) || isMarqueeObject(target)) return;
      const layerId = getObjectLayerId(target);
      if (!layerId) return;
      const peers = canvas.getObjects().filter(
        (o) =>
          o !== target &&
          getObjectLayerId(o) === layerId &&
          !isMarqueeObject(o) &&
          !isBackgroundImage(o) &&
          // Paint overlays are doc-sized scene-aligned UI
          // artifacts. Including them in the ActiveSelection
          // inflates the Move-tool bbox to the full document
          // (the user sees a giant bbox far past the layer's
          // image). Tradeoff: retouch strokes don't translate
          // when the layer moves — acceptable v1 since the
          // overlay is also being clipped to the image bbox
          // at creation time (see `ensurePaintSurface`).
          !isPaintOverlay(o),
      );
      if (peers.length === 0) return;
      const all = [target, ...peers];
      isExpanding = true;
      try {
        canvas.discardActiveObject();
        const sel = new fabric.ActiveSelection(all, { canvas });
        canvas.setActiveObject(sel);
        canvas.requestRenderAll();
      } finally {
        isExpanding = false;
      }
    };

    const handleSelection = (selected: fabric.FabricObject[] | undefined) => {
      fireSelection(selected);
      expandSelectionToLayer(selected);
    };
    canvas.on('selection:created', (e) => handleSelection(e.selected));
    canvas.on('selection:updated', (e) => handleSelection(e.selected));
    canvas.on('selection:cleared', () => onSelectionChangedRef.current?.([]));

    canvas.on('mouse:move', (opt) => {
      if (!opt.e) return;
      const listeners = cursorListenersRef.current;
      if (listeners.size === 0) return;
      const scenePoint = canvas.getScenePoint(opt.e);
      const x = Math.round(scenePoint.x);
      const y = Math.round(scenePoint.y);
      for (const cb of listeners) cb(x, y);
    });

    // Mouse wheel zoom — zoom toward the cursor position
    canvas.on('mouse:wheel', (opt) => {
      const e = opt.e as WheelEvent;
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY;
      const vt = canvas.viewportTransform;
      if (!vt) return;

      const currentZoom = vt[0];
      const factor = delta > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.1, Math.min(10, currentZoom * factor));

      // Zoom toward the mouse position. `zoomToPoint` expects a point
      // in canvas-element (viewport) pixel coordinates — i.e., before
      // the viewport transform is applied. Passing a scene-space point
      // would double-transform and the image would jump away from the
      // cursor on every wheel tick. (The touch-zoom path uses
      // `touchMidpoint`, which is already canvas-relative.)
      const point = canvas.getViewportPoint(e);
      canvas.zoomToPoint(point, newZoom);
      if (marqueeRef.current.obj) {
        applyMarqueeStroke(canvas, marqueeRef.current.obj);
        for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
      }
      canvas.renderAll();

      onZoomChangedRef.current?.(newZoom);
      updateViewportInfoRef.current();
    });

    // Move tool — drag empty canvas to pan the viewport (drag on object lets Fabric move it)
    canvas.on('mouse:down', (opt) => {
      // Tool handlers all gate on `activeToolRef.current === '<tool>'`
      // and early-return otherwise. During a non-affine Transform
      // session, `setupNonAffineOverlay` overrides the ref to 'ai'
      // (a tool with no registered handlers) so every tool's
      // mouse:* path early-returns uniformly. Restored on
      // teardown.
      if (activeToolRef.current !== 'move') return;
      if (opt.e === undefined) return;
      // Fabric v7 changed `findTarget` to always return a
      // `FullTargetsInfoWithContainer` object (truthy even when no
      // hit) instead of the v6 `FabricObject | undefined`. Read
      // `.target` to recover the v6 boolean semantics — without this,
      // the early-return fires on every click and silently breaks
      // the move tool / marquee / "click empty canvas to add another
      // text or shape" flow.
      if (canvas.findTarget(opt.e).target) return;

      const point = clientPointFromEvent(opt.e);
      panRef.current = { isPanning: true, lastX: point.x, lastY: point.y };
      canvas.defaultCursor = 'grabbing';
      canvas.setCursor('grabbing');
    });

    canvas.on('mouse:move', (opt) => {
      if (!panRef.current.isPanning || !opt.e) return;
      const point = clientPointFromEvent(opt.e);
      const dx = point.x - panRef.current.lastX;
      const dy = point.y - panRef.current.lastY;
      canvas.relativePan(new fabric.Point(dx, dy));
      panRef.current.lastX = point.x;
      panRef.current.lastY = point.y;
      updateViewportInfoRef.current();
    });

    canvas.on('mouse:up', () => {
      if (panRef.current.isPanning) {
        panRef.current.isPanning = false;
        canvas.defaultCursor = 'grab';
      }
    });

    // Shape tool — click on empty canvas to drop a new shape at the click
    // position. Click on an existing shape lets Fabric handle it (select/drag).
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'shape') return;
      if (!opt.e) return;
      if (canvas.findTarget(opt.e).target) return;
      const point = canvas.getScenePoint(opt.e);
      onShapeToolClickRef.current?.({ x: point.x, y: point.y });
    });

    // Text tool — click on empty canvas to drop a new IText at the click position.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'text') return;
      if (!opt.e) return;
      if (canvas.findTarget(opt.e).target) return;
      const point = canvas.getScenePoint(opt.e);
      onTextToolClickRef.current?.({ x: point.x, y: point.y });
    });

    // Pen tool (Tier 2 Group A). Three variants share the same
    // anchor accumulator; the per-event behavior branches on the
    // current variant. Click-back-on-first-anchor closes the path
    // (Photoshop convention); double-click finishes as open. The
    // close-anchor detection uses scene-pixel distance so it
    // scales with zoom naturally.
    const PEN_CLOSE_RADIUS = 8;
    const FREEFORM_MIN_SAMPLE_DIST = 4;
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'pen') return;
      if (!opt.e) return;
      const settings = penSettingsRef.current;
      const point = canvas.getScenePoint(opt.e);

      // Initialise the working state on the first click of a new
      // session. Subsequent clicks append to the existing list.
      if (!penStateRef.current) {
        penStateRef.current = {
          anchors: [],
          previewObj: null,
          draggingAnchorIndex: null,
          freeformActive: false,
        };
      }
      const state = penStateRef.current;

      if (settings.variant === 'freeform') {
        // Freeform starts an active sampling stroke. Each mouse:move
        // adds a sample point; mouse:up finishes the path.
        state.freeformActive = true;
        state.anchors = [{ x: point.x, y: point.y, type: 'smooth' }];
        updatePenPreview();
        return;
      }

      // Curvature / Pen — first check for click-on-first-anchor
      // to close the path. Requires ≥2 anchors so a single-anchor
      // session doesn't auto-close.
      if (state.anchors.length >= 2 && settings.autoAddDelete) {
        const a0 = state.anchors[0];
        const dx = a0.x - point.x;
        const dy = a0.y - point.y;
        if (dx * dx + dy * dy <= PEN_CLOSE_RADIUS * PEN_CLOSE_RADIUS) {
          finalizePenPath(true);
          return;
        }
      }

      // Otherwise append a fresh anchor and recompute handles per
      // variant.
      const newAnchor: VectorAnchor = {
        x: point.x,
        y: point.y,
        type: settings.variant === 'curvature' ? 'smooth' : 'corner',
      };
      state.anchors.push(newAnchor);

      if (settings.variant === 'curvature') {
        // Catmull-Rom-style: every click re-fits all handles for
        // a C1-continuous curve through every anchor.
        const positions = state.anchors.map((a) => ({ x: a.x, y: a.y, type: a.type }));
        state.anchors = recomputeCurvatureHandles(positions, false);
      } else {
        // Pen variant: stage the just-placed anchor for drag-to-
        // create-smooth-handles via mouse:move.
        state.draggingAnchorIndex = state.anchors.length - 1;
      }
      updatePenPreview();
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'pen') return;
      const state = penStateRef.current;
      if (!state || !opt.e) return;
      const settings = penSettingsRef.current;
      const point = canvas.getScenePoint(opt.e);

      if (settings.variant === 'freeform') {
        if (!state.freeformActive) return;
        const last = state.anchors[state.anchors.length - 1];
        const dx = point.x - last.x;
        const dy = point.y - last.y;
        if (dx * dx + dy * dy < FREEFORM_MIN_SAMPLE_DIST * FREEFORM_MIN_SAMPLE_DIST) return;
        state.anchors.push({ x: point.x, y: point.y, type: 'smooth' });
        updatePenPreview();
        return;
      }

      if (state.draggingAnchorIndex === null) return;
      const dragIdx = state.draggingAnchorIndex;
      const anchor = state.anchors[dragIdx];
      if (!anchor) return;
      // Pen variant drag: pull-vector becomes the outgoing handle;
      // mirror into the incoming handle so the segment-on-either-
      // side runs smoothly through the anchor. Above a tiny
      // threshold the type promotes from 'corner' to 'smooth'.
      const outDx = point.x - anchor.x;
      const outDy = point.y - anchor.y;
      const len = Math.hypot(outDx, outDy);
      const next: VectorAnchor =
        len < 2
          ? {
              ...anchor,
              type: 'corner',
              inX: undefined,
              inY: undefined,
              outX: undefined,
              outY: undefined,
            }
          : {
              ...anchor,
              type: 'smooth',
              inX: -outDx,
              inY: -outDy,
              outX: outDx,
              outY: outDy,
            };
      state.anchors[dragIdx] = next;
      updatePenPreview();
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'pen') return;
      const state = penStateRef.current;
      if (!state) return;
      const settings = penSettingsRef.current;

      if (settings.variant === 'freeform') {
        if (!state.freeformActive) return;
        state.freeformActive = false;
        // Smooth the sampled stroke before finalize: Catmull-Rom
        // through every sample produces a clean curve in place of
        // the raw polyline.
        if (state.anchors.length >= 2) {
          const positions = state.anchors.map((a) => ({ x: a.x, y: a.y, type: 'smooth' as const }));
          state.anchors = recomputeCurvatureHandles(positions, false);
          // Freeform commits the whole path on mouse:up — one
          // stroke = one finalized path.
          finalizePenPath(false);
        } else {
          // Pure click-without-drag in Freeform is a no-op.
          clearPenState();
        }
        return;
      }

      // Pen variant: end the in-progress drag; the anchor's
      // handles are already in their final shape from mouse:move.
      if (state.draggingAnchorIndex !== null) {
        state.draggingAnchorIndex = null;
      }
    });

    // Double-click finishes the current pen path as OPEN — the
    // alternative to Enter from the keyboard. Mirrors Photoshop's
    // double-click-to-finish convention.
    canvas.on('mouse:dblclick', () => {
      if (activeToolRef.current !== 'pen') return;
      if (penStateRef.current) finalizePenPath(false);
    });

    // Path Edit mode (Tier 2 Group A PR-B). Gated on the overlay
    // ref being non-null — when active, `activeToolRef` is forced
    // to `'ai'` so every other tool handler short-circuits, and
    // the canvas-level mouse:* hits below take over.
    canvas.on('mouse:down', (opt) => {
      const overlay = pathEditOverlayRef.current;
      if (!overlay || !opt.e) return;
      const point = canvas.getScenePoint(opt.e);
      const sub = overlay.workingPath.subPaths[0];
      if (!sub) return;

      // Selected anchor's handles take hit-test priority — when a
      // handle is rendered, it sits on top of the anchor and the
      // user's intent is almost always to grab the handle.
      //
      // Hit radii divide by zoom so the clickable area on SCREEN
      // matches the rendered handle/anchor size. Without this,
      // zoomed-in views would have huge hit zones (a single screen
      // pixel covers many scene pixels) and zoomed-out views would
      // require pixel-perfect clicks on tiny anchors.
      const zoom = canvas.getZoom() || 1;
      const handleHitRadius = PATH_EDIT_HANDLE_HIT_RADIUS_SCENE / zoom;
      const anchorHitRadius = PATH_EDIT_ANCHOR_HIT_RADIUS_SCENE / zoom;
      if (overlay.selectedAnchorIndex !== null) {
        const sel = sub.anchors[overlay.selectedAnchorIndex];
        if (sel) {
          const handle = hitTestHandle(sel, point.x, point.y, handleHitRadius);
          if (handle) {
            overlay.drag = {
              kind: handle === 'in' ? 'handleIn' : 'handleOut',
              anchorIndex: overlay.selectedAnchorIndex,
            };
            return;
          }
        }
      }

      // Anchor hit-test next. A new selection refreshes the
      // overlay so the previously-selected anchor's handles
      // disappear and the new selection's appear.
      const hit = hitTestAnchor(overlay.workingPath, point.x, point.y, anchorHitRadius);
      if (hit) {
        overlay.selectedAnchorIndex = hit.anchorIndex;
        overlay.drag = { kind: 'anchor', anchorIndex: hit.anchorIndex };
        updatePathEditOverlayRef.current?.();
        onPathEditSelectionChangedRef.current?.(hit.anchorIndex);
        return;
      }

      // Empty space — clear the selection. No drag.
      if (overlay.selectedAnchorIndex !== null) {
        overlay.selectedAnchorIndex = null;
        overlay.drag = null;
        updatePathEditOverlayRef.current?.();
        onPathEditSelectionChangedRef.current?.(null);
      }
    });

    canvas.on('mouse:move', (opt) => {
      const overlay = pathEditOverlayRef.current;
      if (!overlay || !overlay.drag || !opt.e) return;
      const point = canvas.getScenePoint(opt.e);
      const sub = overlay.workingPath.subPaths[0];
      if (!sub) return;
      const idx = overlay.drag.anchorIndex;
      const a = sub.anchors[idx];
      if (!a) return;

      if (overlay.drag.kind === 'anchor') {
        // Move the anchor. Handles are stored RELATIVE to the
        // anchor, so they translate with it for free — no extra
        // arithmetic needed here.
        sub.anchors[idx] = { ...a, x: point.x, y: point.y };
      } else if (overlay.drag.kind === 'handleIn') {
        const dx = point.x - a.x;
        const dy = point.y - a.y;
        // Mirror to the OUT handle when the anchor is `smooth`
        // so both segments stay tangent-continuous. `asymmetric`
        // keeps tangent direction but not magnitude; `corner`
        // doesn't mirror at all. The mirror runs only when the
        // outX/outY are defined (otherwise we'd be inventing a
        // tangent the user didn't ask for).
        const next: VectorAnchor = { ...a, inX: dx, inY: dy };
        if (a.type === 'smooth' && (a.outX !== undefined || a.outY !== undefined)) {
          next.outX = -dx;
          next.outY = -dy;
        } else if (a.type === 'asymmetric' && (a.outX !== undefined || a.outY !== undefined)) {
          const inLen = Math.hypot(dx, dy);
          const outLen = Math.hypot(a.outX ?? 0, a.outY ?? 0);
          if (inLen > 0 && outLen > 0) {
            const k = outLen / inLen;
            next.outX = -dx * k;
            next.outY = -dy * k;
          }
        }
        sub.anchors[idx] = next;
      } else if (overlay.drag.kind === 'handleOut') {
        const dx = point.x - a.x;
        const dy = point.y - a.y;
        const next: VectorAnchor = { ...a, outX: dx, outY: dy };
        if (a.type === 'smooth' && (a.inX !== undefined || a.inY !== undefined)) {
          next.inX = -dx;
          next.inY = -dy;
        } else if (a.type === 'asymmetric' && (a.inX !== undefined || a.inY !== undefined)) {
          const outLen = Math.hypot(dx, dy);
          const inLen = Math.hypot(a.inX ?? 0, a.inY ?? 0);
          if (outLen > 0 && inLen > 0) {
            const k = inLen / outLen;
            next.inX = -dx * k;
            next.inY = -dy * k;
          }
        }
        sub.anchors[idx] = next;
      }
      updatePathEditOverlayRef.current?.();
    });

    canvas.on('mouse:up', () => {
      const overlay = pathEditOverlayRef.current;
      if (overlay) overlay.drag = null;
    });

    // Clone Stamp + Heal Brush (Retouch tool, variant=clone or
    // heal). Both variants share the custom mouse:down/move/up
    // handlers, the source-set state, the reticle, and the stamp
    // pipeline. Heal layers a per-stamp tone correction on top of
    // the stamped source pixels (transferring source TEXTURE
    // while matching destination TONE). Three input paths
    // converge on `setCloneSource`: armed-mode tap, alt-click,
    // and long-press (handled in the touch listeners below). On
    // a non-source click, mouse:down begins a stroke; mouse:move
    // stamps source pixels at each cursor sample; mouse:up ends
    // and (if Aligned=false) clears the offset.
    const isStampVariant = (v: RetouchVariant | undefined): v is 'clone' | 'heal' =>
      v === 'clone' || v === 'heal';
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (!isStampVariant(settings?.variant)) return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);

      // Source-set paths: armed (Set Source button) or alt-click.
      const altPressed = (opt.e as MouseEvent).altKey === true;
      if (cloneArmedRef.current || altPressed) {
        cloneSourceRef.current = { x: point.x, y: point.y };
        cloneArmedRef.current = false;
        // A new source resets any persisted Aligned offset —
        // otherwise the next stroke would sample with the OLD
        // offset relative to the new source, which feels broken.
        cloneAlignedOffsetRef.current = null;
        renderCloneReticleRef.current?.();
        onCloneSourceChangedRef.current?.({ x: point.x, y: point.y });
        return;
      }

      // Otherwise, this is the start of a paint stroke. Source
      // must be set, otherwise no-op (with a hint surfaced via
      // callback).
      if (!cloneSourceRef.current) {
        onCloneStrokeRejectedRef.current?.();
        return;
      }
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;

      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;

      // Build the per-stroke state. Source snapshot = the active
      // layer's full visible rendering, composited at scene-space
      // coords. For the bg layer that's identical to `surface.canvas`
      // (the bg paint surface IS its own canvas at identity).
      // For non-bg layers it composites the underlying image AND
      // the paint overlay together — otherwise clone-stamp would
      // sample the empty overlay and produce invisible strokes.
      // Only Sample=current is supported; Sample=currentBelow /
      // all need cross-layer compositing and aren't implemented.
      const dims = imageDimsRef.current;
      const snapshot = document.createElement('canvas');
      snapshot.width = dims.width;
      snapshot.height = dims.height;
      const sCtx = snapshot.getContext('2d', { willReadFrequently: true });
      if (!sCtx) return;
      snapshotLayerToContext(sCtx, layerId);

      const radius = Math.max(1, Math.round(settings.size / 2));
      const brushMask = buildCloneBrushMask(radius, settings.hardness / 100);
      // Aligned mode lives on the Clone settings only — Heal is
      // always non-aligned (the common heal workflow is
      // dab-and-re-source rather than continuous drag).
      const aligned = settings.variant === 'clone' ? settings.clone.aligned : false;
      const offset =
        aligned && cloneAlignedOffsetRef.current
          ? { ...cloneAlignedOffsetRef.current }
          : {
              x: cloneSourceRef.current.x - point.x,
              y: cloneSourceRef.current.y - point.y,
            };
      // Stash the offset so the next stroke can pick it up when
      // Aligned remains on. If Aligned flips off mid-session, we
      // recompute fresh anyway.
      if (aligned) cloneAlignedOffsetRef.current = { ...offset };

      cloneStrokeRef.current = {
        offset,
        sourceSnapshot: snapshot,
        brushMask,
        brushMaskImageData: null,
        radius,
        aligned,
        strokeAlpha: (settings.opacity / 100) * (settings.flow / 100),
        variant: settings.variant,
        healMode: settings.heal.mode,
      };

      // Stamp the first sample.
      stampClone(point.x, point.y);
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (!isStampVariant(settings?.variant)) return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);
      // Active stroke: stamp at cursor (the stamp loop also moves
      // the reticle to cursor + offset).
      if (cloneStrokeRef.current) {
        stampClone(point.x, point.y);
        return;
      }
      // Hover-only paths (no stroke): drive the reticle so the
      // user can preview where the next stamp will sample from.
      // Skip while source-set mode is active — clicking is about
      // to set a new source, so hover-tracking the stale source
      // would be misleading.
      if (cloneArmedRef.current) return;
      if ((opt.e as MouseEvent).altKey) return;
      const src = cloneSourceRef.current;
      if (!src) return;
      const reticle = cloneReticleRef.current;
      if (!reticle) return;
      // Aligned mode with persisted offset: reticle follows
      // cursor at offset (live preview of next sample location).
      // Otherwise: reticle sits at the source point — no movement
      // needed, but ensure it's there in case zoom/pan changed.
      // Heal in v1 is always non-aligned, so we only consult the
      // clone-namespace toggle when on the clone variant.
      const aligned =
        settings.variant === 'clone' && settings.clone.aligned && cloneAlignedOffsetRef.current;
      const target = aligned
        ? {
            x: point.x + cloneAlignedOffsetRef.current!.x,
            y: point.y + cloneAlignedOffsetRef.current!.y,
          }
        : src;
      if (reticle.left !== target.x || reticle.top !== target.y) {
        for (const bar of reticle.bars) {
          bar.set({ left: target.x, top: target.y });
          bar.setCoords();
        }
        reticle.left = target.x;
        reticle.top = target.y;
        canvas.requestRenderAll();
      }
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (!isStampVariant(settings?.variant)) return;
      const stroke = cloneStrokeRef.current;
      if (!stroke) return;
      cloneStrokeRef.current = null;
      if (stroke.aligned) {
        // Reticle stays at the last sampling position so the user
        // can see where the next stroke will resume from. Aligned
        // offset already persisted in mouse:down.
      } else {
        // Drop the persisted offset so the next stroke recomputes
        // from source. Reset the reticle visually to the original
        // source point so the user sees that subsequent strokes
        // start over from there.
        cloneAlignedOffsetRef.current = null;
        renderCloneReticleRef.current?.();
      }
      // Tell consumers a stroke just baked pixels — for snapshot
      // capture and dirty-marking via the existing pipeline.
      onObjectModifiedRef.current?.();
    });

    // Stamp the source snapshot at one cursor position. Reads
    // `cloneStrokeRef` for the per-stroke offset and brush
    // configuration. The composite is:
    //   1. Crop the source snapshot at (cursor + offset) — radius
    //   2. Apply the brush mask (radial alpha falloff) via source-in
    //   3. Apply per-stroke alpha (opacity × flow)
    //   4. Composite source-over onto the layer's paint surface
    //      at `cursor`
    function stampClone(cx: number, cy: number) {
      const stroke = cloneStrokeRef.current;
      if (!stroke) return;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;
      const targetCtx = target.getContext('2d');
      if (!targetCtx) return;

      const r = stroke.radius;
      const size = r * 2;
      const sx = Math.round(cx + stroke.offset.x - r);
      const sy = Math.round(cy + stroke.offset.y - r);
      // Convert scene cursor coords to the surface canvas's local
      // pixel coords. Bg surface has offset (0, 0) so this is a
      // no-op there. A pasted/rasterized image layer has its own
      // intrinsic canvas as the surface, positioned at the layer's
      // scene (offsetX, offsetY) — strokes need to be subtracted
      // by the offset to land on the right pixels.
      const dx = Math.round(cx - r - surface.offsetX);
      const dy = Math.round(cy - r - surface.offsetY);

      // Compose a temp stamp = source crop × brush mask × stroke
      // alpha, then drawImage onto the target. Doing it on a temp
      // canvas keeps the math tidy and survives partial-out-of-
      // bounds source crops (drawImage clips silently).
      const stamp = document.createElement('canvas');
      stamp.width = size;
      stamp.height = size;
      const stampCtx = stamp.getContext('2d', { willReadFrequently: stroke.variant === 'heal' });
      if (!stampCtx) return;
      stampCtx.drawImage(stroke.sourceSnapshot, sx, sy, size, size, 0, 0, size, size);

      // Heal in `normal` mode: shift the stamp's tone toward
      // the destination's average color in the brush footprint,
      // weighted by the brush mask. Net effect — the cloned
      // TEXTURE is preserved but the MEAN COLOR matches what's
      // already there, so the patch blends in instead of
      // looking pasted-on. Done before the mask composite so
      // the shift uses the full source crop's pixel data, not
      // the post-masked thin edge.
      //
      // Heal in `replace` mode (or Clone, always): skip the
      // tone shift — source pixels stamp directly. Replace mode
      // is functionally equivalent to Clone but kept available
      // from the Heal variant so users who've set up a heal
      // workflow can do a clone-style dab without switching
      // tools.
      if (stroke.variant === 'heal' && stroke.healMode === 'normal') {
        // Build the dest patch as a baseline+target composite for
        // non-bg layers (else heal samples an empty overlay and
        // shifts the stamp toward transparent black → "very dark
        // line" symptom). For bg the composite degenerates to a
        // straight read of `target`.
        const healDest = acquireDabCanvas(destSampleCanvasCache, size);
        const healDestCtx = healDest.getContext('2d', { willReadFrequently: true });
        if (healDestCtx) {
          healDestCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
          applyHealToneShift(stampCtx, healDest, size, stroke);
        }
      }

      stampCtx.globalCompositeOperation = 'destination-in';
      stampCtx.drawImage(stroke.brushMask, 0, 0);
      stampCtx.globalCompositeOperation = 'source-over';

      // Apply marquee clipping if a finalized selection is
      // active — same rule as bakeStrokeOntoSurface.
      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      targetCtx.save();
      if (hasMarquee) applyMarqueeClipToContext(targetCtx, m, surface.offsetX, surface.offsetY);
      targetCtx.globalAlpha = stroke.strokeAlpha;
      targetCtx.drawImage(stamp, dx, dy);
      targetCtx.globalAlpha = 1;
      targetCtx.restore();

      surface.fabricImg.setElement(target);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();

      // Move the source reticle to the live sampling position so
      // the user sees exactly where pixels are being pulled from
      // as they drag. After mouse:up, the reticle either stays
      // (Aligned ON) or snaps back to the original source
      // (Aligned OFF) — both cases handled in mouse:up.
      const reticle = cloneReticleRef.current;
      if (reticle) {
        const tx = cx + stroke.offset.x;
        const ty = cy + stroke.offset.y;
        for (const bar of reticle.bars) {
          bar.set({ left: tx, top: ty });
          bar.setCoords();
        }
        reticle.left = tx;
        reticle.top = ty;
      }

      canvas.requestRenderAll();
    }

    // Smudge (Retouch tool, variant=smudge). Drags a sample
    // buffer along the stroke, mixing it with the destination
    // pixels by Strength at each dab — the classic "wet finger"
    // smear. Has its own pipeline (no source-set / reticle); the
    // first dab seeds the sample buffer either from the dest
    // patch (default) or from the foreground color (Finger
    // Painting toggle).
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'smudge') return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);

      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;

      const radius = Math.max(1, Math.round(settings.size / 2));
      const size = radius * 2;
      // Convert scene cursor coords to canvas-local. See per-dab
      // sites for the rationale.
      const dx = Math.round(point.x - radius - surface.offsetX);
      const dy = Math.round(point.y - radius - surface.offsetY);

      // Seed the sample buffer. Default = current dest patch
      // (smudge starts from what's there). Finger Painting =
      // start with the foreground color, so the first dab paints
      // a streak that fades into the dest as the stroke smears.
      const sampleBuffer = document.createElement('canvas');
      sampleBuffer.width = size;
      sampleBuffer.height = size;
      const sCtx = sampleBuffer.getContext('2d', { willReadFrequently: true });
      if (!sCtx) return;
      if (settings.smudge.fingerPainting) {
        sCtx.fillStyle = brushColorRef.current;
        sCtx.fillRect(0, 0, size, size);
      } else {
        sCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
      }

      const brushMask = buildCloneBrushMask(radius, settings.hardness / 100);

      smudgeStrokeRef.current = {
        sampleBuffer,
        brushMask,
        brushMaskImageData: null,
        radius,
        strength: settings.smudge.strength / 100,
        strokeAlpha: (settings.opacity / 100) * (settings.flow / 100),
      };

      // First dab. With Finger Painting OFF this is a no-op
      // (sample = dest at this position so the mix yields the
      // dest unchanged) — the smear builds up as the user drags.
      // With Finger Painting ON this paints the initial color
      // streak.
      smudgeDab(point.x, point.y);
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'smudge') return;
      if (!opt.e) return;
      if (!smudgeStrokeRef.current) return;
      const point = canvas.getScenePoint(opt.e);
      smudgeDab(point.x, point.y);
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'smudge') return;
      const stroke = smudgeStrokeRef.current;
      if (!stroke) return;
      smudgeStrokeRef.current = null;
      onObjectModifiedRef.current?.();
    });

    // One smudge dab. Read the dest patch + sample buffer + mask,
    // run the per-pixel mix, write the result to the surface
    // (marquee-clipped, scaled by stroke alpha), and replace the
    // sample buffer with the mixed result so the next dab carries
    // it forward. Strength is captured at stroke start so a mid-
    // stroke slider drag doesn't change the smear feel.
    function smudgeDab(cx: number, cy: number) {
      const stroke = smudgeStrokeRef.current;
      if (!stroke) return;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;
      const targetCtx = target.getContext('2d');
      if (!targetCtx) return;

      const r = stroke.radius;
      const size = r * 2;
      // Convert scene cursor coords to the surface canvas's local
      // pixel coords. Bg surface has offset (0, 0) so this is a
      // no-op there. A pasted/rasterized image layer has its own
      // intrinsic canvas as the surface, positioned at the layer's
      // scene (offsetX, offsetY) — strokes need to be subtracted
      // by the offset to land on the right pixels.
      const dx = Math.round(cx - r - surface.offsetX);
      const dy = Math.round(cy - r - surface.offsetY);

      // Read the current dest patch.
      const destSample = acquireDabCanvas(destSampleCanvasCache, size);
      const destCtx = destSample.getContext('2d', { willReadFrequently: true });
      if (!destCtx) return;
      destCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
      const destImg = destCtx.getImageData(0, 0, size, size);

      // Read the sample buffer (the trailing pixels carried
      // from the previous dab — or the seed at stroke start).
      const sampleCtx = stroke.sampleBuffer.getContext('2d', { willReadFrequently: true });
      if (!sampleCtx) return;
      const sampleImg = sampleCtx.getImageData(0, 0, size, size);

      // Lazy-cache the mask ImageData on the stroke so per-dab
      // getImageData stays cheap (mask doesn't change per-stroke).
      if (!stroke.brushMaskImageData) {
        const mctx = stroke.brushMask.getContext('2d', { willReadFrequently: true });
        if (!mctx) return;
        stroke.brushMaskImageData = mctx.getImageData(0, 0, size, size);
      }
      const maskImg = stroke.brushMaskImageData;

      // Per-pixel mix → mixed ImageData.
      const out = applyMaskedPixelMix(destImg, sampleImg, maskImg, stroke.strength);

      // Render the mixed result onto a stamp canvas so we can
      // composite it onto the surface with optional marquee clip
      // and stroke-alpha scaling. globalAlpha + source-over with
      // an opaque-ish stamp gives `final = stamp*a + dest*(1-a)`,
      // which scales the per-dab change correctly when
      // opacity*flow < 1.
      const stamp = acquireDabCanvas(stampCanvasCache, size);
      const stampCtx = stamp.getContext('2d');
      if (!stampCtx) return;
      stampCtx.putImageData(out, 0, 0);

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      targetCtx.save();
      if (hasMarquee) applyMarqueeClipToContext(targetCtx, m, surface.offsetX, surface.offsetY);
      targetCtx.globalAlpha = stroke.strokeAlpha;
      targetCtx.drawImage(stamp, dx, dy);
      targetCtx.globalAlpha = 1;
      targetCtx.restore();

      // Carry the mix forward — next dab drags THIS result, not
      // the prior sample. That's what produces the continuous
      // smear (each dab is a slightly shifted blend of all
      // upstream pixels along the path).
      sampleCtx.clearRect(0, 0, size, size);
      sampleCtx.putImageData(out, 0, 0);

      surface.fabricImg.setElement(target);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.requestRenderAll();
    }

    // Blur (Retouch tool, variant=blur). Reads the dest patch
    // under the brush, runs a Gaussian blur on it (with padding
    // so the kernel doesn't sample transparent black from outside
    // the patch), and mixes the blurred result back into the dest
    // by Strength. Independent pipeline from Smudge — no carry-
    // forward sample buffer. Repeated dabs accumulate because
    // each dab re-reads the (already-slightly-blurred) dest, so
    // the user builds up smoothness by dragging back and forth.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'blur') return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);

      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      if (!ensurePaintSurfaceRef.current?.(layerId)) return;

      const radius = Math.max(1, Math.round(settings.size / 2));
      const brushMask = buildCloneBrushMask(radius, settings.hardness / 100);
      // Sigma scales with brush radius, NOT strength — matching
      // Photoshop's Blur tool semantics (the kernel is intrinsic
      // to brush size, strength controls mix amount). radius/4
      // gives a visually meaningful kernel without crushing
      // detail in one dab; min 0.5 keeps strength=tiny still
      // doing something.
      const sigma = Math.max(0.5, radius / 4);

      blurStrokeRef.current = {
        brushMask,
        brushMaskImageData: null,
        radius,
        sigma,
        strength: settings.blur.strength / 100,
        strokeAlpha: (settings.opacity / 100) * (settings.flow / 100),
      };

      blurDab(point.x, point.y);
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'blur') return;
      if (!opt.e) return;
      if (!blurStrokeRef.current) return;
      const point = canvas.getScenePoint(opt.e);
      blurDab(point.x, point.y);
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'blur') return;
      if (!blurStrokeRef.current) return;
      blurStrokeRef.current = null;
      onObjectModifiedRef.current?.();
    });

    // One blur dab. Reads a *padded* dest patch (size + 2*pad)
    // so the Gaussian kernel always has real pixels in its
    // sample radius — without padding, the blur near the patch
    // edge pulls in transparent black from outside and produces
    // a dark vignette artifact. Pad ≈ 2*sigma covers ~95% of
    // the kernel weight.
    function blurDab(cx: number, cy: number) {
      const stroke = blurStrokeRef.current;
      if (!stroke) return;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;
      const targetCtx = target.getContext('2d');
      if (!targetCtx) return;

      const r = stroke.radius;
      const size = r * 2;
      // Convert scene cursor coords to the surface canvas's local
      // pixel coords. Bg surface has offset (0, 0) so this is a
      // no-op there. A pasted/rasterized image layer has its own
      // intrinsic canvas as the surface, positioned at the layer's
      // scene (offsetX, offsetY) — strokes need to be subtracted
      // by the offset to land on the right pixels.
      const dx = Math.round(cx - r - surface.offsetX);
      const dy = Math.round(cy - r - surface.offsetY);

      const pad = Math.max(0, Math.ceil(stroke.sigma * 2));

      // Padded source: brush footprint plus a `pad`-pixel
      // border on each side, with the border filled by edge-
      // extension of the in-bounds surface. Without edge-
      // extension, the Gaussian below pulls transparent black
      // from outside the canvas and stamps a dark halo near the
      // edge. See `buildEdgeExtendedPaddedSource`.
      const padded = buildEdgeExtendedPaddedSource(target, dx, dy, size, pad);
      if (!padded) return;

      // Apply CSS Gaussian blur via ctx.filter, then crop the
      // center back to brush size. Browsers all support
      // `filter: blur(Npx)` on canvas 2D contexts; the resulting
      // blur quality is plenty for an interactive retouch tool.
      const blurred = document.createElement('canvas');
      blurred.width = size;
      blurred.height = size;
      const blurredCtx = blurred.getContext('2d', { willReadFrequently: true });
      if (!blurredCtx) return;
      blurredCtx.filter = `blur(${stroke.sigma}px)`;
      blurredCtx.drawImage(padded, -pad, -pad);
      blurredCtx.filter = 'none';

      // Read dest patch (un-blurred), blurred patch, and mask.
      const destSample = acquireDabCanvas(destSampleCanvasCache, size);
      const destCtx = destSample.getContext('2d', { willReadFrequently: true });
      if (!destCtx) return;
      destCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
      const destImg = destCtx.getImageData(0, 0, size, size);
      const blurredImg = blurredCtx.getImageData(0, 0, size, size);

      if (!stroke.brushMaskImageData) {
        const mctx = stroke.brushMask.getContext('2d', { willReadFrequently: true });
        if (!mctx) return;
        stroke.brushMaskImageData = mctx.getImageData(0, 0, size, size);
      }
      const maskImg = stroke.brushMaskImageData;

      // Mix: out = dest*(1-m) + blurred*m, m = maskAlpha *
      // strength. Strength=0 → no change; strength=1 → full blur
      // at brush center, fading to no-change at the soft edge.
      const out = applyMaskedPixelMix(destImg, blurredImg, maskImg, stroke.strength);

      const stamp = acquireDabCanvas(stampCanvasCache, size);
      const stampCtx = stamp.getContext('2d');
      if (!stampCtx) return;
      stampCtx.putImageData(out, 0, 0);

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      targetCtx.save();
      if (hasMarquee) applyMarqueeClipToContext(targetCtx, m, surface.offsetX, surface.offsetY);
      targetCtx.globalAlpha = stroke.strokeAlpha;
      targetCtx.drawImage(stamp, dx, dy);
      targetCtx.globalAlpha = 1;
      targetCtx.restore();

      surface.fabricImg.setElement(target);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.requestRenderAll();
    }

    // Sharpen (Retouch tool, variant=sharpen). Unsharp-mask: the
    // dest patch is blurred (Gaussian, padded source like Blur),
    // and the difference dest-minus-blurred is the high-frequency
    // detail. We mix `dest + detail` back into the surface, with
    // the brush-mask + Strength weighting via applyMaskedPixelMix.
    // Strength=0 → no change; strength=1 → full detail boost at
    // brush center, fading at the soft edge. Repeated dabs
    // accumulate because each dab re-reads the (already-slightly-
    // sharpened) dest.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'sharpen') return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);

      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      if (!ensurePaintSurfaceRef.current?.(layerId)) return;

      const radius = Math.max(1, Math.round(settings.size / 2));
      const brushMask = buildCloneBrushMask(radius, settings.hardness / 100);
      // Sigma scales with brush radius (same kernel choice as
      // Blur); Strength controls mix amount, not kernel size.
      const sigma = Math.max(0.5, radius / 4);

      sharpenStrokeRef.current = {
        brushMask,
        brushMaskImageData: null,
        radius,
        sigma,
        strength: settings.sharpen.strength / 100,
        strokeAlpha: (settings.opacity / 100) * (settings.flow / 100),
        appliedMask: null,
        appliedMaskW: 0,
        appliedMaskH: 0,
      };

      sharpenDab(point.x, point.y);
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'sharpen') return;
      if (!opt.e) return;
      if (!sharpenStrokeRef.current) return;
      const point = canvas.getScenePoint(opt.e);
      sharpenDab(point.x, point.y);
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'sharpen') return;
      if (!sharpenStrokeRef.current) return;
      sharpenStrokeRef.current = null;
      onObjectModifiedRef.current?.();
    });

    // One sharpen dab. Mirrors blurDab's padded-source dance —
    // see that function for the rationale on padding (avoids dark
    // halos at the patch edge from the Gaussian sampling outside
    // the surface).
    function sharpenDab(cx: number, cy: number) {
      const stroke = sharpenStrokeRef.current;
      if (!stroke) return;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;
      const targetCtx = target.getContext('2d');
      if (!targetCtx) return;

      const r = stroke.radius;
      const size = r * 2;
      // Convert scene cursor coords to the surface canvas's local
      // pixel coords. Bg surface has offset (0, 0) so this is a
      // no-op there. A pasted/rasterized image layer has its own
      // intrinsic canvas as the surface, positioned at the layer's
      // scene (offsetX, offsetY) — strokes need to be subtracted
      // by the offset to land on the right pixels.
      const dx = Math.round(cx - r - surface.offsetX);
      const dy = Math.round(cy - r - surface.offsetY);

      const pad = Math.max(0, Math.ceil(stroke.sigma * 2));

      // Padded source with edge-extended border — see Blur.
      const padded = buildEdgeExtendedPaddedSource(target, dx, dy, size, pad);
      if (!padded) return;

      const blurred = document.createElement('canvas');
      blurred.width = size;
      blurred.height = size;
      const blurredCtx = blurred.getContext('2d', { willReadFrequently: true });
      if (!blurredCtx) return;
      blurredCtx.filter = `blur(${stroke.sigma}px)`;
      blurredCtx.drawImage(padded, -pad, -pad);
      blurredCtx.filter = 'none';

      const destSample = acquireDabCanvas(destSampleCanvasCache, size);
      const destCtx = destSample.getContext('2d', { willReadFrequently: true });
      if (!destCtx) return;
      destCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
      const destImg = destCtx.getImageData(0, 0, size, size);
      const blurredImg = blurredCtx.getImageData(0, 0, size, size);

      // Unsharp-mask sample: per-pixel `dest + (dest - blurred)`,
      // i.e. `2*dest - blurred`. Out-of-range values clamp
      // automatically because ImageData is Uint8ClampedArray.
      // Alpha is preserved from dest — sharpening must never
      // change opacity.
      const sharpenedImg = new ImageData(size, size);
      const sData = sharpenedImg.data;
      const dData = destImg.data;
      const bData = blurredImg.data;
      const len = sData.length;
      for (let i = 0; i < len; i += 4) {
        sData[i] = 2 * dData[i] - bData[i];
        sData[i + 1] = 2 * dData[i + 1] - bData[i + 1];
        sData[i + 2] = 2 * dData[i + 2] - bData[i + 2];
        sData[i + 3] = dData[i + 3];
      }

      if (!stroke.brushMaskImageData) {
        const mctx = stroke.brushMask.getContext('2d', { willReadFrequently: true });
        if (!mctx) return;
        stroke.brushMaskImageData = mctx.getImageData(0, 0, size, size);
      }
      const maskImg = stroke.brushMaskImageData;

      // Lazy-allocate the per-stroke applied-mask buffer on the
      // first dab — only now do we know the active layer's
      // dimensions. Uint8 (resolution 1/255) is plenty for a mix
      // weight; a 4MP layer = 4MB which is acceptable for the
      // duration of one stroke. Layers above
      // `MAX_RETOUCH_LAYER_PIXELS` (100MP / 100MB) abandon the
      // stroke rather than allocating an unbounded buffer.
      if (!stroke.appliedMask) {
        const lw = target.width;
        const lh = target.height;
        if (lw * lh > MAX_RETOUCH_LAYER_PIXELS) {
          console.warn(
            `[retouch] Sharpen: layer ${lw}×${lh} (${lw * lh} px) exceeds ${MAX_RETOUCH_LAYER_PIXELS} pixel budget — abandoning stroke`,
          );
          sharpenStrokeRef.current = null;
          return;
        }
        stroke.appliedMaskW = lw;
        stroke.appliedMaskH = lh;
        stroke.appliedMask = new Uint8Array(lw * lh);
      }
      const applied = stroke.appliedMask;
      const aw = stroke.appliedMaskW;
      const ah = stroke.appliedMaskH;

      // Inline mix with per-pixel capacity check. `applied[li]`
      // tracks cumulative brush-mask-alpha applied at layer
      // pixel (lx, ly) during this stroke (0–255, where 255 =
      // a full-strength dose has been delivered). Each new dab
      // can only contribute the lesser of (a) what its brush
      // mask requests at this pixel and (b) what's still
      // available before the cap. Final mix weight m =
      // (give/255) * stroke.strength.
      const out = new ImageData(size, size);
      const oData = out.data;
      const destData = destImg.data;
      const sharpData = sharpenedImg.data;
      const maskData = maskImg.data;
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const i = (y * size + x) * 4;
          const lx = dx + x;
          const ly = dy + y;
          let m = 0;
          if (lx >= 0 && lx < aw && ly >= 0 && ly < ah) {
            const requestUnits = maskData[i + 3];
            const li = ly * aw + lx;
            const remainingUnits = 255 - applied[li];
            const giveUnits = requestUnits < remainingUnits ? requestUnits : remainingUnits;
            if (giveUnits > 0) {
              applied[li] = applied[li] + giveUnits;
              m = (giveUnits / 255) * stroke.strength;
            }
          }
          const inv = 1 - m;
          oData[i] = destData[i] * inv + sharpData[i] * m;
          oData[i + 1] = destData[i + 1] * inv + sharpData[i + 1] * m;
          oData[i + 2] = destData[i + 2] * inv + sharpData[i + 2] * m;
          oData[i + 3] = destData[i + 3] * inv + sharpData[i + 3] * m;
        }
      }

      const stamp = acquireDabCanvas(stampCanvasCache, size);
      const stampCtx = stamp.getContext('2d');
      if (!stampCtx) return;
      stampCtx.putImageData(out, 0, 0);

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      targetCtx.save();
      if (hasMarquee) applyMarqueeClipToContext(targetCtx, m, surface.offsetX, surface.offsetY);
      targetCtx.globalAlpha = stroke.strokeAlpha;
      targetCtx.drawImage(stamp, dx, dy);
      targetCtx.globalAlpha = 1;
      targetCtx.restore();

      surface.fabricImg.setElement(target);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.requestRenderAll();
    }

    // Dodge (Retouch tool, variant=dodge). Lightens pixels under
    // the brush via the standard `pixel + (255 - pixel) *
    // exposure * rangeWeight` formula (asymptotic toward 255 —
    // bounded). rangeWeight is luminance-banded
    // (shadows / midtones / highlights). The per-dab math is
    // bounded so we don't need Sharpen's per-stroke cap, but
    // dab cadence on a held cursor would still escalate fast at
    // high exposure — brush spacing throttles that: a dab fires
    // only when the cursor has moved at least
    // `radius * DODGE_BURN_SPACING_RATIO` since the last dab.
    // Drag = controlled buildup, hold-still = nothing further,
    // back-and-forth in one stroke = layered effect (the PS
    // Dodge feel).
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'dodge') return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);

      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      if (!ensurePaintSurfaceRef.current?.(layerId)) return;

      const radius = Math.max(1, Math.round(settings.size / 2));
      const brushMask = buildCloneBrushMask(radius, settings.hardness / 100);

      dodgeStrokeRef.current = {
        brushMask,
        brushMaskImageData: null,
        radius,
        range: settings.dodge.range,
        exposure: settings.dodge.exposure / 100,
        strokeAlpha: (settings.opacity / 100) * (settings.flow / 100),
        lastDabX: point.x,
        lastDabY: point.y,
        appliedMask: null,
        appliedMaskW: 0,
        appliedMaskH: 0,
      };

      dodgeDab(point.x, point.y);
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'dodge') return;
      if (!opt.e) return;
      const stroke = dodgeStrokeRef.current;
      if (!stroke) return;
      const point = canvas.getScenePoint(opt.e);

      // Brush spacing — only fire a dab if we've moved at least
      // a quarter brush-radius since the last dab.
      const minStep = stroke.radius * DODGE_BURN_SPACING_RATIO;
      const ddx = point.x - stroke.lastDabX;
      const ddy = point.y - stroke.lastDabY;
      if (ddx * ddx + ddy * ddy < minStep * minStep) return;

      stroke.lastDabX = point.x;
      stroke.lastDabY = point.y;
      dodgeDab(point.x, point.y);
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'dodge') return;
      if (!dodgeStrokeRef.current) return;
      dodgeStrokeRef.current = null;
      onObjectModifiedRef.current?.();
    });

    // One dodge dab. Reads the dest patch directly (no padding
    // canvas needed — Dodge has no Gaussian step, so there's no
    // edge-sample-from-outside issue), computes the dodged
    // sample per-pixel using luminance + range weight, then
    // mixes it into the surface weighted by brush mask alpha.
    // Per-channel application — color may shift slightly toward
    // white at strong dodge; modern Photoshop's "Protect Tones"
    // mode is HSL-based and is a v2 feature.
    function dodgeDab(cx: number, cy: number) {
      const stroke = dodgeStrokeRef.current;
      if (!stroke) return;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;
      const targetCtx = target.getContext('2d');
      if (!targetCtx) return;

      const r = stroke.radius;
      const size = r * 2;
      // Convert scene cursor coords to the surface canvas's local
      // pixel coords. Bg surface has offset (0, 0) so this is a
      // no-op there. A pasted/rasterized image layer has its own
      // intrinsic canvas as the surface, positioned at the layer's
      // scene (offsetX, offsetY) — strokes need to be subtracted
      // by the offset to land on the right pixels.
      const dx = Math.round(cx - r - surface.offsetX);
      const dy = Math.round(cy - r - surface.offsetY);

      const destSample = acquireDabCanvas(destSampleCanvasCache, size);
      const destCtx = destSample.getContext('2d', { willReadFrequently: true });
      if (!destCtx) return;
      destCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
      const destImg = destCtx.getImageData(0, 0, size, size);

      if (!stroke.brushMaskImageData) {
        const mctx = stroke.brushMask.getContext('2d', { willReadFrequently: true });
        if (!mctx) return;
        stroke.brushMaskImageData = mctx.getImageData(0, 0, size, size);
      }
      const maskImg = stroke.brushMaskImageData;

      // Lazy-allocate the per-stroke applied-mask buffer on the
      // first dab — only now do we know the active layer's
      // dimensions. See sharpenDab + MAX_RETOUCH_LAYER_PIXELS
      // for the same pattern + size cap.
      if (!stroke.appliedMask) {
        const lw = target.width;
        const lh = target.height;
        if (lw * lh > MAX_RETOUCH_LAYER_PIXELS) {
          console.warn(
            `[retouch] Dodge: layer ${lw}×${lh} (${lw * lh} px) exceeds ${MAX_RETOUCH_LAYER_PIXELS} pixel budget — abandoning stroke`,
          );
          dodgeStrokeRef.current = null;
          return;
        }
        stroke.appliedMaskW = lw;
        stroke.appliedMaskH = lh;
        stroke.appliedMask = new Uint8Array(lw * lh);
      }
      const applied = stroke.appliedMask;
      const aw = stroke.appliedMaskW;
      const ah = stroke.appliedMaskH;

      const out = new ImageData(size, size);
      const oData = out.data;
      const dData = destImg.data;
      const mData = maskImg.data;
      const exposure = stroke.exposure;
      const range = stroke.range;
      // Per-pixel mix with cap. Each pixel's request this dab is
      // its brush-mask-alpha; the actual contribution is capped
      // by remaining capacity (255 − applied[px]). The capacity
      // unit is brush-mask-alpha (0–255), independent of
      // exposure / rangeWeight / the per-dab scale, so the cap
      // semantics ("each pixel absorbs at most one full mask
      // dose per stroke") hold uniformly across slider settings.
      // Final m at this pixel = (give/255) × exposure ×
      // rangeWeight × DODGE_BURN_PER_DAB_SCALE.
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const i = (y * size + x) * 4;
          const r0 = dData[i];
          const g0 = dData[i + 1];
          const b0 = dData[i + 2];
          const a0 = dData[i + 3];
          const lx = dx + x;
          const ly = dy + y;
          let m = 0;
          if (lx >= 0 && lx < aw && ly >= 0 && ly < ah) {
            const requestUnits = mData[i + 3];
            const li = ly * aw + lx;
            const remainingUnits = 255 - applied[li];
            const giveUnits = requestUnits < remainingUnits ? requestUnits : remainingUnits;
            if (giveUnits > 0) {
              applied[li] = applied[li] + giveUnits;
              const lum = (0.299 * r0 + 0.587 * g0 + 0.114 * b0) / 255;
              const rw = dodgeBurnRangeWeight(lum, range);
              m = (giveUnits / 255) * exposure * rw * DODGE_BURN_PER_DAB_SCALE;
            }
          }
          oData[i] = r0 + (255 - r0) * m;
          oData[i + 1] = g0 + (255 - g0) * m;
          oData[i + 2] = b0 + (255 - b0) * m;
          oData[i + 3] = a0;
        }
      }

      const stamp = acquireDabCanvas(stampCanvasCache, size);
      const stampCtx = stamp.getContext('2d');
      if (!stampCtx) return;
      stampCtx.putImageData(out, 0, 0);

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      targetCtx.save();
      if (hasMarquee) applyMarqueeClipToContext(targetCtx, m, surface.offsetX, surface.offsetY);
      targetCtx.globalAlpha = stroke.strokeAlpha;
      targetCtx.drawImage(stamp, dx, dy);
      targetCtx.globalAlpha = 1;
      targetCtx.restore();

      surface.fabricImg.setElement(target);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.requestRenderAll();
    }

    // Burn (Retouch tool, variant=burn). Multiplicative inverse
    // of Dodge: `pixel * (1 - m)` per channel pushes toward 0
    // instead of toward 255. Same range curves
    // (dodgeBurnRangeWeight), same brush spacing, same
    // per-stroke cap. See dodgeDab and the dodgeStrokeRef type
    // doc for the rationale on each — the asymmetry between
    // Dodge and Burn is purely "lighten vs darken"; everything
    // else is shared.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'burn') return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);

      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      if (!ensurePaintSurfaceRef.current?.(layerId)) return;

      const radius = Math.max(1, Math.round(settings.size / 2));
      const brushMask = buildCloneBrushMask(radius, settings.hardness / 100);

      burnStrokeRef.current = {
        brushMask,
        brushMaskImageData: null,
        radius,
        range: settings.burn.range,
        exposure: settings.burn.exposure / 100,
        strokeAlpha: (settings.opacity / 100) * (settings.flow / 100),
        lastDabX: point.x,
        lastDabY: point.y,
        appliedMask: null,
        appliedMaskW: 0,
        appliedMaskH: 0,
      };

      burnDab(point.x, point.y);
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'burn') return;
      if (!opt.e) return;
      const stroke = burnStrokeRef.current;
      if (!stroke) return;
      const point = canvas.getScenePoint(opt.e);

      const minStep = stroke.radius * DODGE_BURN_SPACING_RATIO;
      const ddx = point.x - stroke.lastDabX;
      const ddy = point.y - stroke.lastDabY;
      if (ddx * ddx + ddy * ddy < minStep * minStep) return;

      stroke.lastDabX = point.x;
      stroke.lastDabY = point.y;
      burnDab(point.x, point.y);
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'burn') return;
      if (!burnStrokeRef.current) return;
      burnStrokeRef.current = null;
      onObjectModifiedRef.current?.();
    });

    function burnDab(cx: number, cy: number) {
      const stroke = burnStrokeRef.current;
      if (!stroke) return;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;
      const targetCtx = target.getContext('2d');
      if (!targetCtx) return;

      const r = stroke.radius;
      const size = r * 2;
      // Convert scene cursor coords to the surface canvas's local
      // pixel coords. Bg surface has offset (0, 0) so this is a
      // no-op there. A pasted/rasterized image layer has its own
      // intrinsic canvas as the surface, positioned at the layer's
      // scene (offsetX, offsetY) — strokes need to be subtracted
      // by the offset to land on the right pixels.
      const dx = Math.round(cx - r - surface.offsetX);
      const dy = Math.round(cy - r - surface.offsetY);

      const destSample = acquireDabCanvas(destSampleCanvasCache, size);
      const destCtx = destSample.getContext('2d', { willReadFrequently: true });
      if (!destCtx) return;
      destCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
      const destImg = destCtx.getImageData(0, 0, size, size);

      if (!stroke.brushMaskImageData) {
        const mctx = stroke.brushMask.getContext('2d', { willReadFrequently: true });
        if (!mctx) return;
        stroke.brushMaskImageData = mctx.getImageData(0, 0, size, size);
      }
      const maskImg = stroke.brushMaskImageData;

      if (!stroke.appliedMask) {
        const lw = target.width;
        const lh = target.height;
        if (lw * lh > MAX_RETOUCH_LAYER_PIXELS) {
          console.warn(
            `[retouch] Burn: layer ${lw}×${lh} (${lw * lh} px) exceeds ${MAX_RETOUCH_LAYER_PIXELS} pixel budget — abandoning stroke`,
          );
          burnStrokeRef.current = null;
          return;
        }
        stroke.appliedMaskW = lw;
        stroke.appliedMaskH = lh;
        stroke.appliedMask = new Uint8Array(lw * lh);
      }
      const applied = stroke.appliedMask;
      const aw = stroke.appliedMaskW;
      const ah = stroke.appliedMaskH;

      const out = new ImageData(size, size);
      const oData = out.data;
      const dData = destImg.data;
      const mData = maskImg.data;
      const exposure = stroke.exposure;
      const range = stroke.range;
      // Per-pixel mix, cap-aware (mirrors dodgeDab's loop). The
      // only difference is the formula: `pixel * (1 - m)`
      // multiplies toward 0 instead of `pixel + (255 - pixel)*m`
      // adding toward 255.
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const i = (y * size + x) * 4;
          const r0 = dData[i];
          const g0 = dData[i + 1];
          const b0 = dData[i + 2];
          const a0 = dData[i + 3];
          const lx = dx + x;
          const ly = dy + y;
          let m = 0;
          if (lx >= 0 && lx < aw && ly >= 0 && ly < ah) {
            const requestUnits = mData[i + 3];
            const li = ly * aw + lx;
            const remainingUnits = 255 - applied[li];
            const giveUnits = requestUnits < remainingUnits ? requestUnits : remainingUnits;
            if (giveUnits > 0) {
              applied[li] = applied[li] + giveUnits;
              const lum = (0.299 * r0 + 0.587 * g0 + 0.114 * b0) / 255;
              const rw = dodgeBurnRangeWeight(lum, range);
              m = (giveUnits / 255) * exposure * rw * DODGE_BURN_PER_DAB_SCALE;
            }
          }
          const inv = 1 - m;
          oData[i] = r0 * inv;
          oData[i + 1] = g0 * inv;
          oData[i + 2] = b0 * inv;
          oData[i + 3] = a0;
        }
      }

      const stamp = acquireDabCanvas(stampCanvasCache, size);
      const stampCtx = stamp.getContext('2d');
      if (!stampCtx) return;
      stampCtx.putImageData(out, 0, 0);

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      targetCtx.save();
      if (hasMarquee) applyMarqueeClipToContext(targetCtx, m, surface.offsetX, surface.offsetY);
      targetCtx.globalAlpha = stroke.strokeAlpha;
      targetCtx.drawImage(stamp, dx, dy);
      targetCtx.globalAlpha = 1;
      targetCtx.restore();

      surface.fabricImg.setElement(target);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.requestRenderAll();
    }

    // Sponge (Retouch tool, variant=sponge). Shifts saturation
    // without affecting brightness:
    //   desaturate: pixel + (L - pixel) * m   (toward gray of L)
    //   saturate:   pixel + (pixel - L) * m   (away from gray of L)
    // where L = perceived luminance of the pixel. Same brush
    // spacing + per-stroke cap pattern as Dodge/Burn. See
    // dodgeStrokeRef type doc for the full rationale on each
    // limiter — the asymmetry between Sponge and Dodge/Burn is
    // (a) Mode replaces Range bands, (b) the per-channel formula
    // is luminance-relative instead of brightness-shift.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'sponge') return;
      if (!opt.e) return;
      const point = canvas.getScenePoint(opt.e);

      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      if (!ensurePaintSurfaceRef.current?.(layerId)) return;

      const radius = Math.max(1, Math.round(settings.size / 2));
      const brushMask = buildCloneBrushMask(radius, settings.hardness / 100);

      spongeStrokeRef.current = {
        brushMask,
        brushMaskImageData: null,
        radius,
        mode: settings.sponge.mode,
        flow: settings.sponge.flow / 100,
        strokeAlpha: (settings.opacity / 100) * (settings.flow / 100),
        lastDabX: point.x,
        lastDabY: point.y,
        appliedMask: null,
        appliedMaskW: 0,
        appliedMaskH: 0,
      };

      spongeDab(point.x, point.y);
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'sponge') return;
      if (!opt.e) return;
      const stroke = spongeStrokeRef.current;
      if (!stroke) return;
      const point = canvas.getScenePoint(opt.e);

      const minStep = stroke.radius * DODGE_BURN_SPACING_RATIO;
      const ddx = point.x - stroke.lastDabX;
      const ddy = point.y - stroke.lastDabY;
      if (ddx * ddx + ddy * ddy < minStep * minStep) return;

      stroke.lastDabX = point.x;
      stroke.lastDabY = point.y;
      spongeDab(point.x, point.y);
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'retouch') return;
      const settings = retouchSettingsRef.current;
      if (settings?.variant !== 'sponge') return;
      if (!spongeStrokeRef.current) return;
      spongeStrokeRef.current = null;
      onObjectModifiedRef.current?.();
    });

    function spongeDab(cx: number, cy: number) {
      const stroke = spongeStrokeRef.current;
      if (!stroke) return;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return;
      const surface = ensurePaintSurfaceRef.current?.(layerId);
      if (!surface) return;
      const target = surface.canvas;
      const targetCtx = target.getContext('2d');
      if (!targetCtx) return;

      const r = stroke.radius;
      const size = r * 2;
      // Convert scene cursor coords to the surface canvas's local
      // pixel coords. Bg surface has offset (0, 0) so this is a
      // no-op there. A pasted/rasterized image layer has its own
      // intrinsic canvas as the surface, positioned at the layer's
      // scene (offsetX, offsetY) — strokes need to be subtracted
      // by the offset to land on the right pixels.
      const dx = Math.round(cx - r - surface.offsetX);
      const dy = Math.round(cy - r - surface.offsetY);

      const destSample = acquireDabCanvas(destSampleCanvasCache, size);
      const destCtx = destSample.getContext('2d', { willReadFrequently: true });
      if (!destCtx) return;
      destCtx.drawImage(target, dx, dy, size, size, 0, 0, size, size);
      const destImg = destCtx.getImageData(0, 0, size, size);

      if (!stroke.brushMaskImageData) {
        const mctx = stroke.brushMask.getContext('2d', { willReadFrequently: true });
        if (!mctx) return;
        stroke.brushMaskImageData = mctx.getImageData(0, 0, size, size);
      }
      const maskImg = stroke.brushMaskImageData;

      if (!stroke.appliedMask) {
        const lw = target.width;
        const lh = target.height;
        if (lw * lh > MAX_RETOUCH_LAYER_PIXELS) {
          console.warn(
            `[retouch] Sponge: layer ${lw}×${lh} (${lw * lh} px) exceeds ${MAX_RETOUCH_LAYER_PIXELS} pixel budget — abandoning stroke`,
          );
          spongeStrokeRef.current = null;
          return;
        }
        stroke.appliedMaskW = lw;
        stroke.appliedMaskH = lh;
        stroke.appliedMask = new Uint8Array(lw * lh);
      }
      const applied = stroke.appliedMask;
      const aw = stroke.appliedMaskW;
      const ah = stroke.appliedMaskH;

      const out = new ImageData(size, size);
      const oData = out.data;
      const dData = destImg.data;
      const mData = maskImg.data;
      const flow = stroke.flow;
      const isSaturate = stroke.mode === 'saturate';
      // Per-pixel mix, cap-aware (mirrors dodgeDab / burnDab
      // structure). The per-channel formula is luminance-
      // relative — each channel is pulled toward / pushed away
      // from the *pixel's own* luminance gray, which preserves
      // perceived brightness while shifting saturation.
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const i = (y * size + x) * 4;
          const r0 = dData[i];
          const g0 = dData[i + 1];
          const b0 = dData[i + 2];
          const a0 = dData[i + 3];
          const lx = dx + x;
          const ly = dy + y;
          let m = 0;
          if (lx >= 0 && lx < aw && ly >= 0 && ly < ah) {
            const requestUnits = mData[i + 3];
            const li = ly * aw + lx;
            const remainingUnits = 255 - applied[li];
            const giveUnits = requestUnits < remainingUnits ? requestUnits : remainingUnits;
            if (giveUnits > 0) {
              applied[li] = applied[li] + giveUnits;
              m = (giveUnits / 255) * flow * DODGE_BURN_PER_DAB_SCALE;
            }
          }
          const lum = 0.299 * r0 + 0.587 * g0 + 0.114 * b0;
          if (isSaturate) {
            // Push away from gray of same luminance. Out-of-
            // range RGB clamps via Uint8ClampedArray on the
            // ImageData write.
            oData[i] = r0 + (r0 - lum) * m;
            oData[i + 1] = g0 + (g0 - lum) * m;
            oData[i + 2] = b0 + (b0 - lum) * m;
          } else {
            // Mix toward gray of same luminance.
            oData[i] = r0 + (lum - r0) * m;
            oData[i + 1] = g0 + (lum - g0) * m;
            oData[i + 2] = b0 + (lum - b0) * m;
          }
          oData[i + 3] = a0;
        }
      }

      const stamp = acquireDabCanvas(stampCanvasCache, size);
      const stampCtx = stamp.getContext('2d');
      if (!stampCtx) return;
      stampCtx.putImageData(out, 0, 0);

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      targetCtx.save();
      if (hasMarquee) applyMarqueeClipToContext(targetCtx, m, surface.offsetX, surface.offsetY);
      targetCtx.globalAlpha = stroke.strokeAlpha;
      targetCtx.drawImage(stamp, dx, dy);
      targetCtx.globalAlpha = 1;
      targetCtx.restore();

      surface.fabricImg.setElement(target);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.requestRenderAll();
    }

    // Fill tool — click to flood-fill the clicked pixel color region with the
    // current brush color. The fill is rendered as a new image-shaped object
    // tagged with the active layerId, and clipped to the active marquee if one
    // is finalized. Tolerance/contiguous reuse the magic-wand settings.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'fill') return;
      if (!opt.e) return;
      const bg = getBackgroundImage();
      if (!bg) return;
      const pointer = canvas.getScenePoint(opt.e);
      const px = Math.round(pointer.x);
      const py = Math.round(pointer.y);
      const bgW = bg.width || 0;
      const bgH = bg.height || 0;
      if (px < 0 || px >= bgW || py < 0 || py >= bgH) return;

      const imgEl = bg.getElement() as HTMLImageElement | HTMLCanvasElement;
      const sampler = document.createElement('canvas');
      sampler.width = bgW;
      sampler.height = bgH;
      const sCtx = sampler.getContext('2d');
      if (!sCtx) return;
      sCtx.drawImage(imgEl, 0, 0);
      const imgData = sCtx.getImageData(0, 0, bgW, bgH);

      const mask = computeWandMask(
        imgData,
        px,
        py,
        wandToleranceRef.current,
        wandContiguousRef.current,
      );

      // If a finalized marquee is active, constrain the fill: the click must
      // land inside the marquee, and the wand mask is intersected with the
      // marquee mask so only pixels inside the marquee are painted. This
      // applies regardless of the marquee shape (rect, ellipse, lasso,
      // polygon, wand).
      const fillMarquee = marqueeRef.current;
      if (fillMarquee.obj && !fillMarquee.isDrawing) {
        const marqueeMask = buildMarqueeMask(fillMarquee, bgW, bgH);
        if (marqueeMask) {
          const clickIdx = py * bgW + px;
          if (!marqueeMask[clickIdx]) return;
          for (let i = 0; i < mask.length; i++) {
            if (!marqueeMask[i]) mask[i] = 0;
          }
        }
      }

      // Render the fill into an offscreen canvas: brushColor where mask=1,
      // transparent elsewhere.
      const fillCanvas = document.createElement('canvas');
      fillCanvas.width = bgW;
      fillCanvas.height = bgH;
      const fCtx = fillCanvas.getContext('2d');
      if (!fCtx) return;
      const color = brushColorRef.current;
      const r = parseInt(color.slice(1, 3), 16) || 0;
      const g = parseInt(color.slice(3, 5), 16) || 0;
      const b = parseInt(color.slice(5, 7), 16) || 0;
      const fillData = fCtx.createImageData(bgW, bgH);
      let painted = 0;
      for (let i = 0; i < mask.length; i++) {
        if (mask[i]) {
          const di = i * 4;
          fillData.data[di] = r;
          fillData.data[di + 1] = g;
          fillData.data[di + 2] = b;
          fillData.data[di + 3] = 255;
          painted++;
        }
      }
      if (painted === 0) return;
      fCtx.putImageData(fillData, 0, 0);

      const fillImg = new fabric.FabricImage(fillCanvas, {
        left: 0,
        top: 0,
        // v7 origin default flipped to center; we position by top-left.
        originX: 'left',
        originY: 'top',
        scaleX: 1,
        scaleY: 1,
        selectable: true,
        evented: true,
      });
      (fillImg as fabric.FabricImage & { id: string }).id = `fill_${Date.now()}`;

      const layerId = activeLayerIdRef.current;
      if (layerId) setObjectLayerId(fillImg, layerId);

      canvas.add(fillImg);
      canvas.renderAll();
      onObjectModifiedRef.current?.();
    });

    // Marquee selection — supports rect, ellipse, lasso, and polygon shapes.
    // Rect/ellipse: drag to size. Lasso: drag to capture freeform path.
    // Polygon: each click adds a vertex; close by clicking near the first point or double-clicking.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'marquee') return;
      if (opt.e === undefined) return;
      if (canvas.findTarget(opt.e).target) return;

      const pointer = canvas.getScenePoint(opt.e);
      const m = marqueeRef.current;

      if (m.shape === 'rect' || m.shape === 'ellipse') {
        if (m.obj) canvas.remove(m.obj);
        const obj =
          m.shape === 'rect'
            ? new fabric.Rect({
                ...MARQUEE_STYLE,
                left: pointer.x,
                top: pointer.y,
                width: 0,
                height: 0,
              })
            : new fabric.Ellipse({
                ...MARQUEE_STYLE,
                left: pointer.x,
                top: pointer.y,
                rx: 0,
                ry: 0,
              });
        tagAsMarquee(obj);
        applyMarqueeColor(obj, marqueeColorRef.current);
        applyMarqueeStroke(canvas, obj);
        canvas.add(obj);
        m.obj = obj;
        m.startX = pointer.x;
        m.startY = pointer.y;
        m.points = [];
        m.isDrawing = true;
      } else if (m.shape === 'lasso') {
        if (m.obj) canvas.remove(m.obj);
        m.points = [{ x: pointer.x, y: pointer.y }];
        const poly = new fabric.Polyline(m.points, MARQUEE_STYLE);
        tagAsMarquee(poly);
        applyMarqueeColor(poly, marqueeColorRef.current);
        applyMarqueeStroke(canvas, poly);
        canvas.add(poly);
        m.obj = poly;
        m.isDrawing = true;
      } else if (m.shape === 'polygon') {
        if (!m.isDrawing) {
          if (m.obj) canvas.remove(m.obj);
          m.points = [{ x: pointer.x, y: pointer.y }];
          const poly = new fabric.Polyline(m.points, MARQUEE_STYLE);
          tagAsMarquee(poly);
          applyMarqueeColor(poly, marqueeColorRef.current);
          applyMarqueeStroke(canvas, poly);
          canvas.add(poly);
          m.obj = poly;
          m.isDrawing = true;
        } else {
          const first = m.points[0];
          const dx = pointer.x - first.x;
          const dy = pointer.y - first.y;
          const threshold = POLYGON_CLOSE_THRESHOLD_PX / canvas.getZoom();
          if (m.points.length >= 3 && dx * dx + dy * dy < threshold * threshold) {
            closePolygonMarquee(canvas, m, marqueeColorRef.current);
          } else {
            m.points = [...m.points, { x: pointer.x, y: pointer.y }];
            replaceMarqueePolyline(canvas, m, marqueeColorRef.current);
          }
        }
      } else if (m.shape === 'wand') {
        const bg = getBackgroundImage();
        if (!bg) {
          canvas.renderAll();
          return;
        }
        const imgEl = bg.getElement() as HTMLImageElement | HTMLCanvasElement;
        const bgW = bg.width || 0;
        const bgH = bg.height || 0;
        const px = Math.round(pointer.x);
        const py = Math.round(pointer.y);
        if (px < 0 || px >= bgW || py < 0 || py >= bgH) {
          canvas.renderAll();
          return;
        }

        // Clear any prior wand selection first.
        if (m.obj) canvas.remove(m.obj);
        for (const o of m.extraObjs) canvas.remove(o);
        m.obj = null;
        m.extraObjs = [];
        m.points = [];
        m.wandMaskCanvas = null;

        // Sample image pixels and compute the mask.
        const offscreen = document.createElement('canvas');
        offscreen.width = bgW;
        offscreen.height = bgH;
        const ctx = offscreen.getContext('2d');
        if (!ctx) {
          canvas.renderAll();
          return;
        }
        ctx.drawImage(imgEl, 0, 0);
        const imgData = ctx.getImageData(0, 0, bgW, bgH);
        const tolerance = wandToleranceRef.current;
        const contiguous = wandContiguousRef.current;
        const mask = computeWandMask(imgData, px, py, tolerance, contiguous);

        // Cache the mask as an opaque-where-selected canvas for the eraser path.
        // `willReadFrequently` so the morphology helpers can `getImageData`
        // back from this canvas without the browser warning.
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = bgW;
        maskCanvas.height = bgH;
        const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
        if (maskCtx) {
          const maskData = maskCtx.createImageData(bgW, bgH);
          for (let i = 0; i < mask.length; i++) {
            if (mask[i]) {
              const di = i * 4;
              maskData.data[di] = 255;
              maskData.data[di + 1] = 255;
              maskData.data[di + 2] = 255;
              maskData.data[di + 3] = 255;
            }
          }
          maskCtx.putImageData(maskData, 0, 0);
        }
        m.wandMaskCanvas = maskCanvas;
        m.wandTolerance = tolerance;
        m.wandContiguous = contiguous;

        // Trace contours into a single fabric.Path with evenodd
        // fill, so masks with holes (subject + interior bg) render
        // as a topology-correct donut instead of overlapping fills.
        const contours = traceContours(mask, bgW, bgH);
        const path = buildMarqueePath(contours);
        if (path) {
          tagAsMarquee(path);
          applyMarqueeColor(path, marqueeColorRef.current);
          applyMarqueeStroke(canvas, path);
          canvas.add(path);
          m.obj = path;
          m.extraObjs = [];
        }
        m.isDrawing = false;
      }
      canvas.renderAll();
      refreshMarqueeSelectionState();
    });

    canvas.on('mouse:move', (opt) => {
      const m = marqueeRef.current;
      if (!m.isDrawing || !m.obj || !opt.e) return;
      if (m.shape === 'polygon') return; // polygon advances on click, not move

      const pointer = canvas.getScenePoint(opt.e);

      const shiftHeld = !!(opt.e as MouseEvent).shiftKey;
      const constrainSquare = forceSquareRef.current || shiftHeld;

      if (m.shape === 'rect') {
        const dx = pointer.x - m.startX;
        const dy = pointer.y - m.startY;
        let width: number;
        let height: number;
        let left: number;
        let top: number;
        if (constrainSquare) {
          const size = Math.max(Math.abs(dx), Math.abs(dy));
          width = size;
          height = size;
          left = dx >= 0 ? m.startX : m.startX - size;
          top = dy >= 0 ? m.startY : m.startY - size;
        } else {
          width = Math.abs(dx);
          height = Math.abs(dy);
          left = Math.min(m.startX, pointer.x);
          top = Math.min(m.startY, pointer.y);
        }
        (m.obj as fabric.Rect).set({ left, top, width, height });
      } else if (m.shape === 'ellipse') {
        const dx = pointer.x - m.startX;
        const dy = pointer.y - m.startY;
        let w: number;
        let h: number;
        let left: number;
        let top: number;
        if (constrainSquare) {
          const size = Math.max(Math.abs(dx), Math.abs(dy));
          w = size;
          h = size;
          left = dx >= 0 ? m.startX : m.startX - size;
          top = dy >= 0 ? m.startY : m.startY - size;
        } else {
          w = Math.abs(dx);
          h = Math.abs(dy);
          left = Math.min(m.startX, pointer.x);
          top = Math.min(m.startY, pointer.y);
        }
        (m.obj as fabric.Ellipse).set({ left, top, rx: w / 2, ry: h / 2 });
      } else if (m.shape === 'lasso') {
        m.points = [...m.points, { x: pointer.x, y: pointer.y }];
        replaceMarqueePolyline(canvas, m, marqueeColorRef.current);
      }
      canvas.renderAll();
    });

    canvas.on('mouse:up', () => {
      const m = marqueeRef.current;
      if (!m.isDrawing || !m.obj) return;
      if (m.shape === 'polygon') return; // polygon stays open until explicit close

      if (m.shape === 'rect' || m.shape === 'ellipse') {
        m.isDrawing = false;
        const w =
          m.shape === 'rect'
            ? (m.obj as fabric.Rect).width || 0
            : ((m.obj as fabric.Ellipse).rx || 0) * 2;
        const h =
          m.shape === 'rect'
            ? (m.obj as fabric.Rect).height || 0
            : ((m.obj as fabric.Ellipse).ry || 0) * 2;
        if (w < 3 && h < 3) {
          canvas.remove(m.obj);
          m.obj = null;
        }
      } else if (m.shape === 'lasso') {
        m.isDrawing = false;
        if (m.points.length < 3) {
          canvas.remove(m.obj);
          m.obj = null;
          m.points = [];
        } else {
          canvas.remove(m.obj);
          const closed = new fabric.Polygon(m.points, MARQUEE_STYLE);
          tagAsMarquee(closed);
          applyMarqueeColor(closed, marqueeColorRef.current);
          applyMarqueeStroke(canvas, closed);
          canvas.add(closed);
          m.obj = closed;
        }
      }
      canvas.renderAll();
      refreshMarqueeSelectionState();
    });

    // Non-affine Transform handle drag (Tier 1 Group E Phase 1b).
    // Custom mouse handlers — handles are non-selectable visual
    // overlays, so Fabric's selection system doesn't route the
    // drag. Initial implementation used selectable Circles +
    // object:moving but Fabric's hit-test priorities (active
    // object eats clicks within its bbox even with
    // preserveObjectStacking=true) made handles silently
    // unresponsive. Hit-test now happens manually via scene-space
    // distance to each handle position; HANDLE_HIT_RADIUS is
    // bumped above the visual radius so the user doesn't need
    // pixel-perfect aim.
    const HANDLE_HIT_RADIUS = 12;
    canvas.on('mouse:down', (opt) => {
      const overlay = transformOverlayRef.current;
      if (!overlay || !opt.e) return;
      const point = canvas.getScenePoint(opt.e);
      const points: { x: number; y: number }[] =
        overlay.mode === 'warp' ? [...overlay.warpGrid] : [...overlay.distortQuad];
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let i = 0; i < points.length; i++) {
        const dx = points[i].x - point.x;
        const dy = points[i].y - point.y;
        const d = Math.hypot(dx, dy);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      if (bestIdx < 0 || bestDist > HANDLE_HIT_RADIUS) return;
      transformDragRef.current = { handleIndex: bestIdx };
    });

    canvas.on('mouse:move', (opt) => {
      const drag = transformDragRef.current;
      const overlay = transformOverlayRef.current;
      if (!drag || !overlay || !opt.e) return;
      const point = canvas.getScenePoint(opt.e);
      const idx = drag.handleIndex;
      const x = point.x;
      const y = point.y;
      if (overlay.mode === 'warp') {
        const next = [...overlay.warpGrid] as { x: number; y: number }[];
        next[idx] = { x, y };
        overlay.warpGrid = next as unknown as WarpGrid;
        const h = overlay.handles[idx];
        if (h) {
          h.set({ left: x, top: y });
          h.setCoords();
        }
      } else {
        const next = [...overlay.distortQuad] as { x: number; y: number }[];
        next[idx] = { x, y };
        const h = overlay.handles[idx];
        if (h) {
          h.set({ left: x, top: y });
          h.setCoords();
        }
        if (overlay.mode === 'perspective') {
          // Mirror co-lateral corner for 1-point perspective.
          // Pairs: tl(0) ↔ tr(1), bl(3) ↔ br(2).
          const initialQuad: ProjectiveQuad = [
            { x: overlay.bbox.left, y: overlay.bbox.top },
            { x: overlay.bbox.left + overlay.bbox.width, y: overlay.bbox.top },
            {
              x: overlay.bbox.left + overlay.bbox.width,
              y: overlay.bbox.top + overlay.bbox.height,
            },
            { x: overlay.bbox.left, y: overlay.bbox.top + overlay.bbox.height },
          ];
          const pairIdx = idx === 0 ? 1 : idx === 1 ? 0 : idx === 2 ? 3 : 2;
          const dx = x - initialQuad[idx].x;
          const dy = y - initialQuad[idx].y;
          next[pairIdx] = {
            x: initialQuad[pairIdx].x - dx,
            y: initialQuad[pairIdx].y + dy,
          };
          const peer = overlay.handles[pairIdx];
          if (peer) {
            peer.set({ left: next[pairIdx].x, top: next[pairIdx].y });
            peer.setCoords();
          }
        }
        overlay.distortQuad = next as unknown as ProjectiveQuad;
      }
      updateNonAffinePreviewRef.current?.();
    });

    canvas.on('mouse:up', () => {
      transformDragRef.current = null;
    });

    canvas.on('mouse:dblclick', () => {
      if (activeToolRef.current !== 'marquee') return;
      const m = marqueeRef.current;
      if (m.shape !== 'polygon' || !m.isDrawing) return;
      // The browser fires two mousedowns before dblclick, so the second
      // mousedown already added a vertex at the dblclick position. Pop it
      // so the closing edge runs to the previous vertex, not back to itself.
      if (m.points.length >= 2) {
        m.points = m.points.slice(0, -1);
      }
      if (m.points.length < 3) return;
      closePolygonMarquee(canvas, m, marqueeColorRef.current);
      canvas.renderAll();
      refreshMarqueeSelectionState();
    });

    // Quick Mask brush handlers (Tier 1 Group D). Active only when
    // the user has toggled into Quick Mask mode from the StatusBar
    // selection chip popover — the reducer flips `activeTool` to
    // `'quickmask'` atomically (see `useImageEditorState` →
    // `SET_QUICK_MASK_ACTIVE`). Strokes paint into the working
    // alpha mask via a soft radial-gradient dab; the visual overlay
    // is rebuilt from the mask after each stroke.
    canvas.on('mouse:down', (opt) => {
      if (activeToolRef.current !== 'quickmask') return;
      const qmCanvas = quickMaskCanvasRef.current;
      if (!qmCanvas || !opt.e) return;
      const point = canvas.getScenePoint(opt.e);
      const altPressed = (opt.e as MouseEvent).altKey === true;
      // Default mode from the panel (or status-bar) toggle; Alt
      // inverts for the duration of the stroke.
      const baseMode = quickMaskModeRef.current;
      const inverted = altPressed ? (baseMode === 'add' ? 'subtract' : 'add') : baseMode;
      quickMaskStrokeRef.current = {
        mode: inverted,
        radius: Math.max(1, brushSizeRef.current / 2),
        // Soft brush — center 50% radius is fully opaque, outer
        // edge fades to 0. Matches Photoshop's default brush
        // hardness for QM (50%).
        hardness: 0.5,
        opacity: brushOpacityRef.current,
        lastX: point.x,
        lastY: point.y,
      };
      paintQuickMaskDabRef.current?.(point.x, point.y);
      refreshQuickMaskOverlayRef.current?.();
    });

    canvas.on('mouse:move', (opt) => {
      if (activeToolRef.current !== 'quickmask') return;
      const stroke = quickMaskStrokeRef.current;
      if (!stroke || !opt.e) return;
      const point = canvas.getScenePoint(opt.e);
      const dx = point.x - stroke.lastX;
      const dy = point.y - stroke.lastY;
      const dist = Math.hypot(dx, dy);
      // Brush spacing = radius × 0.25 (Photoshop default). Cheap and
      // gives a continuous-feeling stroke without stamping every
      // pointer sample.
      const spacing = Math.max(1, stroke.radius * 0.25);
      if (dist < spacing) return;
      const steps = Math.ceil(dist / spacing);
      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        paintQuickMaskDabRef.current?.(stroke.lastX + dx * t, stroke.lastY + dy * t);
      }
      stroke.lastX = point.x;
      stroke.lastY = point.y;
      refreshQuickMaskOverlayRef.current?.();
    });

    canvas.on('mouse:up', () => {
      if (activeToolRef.current !== 'quickmask') return;
      quickMaskStrokeRef.current = null;
    });

    // Two-finger pinch zoom on touch devices. Bound to the upper canvas so we get
    // raw touch events with full multi-touch info (Fabric's mouse:* events only
    // surface the first finger). passive:false lets us preventDefault to suppress
    // the browser's native pinch-zoom of the page.
    const upperEl = canvas.upperCanvasEl as HTMLCanvasElement | undefined;
    const touchDistance = (e: TouchEvent) => {
      const a = e.touches[0];
      const b = e.touches[1];
      return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
    };
    const touchMidpoint = (e: TouchEvent, el: HTMLElement) => {
      const a = e.touches[0];
      const b = e.touches[1];
      const rect = el.getBoundingClientRect();
      return new fabric.Point(
        (a.clientX + b.clientX) / 2 - rect.left,
        (a.clientY + b.clientY) / 2 - rect.top,
      );
    };
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 2 || !upperEl) return;
      e.preventDefault();
      const vt = canvas.viewportTransform;
      pinchRef.current = {
        isPinching: true,
        startDist: touchDistance(e),
        startZoom: vt ? vt[0] : 1,
      };
      panRef.current.isPanning = false;
      // Cancel any in-progress / finalized marquee — including wand contours
      // (extraObjs) and the cached wand mask, which the marquee tool tracks
      // alongside the primary object.
      const m = marqueeRef.current;
      if (m.obj || m.extraObjs.length > 0) {
        if (m.obj) canvas.remove(m.obj);
        for (const o of m.extraObjs) canvas.remove(o);
        m.obj = null;
        m.extraObjs = [];
        m.points = [];
        m.wandMaskCanvas = null;
        m.isDrawing = false;
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!pinchRef.current.isPinching || e.touches.length !== 2 || !upperEl) return;
      e.preventDefault();
      const newDist = touchDistance(e);
      if (pinchRef.current.startDist === 0) return;
      const factor = newDist / pinchRef.current.startDist;
      const newZoom = Math.max(0.1, Math.min(10, pinchRef.current.startZoom * factor));
      canvas.zoomToPoint(touchMidpoint(e, upperEl), newZoom);
      if (marqueeRef.current.obj) {
        applyMarqueeStroke(canvas, marqueeRef.current.obj);
        for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
      }
      canvas.renderAll();
      onZoomChangedRef.current?.(newZoom);
      updateViewportInfoRef.current();
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        pinchRef.current.isPinching = false;
      }
    };
    if (upperEl) {
      upperEl.addEventListener('touchstart', onTouchStart, { passive: false });
      upperEl.addEventListener('touchmove', onTouchMove, { passive: false });
      upperEl.addEventListener('touchend', onTouchEnd);
      upperEl.addEventListener('touchcancel', onTouchEnd);
    }

    // Abandon any in-flight retouch stroke if the window loses
    // focus (alt-tab, browser switcher) or the pointer release
    // happens outside the canvas — `mouse:up` won't fire in
    // either case and the per-stroke ref + `appliedMask` would
    // leak. The didClean guard prevents double-firing
    // onObjectModified on a normal mouse:up release (Fabric's
    // mouse:up runs alongside window pointerup; whichever runs
    // second sees null refs and skips the call).
    const onAbandonStroke = () => {
      let didClean = false;
      if (cloneStrokeRef.current) {
        cloneStrokeRef.current = null;
        didClean = true;
      }
      if (smudgeStrokeRef.current) {
        smudgeStrokeRef.current = null;
        didClean = true;
      }
      if (blurStrokeRef.current) {
        blurStrokeRef.current = null;
        didClean = true;
      }
      if (sharpenStrokeRef.current) {
        sharpenStrokeRef.current = null;
        didClean = true;
      }
      if (dodgeStrokeRef.current) {
        dodgeStrokeRef.current = null;
        didClean = true;
      }
      if (burnStrokeRef.current) {
        burnStrokeRef.current = null;
        didClean = true;
      }
      if (spongeStrokeRef.current) {
        spongeStrokeRef.current = null;
        didClean = true;
      }
      if (didClean) onObjectModifiedRef.current?.();
    };
    window.addEventListener('blur', onAbandonStroke);
    window.addEventListener('pointerup', onAbandonStroke);

    return () => {
      observer.disconnect();
      if (upperEl) {
        upperEl.removeEventListener('touchstart', onTouchStart);
        upperEl.removeEventListener('touchmove', onTouchMove);
        upperEl.removeEventListener('touchend', onTouchEnd);
        upperEl.removeEventListener('touchcancel', onTouchEnd);
      }
      window.removeEventListener('blur', onAbandonStroke);
      window.removeEventListener('pointerup', onAbandonStroke);
      // Drop any cursor subscribers that survived their host
      // component's unmount. StrictMode mount-unmount-mount and
      // any future consumer that forgets to unsubscribe would
      // otherwise pile up in the Set — defense in depth.
      cursorListenersRef.current.clear();
      canvas.dispose();
      canvasRef.current = null;
    };
  }, []);

  /** Zoom and pan the viewport so the image fits centered in the wrapper */
  const zoomToFit = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return 1;

    const vpW = wrapper.clientWidth;
    const vpH = wrapper.clientHeight;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;
    if (!imgW || !imgH || !vpW || !vpH) return 1;

    // Ensure canvas element matches wrapper
    canvas.setDimensions({ width: vpW, height: vpH });

    const padding = 40;
    const zoom = Math.min((vpW - padding * 2) / imgW, (vpH - padding * 2) / imgH, 1);

    const panX = (vpW - imgW * zoom) / 2;
    const panY = (vpH - imgH * zoom) / 2;

    canvas.setViewportTransform([zoom, 0, 0, zoom, panX, panY]);
    if (marqueeRef.current.obj) {
      applyMarqueeStroke(canvas, marqueeRef.current.obj);
      for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
    }
    canvas.renderAll();
    updateViewportInfo();
    return zoom;
  }, [wrapperRef, updateViewportInfo]);

  /** Load an image onto the canvas. Returns the fit zoom level. */
  const loadImage = useCallback(
    async (src: string): Promise<number> => {
      const canvas = canvasRef.current;
      if (!canvas) return 1;

      const img = await fabric.FabricImage.fromURL(src, { crossOrigin: 'anonymous' });
      backgroundImageRef.current = img;
      tagAsBackground(img);

      const imgW = img.width || 1;
      const imgH = img.height || 1;
      imageDimsRef.current = { width: imgW, height: imgH };

      // Replace the loaded HTMLImageElement with a same-sized canvas so the
      // bg becomes a paintable raster surface.
      const bgCanvas = ensureCanvasElement(
        img.getElement() as HTMLImageElement | HTMLCanvasElement,
        imgW,
        imgH,
      );
      img.setElement(bgCanvas);

      img.set({
        left: 0,
        top: 0,
        // Fabric v7 changed the default origin from 'left'/'top' to
        // 'center'/'center'. The bg image is positioned by its
        // top-left corner at (0, 0), so we restore the v6 default
        // explicitly. Without this, half the image renders into
        // negative coords ("only the bottom half visible in the
        // upper-left").
        originX: 'left',
        originY: 'top',
        scaleX: 1,
        scaleY: 1,
        selectable: false,
        evented: false,
        erasable: false,
        // Disable Fabric's per-object cache so live edits to the underlying
        // canvas show up on the next render without manual invalidation.
        objectCaching: false,
      } as Partial<fabric.FabricImage>);

      // The paint-surface map is keyed by layerId, which the caller sets via
      // `setBackgroundLayerId`. Reset any prior entries here; the new bg will
      // be registered when its layerId is assigned.
      paintSurfacesRef.current.clear();

      // Clear any marquee left over from the previous image. `canvas.clear()`
      // below removes the marquee's fabric.Rect from the canvas, but our
      // marqueeRef would still hold an orphaned reference whose stored
      // width/height/coords are in the OLD image's scene space. A subsequent
      // Smart Create / Smart Delete would then crop at coordinates that no
      // longer match the active layer's pixel grid, producing an empty
      // source for the model and an invisible bake.
      marqueeRef.current.obj = null;
      marqueeRef.current.extraObjs = [];
      marqueeRef.current.points = [];
      marqueeRef.current.wandMaskCanvas = null;
      marqueeRef.current.isDrawing = false;
      setHasMarqueeSelection(false);

      canvas.clear();
      canvas.add(img);
      canvas.sendObjectToBack(img);

      // Wait for layout to settle, then fit
      await new Promise((r) => requestAnimationFrame(r));
      const zoom = zoomToFit();

      onCanvasDimensions?.(imgW, imgH);
      return zoom;
    },
    [onCanvasDimensions, zoomToFit],
  );

  /** Combine a hex color and 0–1 opacity into a Fabric-friendly rgba string. */
  const composeBrushColor = (hex: string, opacity: number): string => {
    const r = parseInt(hex.slice(1, 3), 16) || 0;
    const g = parseInt(hex.slice(3, 5), 16) || 0;
    const b = parseInt(hex.slice(5, 7), 16) || 0;
    const a = Math.max(0, Math.min(1, opacity));
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  /** Enable free drawing mode */
  const enableDrawMode = useCallback((settings: BrushSettings) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.isDrawingMode = true;
    const brush = new fabric.PencilBrush(canvas);
    brush.color = composeBrushColor(settings.color, settings.opacity);
    brush.width = settings.width;
    canvas.freeDrawingBrush = brush;
    brushSizeRef.current = settings.width;
    brushOpacityRef.current = settings.opacity;
  }, []);

  /**
   * Mutate the active free-drawing brush's color/width/opacity in place. Use
   * this on settings changes while the Draw tool is active so the next stroke
   * picks up the new values without re-toggling tools. For Eraser, only the
   * width is updated (the color is hard-coded for destination-out semantics).
   */
  const updateBrush = useCallback((settings: BrushSettings) => {
    // Always mirror opacity into the eraser ref — even when the active tool
    // is Draw, the user might switch to Eraser later expecting the slider
    // they just set to apply.
    eraserOpacityRef.current = settings.opacity;
    // Mirror width + opacity for tools that don't go through Fabric's
    // freeDrawingBrush (Quick Mask).
    brushSizeRef.current = settings.width;
    brushOpacityRef.current = settings.opacity;
    const canvas = canvasRef.current;
    if (!canvas || !canvas.freeDrawingBrush) return;
    if (activeToolRef.current === 'draw') {
      canvas.freeDrawingBrush.color = composeBrushColor(settings.color, settings.opacity);
    }
    if (activeToolRef.current === 'draw' || activeToolRef.current === 'eraser') {
      canvas.freeDrawingBrush.width = settings.width;
    }
  }, []);

  /** Disable drawing mode */
  const disableDrawMode = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.isDrawingMode = false;
  }, []);

  /** Add a text object to the canvas center (in image coordinates) */
  const addText = useCallback(
    (
      text: string,
      settings: TextSettings,
      layerId?: string,
      position?: { x: number; y: number },
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;

      const textObj = new fabric.IText(text, {
        left: position ? position.x : imgW / 2,
        top: position ? position.y : imgH / 2,
        fontFamily: settings.fontFamily,
        fontSize: settings.fontSize,
        fontWeight: settings.fontWeight,
        fontStyle: settings.fontStyle,
        underline: settings.underline,
        textAlign: settings.textAlign,
        fill: settings.fill,
        originX: 'center',
        originY: 'center',
      });

      (textObj as fabric.IText & { id: string }).id = `text_${Date.now()}`;
      if (layerId) setObjectLayerId(textObj, layerId);
      canvas.add(textObj);
      canvas.setActiveObject(textObj);
      // Stash the arch slider value on the object for later round-trip; build
      // the path now that Fabric has measured the text's width.
      (textObj as fabric.IText & { archValue: number }).archValue = settings.arch;
      if (settings.arch !== 0) {
        const path = buildTextArchPath(textObj.width || 0, settings.arch);
        if (path) textObj.set({ path });
      }
      // Drop straight into edit mode with the placeholder text selected so
      // the user can type to replace it.
      textObj.enterEditing();
      textObj.selectAll();
      canvas.renderAll();
      // Snapshot is the caller's responsibility (see addShape comment).
    },
    [],
  );

  /** Apply TextSettings to the active IText. No-op if active isn't an IText. */
  const updateActiveText = useCallback((settings: TextSettings) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (!(active instanceof fabric.IText)) return;
    active.set({
      fontFamily: settings.fontFamily,
      fontSize: settings.fontSize,
      fontWeight: settings.fontWeight,
      fontStyle: settings.fontStyle,
      underline: settings.underline,
      textAlign: settings.textAlign,
      fill: settings.fill,
    });
    // Persist arch value even when a vector path binding is active —
    // unbinding later should restore the previous arch curvature.
    (active as fabric.IText & { archValue: number }).archValue = settings.arch;

    // Path slot ownership:
    //   - When this text is bound to a vector layer's path, the
    //     `.path` slot is managed by EditorPage's text-path-sync
    //     effect (via `setTextPathBinding`). We must NOT touch it
    //     here — overwriting with an arch path would visibly
    //     break the binding until the next sync tick.
    //   - When unbound, fall back to the original arch logic. The
    //     temp Text re-measures the natural width without a path
    //     so the arch geometry tracks the text width even when the
    //     previous path was already shaping the glyphs.
    const layerId = getObjectLayerId(active);
    const isBoundToVectorPath = layerId ? textPathBoundLayerIdsRef.current.has(layerId) : false;
    if (!isBoundToVectorPath) {
      if (settings.arch === 0) {
        active.set({ path: undefined });
      } else {
        const measureText = new fabric.Text(active.text || '', {
          fontFamily: settings.fontFamily,
          fontSize: settings.fontSize,
          fontWeight: settings.fontWeight,
          fontStyle: settings.fontStyle,
        });
        const naturalWidth = measureText.width || active.width || 0;
        const path = buildTextArchPath(naturalWidth, settings.arch);
        if (path) active.set({ path });
      }
    }
    active.setCoords();
    canvas.requestRenderAll();
  }, []);

  /** Bind or unbind a text layer's `fabric.IText.path` to a vector
   *  layer's geometry. Caller is EditorPage's text-path-sync effect;
   *  it computes the SVG path-data string from the bound vector
   *  layer's `VectorPath` via `pathToSvgData` and hands it in here.
   *  `null` unbinds — the IText's path slot reverts to the arch
   *  path if `archValue !== 0`, or clears entirely.
   *
   *  The `textPathBoundLayerIdsRef` mirror is updated synchronously
   *  so `updateActiveText` (which may fire in the same render pass
   *  for a different reason — e.g. font-size tweak while bound)
   *  sees the binding state and skips its arch path logic.
   *
   *  Returns true when a matching FabricImage IText was found and
   *  updated; false otherwise (text layer doesn't exist on the
   *  canvas yet — caller should retry once the layer is realized).
   *
   *  TODO(text-on-path): refresh-desync follow-up. After save + page
   *  reload, the IText occasionally renders against pre-edit path
   *  geometry until the user manually unbinds + rebinds. The cross-
   *  rAF split below mimics the manual recovery path for the LIVE
   *  rebind case (and works there), but it doesn't survive
   *  `canvas.loadFromJSON`'s post-load `renderAll` — Fabric's IText
   *  layout cache (`__charBounds` / `_textLines`) is filled against
   *  the saved path during load and our subsequent rebind doesn't
   *  always invalidate it. Root cause is on Fabric's side; needs a
   *  proper invalidation entry-point. Once fixed, this split can
   *  collapse back to a synchronous `set({ path })` + a clean
   *  contract ("binding is applied when this returns"). */
  const setTextPathBinding = useCallback(
    (textLayerId: string, vectorPathSvgData: string | null): boolean => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      if (vectorPathSvgData) textPathBoundLayerIdsRef.current.add(textLayerId);
      else textPathBoundLayerIdsRef.current.delete(textLayerId);
      for (const obj of canvas.getObjects()) {
        if (getObjectLayerId(obj) !== textLayerId) continue;
        if (!(obj instanceof fabric.IText)) continue;
        const itext = obj as fabric.IText & { initDimensions?: () => void };
        if (vectorPathSvgData) {
          const path = new fabric.Path(vectorPathSvgData, {
            fill: '',
            stroke: '',
            objectCaching: false,
            visible: false,
          });
          // Cross-frame clear → set. Synchronously calling
          // `set({ path: undefined })` then `set({ path: newPath })`
          // in the same tick lets Fabric coalesce the transitions
          // away — IText's cached layout (computed against whatever
          // path was attached when the prior render ran) survives
          // both sets, so the new path's geometry isn't honored.
          // Manual unbind+rebind (two separate setTextPathBinding
          // calls across React renders) works because there's a
          // render commit between the cleared-state and new-state
          // sets. The split below mimics that behavior in a single
          // public call: frame 1 commits the cleared state; frame
          // 2 attaches the new path against a freshly cleared
          // IText cache. requestAnimationFrame chains keep the
          // sequence ordered relative to Fabric's render loop.
          obj.set({ path: undefined });
          obj.dirty = true;
          obj.setCoords();
          canvas.requestRenderAll();
          requestAnimationFrame(() => {
            const stillOnCanvas = canvas.getObjects().includes(obj);
            if (!stillOnCanvas) return;
            obj.set({ path });
            if (typeof itext.initDimensions === 'function') itext.initDimensions();
            obj.dirty = true;
            obj.setCoords();
            canvas.requestRenderAll();
          });
        } else {
          const archValue = (obj as fabric.IText & { archValue?: number }).archValue ?? 0;
          if (archValue !== 0) {
            const archPath = buildTextArchPath(obj.width ?? 0, archValue);
            obj.set({ path: undefined });
            if (archPath) obj.set({ path: archPath });
          } else {
            obj.set({ path: undefined });
          }
          if (typeof itext.initDimensions === 'function') itext.initDimensions();
          obj.dirty = true;
          obj.setCoords();
          canvas.requestRenderAll();
        }
        return true;
      }
      return false;
    },
    [],
  );

  /** Read the active IText's properties as a TextSettings snapshot. */
  const getActiveTextSettings = useCallback((): TextSettings | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const active = canvas.getActiveObject();
    if (!(active instanceof fabric.IText)) return null;
    const fontWeightRaw = active.fontWeight;
    const fontWeight: 'normal' | 'bold' =
      fontWeightRaw === 'bold' || fontWeightRaw === 700 ? 'bold' : 'normal';
    const fontStyleRaw = active.fontStyle;
    const fontStyle: 'normal' | 'italic' = fontStyleRaw === 'italic' ? 'italic' : 'normal';
    const textAlignRaw = active.textAlign;
    const textAlign: 'left' | 'center' | 'right' =
      textAlignRaw === 'center' || textAlignRaw === 'right' ? textAlignRaw : 'left';
    const archValue = (active as fabric.IText & { archValue?: number }).archValue ?? 0;
    return {
      fontFamily: active.fontFamily ?? 'Arial',
      fontSize: active.fontSize ?? 24,
      fontWeight,
      fontStyle,
      underline: active.underline ?? false,
      textAlign,
      fill: typeof active.fill === 'string' ? active.fill : '#000000',
      arch: archValue,
    };
  }, []);

  /** Add a shape at the canvas center (in image coordinates), or at a given
   *  scene-space `position` if provided. */
  const addShape = useCallback(
    (
      shapeType: ShapeType,
      settings: ShapeSettings,
      layerId?: string,
      position?: { x: number; y: number },
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;
      const center = position
        ? { left: position.x, top: position.y }
        : { left: imgW / 2, top: imgH / 2 };
      let shape: fabric.FabricObject;

      switch (shapeType) {
        case 'rect':
          shape = new fabric.Rect({
            ...center,
            width: 150,
            height: 100,
            fill: resolveShapeFill(settings.fill),
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
            rx: settings.cornerRadius,
            ry: settings.cornerRadius,
            originX: 'center',
            originY: 'center',
          });
          break;
        case 'circle':
          shape = new fabric.Circle({
            ...center,
            radius: 60,
            fill: resolveShapeFill(settings.fill),
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
            originX: 'center',
            originY: 'center',
          });
          break;
        case 'triangle':
          shape = new fabric.Triangle({
            ...center,
            width: 120,
            height: 120,
            fill: resolveShapeFill(settings.fill),
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
            originX: 'center',
            originY: 'center',
          });
          break;
        case 'line':
          shape = new fabric.Line([center.left - 75, center.top, center.left + 75, center.top], {
            stroke: settings.stroke,
            strokeWidth: settings.strokeWidth,
          });
          break;
        default:
          return;
      }

      (shape as fabric.FabricObject & { id: string }).id = `shape_${Date.now()}`;
      if (layerId) setObjectLayerId(shape, layerId);
      canvas.add(shape);
      canvas.setActiveObject(shape);
      canvas.renderAll();
      // Snapshot is the caller's responsibility (see addText comment).
    },
    [],
  );

  /**
   * Live-update the active shape with new shape settings. If the type matches,
   * mutate fill/stroke/strokeWidth/cornerRadius in place. If the type changed,
   * replace the active shape with a fresh one at the same position +
   * layerId, then make the new shape active. No-op if the active object isn't
   * a shape (or there's no active object).
   */
  const updateActiveShape = useCallback((settings: ShapeSettings) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (!active) return;

    const currentType: ShapeType | null =
      active instanceof fabric.Rect
        ? 'rect'
        : active instanceof fabric.Circle
          ? 'circle'
          : active instanceof fabric.Triangle
            ? 'triangle'
            : active instanceof fabric.Line
              ? 'line'
              : null;
    if (!currentType) return;

    if (currentType === settings.type) {
      const updates: Partial<fabric.FabricObjectProps> = {
        fill: resolveShapeFill(settings.fill),
        stroke: settings.stroke,
        strokeWidth: settings.strokeWidth,
      };
      active.set(updates);
      if (active instanceof fabric.Rect) {
        active.set({ rx: settings.cornerRadius, ry: settings.cornerRadius });
      }
      active.setCoords();
      canvas.requestRenderAll();
      return;
    }

    // Type changed — recreate at the same position and re-tag with the same
    // layerId so the layer association is preserved.
    const layerId = getObjectLayerId(active);
    const left = active.left;
    const top = active.top;
    const originX = active.originX;
    const originY = active.originY;

    let next: fabric.FabricObject;
    switch (settings.type) {
      case 'rect':
        next = new fabric.Rect({
          left,
          top,
          originX,
          originY,
          width: 150,
          height: 100,
          fill: resolveShapeFill(settings.fill),
          stroke: settings.stroke,
          strokeWidth: settings.strokeWidth,
          rx: settings.cornerRadius,
          ry: settings.cornerRadius,
        });
        break;
      case 'circle':
        next = new fabric.Circle({
          left,
          top,
          originX,
          originY,
          radius: 60,
          fill: resolveShapeFill(settings.fill),
          stroke: settings.stroke,
          strokeWidth: settings.strokeWidth,
        });
        break;
      case 'triangle':
        next = new fabric.Triangle({
          left,
          top,
          originX,
          originY,
          width: 120,
          height: 120,
          fill: resolveShapeFill(settings.fill),
          stroke: settings.stroke,
          strokeWidth: settings.strokeWidth,
        });
        break;
      case 'line': {
        const cx = left ?? 0;
        const cy = top ?? 0;
        next = new fabric.Line([cx - 75, cy, cx + 75, cy], {
          stroke: settings.stroke,
          strokeWidth: settings.strokeWidth,
        });
        break;
      }
      default:
        return;
    }

    canvas.remove(active);
    canvas.discardActiveObject();
    (next as fabric.FabricObject & { id: string }).id = `shape_${Date.now()}`;
    if (layerId) setObjectLayerId(next, layerId);
    canvas.add(next);
    canvas.setActiveObject(next);
    canvas.requestRenderAll();
  }, []);

  // ─── Pen tool (Tier 2 Group A) ───────────────────────────────────
  //
  // Construction-time state lives in `penStateRef` and is rebuilt
  // into a preview Fabric.Path on every relevant change. Finalize
  // emits a snapshot through `onPenToolFinalize` and tears down the
  // preview; EditorPage creates the persistent vector layer.

  /** Mirror EditorPage's `state.penSettings` so the canvas-side
   *  mouse handlers see the latest variant + stroke + fill without
   *  re-binding the listeners. Calling pattern matches the
   *  retouch-settings ref mirror. */
  /** Stroke/fill values resolved from the pen settings into the
   *  shape Fabric expects. Vector fill reuses the same gradient-
   *  capable picker as Shape settings. */
  const applyPenStyle = useCallback((target: fabric.Path, settings: PenSettings): void => {
    const fillResolved = resolveShapeFill(settings.fill);
    target.set({
      stroke: settings.strokeColor,
      strokeWidth: settings.strokeWidth,
      strokeUniform: true,
      // Fabric's fill prop accepts strings + gradient objects; the
      // resolver returns whichever matches the input value.
      fill: fillResolved,
      // Preview should ignore selection / events while drawing —
      // clicks need to fall through to the canvas-level mouse
      // handlers which manage the path-creation flow.
      selectable: false,
      evented: false,
      objectCaching: false,
    });
  }, []);

  /** Build a `fabric.Path` from the current penStateRef.anchors.
   *  The constructed object is meant for the in-progress preview
   *  only — `selectable: false`, `evented: false`, tagged with
   *  `_isPenPreview: true` so `applyLayerOrder` keeps it on top
   *  and `cancelPenPath` can sweep stale instances. */
  const buildPenPreviewObject = useCallback(
    (anchors: VectorAnchor[], closed: boolean, settings: PenSettings): fabric.Path | null => {
      if (anchors.length === 0) return null;
      const data = pathToSvgData({ subPaths: [{ closed, anchors }] });
      if (!data) return null;
      const obj = new fabric.Path(data, {
        // Fabric's default originX/originY for Path is 'left'/'top',
        // and it parses absolute path-data coords as scene coords.
        // Keep that — the SVG `M` command already encodes our
        // anchor positions in scene space.
        originX: 'left',
        originY: 'top',
      });
      applyPenStyle(obj, settings);
      (obj as fabric.Path & { _isPenPreview?: boolean })._isPenPreview = true;
      return obj;
    },
    [applyPenStyle],
  );

  /** Replace the preview object on the canvas with one built from
   *  the latest `penStateRef.anchors`. Idempotent; safe to call
   *  multiple times per session. */
  const updatePenPreview = useCallback((): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const state = penStateRef.current;
    if (!state) return;
    // Always remove the prior preview first — replacing
    // `_element` would be cheaper but Fabric.Path doesn't expose a
    // mutating API for its path data; reconstruction is the
    // supported shape. The construction-time cost is bounded
    // (≤ small N anchors per session).
    if (state.previewObj) {
      canvas.remove(state.previewObj);
      state.previewObj = null;
    }
    const obj = buildPenPreviewObject(state.anchors, false, penSettingsRef.current);
    if (obj) {
      canvas.add(obj);
      state.previewObj = obj;
    }
    canvas.requestRenderAll();
  }, [buildPenPreviewObject]);

  /** Discard any in-progress pen state and clean up the preview.
   *  Called from `cancelPenPath`, the Escape keyboard handler,
   *  and the tool-change path (switching away from pen mid-draw). */
  /** Mirror EditorPage's `state.penSettings` so the canvas-side
   *  mouse handlers see the latest variant + stroke + fill without
   *  re-binding the listeners. Calling pattern matches the
   *  retouch-settings ref mirror; also re-renders the in-progress
   *  preview when a session is active so style changes mid-draw
   *  show up immediately. */
  const setPenSettings = useCallback(
    (settings: PenSettings) => {
      penSettingsRef.current = settings;
      // Re-render the preview with the new stroke / fill if a path
      // is currently being drawn. Without this, changing a slider
      // mid-session would leave the preview using the OLD style
      // until the next click — which the user perceives as the
      // path "disappearing then coming back."
      if (penStateRef.current && penStateRef.current.anchors.length > 0) {
        updatePenPreview();
      }
    },
    [updatePenPreview],
  );

  const clearPenState = useCallback((): void => {
    const canvas = canvasRef.current;
    // Always sweep `_isPenPreview` objects, even if the ref's
    // `previewObj` slot is empty. The mouse:down handler can be
    // mid-write to the state when a tool-switch effect races
    // through cancelPenPath; without the unconditional sweep an
    // orphan preview survives, and `applyLayerOrder` (which now
    // brings `_isPenPreview` to front alongside the marquee /
    // crop-rect overlays) will faithfully resurface it on the
    // next state.document tick — making the path "reappear"
    // after the user clicks with the new tool.
    if (canvas) {
      for (const obj of canvas.getObjects()) {
        const tagged = obj as fabric.Object & { _isPenPreview?: boolean };
        if (tagged._isPenPreview) canvas.remove(obj);
      }
      canvas.requestRenderAll();
    }
    penStateRef.current = null;
  }, []);

  /** Public cancel — drops the preview, fires no callback. */
  const cancelPenPath = useCallback((): void => {
    clearPenState();
  }, [clearPenState]);

  /** Public finalize — emits the path via `onPenToolFinalize` and
   *  clears the preview. Caller (EditorPage) is responsible for
   *  creating the vector LayerNode + mounting the persistent
   *  Fabric.Path. A path with fewer than 2 anchors is treated as
   *  empty and silently discarded (matches Photoshop's
   *  "click-once-then-Escape = no-op" behavior). */
  const finalizePenPath = useCallback(
    (closed: boolean): boolean => {
      const state = penStateRef.current;
      if (!state || state.anchors.length < 2) {
        clearPenState();
        return false;
      }
      const path: VectorPath = {
        subPaths: [{ closed, anchors: state.anchors.slice() }],
        stroke: {
          color: penSettingsRef.current.strokeColor,
          width: penSettingsRef.current.strokeWidth,
        },
        fill: penSettingsRef.current.fill,
      };
      // Clear state BEFORE invoking the callback so the editor's
      // own re-render via the document-change effect (which may
      // call back into here via setTool / addVectorObject) sees a
      // clean slate.
      clearPenState();
      onPenToolFinalizeRef.current?.(path, { closed });
      return true;
    },
    [clearPenState],
  );

  /** Mount a persistent `fabric.Path` for an existing vector
   *  LayerNode. Called from EditorPage after `addLayer`
   *  dispatches; tags the Fabric object with the layer id so
   *  every layer-aware helper (selection, opacity, ordering,
   *  effects) treats it like any other layer object. */
  const addVectorObject = useCallback((layerId: string, path: VectorPath): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const data = pathToSvgData(path);
    if (!data) return;
    const obj = new fabric.Path(data, {
      originX: 'left',
      originY: 'top',
      stroke: path.stroke?.color ?? '#000000',
      strokeWidth: path.stroke?.width ?? 2,
      strokeUniform: true,
      fill: resolveShapeFill(path.fill ?? 'transparent'),
      // Keep Fabric's default object caching enabled — the
      // per-object offscreen cache is what makes
      // `globalCompositeOperation` blend modes work. Without it,
      // stroke pixels render directly to the main canvas and most
      // blend modes silently no-op for transparent-fill paths.
    });
    setObjectLayerId(obj, layerId);
    canvas.add(obj);
    canvas.requestRenderAll();
    // Snapshot for history. Pen-tool creation is one atomic
    // history entry from the user's POV.
    onObjectModifiedRef.current?.();
  }, []);

  /** Update the stroke / strokeWidth / fill of the persistent
   *  `fabric.Path` bound to `layerId` without touching the path
   *  geometry. Used by the shape-settings effect to sync color /
   *  width / fill changes onto a vector layer that DOESN'T qualify
   *  for full regenerate (custom-edited primitives + pen-drawn
   *  paths). Returns true on success. */
  const updateVectorStyle = useCallback(
    (
      layerId: string,
      style: { stroke: string; strokeWidth: number; fill: string | ShapeFillValue },
    ): boolean => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      const obj = canvas
        .getObjects()
        .find((o): o is fabric.Path => o instanceof fabric.Path && getObjectLayerId(o) === layerId);
      if (!obj) return false;
      obj.set({
        stroke: style.stroke,
        strokeWidth: style.strokeWidth,
        fill: resolveShapeFill(style.fill),
      });
      obj.setCoords();
      canvas.requestRenderAll();
      return true;
    },
    [],
  );

  /** Return the scene-space center + bbox of the `fabric.Path`
   *  bound to `layerId`, or null when no matching layer / object.
   *  Used by the shape-settings regenerate effect to position the
   *  rebuilt primitive at the same scene center as the old one. */
  const getActiveVectorBounds = useCallback(
    (
      layerId: string,
    ): {
      center: { x: number; y: number };
      bbox: { left: number; top: number; width: number; height: number };
    } | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const obj = canvas
        .getObjects()
        .find((o): o is fabric.Path => o instanceof fabric.Path && getObjectLayerId(o) === layerId);
      if (!obj) return null;
      const c = obj.getCenterPoint();
      const r = obj.getBoundingRect();
      return {
        center: { x: c.x, y: c.y },
        bbox: { left: r.left, top: r.top, width: r.width, height: r.height },
      };
    },
    [],
  );

  /** Bake the layer's current Fabric transform into its path data,
   *  producing a `VectorPath` in scene-space coordinates. The
   *  source `path` is the raw (unbaked) `LayerNode.path`; the
   *  matrix + pathOffset come from the Fabric object on the
   *  canvas. Used by Boolean Ops so paper.js sees the actual
   *  on-canvas geometry — without this, two layers with different
   *  Fabric translations but identical raw path-data would be
   *  treated as overlapping in the same local frame, and the
   *  result of Subtract/Intersect/Exclude would be nonsense
   *  (empty result or geometry placed at the path-data origin
   *  instead of the visible center). Returns null when no
   *  matching layer is on the canvas. */
  const getBakedVectorPath = useCallback((layerId: string, path: VectorPath): VectorPath | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const obj = canvas
      .getObjects()
      .find((o): o is fabric.Path => o instanceof fabric.Path && getObjectLayerId(o) === layerId);
    if (!obj) return null;
    const matrix = obj.calcTransformMatrix();
    const pathOffset = obj.pathOffset ?? { x: 0, y: 0 };
    return bakeVectorPath(path, matrix, pathOffset);
  }, []);

  /** Replace the `fabric.Path` bound to `layerId` with a fresh one
   *  built from `path`. Used by the ShapePanel-slider regenerate
   *  flow for primitive vector layers — sides / inner / head-size
   *  / stroke / fill changes rebuild the path at the previous
   *  scene-space center so the layer feels like it's "morphing"
   *  in place rather than jumping. Selection is preserved when
   *  the replaced object was active. Returns the new fabric.Path
   *  or null when no matching layer / invalid path. */
  const replaceVectorObject = useCallback(
    (layerId: string, path: VectorPath): fabric.Path | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const old = canvas
        .getObjects()
        .find((o): o is fabric.Path => o instanceof fabric.Path && getObjectLayerId(o) === layerId);
      if (!old) return null;
      const data = pathToSvgData(path);
      if (!data) return null;
      const wasActive = canvas.getActiveObject() === old;
      // Preserve scene-space center across the swap so the layer
      // visibly "morphs" in place. The new fabric.Path's
      // constructor computes its own pathOffset / left / top from
      // the new bbox; we then translate the result so the new
      // bbox center matches the old one.
      const oldCenter = old.getCenterPoint();
      const next = new fabric.Path(data, {
        originX: 'left',
        originY: 'top',
        stroke: path.stroke?.color ?? (old.stroke as string) ?? '#000000',
        strokeWidth: path.stroke?.width ?? old.strokeWidth ?? 2,
        strokeUniform: true,
        fill: resolveShapeFill(path.fill ?? 'transparent'),
        // Preserve any layer-effect shadow + blend mode from the
        // old object so toggling effects + then editing the shape
        // doesn't lose either.
        shadow: old.shadow,
        globalCompositeOperation: (
          old as unknown as { globalCompositeOperation?: GlobalCompositeOperation }
        ).globalCompositeOperation,
        opacity: old.opacity,
        visible: old.visible,
      });
      setObjectLayerId(next, layerId);
      canvas.add(next);
      const newCenter = next.getCenterPoint();
      next.set({
        left: (next.left ?? 0) + (oldCenter.x - newCenter.x),
        top: (next.top ?? 0) + (oldCenter.y - newCenter.y),
      });
      next.setCoords();
      canvas.remove(old);
      if (wasActive) canvas.setActiveObject(next);
      canvas.requestRenderAll();
      return next;
    },
    [],
  );

  // ─── Path Edit mode (Tier 2 Group A PR-B) ────────────────────────
  //
  // On-canvas anchor / handle manipulation for an existing vector
  // layer. Modal session mirroring Free Transform Phase 1b:
  //   - `pathEditOverlayRef` holds the working path + Fabric overlay
  //     objects + a snapshot for Cancel restoration.
  //   - During the session the layer's persistent Fabric.Path is
  //     hidden; a separate "working" Fabric.Path renders the live
  //     state and gets rebuilt on every mutation.
  //   - `activeToolRef` is forced to `'ai'` so every other tool
  //     handler short-circuits — same trick as the non-affine
  //     transform overlay.
  //   - Apply commits via `SET_VECTOR_PATH` (dispatched from
  //     EditorPage); Cancel restores the snapshot.

  /** Tag for every Fabric object that belongs to the Path Edit
   *  overlay (anchors, handles, handle lines, the working path).
   *  Pattern matches `_isMarquee` / `_isPenPreview` etc. so the
   *  layer-order helper keeps overlays on top and teardown can
   *  sweep orphans defensively. */
  const tagPathEditOverlay = (obj: fabric.Object): void => {
    (obj as unknown as { _isPathEditOverlay?: boolean })._isPathEditOverlay = true;
    obj.set({
      selectable: false,
      evented: false,
      hasControls: false,
      hasBorders: false,
      objectCaching: false,
    });
  };

  const pathEditOverlayRef = useRef<{
    layerId: string;
    /** Snapshot of the path at session start. Cancel restores by
     *  re-mounting the original Fabric.Path with this data. */
    initialPath: VectorPath;
    /** Working state — mutated by mouse handlers during drag.
     *  Apply commits via `SET_VECTOR_PATH(layerId, workingPath)`. */
    workingPath: VectorPath;
    /** Style captured from the original Fabric.Path so the working
     *  path renders identically while the user edits. */
    style: { stroke: string; strokeWidth: number; fill: fabric.Path['fill'] };
    /** The layer's persistent Fabric.Path — hidden during the
     *  session, restored on Cancel, removed on Apply. */
    originalPathObj: fabric.Path;
    /** Working Fabric.Path that mirrors workingPath. Rebuilt on
     *  every mutation. */
    workingPathObj: fabric.Path | null;
    anchorObjs: fabric.Rect[];
    handleObjs: { in: fabric.Circle | null; out: fabric.Circle | null };
    handleLineObjs: { in: fabric.Line | null; out: fabric.Line | null };
    drag: { kind: 'anchor' | 'handleIn' | 'handleOut'; anchorIndex: number } | null;
    selectedAnchorIndex: number | null;
    savedActiveTool: EditorTool;
    savedSelection: boolean;
  } | null>(null);

  /** Forward-ref pattern (TS TDZ) so mouse handlers can call the
   *  updater before its declaration. Mirrors `setupNonAffineOverlay`
   *  in the non-affine transform block. */
  const updatePathEditOverlayRef = useRef<(() => void) | null>(null);

  // Overlay handles + anchors render at a CONSTANT SCREEN size.
  // The SCENE-pixel constants (`PATH_EDIT_*_SCENE`) are the visual
  // target at 100% zoom; we divide by the current zoom so a
  // zoomed-in 4K image doesn't render anchors at fist-sized
  // squares and a zoomed-out preview doesn't reduce them to
  // single pixels. The overlay is refreshed on zoom changes via
  // an EditorPage effect that calls `refreshPathEditOverlay`.
  const buildAnchorObj = (a: VectorAnchor, isSelected: boolean): fabric.Rect => {
    const zoom = canvasRef.current?.getZoom() ?? 1;
    const size = PATH_EDIT_ANCHOR_SIZE_SCENE / zoom;
    const r = new fabric.Rect({
      left: a.x - size / 2,
      top: a.y - size / 2,
      width: size,
      height: size,
      fill: isSelected ? PATH_EDIT_OVERLAY_COLOR : '#ffffff',
      stroke: PATH_EDIT_OVERLAY_COLOR,
      strokeWidth: 1,
      strokeUniform: true,
      originX: 'left',
      originY: 'top',
    });
    tagPathEditOverlay(r);
    return r;
  };

  const buildHandleObj = (
    anchorX: number,
    anchorY: number,
    dx: number,
    dy: number,
  ): fabric.Circle => {
    const zoom = canvasRef.current?.getZoom() ?? 1;
    const radius = PATH_EDIT_HANDLE_RADIUS_SCENE / zoom;
    const hx = anchorX + dx;
    const hy = anchorY + dy;
    const c = new fabric.Circle({
      left: hx - radius,
      top: hy - radius,
      radius,
      fill: '#ffffff',
      stroke: PATH_EDIT_OVERLAY_COLOR,
      strokeWidth: 1,
      strokeUniform: true,
      originX: 'left',
      originY: 'top',
    });
    tagPathEditOverlay(c);
    return c;
  };

  const buildHandleLineObj = (
    anchorX: number,
    anchorY: number,
    dx: number,
    dy: number,
  ): fabric.Line => {
    const l = new fabric.Line([anchorX, anchorY, anchorX + dx, anchorY + dy], {
      stroke: PATH_EDIT_OVERLAY_COLOR,
      strokeWidth: 1,
      strokeDashArray: [2, 2],
      strokeUniform: true,
    });
    tagPathEditOverlay(l);
    return l;
  };

  const buildWorkingPathObj = (
    path: VectorPath,
    style: { stroke: string; strokeWidth: number; fill: fabric.Path['fill'] },
  ): fabric.Path | null => {
    const data = pathToSvgData(path);
    if (!data) return null;
    const obj = new fabric.Path(data, {
      originX: 'left',
      originY: 'top',
      stroke: style.stroke,
      strokeWidth: style.strokeWidth,
      strokeUniform: true,
      fill: style.fill,
    });
    tagPathEditOverlay(obj);
    return obj;
  };

  /** Rebuild every overlay Fabric object from the current
   *  workingPath + selectedAnchorIndex. Called after every
   *  mutation (anchor drag, handle drag, selection change). */
  const updatePathEditOverlay = useCallback((): void => {
    const canvas = canvasRef.current;
    const overlay = pathEditOverlayRef.current;
    if (!canvas || !overlay) return;
    // Sweep prior overlay objects. Snapshot the filter result
    // BEFORE removing — `canvas.getObjects()` returns the
    // internal `_objects` array directly in Fabric v7, so
    // mutating it (remove) while iterating skips entries and
    // leaves orphans behind. On a drag tick that orphan reads
    // as "two paths overlaid" — the symptom the QA pass caught.
    const stale = canvas
      .getObjects()
      .filter((o) => (o as fabric.Object & { _isPathEditOverlay?: boolean })._isPathEditOverlay);
    for (const obj of stale) canvas.remove(obj);
    overlay.anchorObjs = [];
    overlay.handleObjs = { in: null, out: null };
    overlay.handleLineObjs = { in: null, out: null };
    overlay.workingPathObj = null;

    // Build the working Fabric.Path first so anchors render on top.
    const pathObj = buildWorkingPathObj(overlay.workingPath, overlay.style);
    if (pathObj) {
      canvas.add(pathObj);
      overlay.workingPathObj = pathObj;
    }

    // Anchor squares.
    const subPath = overlay.workingPath.subPaths[0];
    if (subPath) {
      for (let i = 0; i < subPath.anchors.length; i++) {
        const a = subPath.anchors[i];
        const isSelected = i === overlay.selectedAnchorIndex;
        const obj = buildAnchorObj(a, isSelected);
        canvas.add(obj);
        overlay.anchorObjs.push(obj);
      }
      // Handles for the selected anchor — only render when an
      // anchor is selected (Photoshop UX). Skips a handle when
      // its dx/dy are both undefined (corner anchor with no
      // tangent on that side).
      const idx = overlay.selectedAnchorIndex;
      if (idx !== null) {
        const sel = subPath.anchors[idx];
        if (sel) {
          if (sel.inX !== undefined || sel.inY !== undefined) {
            const dx = sel.inX ?? 0;
            const dy = sel.inY ?? 0;
            const line = buildHandleLineObj(sel.x, sel.y, dx, dy);
            const circ = buildHandleObj(sel.x, sel.y, dx, dy);
            canvas.add(line);
            canvas.add(circ);
            overlay.handleLineObjs.in = line;
            overlay.handleObjs.in = circ;
          }
          if (sel.outX !== undefined || sel.outY !== undefined) {
            const dx = sel.outX ?? 0;
            const dy = sel.outY ?? 0;
            const line = buildHandleLineObj(sel.x, sel.y, dx, dy);
            const circ = buildHandleObj(sel.x, sel.y, dx, dy);
            canvas.add(line);
            canvas.add(circ);
            overlay.handleLineObjs.out = line;
            overlay.handleObjs.out = circ;
          }
        }
      }
    }
    canvas.requestRenderAll();
  }, []);
  updatePathEditOverlayRef.current = updatePathEditOverlay;

  /** Open Path Edit mode on a vector layer. Returns true on success
   *  (the layer exists, has a Fabric.Path, and the editor is in a
   *  usable state); false otherwise. Caller is expected to have
   *  already dispatched `BEGIN_PATH_EDIT` against the reducer. */
  const enterPathEditMode = useCallback(
    (layerId: string, path: VectorPath): boolean => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      // If a previous session is still active, tear it down with
      // no commit so the new session starts clean.
      if (pathEditOverlayRef.current) {
        updatePathEditOverlayRef.current = null;
        pathEditOverlayRef.current = null;
      }

      // Find the layer's persistent Fabric.Path. The vector-layer
      // creation pipeline (addVectorObject + onPenToolFinalize)
      // tags the path with the layer id; without it Path Edit
      // has nothing to operate on.
      let target: fabric.Path | null = null;
      for (const obj of canvas.getObjects()) {
        if (!(obj instanceof fabric.Path)) continue;
        if (getObjectLayerId(obj) !== layerId) continue;
        target = obj;
        break;
      }
      if (!target) return false;

      // Snapshot the style for the working overlay so it matches
      // the layer's appearance during the session.
      const style = {
        stroke: (target.stroke as string) ?? '#000000',
        strokeWidth: target.strokeWidth ?? 2,
        fill: target.fill,
      };

      // Bake the target's current transform (left / top / scale /
      // rotation) + pathOffset into the working path's anchor
      // coordinates. Without this, the anchor overlay renders at
      // the path-data origin (where it was when the layer was
      // first created), while the path itself renders wherever
      // the user moved / scaled / rotated it — anchors detached
      // from the visible curve. On Apply, the new fabric.Path is
      // constructed from this baked path-data; Fabric assigns it
      // a fresh pathOffset + bbox-aligned `left`/`top`, so the
      // visible result is identical but the path-data now lives
      // in the same coord space as the on-canvas geometry.
      //
      // Coordinate chain for the bake math (verified against
      // Fabric v7's `Path.mjs`):
      //   - `pathOffset` is set to `(bbox.left + width/2,
      //     bbox.top + height/2)` — i.e. the POSITIVE bbox-center
      //     in path-data coords.
      //   - Rendering does `ctx.translate(-pathOffset.x,
      //     -pathOffset.y)` before drawing the path-data, so a
      //     path-data point (px, py) ends up at center-relative
      //     local (px - pathOffset.x, py - pathOffset.y).
      //   - `calcTransformMatrix()` then maps that center-relative
      //     local point to scene coords.
      // So: scene = matrix * (pathData − pathOffset).
      const matrix = target.calcTransformMatrix();
      const pathOffset = target.pathOffset ?? { x: 0, y: 0 };
      const bakedPath = bakeVectorPath(path, matrix, pathOffset);

      // Remove the original from the canvas entirely (rather than
      // `visible: false`). The visibility-cascade effect in
      // EditorPage walks every object on every state.document
      // change and resets `visible` from the layer's flag — a
      // mid-session document change (e.g. SET_PATH_EDIT_ANCHOR)
      // would un-hide the original and the user would see the
      // un-edited path bleeding through alongside the working
      // overlay. Cancel adds the original back; Apply doesn't
      // (the new path replaces it).
      canvas.remove(target);

      pathEditOverlayRef.current = {
        layerId,
        initialPath: bakedPath,
        // Deep-ish copy so mutations during the session can't
        // leak back into the caller's snapshot. Sub-paths +
        // anchors are slice'd; nested objects rebuilt fresh.
        workingPath: {
          ...bakedPath,
          subPaths: bakedPath.subPaths.map((sp) => ({
            ...sp,
            anchors: sp.anchors.map((a) => ({ ...a })),
          })),
        },
        style,
        originalPathObj: target,
        workingPathObj: null,
        anchorObjs: [],
        handleObjs: { in: null, out: null },
        handleLineObjs: { in: null, out: null },
        drag: null,
        selectedAnchorIndex: null,
        savedActiveTool: activeToolRef.current,
        savedSelection: canvas.selection,
      };

      // Force-suppress every other tool's mouse handlers during
      // the session. Same trick used by the non-affine transform
      // overlay. Also disable Fabric's group-select rubber-band.
      activeToolRef.current = 'ai';
      canvas.selection = false;
      canvas.discardActiveObject();

      updatePathEditOverlay();
      return true;
    },
    [updatePathEditOverlay],
  );

  /** Close Path Edit mode. `apply: true` commits the working path
   *  to the canvas (replaces the persistent Fabric.Path with one
   *  built from workingPath); `apply: false` restores the
   *  original. Caller (EditorPage) is responsible for dispatching
   *  the corresponding `SET_VECTOR_PATH` (apply) or NOT (cancel)
   *  + the `END_PATH_EDIT` reducer action. */
  const exitPathEditMode = useCallback((apply: boolean): VectorPath | null => {
    const canvas = canvasRef.current;
    const overlay = pathEditOverlayRef.current;
    if (!canvas || !overlay) return null;

    // Sweep every overlay object. Snapshot first — see the
    // matching note in `updatePathEditOverlay`.
    const stale = canvas
      .getObjects()
      .filter((o) => (o as fabric.Object & { _isPathEditOverlay?: boolean })._isPathEditOverlay);
    for (const obj of stale) canvas.remove(obj);

    if (apply) {
      // Replace the layer's persistent Fabric.Path with one built
      // from the (scene-coord) workingPath. The new fabric.Path's
      // constructor computes its own pathOffset / left / top from
      // the path-data bbox, so the result renders at the same
      // scene position the user was editing.
      const data = pathToSvgData(overlay.workingPath);
      if (data) {
        const next = new fabric.Path(data, {
          originX: 'left',
          originY: 'top',
          stroke: overlay.style.stroke,
          strokeWidth: overlay.style.strokeWidth,
          strokeUniform: true,
          fill: overlay.style.fill,
        });
        setObjectLayerId(next, overlay.layerId);
        canvas.add(next);
      }
    } else {
      // Cancel — re-add the (untouched) original Fabric.Path so
      // the layer's on-canvas appearance returns exactly to its
      // pre-session state.
      canvas.add(overlay.originalPathObj);
    }

    // Restore canvas-side state.
    activeToolRef.current = overlay.savedActiveTool;
    canvas.selection = overlay.savedSelection;
    const applied = apply ? overlay.workingPath : null;
    pathEditOverlayRef.current = null;
    canvas.requestRenderAll();
    if (apply) onObjectModifiedRef.current?.();
    return applied;
  }, []);

  /** Set the selected-anchor highlight. Called from the canvas
   *  mouse:down handler (after a hit-test) and from EditorPage
   *  via React state mirror. */
  const setPathEditSelectedAnchor = useCallback(
    (index: number | null): void => {
      const overlay = pathEditOverlayRef.current;
      if (!overlay) return;
      if (overlay.selectedAnchorIndex === index) return;
      overlay.selectedAnchorIndex = index;
      updatePathEditOverlay();
    },
    [updatePathEditOverlay],
  );

  /** Rebuild the Path Edit overlay using the current zoom level.
   *  Called by EditorPage on `state.zoom` changes so anchors +
   *  handles re-render at constant SCREEN size across zoom levels.
   *  No-op when no session is active. */
  const refreshPathEditOverlay = useCallback((): void => {
    if (pathEditOverlayRef.current) updatePathEditOverlay();
  }, [updatePathEditOverlay]);

  /** Set the type of the currently-selected anchor and refresh.
   *  Called from EditPathPanel's type radio. */
  const setPathEditAnchorType = useCallback(
    (type: VectorAnchor['type']): void => {
      const overlay = pathEditOverlayRef.current;
      if (overlay === null || overlay.selectedAnchorIndex === null) return;
      const sub = overlay.workingPath.subPaths[0];
      if (!sub) return;
      const idx = overlay.selectedAnchorIndex;
      const a = sub.anchors[idx];
      if (!a) return;
      let next: VectorAnchor;
      if (type === 'corner') {
        // Corners have no tangent handles. Drop any existing ones
        // so the user sees a sharp transition AND the handle UI
        // disappears.
        next = { ...a, type, inX: undefined, inY: undefined, outX: undefined, outY: undefined };
      } else if (type === 'smooth' && a.inX === undefined && a.outX === undefined) {
        // Smooth anchor with no existing handles — infer tangent
        // direction from prev/next neighbors so the user can
        // actually drag handles. Logic extracted to
        // `lib/vector-path.populateSmoothAnchorHandles` and tested
        // there.
        next = populateSmoothAnchorHandles(sub.anchors, idx, sub.closed);
      } else {
        // 'asymmetric', OR 'smooth' on an anchor that already has
        // handles — change the type label, leave handles intact.
        next = { ...a, type };
      }
      sub.anchors[idx] = next;
      updatePathEditOverlay();
    },
    [updatePathEditOverlay],
  );

  /** Return the currently-selected anchor's type (or null when no
   *  anchor is selected / no session active). Used by EditPathPanel
   *  to display the type radio's current value. */
  const getPathEditSelectedAnchorType = useCallback((): VectorAnchor['type'] | null => {
    const overlay = pathEditOverlayRef.current;
    if (!overlay || overlay.selectedAnchorIndex === null) return null;
    const a = overlay.workingPath.subPaths[0]?.anchors[overlay.selectedAnchorIndex];
    return a?.type ?? null;
  }, []);

  /**
   * Read the active shape's properties as a `ShapeSettings` snapshot. Used to
   * mirror canvas selection state into the Shape panel so changing the panel
   * sliders edits the *currently selected* shape, not whatever was last
   * touched. Returns null if the active object isn't a shape (or absent).
   */
  const getActiveShapeSettings = useCallback((): ShapeSettings | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const active = canvas.getActiveObject();
    if (!active) return null;

    let type: ShapeType;
    let cornerRadius = 0;
    if (active instanceof fabric.Rect) {
      type = 'rect';
      cornerRadius = (active as fabric.Rect).rx ?? 0;
    } else if (active instanceof fabric.Circle) {
      type = 'circle';
    } else if (active instanceof fabric.Triangle) {
      type = 'triangle';
    } else if (active instanceof fabric.Line) {
      type = 'line';
    } else {
      return null;
    }

    const fill = readShapeFill(active.fill);
    const stroke = typeof active.stroke === 'string' ? active.stroke : '#000000';
    return {
      type,
      fill,
      stroke,
      strokeWidth: active.strokeWidth ?? 0,
      cornerRadius,
    };
  }, []);

  /**
   * Destructively convert the background image to grayscale by rewriting
   * its underlying pixels with the Rec. 601 luminance of each pixel
   * (`0.299·R + 0.587·G + 0.114·B`). User-adjustable filters (brightness,
   * contrast, saturation) continue to apply on top of the now-grayscale
   * pixels. If the bg is registered as a paint surface, the surface map
   * is updated to point at the new canvas so subsequent strokes draw on
   * the same pixels we render from. Returns false if there's no bg image.
   */
  const convertToGrayscale = useCallback((): boolean => {
    const canvas = canvasRef.current;
    const img = getBackgroundImage();
    if (!canvas || !img) return false;
    const w = img.width ?? 0;
    const h = img.height ?? 0;
    if (w <= 0 || h <= 0) return false;

    const off = document.createElement('canvas');
    off.width = w;
    off.height = h;
    const ctx = off.getContext('2d');
    if (!ctx) return false;
    ctx.drawImage(img.getElement() as HTMLImageElement | HTMLCanvasElement, 0, 0, w, h);

    const data = ctx.getImageData(0, 0, w, h);
    const px = data.data;
    for (let i = 0; i < px.length; i += 4) {
      const lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
      px[i] = lum;
      px[i + 1] = lum;
      px[i + 2] = lum;
    }
    ctx.putImageData(data, 0, 0);

    img.setElement(off);
    const bgLayerId = getObjectLayerId(img);
    if (bgLayerId) {
      paintSurfacesRef.current.set(bgLayerId, {
        canvas: off,
        fabricImg: img,
        offsetX: img.left ?? 0,
        offsetY: img.top ?? 0,
      });
    }
    img.dirty = true;
    // Re-run any active filters on the new (grayscale) source.
    if (img.filters && img.filters.length > 0) img.applyFilters();
    img.setCoords();
    canvas.renderAll();
    // Fire onObjectModified so callers that don't manually wire dirty +
    // history (e.g. future direct callers) get the same behavior as
    // EditorPage's handleConvertGrayscale.
    onObjectModifiedRef.current?.();
    return true;
  }, [getBackgroundImage]);

  /** Delete pixels within marquee selection, or remove selected objects */
  const deleteSelected = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const m = marqueeRef.current;

    // If there's a finalized marquee selection, clip-clear pixels inside it
    // on the *active layer's* paint surface only, and remove any non-paint
    // overlay objects on the active layer whose bounding box overlaps. This
    // matches Photoshop's behavior — marquee delete operates on the active
    // layer, not every layer.
    if (m.obj && !m.isDrawing) {
      const isWand = m.shape === 'wand' && !!m.wandMaskCanvas;
      const wandMask = m.wandMaskCanvas;
      const activeLayerId = activeLayerIdRef.current;

      const clipClearSurface = (surface: {
        canvas: HTMLCanvasElement;
        fabricImg: fabric.FabricImage;
      }) => {
        const ctx = surface.canvas.getContext('2d');
        if (!ctx) return;
        // Marquee/wand geometry is in scene coords. If the layer's paint
        // surface has been moved (left/top != 0), translate the context so
        // scene coords map to the right surface pixels.
        const offsetX = -(surface.fabricImg.left ?? 0);
        const offsetY = -(surface.fabricImg.top ?? 0);
        if (isWand && wandMask) {
          ctx.save();
          ctx.translate(offsetX, offsetY);
          ctx.globalCompositeOperation = 'destination-out';
          ctx.drawImage(wandMask, 0, 0);
          ctx.restore();
          return;
        }
        ctx.save();
        ctx.translate(offsetX, offsetY);
        const clipped = applyMarqueeClipToContext(ctx, m);
        if (clipped) ctx.clearRect(0, 0, surface.canvas.width, surface.canvas.height);
        ctx.restore();
      };

      // Find vector overlays (shape/text/pasted-image) on the active layer
      // whose bbox intersects the marquee. These will be rasterized onto the
      // layer's paint surface so the subsequent clip-clear cuts a hole
      // through them — matching Photoshop's "marquee delete cuts through
      // everything on the active layer" behavior.
      const marqueeBounds = (() => {
        if (!m.obj) return null;
        let b = m.obj.getBoundingRect();
        for (const extra of m.extraObjs) {
          const eb = extra.getBoundingRect();
          const left = Math.min(b.left, eb.left);
          const top = Math.min(b.top, eb.top);
          const right = Math.max(b.left + b.width, eb.left + eb.width);
          const bottom = Math.max(b.top + b.height, eb.top + eb.height);
          b = { left, top, width: right - left, height: bottom - top };
        }
        return b;
      })();
      const paintFabricImgs = new Set<fabric.Object>();
      for (const s of paintSurfacesRef.current.values()) paintFabricImgs.add(s.fabricImg);
      const overlaysToRasterize: fabric.Object[] = [];
      if (activeLayerId && marqueeBounds && marqueeBounds.width > 0 && marqueeBounds.height > 0) {
        const mLeft = marqueeBounds.left;
        const mTop = marqueeBounds.top;
        const mRight = mLeft + marqueeBounds.width;
        const mBottom = mTop + marqueeBounds.height;
        // Iterate canvas objects in z-order (back-to-front) so rasterization
        // preserves stacking order on the paint surface.
        for (const o of canvas.getObjects()) {
          if (paintFabricImgs.has(o)) continue;
          if (isMarqueeObject(o)) continue;
          if ((o as unknown as { _isCropRect?: boolean })._isCropRect) continue;
          if ((o as unknown as { _isQuickMaskOverlay?: boolean })._isQuickMaskOverlay) continue;
          if (o === m.obj || m.extraObjs.includes(o)) continue;
          if (getObjectLayerId(o) !== activeLayerId) continue;
          const ob = o.getBoundingRect();
          const intersects =
            ob.left < mRight &&
            ob.left + ob.width > mLeft &&
            ob.top < mBottom &&
            ob.top + ob.height > mTop;
          if (intersects) overlaysToRasterize.push(o);
        }
      }

      // Rasterize intersecting overlays onto the active layer's paint surface,
      // then remove their vector representations from the main canvas. The
      // subsequent clip-clear will erase the marquee region from the surface,
      // including these freshly-rasterized pixels.
      if (activeLayerId && overlaysToRasterize.length > 0) {
        const surface = ensurePaintSurfaceRef.current?.(activeLayerId) ?? null;
        if (surface) {
          const ctx = surface.canvas.getContext('2d');
          if (ctx) {
            const w = surface.canvas.width;
            const h = surface.canvas.height;
            const tempEl = document.createElement('canvas');
            tempEl.width = w;
            tempEl.height = h;
            const tempCanvas = new fabric.StaticCanvas(tempEl, {
              width: w,
              height: h,
              enableRetinaScaling: false,
              renderOnAddRemove: false,
              backgroundColor: 'transparent',
            });
            for (const o of overlaysToRasterize) {
              canvas.remove(o);
              tempCanvas.add(o);
            }
            tempCanvas.renderAll();
            const offsetX = -(surface.fabricImg.left ?? 0);
            const offsetY = -(surface.fabricImg.top ?? 0);
            ctx.save();
            ctx.translate(offsetX, offsetY);
            ctx.drawImage(tempEl, 0, 0);
            ctx.restore();
            tempCanvas.dispose();
            surface.fabricImg.setElement(surface.canvas);
            surface.fabricImg.dirty = true;
            surface.fabricImg.setCoords();
          }
        }
      }

      // Apply clip-clear to the active layer's paint surface only — now
      // containing both pre-existing strokes and any just-rasterized overlay
      // pixels.
      if (activeLayerId) {
        const activeSurface = paintSurfacesRef.current.get(activeLayerId);
        if (activeSurface) {
          clipClearSurface(activeSurface);
          // Force Fabric to re-bind via `setElement` — `dirty = true` alone
          // doesn't propagate canvas mutations reliably.
          activeSurface.fabricImg.setElement(activeSurface.canvas);
          activeSurface.fabricImg.dirty = true;
          activeSurface.fabricImg.setCoords();
        }
      }

      // If the active layer is the Background and bg has filters, re-run them
      // so the filter pipeline picks up the cleared pixels. `setElement`
      // resets `_element` to the (un-filtered) canvas; `applyFilters` rebuilds
      // `_filteredEl` from that.
      const bg = getBackgroundImage();
      if (bg && activeLayerId === getObjectLayerId(bg) && bg.filters && bg.filters.length > 0) {
        bg.applyFilters();
      }

      canvas.discardActiveObject();
      canvas.remove(m.obj);
      for (const o of m.extraObjs) canvas.remove(o);
      m.obj = null;
      m.extraObjs = [];
      m.points = [];
      m.wandMaskCanvas = null;
      setHasMarqueeSelection(false);
      canvas.renderAll();
      onObjectModified?.();
      return;
    }

    // Otherwise, delete selected Fabric objects
    const active = canvas.getActiveObjects();
    if (active.length === 0) return;
    const bg = getBackgroundImage();
    active.forEach((obj) => {
      if (obj === bg) return;
      canvas.remove(obj);
    });
    canvas.discardActiveObject();
    canvas.renderAll();
    onObjectModified?.();
  }, [onObjectModified, getBackgroundImage]);

  /** Set canvas zoom level, keeping content centered */
  const setZoom = useCallback(
    (zoom: number) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas || !wrapper) return;

      const vpW = wrapper.clientWidth;
      const vpH = wrapper.clientHeight;
      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;

      const panX = (vpW - imgW * zoom) / 2;
      const panY = (vpH - imgH * zoom) / 2;
      canvas.setViewportTransform([zoom, 0, 0, zoom, panX, panY]);
      if (marqueeRef.current.obj) {
        applyMarqueeStroke(canvas, marqueeRef.current.obj);
        for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
      }
      canvas.renderAll();
      updateViewportInfo();
    },
    [wrapperRef, updateViewportInfo],
  );

  /** Fit canvas to container (re-center and scale to fit) */
  const fitToContainer = useCallback(() => {
    return zoomToFit();
  }, [zoomToFit]);

  /** Rotate the entire canvas by degrees */
  const rotateCanvas = useCallback(
    (degrees: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.getObjects().forEach((obj) => {
        const currentAngle = obj.angle || 0;
        obj.rotate(currentAngle + degrees);
      });
      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /** Flip canvas objects */
  const flipCanvas = useCallback(
    (direction: 'horizontal' | 'vertical') => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.getObjects().forEach((obj) => {
        if (direction === 'horizontal') {
          obj.set('flipX', !obj.flipX);
        } else {
          obj.set('flipY', !obj.flipY);
        }
      });
      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /** Build the toDataURL options that capture exactly the image
   *  region at native resolution (imgW × imgH), regardless of the
   *  current zoom / pan / canvas-DOM size.
   *
   *  Fabric's `toCanvasElement` formula (StaticCanvas.mjs):
   *    scaledWidth   = width * multiplier
   *    newZoom       = zoom * multiplier
   *    newVp.tx      = (vp.tx - left) * multiplier
   *  We want output dimensions = imgW × imgH, newZoom = 1, newVp = 0
   *  so the rendered region is exactly scene (0, 0)-(imgW, imgH) at
   *  1:1 scale. Solving:
   *    multiplier = 1 / zoom
   *    width      = imgW * zoom    (so scaledWidth = imgW)
   *    height     = imgH * zoom
   *    left       = vp.tx          (so newVp.tx = 0)
   *    top        = vp.ty          (so newVp.ty = 0)
   *
   *  Without these options, `toDataURL` captures the Fabric canvas
   *  DOM at its native size (the wrapper size under canvas-as-
   *  viewport architecture) — a 3024×4032 image saved at "same
   *  dimensions" would come out as ~833×790 with the image scaled
   *  and centered inside transparent padding.
   *
   *  Custom output dimensions are handled separately via
   *  `resizeDataUrlToTarget` because Fabric's `toCanvasElement` only
   *  supports a uniform scalar zoom — non-aspect-preserving scaling
   *  would distort if forced through it. */
  const buildNativeExportOptions = useCallback(() => {
    const canvas = canvasRef.current;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;
    if (!canvas || !imgW || !imgH) return { multiplier: 1 } as const;
    const vp = canvas.viewportTransform;
    const zoom = (vp && vp[0]) || 1;
    const tx = (vp && vp[4]) || 0;
    const ty = (vp && vp[5]) || 0;
    return {
      multiplier: 1 / zoom,
      width: imgW * zoom,
      height: imgH * zoom,
      left: tx,
      top: ty,
    };
  }, []);

  /** Resize a native-resolution data URL to arbitrary target
   *  dimensions via an offscreen canvas. Supports both aspect-
   *  preserving (when target matches source aspect) and non-uniform
   *  (e.g. user types 1500×3100 for a 3024×4032 source) scaling.
   *  Smoothing on for downscales — keeps detail without aliasing. */
  const resizeDataUrlToTarget = useCallback(
    (
      dataUrl: string,
      format: 'png' | 'jpeg' | 'webp',
      quality: number,
      targetW: number,
      targetH: number,
    ): Promise<Blob | null> =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const out = document.createElement('canvas');
          out.width = Math.max(1, Math.round(targetW));
          out.height = Math.max(1, Math.round(targetH));
          const ctx = out.getContext('2d');
          if (!ctx) {
            resolve(null);
            return;
          }
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, out.width, out.height);
          out.toBlob((blob) => resolve(blob), `image/${format}`, quality);
        };
        img.onerror = () => resolve(null);
        img.src = dataUrl;
      }),
    [],
  );

  /** Export canvas to data URL at the source image's native pixel
   *  resolution. Custom-dimension exports go through `exportToBlob`
   *  (async) since arbitrary resizing requires an offscreen canvas
   *  pass that doesn't fit the synchronous `toDataURL` contract. */
  const exportToDataURL = useCallback(
    (format: 'png' | 'jpeg' | 'webp' = 'png', quality = 1) => {
      const canvas = canvasRef.current;
      if (!canvas) return '';
      return canvas.toDataURL({ format, quality, ...buildNativeExportOptions() });
    },
    [buildNativeExportOptions],
  );

  /** Export canvas to blob. Output dims default to the source image's
   *  native pixel resolution; pass `target` to resize. Aspect ratios
   *  are NOT preserved when both target axes are specified — the
   *  output exactly matches the requested dimensions. To preserve
   *  aspect, omit one axis (the other will be filled in). */
  const exportToBlob = useCallback(
    async (
      format: 'png' | 'jpeg' | 'webp' = 'png',
      quality = 1,
      target?: { width?: number; height?: number },
    ): Promise<Blob | null> => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const nativeDataUrl = canvas.toDataURL({
        format,
        quality,
        ...buildNativeExportOptions(),
      });

      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;
      const wantW = target?.width;
      const wantH = target?.height;

      // No target → return native blob.
      if ((!wantW && !wantH) || !imgW || !imgH) {
        try {
          const res = await fetch(nativeDataUrl);
          return await res.blob();
        } catch {
          return null;
        }
      }

      // Fill in a missing axis from the other (aspect-preserving),
      // round to integer pixel dims, and skip the resize round-trip
      // when target == native.
      const finalW = wantW ?? Math.round(imgW * ((wantH as number) / imgH));
      const finalH = wantH ?? Math.round(imgH * ((wantW as number) / imgW));
      if (Math.round(finalW) === imgW && Math.round(finalH) === imgH) {
        try {
          const res = await fetch(nativeDataUrl);
          return await res.blob();
        } catch {
          return null;
        }
      }

      return resizeDataUrlToTarget(nativeDataUrl, format, quality, finalW, finalH);
    },
    [buildNativeExportOptions, resizeDataUrlToTarget],
  );

  /** Serialize canvas to JSON, optionally including custom property names. */
  const toJSON = useCallback((propertiesToInclude?: string[]) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    // Fabric's `toJSON()` is typed without args, but `toObject` accepts the
    // properties array and produces the same JSON-serializable structure.
    return propertiesToInclude && propertiesToInclude.length > 0
      ? canvas.toObject(propertiesToInclude)
      : canvas.toJSON();
  }, []);

  /**
   * Load canvas from JSON. Sets background image refs and dimensions but does
   * NOT modify the viewport transform — the caller decides whether to fit
   * (`fitToContainer`) or restore a previously saved transform
   * (`setViewportTransform`).
   */
  const loadFromJSON = useCallback(
    async (json: object): Promise<void> => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      await canvas.loadFromJSON(json);

      const objects = canvas.getObjects();
      const bgImg = objects.find(
        (o) =>
          o instanceof fabric.FabricImage &&
          (isBackgroundImage(o) || !(o as fabric.FabricImage & { selectable: boolean }).selectable),
      ) as fabric.FabricImage | undefined;

      if (bgImg) {
        // Tag for future lookups, normalize to non-selectable until the user
        // explicitly clicks the Background layer.
        tagAsBackground(bgImg);
        bgImg.set({ selectable: false, evented: false });
        backgroundImageRef.current = bgImg;
        const imgW = bgImg.width || 1;
        const imgH = bgImg.height || 1;
        imageDimsRef.current = { width: imgW, height: imgH };
        canvas.sendObjectToBack(bgImg);
        onCanvasDimensions?.(imgW, imgH);
      }

      // Convert every layer-tagged FabricImage's underlying element to a
      // canvas so it's paintable, and register the surfaces in the map.
      // (Restored sessions have HTMLImageElements after deserialization;
      // we need HTMLCanvasElements to draw onto.) Disable object caching so
      // live writes to the canvas reflect without manual invalidation.
      paintSurfacesRef.current.clear();
      for (const obj of canvas.getObjects()) {
        if (!(obj instanceof fabric.FabricImage)) continue;
        const layerId = getObjectLayerId(obj);
        if (!layerId) continue;
        const w = obj.width ?? 0;
        const h = obj.height ?? 0;
        const surfaceCanvas = ensureCanvasElement(
          obj.getElement() as HTMLImageElement | HTMLCanvasElement,
          w,
          h,
        );
        if (obj.getElement() !== surfaceCanvas) obj.setElement(surfaceCanvas);
        obj.set({ objectCaching: false });
        paintSurfacesRef.current.set(layerId, {
          canvas: surfaceCanvas,
          fabricImg: obj,
          offsetX: obj.left ?? 0,
          offsetY: obj.top ?? 0,
        });
      }

      // Separate pass for clipPath (mask) rehydration. The previous
      // pass only walks FabricImage layers, but masks attach to
      // ANY layer-tagged object (text / shape / paint surface / image)
      // — see addLayerMask. Without rehydrating clipPaths on
      // non-image layers too, mask painting on a text or shape
      // layer silently breaks after refresh because
      // `bakeStrokeOntoMask` calls `getContext('2d')` on the mask
      // element and an `HTMLImageElement` doesn't support that.
      //
      // ALSO restores the raw/effective split after a serialize→
      // load round-trip. `installMaskClipPathSerializer` makes
      // `toObject` save RAW as the element; after load, the canvas
      // element IS raw. We allocate a fresh EFFECTIVE canvas + copy
      // raw → effective identity (so the visible state matches the
      // saved state immediately), attach raw, install the serializer
      // on the new instance. The mask-sync effect in EditorPage
      // fires after this and re-materializes with the live mask
      // config from `state.document`, which catches feather/invert/
      // density correctly (effective is just an identity copy at
      // this point — config gets applied in materializeMaskClipPath).
      for (const obj of canvas.getObjects()) {
        const cp = (obj as fabric.FabricObject).clipPath;
        if (!(cp instanceof fabric.FabricImage)) continue;
        const cpW = cp.width ?? obj.width ?? 0;
        const cpH = cp.height ?? obj.height ?? 0;
        const maskCanvas = ensureCanvasElement(
          cp.getElement() as HTMLImageElement | HTMLCanvasElement,
          cpW,
          cpH,
        );
        if (cp.getElement() !== maskCanvas) cp.setElement(maskCanvas);

        // Raw/effective split rehydration. Skip when raw is already
        // attached — applyLayerOrder runs on every layer ordering
        // change, not just session-load; idempotency keeps the post-
        // load state stable.
        if (!readRawMaskCanvas(cp)) {
          const raw = cp.getElement() as HTMLCanvasElement;
          const effective = document.createElement('canvas');
          effective.width = raw.width;
          effective.height = raw.height;
          const ectx = effective.getContext('2d', { willReadFrequently: true });
          if (ectx) ectx.drawImage(raw, 0, 0);
          attachRawMaskCanvas(cp, raw);
          cp.setElement(effective);
          installMaskClipPathSerializer(cp);
        }
      }

      canvas.renderAll();
    },
    [onCanvasDimensions],
  );

  /** Apply a saved [a, b, c, d, e, f] viewport transform. Returns the resulting zoom. */
  const setViewportTransform = useCallback(
    (vt: number[]): number => {
      const canvas = canvasRef.current;
      if (!canvas || vt.length !== 6) return 1;
      canvas.setViewportTransform([vt[0], vt[1], vt[2], vt[3], vt[4], vt[5]]);
      if (marqueeRef.current.obj) {
        applyMarqueeStroke(canvas, marqueeRef.current.obj);
        for (const o of marqueeRef.current.extraObjs) applyMarqueeStroke(canvas, o);
      }
      canvas.renderAll();
      updateViewportInfo();
      return vt[0];
    },
    [updateViewportInfo],
  );

  /** Snapshot the current viewport transform for persistence. */
  const getViewportTransform = useCallback((): number[] | null => {
    const canvas = canvasRef.current;
    const vt = canvas?.viewportTransform;
    return vt ? [...vt] : null;
  }, []);

  /** Clear the active marquee selection (any shape) if present */
  const clearMarquee = useCallback(() => {
    const canvas = canvasRef.current;
    const m = marqueeRef.current;
    if (!canvas) return;
    if (m.obj || m.extraObjs.length > 0) {
      if (m.obj) canvas.remove(m.obj);
      for (const o of m.extraObjs) canvas.remove(o);
      m.obj = null;
      m.extraObjs = [];
      m.points = [];
      m.wandMaskCanvas = null;
      m.isDrawing = false;
      canvas.renderAll();
      setHasMarqueeSelection(false);
    }
  }, []);

  /** Expand or contract the active marquee by `deltaPx` pixels on
   *  every side (negative for shrink). Three implementation paths
   *  depending on the marquee shape:
   *
   *    - **Rect / ellipse**: closed-form math on the Fabric object's
   *      own dimensions. Stays the original shape after the op.
   *    - **Lasso / polygon**: rasterize the current vertices into a
   *      binary mask, dilate or erode by `|deltaPx|` via a Euclidean
   *      kernel, re-trace contours back to polygons. The marquee
   *      transitions to a `wand`-style multi-contour selection
   *      (single contour for simple shapes, multiple if shrinking
   *      a thin shape splits it). Loses precise vertex positions
   *      but preserves the selected area faithfully.
   *    - **Wand**: same morphology applied to the cached
   *      `wandMaskCanvas`, then re-traced.
   *
   *  `deltaPx` is in scene-space pixels (so it scales naturally with
   *  image resolution, not viewport zoom). The result is clamped to
   *  the image bounds. */
  const growMarqueeBy = useCallback(
    (deltaPx: number) => {
      const canvas = canvasRef.current;
      const m = marqueeRef.current;
      if (!canvas || !m.obj || deltaPx === 0) return;
      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;

      if (m.shape === 'rect') {
        const r = m.obj as fabric.Rect;
        const left = (r.left ?? 0) - deltaPx;
        const top = (r.top ?? 0) - deltaPx;
        const width = (r.width ?? 0) + 2 * deltaPx;
        const height = (r.height ?? 0) + 2 * deltaPx;
        if (width < 1 || height < 1) return;
        const clampedLeft = Math.max(0, Math.min(imgW - 1, left));
        const clampedTop = Math.max(0, Math.min(imgH - 1, top));
        const clampedW = Math.max(1, Math.min(imgW - clampedLeft, width));
        const clampedH = Math.max(1, Math.min(imgH - clampedTop, height));
        r.set({ left: clampedLeft, top: clampedTop, width: clampedW, height: clampedH });
        r.setCoords();
        canvas.renderAll();
        return;
      }

      if (m.shape === 'ellipse') {
        const e = m.obj as fabric.Ellipse;
        const left = (e.left ?? 0) - deltaPx;
        const top = (e.top ?? 0) - deltaPx;
        const rx = (e.rx ?? 0) + deltaPx;
        const ry = (e.ry ?? 0) + deltaPx;
        if (rx < 1 || ry < 1) return;
        e.set({
          left: Math.max(0, left),
          top: Math.max(0, top),
          rx: Math.max(1, Math.min(imgW / 2, rx)),
          ry: Math.max(1, Math.min(imgH / 2, ry)),
        });
        e.setCoords();
        canvas.renderAll();
        return;
      }

      // Mask-based path for lasso / polygon / wand.
      if (imgW <= 0 || imgH <= 0) return;

      let mask: Uint8Array | null = null;
      if (m.shape === 'lasso' || m.shape === 'polygon') {
        // Source vertices for fill. `m.points` holds the polygon
        // points authoritatively (Fabric Polygon's `points` may be
        // re-centered after mounts; keep the canonical list).
        if (m.points.length < 3) return;
        mask = polygonPointsToMask(m.points, imgW, imgH);
      } else if (m.shape === 'wand' && m.wandMaskCanvas) {
        const result = canvasToBinaryMask(m.wandMaskCanvas);
        mask = result.mask;
      }
      if (!mask) return;

      const radius = Math.abs(Math.round(deltaPx));
      if (radius === 0) return;
      const next =
        deltaPx > 0 ? dilateMask(mask, imgW, imgH, radius) : erodeMask(mask, imgW, imgH, radius);

      // If erosion wiped the whole selection, treat it as a deselect
      // — anything else (a few stray pixels, etc.) would be confusing.
      let any = 0;
      for (let i = 0; i < next.length; i++)
        if (next[i]) {
          any = 1;
          break;
        }
      if (!any) {
        clearMarquee();
        return;
      }

      // Replace the marquee with wand-style contours from the new
      // mask. Drop existing fabric polys + cached wand canvas first.
      if (m.obj) canvas.remove(m.obj);
      for (const o of m.extraObjs) canvas.remove(o);
      m.obj = null;
      m.extraObjs = [];
      m.points = [];

      m.wandMaskCanvas = binaryMaskToCanvas(next, imgW, imgH);
      m.shape = 'wand';

      const contours = traceContours(next, imgW, imgH);
      const path = buildMarqueePath(contours);
      if (!path) {
        clearMarquee();
        return;
      }
      tagAsMarquee(path);
      applyMarqueeColor(path, marqueeColorRef.current);
      applyMarqueeStroke(canvas, path);
      canvas.add(path);
      m.obj = path;
      m.extraObjs = [];
      m.isDrawing = false;
      canvas.renderAll();
      refreshMarqueeSelectionState();
    },
    [clearMarquee, refreshMarqueeSelectionState],
  );

  /** True when the active marquee supports grow / shrink. Now true
   *  for every shape with a finalized selection — rect / ellipse use
   *  closed-form math; lasso / polygon / wand go through the mask
   *  morphology path (and transition to a wand-style selection). */
  const canGrowMarquee = useCallback((): boolean => {
    const m = marqueeRef.current;
    if (!m.obj || m.isDrawing) return false;
    if (m.shape === 'rect' || m.shape === 'ellipse') return true;
    if (m.shape === 'wand' && m.wandMaskCanvas) return true;
    if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 3) return true;
    return false;
  }, []);

  /** Install a pre-built binary mask as a wand-style marquee. The
   *  mask canvas is expected to be alpha-mask format (alpha > 127
   *  means "selected"). Sized to the source image dimensions —
   *  callers responsible for upstream resampling if their mask
   *  came from a differently-sized provider response. Used by the
   *  AI Select Subject route, which returns a 1-channel mask sized
   *  to the source image.
   *
   *  Behaves like the magic-wand path: traces contours into Fabric
   *  Polygons, marks `m.shape` as `'wand'`, refreshes the cached
   *  mask canvas the eraser path reads. Subsequent grow / shrink
   *  operations work the same as they do for any wand selection. */
  const setSelectionFromMask = useCallback(
    (maskCanvas: HTMLCanvasElement): boolean => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;
      if (imgW <= 0 || imgH <= 0) return false;

      // If the supplied mask doesn't match the image dims, draw it
      // scaled into a properly-sized buffer so traceContours emits
      // points in scene space rather than provider-output space.
      let normalized = maskCanvas;
      if (maskCanvas.width !== imgW || maskCanvas.height !== imgH) {
        const tmp = document.createElement('canvas');
        tmp.width = imgW;
        tmp.height = imgH;
        const tCtx = tmp.getContext('2d', { willReadFrequently: true });
        if (!tCtx) return false;
        tCtx.imageSmoothingEnabled = false;
        tCtx.drawImage(maskCanvas, 0, 0, imgW, imgH);
        normalized = tmp;
      }

      const { mask } = canvasToBinaryMask(normalized);
      // Reject empty masks — the provider returned something with
      // no foreground at all (rare, but possible on solid-color
      // images or when segmentation can't find a subject).
      let any = 0;
      for (let i = 0; i < mask.length; i++)
        if (mask[i]) {
          any = 1;
          break;
        }
      if (!any) return false;

      // Drop any existing marquee — including wand contours and
      // their cached mask — before installing the new selection.
      const m = marqueeRef.current;
      if (m.obj) canvas.remove(m.obj);
      for (const o of m.extraObjs) canvas.remove(o);
      m.obj = null;
      m.extraObjs = [];
      m.points = [];
      m.wandMaskCanvas = null;
      m.isDrawing = false;

      m.shape = 'wand';
      m.wandMaskCanvas = binaryMaskToCanvas(mask, imgW, imgH);

      const contours = traceContours(mask, imgW, imgH);
      const path = buildMarqueePath(contours);
      if (!path) return false;
      tagAsMarquee(path);
      applyMarqueeColor(path, marqueeColorRef.current);
      applyMarqueeStroke(canvas, path);
      canvas.add(path);
      m.obj = path;
      m.extraObjs = [];

      canvas.renderAll();
      refreshMarqueeSelectionState();
      return true;
    },
    [refreshMarqueeSelectionState],
  );

  /** Internal: rasterize the marqueeRef.current state into a
   *  Uint8Array sized to the image. Delegates per-shape rules to the
   *  pure `selectionToBinaryMask` helper. Returns an all-zero mask
   *  when there's no selection (callers can branch on
   *  `isMaskNonEmpty`). */
  const marqueeToBinaryMask = useCallback(
    (m: typeof marqueeRef.current, imgW: number, imgH: number): Uint8Array => {
      if (!m.obj) return new Uint8Array(imgW * imgH);
      if (m.shape === 'rect') {
        const r = m.obj as fabric.Rect;
        return selectionToBinaryMask(
          {
            shape: 'rect',
            left: r.left ?? 0,
            top: r.top ?? 0,
            width: r.width ?? 0,
            height: r.height ?? 0,
            scaleX: r.scaleX ?? 1,
            scaleY: r.scaleY ?? 1,
          },
          imgW,
          imgH,
        );
      }
      if (m.shape === 'ellipse') {
        const e = m.obj as fabric.Ellipse;
        return selectionToBinaryMask(
          {
            shape: 'ellipse',
            left: e.left ?? 0,
            top: e.top ?? 0,
            rx: e.rx ?? 0,
            ry: e.ry ?? 0,
            scaleX: e.scaleX ?? 1,
            scaleY: e.scaleY ?? 1,
          },
          imgW,
          imgH,
        );
      }
      if (m.shape === 'lasso' || m.shape === 'polygon') {
        return selectionToBinaryMask({ shape: m.shape, points: m.points }, imgW, imgH);
      }
      if (m.shape === 'wand' && m.wandMaskCanvas) {
        return selectionToBinaryMask({ shape: 'wand', maskCanvas: m.wandMaskCanvas }, imgW, imgH);
      }
      return new Uint8Array(imgW * imgH);
    },
    [],
  );

  /** Return the current marquee rasterized as an alpha-mask canvas
   *  sized to the image. White (alpha=255) where selected,
   *  transparent elsewhere. Used by Quick Mask to seed the edit
   *  canvas, by Color Range to constrain sampling, and by Refine
   *  Edge to populate the initial working mask. Returns `null` when
   *  there is no active selection or the image dims are unknown. */
  const getSelectionAsMaskCanvas = useCallback((): HTMLCanvasElement | null => {
    const m = marqueeRef.current;
    if (!m.obj || m.isDrawing) return null;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;
    if (imgW <= 0 || imgH <= 0) return null;
    const mask = marqueeToBinaryMask(m, imgW, imgH);
    if (!isMaskNonEmpty(mask)) return null;
    return binaryMaskToCanvas(mask, imgW, imgH);
  }, [marqueeToBinaryMask]);

  // === Quick Mask (Tier 1 Group D) ===========================
  // The QM canvas is the source of truth for the working alpha mask;
  // the overlay FabricImage is a presentation-only red tint built
  // from it. Brush handlers paint into the QM canvas via the
  // helpers below; `enterQuickMask` / `exitQuickMask` are the only
  // surface React touches.

  /** Paint a single brush dab into the Quick Mask canvas at scene-
   *  space (x, y). Soft falloff via radial gradient. `mode` and
   *  `radius`/`opacity` come from the per-stroke state set on
   *  mouse:down. */
  const paintQuickMaskDab = useCallback((x: number, y: number) => {
    const qm = quickMaskCanvasRef.current;
    const stroke = quickMaskStrokeRef.current;
    if (!qm || !stroke) return;
    const ctx = qm.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    const { radius, hardness, opacity, mode } = stroke;
    // Inner-radius = (1 - hardness inverted) — at hardness 0 the
    // gradient is fully soft (center at 0%); at hardness 1 the
    // gradient is hard-edged (inner = outer). We use 0.5 by default
    // in QM, which gives a tight Photoshop-like brush.
    const innerR = radius * hardness;
    const grad = ctx.createRadialGradient(x, y, innerR, x, y, radius);
    if (mode === 'add') {
      grad.addColorStop(0, `rgba(255, 255, 255, ${opacity})`);
      grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.globalCompositeOperation = 'source-over';
    } else {
      grad.addColorStop(0, `rgba(0, 0, 0, ${opacity})`);
      grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      // destination-out clears alpha rather than painting black —
      // exactly the "remove from selection" semantic we want.
      ctx.globalCompositeOperation = 'destination-out';
    }
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
  }, []);
  paintQuickMaskDabRef.current = paintQuickMaskDab;

  /** Rebuild the red-tinted overlay image from the current QM
   *  canvas. The overlay is a single FabricImage on top of every
   *  layer — its element is a tint canvas whose RGB is solid red
   *  and whose alpha is `0.5 * (1 - qmMaskAlpha/255)` so SELECTED
   *  pixels show through and UN-SELECTED pixels get a 50% red
   *  matte. (Photoshop's Quick Mask is configurable; we ship with
   *  the default "Masked Areas" color.) */
  const refreshQuickMaskOverlay = useCallback(() => {
    const overlay = quickMaskOverlayRef.current;
    const qm = quickMaskCanvasRef.current;
    const canvas = canvasRef.current;
    if (!overlay || !qm || !canvas) return;
    const w = qm.width;
    const h = qm.height;
    const tint = (overlay.getElement() as HTMLCanvasElement) ?? document.createElement('canvas');
    if (tint.width !== w || tint.height !== h) {
      tint.width = w;
      tint.height = h;
    }
    const tctx = tint.getContext('2d', { willReadFrequently: true });
    const qctx = qm.getContext('2d', { willReadFrequently: true });
    if (!tctx || !qctx) return;
    const qd = qctx.getImageData(0, 0, w, h).data;
    const td = tctx.createImageData(w, h);
    const tdata = td.data;
    for (let i = 0; i < qd.length; i += 4) {
      // qd[i+3] is the QM alpha (255 inside selection, 0 outside).
      // The matte covers the OUTSIDE, so the overlay alpha is the
      // inverse, scaled to 50%.
      const inside = qd[i + 3];
      const matte = ((255 - inside) * 0.5) | 0;
      tdata[i] = 255; // red
      tdata[i + 1] = 0;
      tdata[i + 2] = 0;
      tdata[i + 3] = matte;
    }
    tctx.putImageData(td, 0, 0);
    overlay.setElement(tint);
    overlay.dirty = true;
    canvas.requestRenderAll();
  }, []);
  refreshQuickMaskOverlayRef.current = refreshQuickMaskOverlay;

  /** Mirror the panel-set QM brush mode into the canvas-side ref
   *  read by the brush handler. */
  const setQuickMaskMode = useCallback((mode: 'add' | 'subtract') => {
    quickMaskModeRef.current = mode;
  }, []);

  /** Enter Quick Mask mode. Seeds the QM working canvas from the
   *  current selection (or starts blank if no selection), installs
   *  the visual overlay, and clears the marquee from the Fabric
   *  canvas (the mask is now the source of truth — the marquee
   *  would otherwise show stale boundary lines that don't reflect
   *  the user's brush edits).
   *
   *  The reducer flips `activeTool` to `'quickmask'` BEFORE calling
   *  this — see EditorPage's `quickMaskActive` effect. The two are
   *  expected to stay in sync.
   *
   *  Returns true on success, false when the canvas isn't ready
   *  (no image loaded). */
  const enterQuickMask = useCallback((): boolean => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;
    if (imgW <= 0 || imgH <= 0) return false;

    // Idempotency: sweep any pre-existing QM overlays so a re-entry
    // (effect re-fires while quickMaskActive stays true, or a
    // missed exit) doesn't stack red mattes. Each call into this
    // fn produces a fresh overlay regardless of prior state.
    for (const o of canvas.getObjects()) {
      if ((o as unknown as { _isQuickMaskOverlay?: boolean })._isQuickMaskOverlay) {
        canvas.remove(o);
      }
    }
    quickMaskOverlayRef.current = null;

    // Build the working alpha mask. Seed from the current selection
    // if there is one; otherwise preserve the prior in-flight QM
    // canvas (so a stray re-entry doesn't wipe the user's painted
    // strokes), else start fully empty.
    const seed = getSelectionAsMaskCanvas();
    let qm = quickMaskCanvasRef.current;
    if (!qm || qm.width !== imgW || qm.height !== imgH || seed) {
      qm = document.createElement('canvas');
      qm.width = imgW;
      qm.height = imgH;
      const qctx = qm.getContext('2d', { willReadFrequently: true });
      if (!qctx) return false;
      if (seed) qctx.drawImage(seed, 0, 0);
      quickMaskCanvasRef.current = qm;
    }

    // Drop the existing marquee from the Fabric canvas. The mask
    // we just captured carries the same data, and a lingering
    // marching-ants outline would be misleading during QM editing.
    const m = marqueeRef.current;
    if (m.obj) canvas.remove(m.obj);
    for (const o of m.extraObjs) canvas.remove(o);
    m.obj = null;
    m.extraObjs = [];
    m.points = [];
    m.wandMaskCanvas = null;
    m.isDrawing = false;

    // Build the overlay's backing canvas and FabricImage. The
    // backing is sized to the image; the FabricImage anchors at
    // (0, 0) so the overlay aligns 1:1 with paint-layer pixels.
    const tint = document.createElement('canvas');
    tint.width = imgW;
    tint.height = imgH;
    const overlay = new fabric.FabricImage(tint, {
      left: 0,
      top: 0,
      originX: 'left',
      originY: 'top',
      selectable: false,
      evented: false,
      excludeFromExport: true,
      hoverCursor: 'default',
      objectCaching: false,
    });
    // Tag for filter-out paths (similar to `_isMarquee`,
    // `_isCloneReticle`). Layer-order helpers skip tagged overlays.
    (overlay as unknown as { _isQuickMaskOverlay: boolean })._isQuickMaskOverlay = true;
    canvas.add(overlay);
    canvas.bringObjectToFront(overlay);
    quickMaskOverlayRef.current = overlay;

    refreshQuickMaskOverlay();
    refreshMarqueeSelectionState();
    return true;
  }, [getSelectionAsMaskCanvas, refreshMarqueeSelectionState, refreshQuickMaskOverlay]);

  /** Exit Quick Mask mode. Converts the working alpha mask back
   *  into a wand-style selection via `setSelectionFromMask`,
   *  removes the overlay, clears the refs. Returns whether a
   *  selection was installed — an all-empty mask (the user
   *  cleared everything while in QM) returns false and leaves the
   *  canvas with no selection. */
  /** Peek the current Quick Mask alpha canvas without exiting QM.
   *  Returns a CLONE so the caller can hand it to downstream
   *  consumers (layer-mask attach, etc.) without worrying about
   *  the QM session mutating it from under them. Null when QM
   *  isn't active. */
  const getQuickMaskCanvas = useCallback((): HTMLCanvasElement | null => {
    const qm = quickMaskCanvasRef.current;
    if (!qm) return null;
    const clone = document.createElement('canvas');
    clone.width = qm.width;
    clone.height = qm.height;
    const ctx = clone.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(qm, 0, 0);
    return clone;
  }, []);

  /** Clean up Quick Mask state WITHOUT installing the painted mask
   *  as a marquee. Used by the "Apply as Layer Mask" exit path —
   *  the caller has already applied the mask elsewhere and just
   *  wants to dismiss the overlay. Idempotent. */
  const discardQuickMask = useCallback((): void => {
    const canvas = canvasRef.current;
    if (canvas) {
      const stale: fabric.FabricObject[] = [];
      for (const o of canvas.getObjects()) {
        if ((o as unknown as { _isQuickMaskOverlay?: boolean })._isQuickMaskOverlay) {
          stale.push(o);
        }
      }
      for (const o of stale) canvas.remove(o);
      canvas.requestRenderAll();
    }
    quickMaskOverlayRef.current = null;
    quickMaskCanvasRef.current = null;
    quickMaskStrokeRef.current = null;
  }, []);

  /** Attach a layer mask to a layer using a caller-supplied alpha
   *  canvas as the mask source. Sibling to `addLayerMask` (which
   *  fills the mask reveal-all or from the active marquee). Used
   *  by Quick Mask's "Apply as Layer Mask" output path. */
  const applyLayerMaskFromCanvas = useCallback(
    (layerId: string, sourceMaskCanvas: HTMLCanvasElement): LayerMask | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;
      if (imgW <= 0 || imgH <= 0) return null;

      // Find target object (or lazily create paint surface for empty
      // layers — same fallback path `addLayerMask` uses).
      let targetObj: fabric.Object | null = null;
      for (const o of canvas.getObjects()) {
        if (getObjectLayerId(o) === layerId) {
          targetObj = o;
          break;
        }
      }
      if (!targetObj) {
        const surface = ensurePaintSurfaceRef.current?.(layerId);
        targetObj = surface?.fabricImg ?? null;
      }
      if (!targetObj) return null;

      // Build the RAW alpha-mask canvas at image dims. Normalize via
      // a scale-blit if the supplied mask doesn't match (defensive —
      // QM hands us same-dim canvases today, but Color Range and
      // future callers may not). See `addLayerMask` for the rationale
      // behind the raw/effective split.
      const rawCanvas = document.createElement('canvas');
      rawCanvas.width = imgW;
      rawCanvas.height = imgH;
      const rCtx = rawCanvas.getContext('2d', { willReadFrequently: true });
      if (!rCtx) return null;
      rCtx.imageSmoothingEnabled = sourceMaskCanvas.width === imgW;
      rCtx.drawImage(sourceMaskCanvas, 0, 0, imgW, imgH);

      const effectiveCanvas = document.createElement('canvas');
      effectiveCanvas.width = imgW;
      effectiveCanvas.height = imgH;

      const initialMaskConfig: LayerMask = {
        id: `lmask_${layerId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        enabled: true,
        inverted: false,
        linked: true,
        density: 1,
        feather: 0,
        fabricObjectId: '',
      };
      materializeEffectiveMaskInto(
        effectiveCanvas,
        rawCanvas,
        maskMaterializationConfig(initialMaskConfig),
        imgW,
        imgH,
      );

      const maskImg = new fabric.FabricImage(effectiveCanvas, {
        left: 0,
        top: 0,
        originX: 'left',
        originY: 'top',
        selectable: false,
        evented: false,
      });
      const maskFabricId = `fmask_${layerId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      (maskImg as fabric.FabricImage & { id: string }).id = maskFabricId;
      (maskImg as fabric.FabricObject & { absolutePositioned: boolean }).absolutePositioned = true;
      attachRawMaskCanvas(maskImg, rawCanvas);
      installMaskClipPathSerializer(maskImg);

      targetObj.clipPath = maskImg;
      canvas.requestRenderAll();

      initialMaskConfig.fabricObjectId = maskFabricId;
      maskConfigsRef.current.set(layerId, initialMaskConfig);
      return initialMaskConfig;
    },
    [],
  );

  const exitQuickMask = useCallback((): boolean => {
    const canvas = canvasRef.current;
    const qm = quickMaskCanvasRef.current;
    // Sweep every tagged QM overlay rather than relying on the
    // single tracked ref. Defensive against the re-entry edge case
    // where an old overlay was orphaned by an enter call that
    // overwrote the ref without removing the prior FabricImage.
    if (canvas) {
      const stale: fabric.FabricObject[] = [];
      for (const o of canvas.getObjects()) {
        if ((o as unknown as { _isQuickMaskOverlay?: boolean })._isQuickMaskOverlay) {
          stale.push(o);
        }
      }
      for (const o of stale) canvas.remove(o);
    }
    quickMaskOverlayRef.current = null;
    quickMaskStrokeRef.current = null;
    let installed = false;
    if (qm && canvas) {
      installed = setSelectionFromMask(qm);
    }
    quickMaskCanvasRef.current = null;
    if (canvas) canvas.requestRenderAll();
    return installed;
  }, [setSelectionFromMask]);

  // === Free Transform (Tier 1 Group E Phase 1) ==================
  // Captured pre-transform state so Cancel can revert the layer
  // exactly. For affine modes (Free/Skew) we only need the matrix
  // properties; for non-affine modes (Distort/Perspective/Warp)
  // we also stash the original element so a baked-pixel apply can
  // be reversed.
  const transformInitialRef = useRef<{
    layerId: string;
    scaleX: number;
    scaleY: number;
    left: number;
    top: number;
    angle: number;
    skewX: number;
    skewY: number;
    /** Origin can shift during Fabric's wrapWithFixedAnchor flow —
     *  the action handlers temporarily swap originX/Y to keep the
     *  opposite corner pinned during drag. Capturing + restoring
     *  these eliminates a class of "Cancel doesn't fully revert"
     *  bugs where the bbox was technically correct but the layer's
     *  origin had shifted. */
    originX: number | string;
    originY: number | string;
    flipX: boolean;
    flipY: boolean;
    originalElement: HTMLImageElement | HTMLCanvasElement | null;
    /** Captured lock flags + controls map so exitTransformMode
     *  can restore the layer's pre-transform config exactly.
     *  `applyTransformControls` mutates these on every mode change
     *  (e.g. Skew mode swaps in custom mid-edge controls); without
     *  the snapshot, a subsequent tool pickup would see stale
     *  transform-only state. */
    lockRotation: boolean;
    lockScalingFlip: boolean;
    lockSkewingX: boolean;
    lockSkewingY: boolean;
    /** Pre-transform interaction flags for the TARGET. Capturing
     *  these makes exit honor the layer's actual original state
     *  rather than hard-coding selectable=true / evented=true.
     *  Paint surfaces (rasterized layers, the bg image acting as
     *  the paint target) come in non-selectable / non-evented so
     *  brush/retouch tools intercept clicks; unconditionally
     *  flipping both to true on exit broke that. */
    selectable: boolean;
    evented: boolean;
    controls: Record<string, fabric.Control>;
    /** Pre-lock selectable/evented state for every sibling object
     *  enterTransformMode mutated. Free Transform doesn't change
     *  `activeTool`, so EditorPage's tool-sync effect doesn't re-
     *  fire on exit — without this snapshot, siblings stay locked
     *  (non-selectable, non-evented) past the session, and the
     *  user can't click them on the canvas until they switch
     *  tools. Per-sibling capture is exact: it survives whatever
     *  the current tool's lock convention is (e.g. Marquee locks
     *  every sibling; Move leaves them all evented). */
    siblings: { obj: fabric.FabricObject; selectable: boolean; evented: boolean }[];
  } | null>(null);

  /** Non-affine transform working state (Tier 1 Group E Phase 1b).
   *  Set up by `setupNonAffineOverlay` when the user picks Distort,
   *  Perspective, or Warp from the mode picker. Holds:
   *    - the current dst-corner quad (Distort/Perspective) or 9-point
   *      mesh (Warp), in canvas pixel coords;
   *    - the Fabric overlay objects we render to show those points
   *      (handles + a dashed outline);
   *    - the live-preview source canvas and target canvas — drag
   *      rasterizes the source through the projective/warp math into
   *      the target, swapping the layer's element so the user sees
   *      the deformation under the handles.
   *
   *  The `previewSource` is a downscaled copy (≤ PREVIEW_LONGEST_PX)
   *  so per-drag rasterization stays interactive on 4K layers; the
   *  full-res bake happens once on Apply.
   *
   *  Coordinate system: handles live in the canvas's scene coords —
   *  same space the layer's `left/top` use. The quad/grid points
   *  reference handle positions directly. */
  const transformOverlayRef = useRef<{
    mode: 'distort' | 'perspective' | 'warp';
    /** Saved active-tool ref + drawing-mode flag so teardown can
     *  restore whatever tool the user was on before entering the
     *  non-affine session. During the session, `activeToolRef` is
     *  forced to `'ai'` — a tool whose mouse handlers we never
     *  registered. Every other tool handler gates on
     *  `activeToolRef.current === 'X'` and early-returns when not
     *  matching, so this single ref-override globally suppresses
     *  tool behaviors that would otherwise compete with handle
     *  drags (Marquee drawing a rect, Move panning, brush painting,
     *  etc.). */
    savedActiveTool: EditorTool;
    savedDrawingMode: boolean;
    /** Pre-session `canvas.selection` flag. Forced to `false` for
     *  the duration of the session so Fabric's group-selection
     *  rubber-band (a transient overlay rect drawn from drag-start
     *  to cursor when `canvas.selection === true` and the click
     *  misses all targets) cannot fire. */
    savedSelection: boolean;
    /** Current dst-corner positions for Distort/Perspective.
     *  Order: top-left, top-right, bottom-right, bottom-left. */
    distortQuad: ProjectiveQuad;
    /** Current 9-point grid for Warp. Order row-major: tl-mt-tr,
     *  ml-c-mr, bl-mb-br. */
    warpGrid: WarpGrid;
    /** Fabric handle circles in same order as `distortQuad` (4) or
     *  `warpGrid` (9). */
    handles: fabric.FabricObject[];
    /** Dashed quad/mesh outline rendered behind the handles. */
    outlines: fabric.FabricObject[];
    /** Layer pixel source at enter time, full resolution.
     *  Captured via `target.toCanvasElement()` so any prior affine
     *  matrix the user applied in Free/Skew is baked into the
     *  source pixels — the non-affine session composes on top of
     *  whatever the affine session did. */
    sourceFull: HTMLCanvasElement;
    /** Downscaled preview source — read by each drag tick's
     *  rasterizer. Sized so the longest side ≤ PREVIEW_LONGEST_PX. */
    sourcePreview: HTMLCanvasElement;
    /** Pre-overlay bounding box in scene coords — used only for
     *  the initial handle positions (handles seed at the bbox
     *  corners) and the bake-step source pixel density
     *  calculation. The session itself doesn't constrain the
     *  deformation to this bbox. */
    bbox: { left: number; top: number; width: number; height: number };
  } | null>(null);

  /** Forward-ref to the latest `updateNonAffinePreview` so the
   *  mount-time mouse-event handlers can invoke the current
   *  closure (rather than the one captured at registration time).
   *  Same pattern as `paintQuickMaskDabRef` etc. */
  const updateNonAffinePreviewRef = useRef<(() => void) | null>(null);

  /** Active-handle drag state. Set on mouse:down when the user
   *  clicks within hit-radius of a handle; cleared on mouse:up.
   *  Stores the index in the overlay's handles array so the
   *  mouse:move handler can route updates to the right quad/grid
   *  slot. */
  const transformDragRef = useRef<{ handleIndex: number } | null>(null);

  /** Forward-refs to the overlay setup / teardown helpers so
   *  `applyTransformControls` (declared earlier in this hook)
   *  can call them without hitting the TS temporal-dead-zone for
   *  the corresponding useCallback declarations below. */
  const setupNonAffineOverlayRef = useRef<
    ((mode: 'distort' | 'perspective' | 'warp') => void) | null
  >(null);
  const teardownNonAffineOverlayRef = useRef<(() => void) | null>(null);
  const bakeNonAffineOverlayRef = useRef<(() => void) | null>(null);

  /** Find the layer's primary Fabric object. For paint surfaces
   *  this is the wrapped FabricImage; for image layers it's the
   *  loaded image; for text/shape it's the IText/Rect/etc. */
  const findLayerObject = useCallback((layerId: string): fabric.FabricObject | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    for (const o of canvas.getObjects()) {
      if (getObjectLayerId(o) === layerId) return o;
    }
    return null;
  }, []);

  /** Rasterize a non-image Fabric object (vector path / shape /
   *  text) into a `FabricImage` and swap it into the canvas in
   *  place. Used by the rasterize-before-warp prompt: Distort,
   *  Perspective and Warp's underlying renderer
   *  (`commitNonAffineDeformation` + `setElement`) is image-only,
   *  so when the user accepts the prompt we convert the layer to
   *  pixels first.
   *
   *  The result is a normal **image layer** (NOT a paint surface):
   *    - bbox-sized canvas positioned at the source's scene-space
   *      bbox.left/bbox.top — so Move tool's resize handles land
   *      on the visible content, not the full document edge
   *    - `selectable: true`, `evented: true` — Move tool can
   *      translate / resize it like any other image layer
   *    - identity scaleX/scaleY/angle: `toCanvasElement` already
   *      baked the source's affine into the pixels, so applying
   *      it again would double-transform
   *    - `objectCaching: false`: matches every other FabricImage
   *      we mount (Fabric's per-object cache mis-renders some
   *      filter / blend combos)
   *
   *  Why NOT a paint surface: brush / retouch / clone tools work
   *  on image layers via `ensurePaintSurface`, which lazily mounts
   *  a doc-sized transparent overlay tagged with the same layerId
   *  on top of the image. The image stays visible underneath, with
   *  its own selectable bbox. Making the rasterized layer itself
   *  doc-sized + non-selectable (like a bg paint surface) was the
   *  wrong default — it took away the user's resize handles and
   *  routed retouch through Move-tool because of stale selectable
   *  flags during transform exit.
   *
   *  Returns true when the swap succeeded. False on:
   *    - canvas not mounted
   *    - no Fabric object for `layerId`
   *    - the object is ALREADY a `FabricImage` (defensive)
   *    - `toCanvasElement` unavailable or produced an empty canvas */
  const rasterizeLayerToImage = useCallback(
    (layerId: string): boolean => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      const old = findLayerObject(layerId);
      if (!old) return false;
      if (old instanceof fabric.FabricImage) return false;
      const objWithToCanvas = old as fabric.FabricObject & {
        toCanvasElement?: () => HTMLCanvasElement;
      };
      if (!objWithToCanvas.toCanvasElement) return false;
      const rendered = objWithToCanvas.toCanvasElement();
      if (!rendered || rendered.width === 0 || rendered.height === 0) return false;

      const br = old.getBoundingRect();
      const wasActive = canvas.getActiveObject() === old;
      const newImg = new fabric.FabricImage(rendered, {
        left: br.left,
        top: br.top,
        originX: 'left',
        originY: 'top',
        scaleX: 1,
        scaleY: 1,
        angle: 0,
        flipX: false,
        flipY: false,
        // Inherit interaction flags from the source. The two callers
        // have different needs: rasterize-before-warp calls this
        // while the layer is `selectable: true` (Free Transform's
        // entry set it), and `ensurePaintSurface`'s auto-rasterize
        // calls this during a retouch session where the layer is
        // `selectable: false` (setTool('retouch') locked it). Either
        // way the new FabricImage should match the old's flags so
        // the active tool's lock pattern stays intact across the
        // swap.
        selectable: old.selectable ?? true,
        evented: old.evented ?? true,
        opacity: old.opacity ?? 1,
        visible: old.visible !== false,
      });
      newImg.set({ objectCaching: false } as Partial<fabric.FabricImage>);
      setObjectLayerId(newImg, layerId);

      canvas.remove(old);
      canvas.add(newImg);
      if (wasActive) {
        canvas.setActiveObject(newImg);
      }

      // If a transform session is currently captured against THIS
      // layer (rasterize-before-warp path), refresh the snapshot so
      // Cancel rolls back to the rasterized image's matrix, not the
      // long-gone vector/shape's. Mirror every captured field —
      // including selectable / evented — so exitTransformMode's
      // restore reflects the new FabricImage's interaction flags
      // and the layer doesn't end up with stale selectable/evented
      // state from the pre-rasterize object.
      const sessionInitial = transformInitialRef.current;
      if (sessionInitial && sessionInitial.layerId === layerId) {
        sessionInitial.scaleX = newImg.scaleX ?? 1;
        sessionInitial.scaleY = newImg.scaleY ?? 1;
        sessionInitial.left = newImg.left ?? 0;
        sessionInitial.top = newImg.top ?? 0;
        sessionInitial.angle = newImg.angle ?? 0;
        sessionInitial.skewX = newImg.skewX ?? 0;
        sessionInitial.skewY = newImg.skewY ?? 0;
        sessionInitial.originX = newImg.originX ?? 'left';
        sessionInitial.originY = newImg.originY ?? 'top';
        sessionInitial.flipX = newImg.flipX ?? false;
        sessionInitial.flipY = newImg.flipY ?? false;
        sessionInitial.selectable = newImg.selectable ?? true;
        sessionInitial.evented = newImg.evented ?? true;
        sessionInitial.originalElement =
          (newImg.getElement() as HTMLImageElement | HTMLCanvasElement) ?? null;
      }

      canvas.requestRenderAll();
      return true;
    },
    [findLayerObject],
  );

  /** Enter Free Transform on `layerId`. Captures the layer's
   *  current Fabric matrix so Cancel can revert. Sets the layer as
   *  the active object so Fabric's native scale/rotate/skew
   *  controls render. Locks other objects so the user can't
   *  accidentally drag siblings while transforming.
   *
   *  Also captures `lockRotation` / `lockScalingFlip` / the
   *  per-instance `controls` ref — `applyTransformControls` may
   *  override these (Skew mode swaps in custom mid-edge controls;
   *  affine modes unlock rotation), and exit must restore them
   *  exactly so the next tool pickup sees the layer in its
   *  original config.
   *
   *  Returns true on success. False when no layer matches the id
   *  (e.g. the layer was deleted between BEGIN dispatch and effect
   *  firing — shouldn't happen but guard defensively). */
  const enterTransformMode = useCallback(
    (layerId: string): boolean => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      const target = findLayerObject(layerId);
      if (!target) return false;

      // Idempotency guard: don't re-capture if the same session is
      // already active. The active-flag effect in EditorPage
      // depends on `canvas` identity, which can re-fire mid-drag
      // when ANY useCallback inside useFabricCanvas's giant memo
      // dep array changes — a re-capture would clobber the
      // pre-drag snapshot with the in-progress drag state, and
      // Cancel would then revert to the drag state instead of
      // the true initial (visible symptom: Cancel does nothing).
      if (transformInitialRef.current?.layerId === layerId) {
        return true;
      }

      transformInitialRef.current = {
        layerId,
        scaleX: target.scaleX ?? 1,
        scaleY: target.scaleY ?? 1,
        left: target.left ?? 0,
        top: target.top ?? 0,
        angle: target.angle ?? 0,
        skewX: target.skewX ?? 0,
        skewY: target.skewY ?? 0,
        originX: target.originX ?? 'left',
        originY: target.originY ?? 'top',
        flipX: target.flipX ?? false,
        flipY: target.flipY ?? false,
        originalElement:
          target instanceof fabric.FabricImage
            ? ((target.getElement() as HTMLImageElement | HTMLCanvasElement) ?? null)
            : null,
        lockRotation: target.lockRotation ?? false,
        lockScalingFlip: target.lockScalingFlip ?? false,
        lockSkewingX: target.lockSkewingX ?? false,
        lockSkewingY: target.lockSkewingY ?? false,
        // Capture happens BEFORE the post-snapshot mutation that
        // sets `selectable: true / evented: true` on the target
        // (so transform handles render). That mutation is
        // intentional — `exitTransformMode` restores from this
        // snapshot, which means it restores to whatever the
        // PREVIOUS tool's lock pattern was (e.g. retouch leaves
        // image layers `selectable: false` so brush handlers
        // intercept). Do not "improve" by capturing after the
        // mutation — that would lock layers to `true` forever
        // post-transform, re-introducing the Move-tool-during-
        // retouch drag bug.
        selectable: target.selectable ?? true,
        evented: target.evented ?? true,
        controls: target.controls,
        siblings: [],
      };

      // Lock siblings, activate target. Bg image stays
      // non-selectable per the editor's locked-Background
      // convention (mirrors the marquee tool's bg lock) UNLESS the
      // user is transforming the bg layer itself — in which case
      // we have to make it selectable so Fabric's transform
      // handles render. Same exception applies to the brush /
      // crop locks once those land in the workspace pattern.
      //
      // Capture each sibling's pre-lock selectable/evented so
      // exitTransformMode can restore them exactly. Free Transform
      // doesn't change `activeTool`, so EditorPage's tool-sync
      // effect doesn't re-fire on Apply/Cancel — explicit per-
      // sibling restore is required to avoid leaving them locked.
      const bg = getBackgroundImage();
      const siblings: { obj: fabric.FabricObject; selectable: boolean; evented: boolean }[] = [];
      canvas.getObjects().forEach((o) => {
        if (o === target) {
          o.set({ selectable: true, evented: true });
        } else if (o !== bg && !isMarqueeObject(o)) {
          siblings.push({ obj: o, selectable: o.selectable ?? true, evented: o.evented ?? true });
          o.set({ selectable: false, evented: false });
        }
      });
      transformInitialRef.current.siblings = siblings;
      // Unlock rotation + scaling-flip regardless of mode. Some
      // layer types (notably the bg image) ship with lockRotation
      // or lockScalingFlip set; Transform mode needs both off to
      // honor the rotate handle and to allow negative-scale flips
      // when the user drags a corner past the opposite side.
      target.lockRotation = false;
      target.lockScalingFlip = false;
      canvas.setActiveObject(target);
      canvas.defaultCursor = 'default';
      canvas.requestRenderAll();
      return true;
    },
    [findLayerObject, getBackgroundImage],
  );

  /** Configure Fabric corner-control visibility + skew unlocks
   *  per Transform sub-mode. Called whenever the user switches
   *  modes via the Transform panel's mode picker (and once at
   *  BEGIN to set the initial Free-mode controls).
   *
   *  - free: default Fabric controls — corners scale, mid-edges
   *    scale (skew requires Shift modifier, matching Photoshop's
   *    Free Transform).
   *  - skew: corners still scale; mid-edges have their action
   *    handler SWAPPED to `skewHandlerX/Y` so bare drag skews
   *    without needing Shift. The pre-transform `controls` map
   *    was snapshotted in `transformInitialRef.controls` so exit
   *    restores the originals.
   *  - distort / perspective / warp: hide Fabric handles entirely;
   *    custom on-canvas overlays handle the interaction (1b). */
  const applyTransformControls = useCallback(
    (mode: 'free' | 'skew' | 'distort' | 'perspective' | 'warp'): void => {
      const canvas = canvasRef.current;
      const initial = transformInitialRef.current;
      if (!canvas || !initial) return;
      const target = findLayerObject(initial.layerId);
      if (!target) return;

      const isAffine = mode === 'free' || mode === 'skew';
      target.setControlsVisibility({
        tl: isAffine,
        tr: isAffine,
        bl: isAffine,
        br: isAffine,
        ml: isAffine,
        mr: isAffine,
        mt: isAffine,
        mb: isAffine,
        mtr: isAffine,
      });
      target.lockRotation = false;
      target.lockScalingFlip = false;
      if (mode === 'skew') {
        // Swap mid-edge controls to skew-only. Fabric's default
        // `scaleSkewX/Y` action conditionally skews on Shift; for
        // dedicated Skew sub-mode we want bare drag = skew, so we
        // replace the controls with ones bound directly to the
        // `skewHandlerX/Y` action handlers.
        //
        // `controls` is normally a shared object on the prototype;
        // assigning a fresh map to the instance overrides it for
        // this layer only. The snapshot restore in exit /
        // mode-switch puts the prototype reference back so we
        // don't leak the per-instance map across the layer's life.
        target.controls = {
          ...initial.controls,
          ml: buildSkewMidEdgeControl('x-', initial.controls.ml),
          mr: buildSkewMidEdgeControl('x+', initial.controls.mr),
          mt: buildSkewMidEdgeControl('y-', initial.controls.mt),
          mb: buildSkewMidEdgeControl('y+', initial.controls.mb),
        };
        target.lockSkewingX = false;
        target.lockSkewingY = false;
      } else {
        // Free (or any non-skew affine path) — restore the
        // snapshotted control map and re-lock skewing so corners
        // and mid-edges fall back to default scale behavior.
        target.controls = initial.controls;
        target.lockSkewingX = true;
        target.lockSkewingY = true;
      }
      // Non-affine modes: tear down any prior overlay then build a
      // fresh one for the new mode. Switching affine→non-affine,
      // non-affine→affine, or within non-affine modes all hit the
      // same setup/teardown calls — keeps the lifecycle uniform.
      if (mode === 'distort' || mode === 'perspective' || mode === 'warp') {
        setupNonAffineOverlayRef.current?.(mode);
      } else {
        teardownNonAffineOverlayRef.current?.();
        // Going from non-affine back to affine: re-enable layer
        // interaction so Fabric's native scale/skew/rotate handles
        // work again. The setup path made it non-selectable so
        // overlay handles could receive clicks; reverse here.
        target.selectable = true;
        target.evented = true;
        canvas.setActiveObject(target);
      }
      canvas.requestRenderAll();
    },
    [findLayerObject],
  );

  /** Read the live Fabric matrix off the transform target. Used by
   *  the TransformPanel to keep its numeric inputs in sync as the
   *  user drags handles on the canvas. Returns null when transform
   *  isn't active or the target was lost. */
  const getTransformMatrix = useCallback((): {
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
    skewX: number;
    skewY: number;
  } | null => {
    const initial = transformInitialRef.current;
    if (!initial) return null;
    const target = findLayerObject(initial.layerId);
    if (!target) return null;
    const sX = target.scaleX ?? 1;
    const sY = target.scaleY ?? 1;
    return {
      x: target.left ?? 0,
      y: target.top ?? 0,
      width: (target.width ?? 0) * sX,
      height: (target.height ?? 0) * sY,
      rotation: target.angle ?? 0,
      skewX: target.skewX ?? 0,
      skewY: target.skewY ?? 0,
    };
  }, [findLayerObject]);

  /** Write back numeric Transform-panel values to the Fabric
   *  matrix. Patch shape matches `getTransformMatrix` output;
   *  width/height map to scale factors via the layer's intrinsic
   *  width/height. */
  const setTransformMatrix = useCallback(
    (patch: {
      x?: number;
      y?: number;
      width?: number;
      height?: number;
      rotation?: number;
      skewX?: number;
      skewY?: number;
    }): void => {
      const canvas = canvasRef.current;
      const initial = transformInitialRef.current;
      if (!canvas || !initial) return;
      const target = findLayerObject(initial.layerId);
      if (!target) return;
      const intrinsicW = target.width ?? 1;
      const intrinsicH = target.height ?? 1;
      const next: Partial<fabric.FabricObjectProps> = {};
      if (patch.x !== undefined) next.left = patch.x;
      if (patch.y !== undefined) next.top = patch.y;
      if (patch.width !== undefined && intrinsicW > 0) {
        next.scaleX = patch.width / intrinsicW;
      }
      if (patch.height !== undefined && intrinsicH > 0) {
        next.scaleY = patch.height / intrinsicH;
      }
      if (patch.rotation !== undefined) next.angle = patch.rotation;
      if (patch.skewX !== undefined) next.skewX = patch.skewX;
      if (patch.skewY !== undefined) next.skewY = patch.skewY;
      target.set(next);
      target.setCoords();
      canvas.requestRenderAll();
    },
    [findLayerObject],
  );

  // === Non-affine Transform overlay (Phase 1b) =================
  // Distort, Perspective, and Warp deform pixels in ways canvas2d
  // can't render natively (projective for the first two, mesh for
  // the third). The shared rendering path: snapshot the layer at
  // session start, then on each handle drag re-rasterize a
  // downscaled preview into a canvas that becomes the layer's
  // element. Apply re-rasterizes at full source resolution and
  // swaps the element for the bake; Cancel restores the original
  // element via the `transformInitialRef` snapshot.

  const PREVIEW_LONGEST_PX = 768;

  /** Tear down the overlay (remove handles + outlines from canvas,
   *  clear the working ref). Doesn't touch the layer's element —
   *  the apply / cancel paths handle that. Idempotent. */
  const teardownNonAffineOverlay = useCallback((): void => {
    const canvas = canvasRef.current;
    const overlay = transformOverlayRef.current;
    if (!canvas) {
      transformOverlayRef.current = null;
      return;
    }
    // Always re-enable hit-test on teardown so subsequent tool
    // interactions work normally. Set first so even an early
    // return (no overlay ref) restores the canvas state.
    canvas.skipTargetFind = false;
    if (!overlay) {
      transformOverlayRef.current = null;
      return;
    }
    // Remove the preview overlay + handles + outlines from the
    // canvas. Restore the hidden layer's opacity so the user sees
    // it again (whether or not it was just baked over — bake calls
    // setElement before teardown, so the restored layer shows the
    // baked deformation if Apply ran, or the original pixels if
    // Cancel ran).
    for (const h of overlay.handles) canvas.remove(h);
    for (const o of overlay.outlines) canvas.remove(o);
    // No previewOverlay or opacity to restore — the target stays
    // visible throughout the session because the live preview
    // drives `target.setElement` + matrix directly. Cancel
    // restoration of the target's element + matrix is handled by
    // `exitTransformMode(false)`.
    //
    // Restore the saved tool ref + drawing-mode + selection flag
    // so the user's previous tool resumes normal behavior after
    // the session.
    activeToolRef.current = overlay.savedActiveTool;
    canvas.isDrawingMode = overlay.savedDrawingMode;
    canvas.selection = overlay.savedSelection;
    transformDragRef.current = null;
    transformOverlayRef.current = null;
    canvas.requestRenderAll();
  }, []);
  teardownNonAffineOverlayRef.current = teardownNonAffineOverlay;

  /** Re-render the preview from the current quad/grid. Called on
   *  every handle-drag tick. Reads handle positions live, computes
   *  the AABB of the deformed quad/grid (so the layer's bbox
   *  tracks the visible deformation instead of staying at the
   *  pre-transform original bbox — the latter produced the
   *  "image scales down with deformation" artifact where
   *  transparent margins inside the original bbox made the layer
   *  look faded), and rasterizes the deformation into the preview
   *  element in coords local to the new AABB. */
  const updateNonAffinePreview = useCallback((): void => {
    const overlay = transformOverlayRef.current;
    const canvas = canvasRef.current;
    const initial = transformInitialRef.current;
    if (!overlay || !canvas || !initial) return;
    const target = findLayerObject(initial.layerId);
    if (!target || !(target instanceof fabric.FabricImage)) return;

    // Live preview: rasterize at the downscaled-source density into
    // a fresh canvas and commit to the target. The Apply-time bake
    // calls the same helper with `sourceFull` and higher subdivision
    // counts. Replacing target.setElement + matrix in one shot is
    // what made the live preview work for non-bg layers (the earlier
    // separate-previewOverlay approach had unresolved Fabric refresh
    // issues for non-bg targets). Cancel restoration is handled by
    // `exitTransformMode(false)` which calls
    // `target.setElement(initial.originalElement)` + restores the
    // initial matrix.
    commitNonAffineDeformation(target, overlay.sourcePreview, overlay, {
      projective: 12,
      warp: 20,
    });

    // Rebuild outlines from current handle positions so the dashed
    // visual tracks the deformed quad/mesh.
    for (const o of overlay.outlines) canvas.remove(o);
    const newOutlines = buildNonAffineOutlines(
      overlay.mode,
      overlay.mode === 'warp' ? overlay.warpGrid : overlay.distortQuad,
    );
    for (const o of newOutlines) {
      canvas.add(o);
      canvas.bringObjectToFront(o);
    }
    // Handles must stay above outlines.
    for (const h of overlay.handles) canvas.bringObjectToFront(h);
    overlay.outlines = newOutlines;

    canvas.renderAll();
  }, [findLayerObject]);
  updateNonAffinePreviewRef.current = updateNonAffinePreview;

  /** Set up the on-canvas overlay for Distort / Perspective / Warp.
   *  Snapshots the layer's current pixel content into a full-res
   *  source canvas and a downscaled preview source; resets the
   *  layer's Fabric transform to identity-with-current-bbox so
   *  subsequent renders show the deformation; builds + adds the
   *  handle + outline Fabric objects. */
  const setupNonAffineOverlay = useCallback(
    (mode: 'distort' | 'perspective' | 'warp'): void => {
      const canvas = canvasRef.current;
      const initial = transformInitialRef.current;
      if (!canvas || !initial) return;
      const target = findLayerObject(initial.layerId);
      if (!target) return;

      // Idempotency guard: if the overlay is already set up for
      // this same mode AND the same layer, do nothing. Without
      // this, the EditorPage mode-mirror effect (which depends on
      // `canvas` identity) could re-fire during a handle drag —
      // any useCallback inside the giant useFabricCanvas memo
      // changing identity churns `canvas`, the effect re-runs,
      // applyTransformControls re-calls this function, which would
      // tear down + rebuild the handles at INITIAL positions and
      // "snap" the user's in-progress drag back. With the guard,
      // mode-switches still work (different mode → setup runs) but
      // spurious re-fires are no-op.
      if (transformOverlayRef.current?.mode === mode && transformOverlayRef.current?.bbox) {
        return;
      }

      // Switching modes tears down any prior overlay so handles
      // and the preview overlay don't stack.
      teardownNonAffineOverlay();

      // Snapshot the layer's current rendered state — captures any
      // affine matrix the user applied in Free/Skew. Non-affine
      // sessions compose on top of that.
      const objWithToCanvas = target as fabric.FabricObject & {
        toCanvasElement?: () => HTMLCanvasElement;
      };
      const sourceFull = objWithToCanvas.toCanvasElement ? objWithToCanvas.toCanvasElement() : null;
      if (!sourceFull) return;

      // Scene-space bbox of the layer at session start. Initial
      // handles seed at the corners of this bbox; the deformation
      // is unconstrained afterward.
      const br = target.getBoundingRect();
      const bbox = {
        left: br.left,
        top: br.top,
        width: br.width,
        height: br.height,
      };

      // Scene canvas dims — used here only to derive the preview
      // scale factor (per-drag rasterization runs at this density
      // for interactive performance; the bake on Apply uses the
      // full `sourceFull` density).
      const sceneWidth = imageDimsRef.current.width || canvas.getWidth();
      const sceneHeight = imageDimsRef.current.height || canvas.getHeight();
      const sceneLongest = Math.max(sceneWidth, sceneHeight);
      const previewScale =
        sceneLongest > PREVIEW_LONGEST_PX ? PREVIEW_LONGEST_PX / sceneLongest : 1;
      const pw = Math.max(1, Math.round(sceneWidth * previewScale));
      const ph = Math.max(1, Math.round(sceneHeight * previewScale));

      // Downscaled rasterization source — read by each drag tick.
      // The bake step reads `sourceFull` for final pixel density.
      const sourcePreview = document.createElement('canvas');
      sourcePreview.width = Math.max(
        1,
        Math.round(sourceFull.width * (pw / sceneWidth) * (sceneWidth / bbox.width)),
      );
      sourcePreview.height = Math.max(
        1,
        Math.round(sourceFull.height * (ph / sceneHeight) * (sceneHeight / bbox.height)),
      );
      const spCtx = sourcePreview.getContext('2d');
      if (spCtx) {
        spCtx.imageSmoothingEnabled = true;
        spCtx.imageSmoothingQuality = 'high';
        spCtx.drawImage(sourceFull, 0, 0, sourcePreview.width, sourcePreview.height);
      }

      // Initial deformation = bbox corners.
      const initialQuad: ProjectiveQuad = [
        { x: bbox.left, y: bbox.top },
        { x: bbox.left + bbox.width, y: bbox.top },
        { x: bbox.left + bbox.width, y: bbox.top + bbox.height },
        { x: bbox.left, y: bbox.top + bbox.height },
      ];
      const initialGrid = defaultWarpGridForBbox(bbox);

      // No separate preview overlay — `updateNonAffinePreview`
      // drives the target's element directly each tick (same
      // mechanism as `bakeNonAffineOverlay`, which has shipped
      // working). Cancel restoration is handled by
      // `exitTransformMode(false)` which `target.setElement`s the
      // initial element back and restores the initial matrix.

      // Build handles + outlines that float on top of the layer.
      const positions: { x: number; y: number }[] =
        mode === 'warp' ? [...initialGrid] : [...initialQuad];
      const handles = positions.map((p) => {
        const h = new fabric.Circle({
          left: p.x,
          top: p.y,
          radius: 7,
          fill: '#ffffff',
          stroke: '#0078d7',
          strokeWidth: 2,
          originX: 'center',
          originY: 'center',
          hasControls: false,
          hasBorders: false,
          selectable: false,
          evented: false,
          hoverCursor: 'move',
          objectCaching: false,
          excludeFromExport: true,
        });
        return h as unknown as fabric.FabricObject;
      });
      for (const h of handles) canvas.add(h);

      const outlines = buildNonAffineOutlines(mode, mode === 'warp' ? initialGrid : initialQuad);
      for (const o of outlines) canvas.add(o);

      // Z-order: outlines above target, handles above outlines.
      for (const o of outlines) canvas.bringObjectToFront(o);
      for (const h of handles) canvas.bringObjectToFront(h);

      // Suppress tool-specific mouse handlers for the duration of
      // the session by overriding activeToolRef to 'ai' (a tool
      // with no registered mouse handlers). Every brush / marquee
      // / retouch / fill / shape / text handler gates on
      // `activeToolRef.current === 'X'` and early-returns; 'ai'
      // doesn't match anything so they all early-return. Also
      // force isDrawingMode off so the Draw / Eraser brush won't
      // paint during a handle drag. Pre-session values are saved
      // and restored in teardown.
      const savedActiveTool = activeToolRef.current;
      const savedDrawingMode = canvas.isDrawingMode;
      const savedSelection = canvas.selection;
      activeToolRef.current = 'ai';
      canvas.isDrawingMode = false;
      // Force Fabric's group-selection rubber-band OFF. Otherwise
      // every handle-miss click falls through to Fabric's "draw a
      // selection rectangle from drag-start to cursor" path, which
      // renders a transient axis-aligned rect overlay during the
      // drag.
      canvas.selection = false;

      transformOverlayRef.current = {
        mode,
        savedActiveTool,
        savedDrawingMode,
        savedSelection,
        distortQuad: initialQuad,
        warpGrid: initialGrid,
        handles,
        outlines,
        sourceFull,
        sourcePreview,
        bbox,
      };

      // Disable Fabric's hit-test entirely for the duration of the
      // session — our custom mouse:down handler does the hit-test
      // against the handle positions directly. Without this,
      // clicks miss-near-handles can fall through to Fabric's
      // selection system and start a drag on whatever the
      // top-most evented object happens to be (the preview
      // overlay is non-evented, but other layers may be evented
      // depending on the active tool).
      canvas.skipTargetFind = true;
      // Cancel any in-progress canvas pan from the Move tool.
      // While the Move tool's mouse:* handlers are gated on
      // `!transformOverlayRef.current` for new pans, this clears
      // a pan that was mid-gesture when the user opened the
      // panel and clicked a mode.
      panRef.current.isPanning = false;

      updateNonAffinePreview();
    },
    [findLayerObject, teardownNonAffineOverlay, updateNonAffinePreview],
  );
  setupNonAffineOverlayRef.current = setupNonAffineOverlay;

  /** Bake the current non-affine deformation at full source
   *  resolution. Replaces the layer's element with the baked
   *  canvas; the Fabric transform stays at identity-with-bbox. */
  const bakeNonAffineOverlay = useCallback((): void => {
    const overlay = transformOverlayRef.current;
    const initial = transformInitialRef.current;
    if (!overlay || !initial) return;
    const target = findLayerObject(initial.layerId);
    if (!target || !(target instanceof fabric.FabricImage)) {
      teardownNonAffineOverlay();
      return;
    }
    // Bake at the deformed AABB sized to keep source pixel density.
    // Same helper used by the live preview tick — the bake just
    // hands in `sourceFull` (full-resolution captured layer pixels)
    // and higher subdivision counts so the final commit is
    // visually clean at any zoom level.
    commitNonAffineDeformation(target, overlay.sourceFull, overlay, {
      projective: 24,
      warp: 32,
    });

    // Sync the paint-surface cache with the new element. The bake
    // calls `target.setElement(dest)` with a fresh canvas — without
    // this update, `paintSurfacesRef[layerId].canvas` still points
    // at the PRE-warp canvas and subsequent brush / retouch / clone
    // strokes write to a hidden canvas that the FabricImage no
    // longer displays. Stale-cache symptom: tool cursor is correct,
    // no marks appear on screen, undo/redo does nothing visible.
    // The cache is only refreshed when an entry already exists for
    // this layer — otherwise we'd be creating ghost entries for
    // image layers that were never used as paint targets.
    const cached = paintSurfacesRef.current.get(initial.layerId);
    if (cached && cached.fabricImg === target) {
      const newElement = target.getElement();
      if (newElement instanceof HTMLCanvasElement) {
        paintSurfacesRef.current.set(initial.layerId, {
          canvas: newElement,
          fabricImg: target,
          offsetX: target.left ?? 0,
          offsetY: target.top ?? 0,
        });
      }
    }

    teardownNonAffineOverlay();
  }, [findLayerObject, teardownNonAffineOverlay]);
  bakeNonAffineOverlayRef.current = bakeNonAffineOverlay;

  /** Apply a Warp preset (Arc / Bulge / Twist / Wave) to the
   *  current overlay. Re-seeds the 9 grid points + updates the
   *  Fabric handle positions + re-renders the preview. No-op when
   *  Warp isn't the active mode or the overlay isn't set up. */
  const applyWarpPresetToOverlay = useCallback(
    (preset: WarpPreset, intensity: number = 1): void => {
      const overlay = transformOverlayRef.current;
      if (!overlay || overlay.mode !== 'warp') return;
      // Compute the new grid in BBOX-LOCAL coords, then offset to
      // scene-space (same as `defaultWarpGridForBbox`).
      const local = applyWarpPreset(overlay.bbox.width, overlay.bbox.height, preset, intensity);
      const scene = local.map((p) => ({
        x: p.x + overlay.bbox.left,
        y: p.y + overlay.bbox.top,
      })) as unknown as WarpGrid;
      overlay.warpGrid = scene;
      // Move the handle Fabric objects to track the new grid.
      for (let i = 0; i < 9; i++) {
        const h = overlay.handles[i];
        if (h) {
          h.set({ left: scene[i].x, top: scene[i].y });
          h.setCoords();
        }
      }
      updateNonAffinePreview();
    },
    [updateNonAffinePreview],
  );

  /** Exit Free Transform. On apply, the Fabric matrix stays as-is
   *  for affine modes (Free/Skew) — Fabric's native rendering
   *  already shows the transformed result, so "applying" is just
   *  leaving the matrix and dropping the active-object handles.
   *  Non-affine modes that baked pixels via a separate path are
   *  also no-op here (the bake already replaced the element).
   *
   *  On cancel, the snapshotted initial state is restored and any
   *  baked element is reverted. */
  const exitTransformMode = useCallback(
    (apply: boolean): void => {
      const canvas = canvasRef.current;
      const initial = transformInitialRef.current;
      if (!canvas || !initial) return;
      const target = findLayerObject(initial.layerId);
      // Non-affine overlay path: on Apply, bake the deformation at
      // full source resolution; on Cancel, just tear down (the
      // captured original element + matrix get restored below).
      if (transformOverlayRef.current) {
        if (apply) {
          bakeNonAffineOverlayRef.current?.();
        } else {
          teardownNonAffineOverlayRef.current?.();
        }
      }
      if (!apply && target) {
        // Restore the captured matrix BEFORE discardActiveObject —
        // Fabric reads transform props during the active-object
        // teardown for its bounding-rect cache. Order:
        //   1. set() the matrix props in one batch.
        //   2. setCoords() to refresh the bbox.
        //   3. dirty=true to invalidate any per-object cache
        //      (some FabricImage configs use objectCaching=true
        //      even when ours don't — defensive).
        //   4. setElement() for FabricImage to undo any non-affine
        //      bake that swapped the underlying element (1b path).
        //
        // origin{X,Y} and flip{X,Y} get restored too — Fabric's
        // `wrapWithFixedAnchor` flow temporarily swaps origin to
        // pin the opposite corner during a drag, and a corner-flip
        // (drag past the opposite side) toggles flipX/flipY.
        // Without restoring these the visual bbox is right but the
        // layer can land "inside out" relative to its pre-transform
        // state.
        target.set({
          scaleX: initial.scaleX,
          scaleY: initial.scaleY,
          left: initial.left,
          top: initial.top,
          angle: initial.angle,
          skewX: initial.skewX,
          skewY: initial.skewY,
          originX: initial.originX,
          originY: initial.originY,
          flipX: initial.flipX,
          flipY: initial.flipY,
        });
        target.setCoords();
        target.dirty = true;
        if (target instanceof fabric.FabricImage && initial.originalElement) {
          target.setElement(initial.originalElement);
        }
      }
      // Restore the snapshotted locks + controls map so the next
      // tool pickup sees the layer exactly as we found it. The
      // controls reference may be the prototype-shared default
      // (most layers) OR a per-instance override (e.g. bg image
      // with lockRotation) — either way `initial.controls` is the
      // right thing to put back.
      if (target) {
        // Reset every control's visibility to true. The previous
        // empty-object call was a no-op (setControlsVisibility
        // only mutates NAMED controls), so non-affine modes —
        // which hide all corners + mid-edges + rotator via
        // applyTransformControls — left the layer with no resize
        // handles after Apply / Cancel. Naming every default
        // control here ensures Move tool's standard 8-handle +
        // rotator UI is restored.
        target.setControlsVisibility({
          tl: true,
          tr: true,
          bl: true,
          br: true,
          ml: true,
          mr: true,
          mt: true,
          mb: true,
          mtr: true,
        });
        target.controls = initial.controls;
        target.lockRotation = initial.lockRotation;
        target.lockScalingFlip = initial.lockScalingFlip;
        target.lockSkewingX = initial.lockSkewingX;
        target.lockSkewingY = initial.lockSkewingY;
        // Restore the PRE-transform interaction flags. Layers
        // that came in selectable/evented (image, vector before
        // rasterize, etc.) get those flags back. Paint surfaces
        // (rasterized layers, bg-as-paint) came in non-selectable
        // / non-evented so brush handlers could intercept clicks
        // — they go back to that state so the next retouch /
        // brush stroke isn't swallowed by Move-tool selection.
        target.selectable = initial.selectable;
        target.evented = initial.evented;
      }
      // Restore every sibling's pre-lock selectable/evented
      // state. enterTransformMode locked them so the user
      // couldn't accidentally drag siblings while transforming;
      // since Free Transform doesn't change `activeTool`, the
      // EditorPage tool-sync effect doesn't re-fire on exit —
      // without this loop, siblings stay locked until the user
      // manually switches tools.
      for (const s of initial.siblings) {
        s.obj.set({ selectable: s.selectable, evented: s.evented });
      }
      canvas.discardActiveObject();
      transformInitialRef.current = null;
      // Sync renderAll() rather than requestRenderAll() — async
      // schedule was producing a brief window where React commits
      // (panel unmount) ran before the canvas redrew, leaving the
      // post-drag pixels on screen because the user perceives the
      // panel closing as "the operation completed" and stops
      // looking for further visual changes. Sync ensures the
      // revert is on screen the same tick we drop the panel.
      canvas.renderAll();
    },
    [findLayerObject],
  );

  /** Invert the current marquee — every pixel inside the selection
   *  becomes outside, and vice versa. Bounded by the image rect, so
   *  inverting an empty selection selects everything and inverting
   *  a full-image selection deselects.
   *
   *  Implementation: rasterize whatever shape currently exists into
   *  a binary mask, NOT the bits, install the result via the same
   *  wand-style polygon path. The output is always shape='wand'
   *  regardless of the input shape (the inverse of a rect is a
   *  frame, not a rect — closed-form per-shape inversion would be
   *  more state to carry without buying anything). */
  const invertMarquee = useCallback((): boolean => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;
    if (imgW <= 0 || imgH <= 0) return false;

    const m = marqueeRef.current;
    if (m.isDrawing) return false;

    // Rasterize the current selection into a mask the size of the
    // image, then NOT the bits. Empty selection → all-zeros → all-
    // ones (select everything).
    const mask = marqueeToBinaryMask(m, imgW, imgH);
    invertMaskInPlace(mask);
    if (!isMaskNonEmpty(mask)) {
      // Inverted selection covers nothing — that means the original
      // covered the whole image. Treat as deselect.
      clearMarquee();
      return true;
    }

    // Drop existing fabric polys + cached state.
    if (m.obj) canvas.remove(m.obj);
    for (const o of m.extraObjs) canvas.remove(o);
    m.obj = null;
    m.extraObjs = [];
    m.points = [];
    m.wandMaskCanvas = null;
    m.isDrawing = false;
    m.shape = 'wand';
    m.wandMaskCanvas = binaryMaskToCanvas(mask, imgW, imgH);

    const contours = traceContours(mask, imgW, imgH);
    const path = buildMarqueePath(contours);
    if (!path) {
      clearMarquee();
      return true;
    }
    tagAsMarquee(path);
    applyMarqueeColor(path, marqueeColorRef.current);
    applyMarqueeStroke(canvas, path);
    canvas.add(path);
    m.obj = path;
    m.extraObjs = [];

    canvas.renderAll();
    refreshMarqueeSelectionState();
    return true;
  }, [clearMarquee, refreshMarqueeSelectionState]);

  /**
   * Render the marqueed region of the active layer's paint surface to a
   * fresh HTMLCanvasElement sized to the marquee's bounding box. Used by
   * copySelection / cutSelection. Returns null if there's no finalized
   * marquee, no active layer, or the active layer doesn't yet have a
   * paint surface.
   */
  const renderMarqueeRegion = useCallback((): {
    canvas: HTMLCanvasElement;
    left: number;
    top: number;
  } | null => {
    const m = marqueeRef.current;
    const layerId = activeLayerIdRef.current;
    if (!m.obj || m.isDrawing || !layerId) return null;
    const surface = paintSurfacesRef.current.get(layerId);
    if (!surface) return null;

    // Compute the bbox via the pure helper (intrinsic properties, not the
    // stale `getBoundingRect()` cache). See the helper's doc comment for
    // why and the unit tests in `lib/marquee-bounds.test.ts`.
    const bounds = (() => {
      if (m.shape === 'rect') {
        const r = m.obj as fabric.Rect;
        return computeMarqueeBounds({
          shape: 'rect',
          left: r.left ?? 0,
          top: r.top ?? 0,
          width: r.width ?? 0,
          height: r.height ?? 0,
        });
      }
      if (m.shape === 'ellipse') {
        const e = m.obj as fabric.Ellipse;
        return computeMarqueeBounds({
          shape: 'ellipse',
          left: e.left ?? 0,
          top: e.top ?? 0,
          rx: e.rx ?? 0,
          ry: e.ry ?? 0,
        });
      }
      if (m.shape === 'lasso' || m.shape === 'polygon') {
        return computeMarqueeBounds({ shape: m.shape, points: m.points });
      }
      if (m.shape === 'wand' && m.obj) {
        // Wand selections are now rendered as a single fabric.Path
        // with evenodd fill (see `buildMarqueePath`). The Path's
        // own bbox spans every contour in the mask, which is the
        // bounding rect we want for copy/cut/render. The actual
        // per-pixel masking happens below in step 2 via the cached
        // `m.wandMaskCanvas`.
        return {
          left: m.obj.left ?? 0,
          top: m.obj.top ?? 0,
          width: (m.obj.width ?? 0) * (m.obj.scaleX ?? 1),
          height: (m.obj.height ?? 0) * (m.obj.scaleY ?? 1),
        };
      }
      return null;
    })();
    if (!bounds) return null;
    const w = Math.round(bounds.width);
    const h = Math.round(bounds.height);
    if (w <= 0 || h <= 0) return null;

    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const ctx = out.getContext('2d');
    if (!ctx) return null;

    // Step 1: Copy the bbox region from the source surface into `out` using
    // the explicit 9-arg drawImage form. surface.canvas pixel (X,Y) =
    // scene (X + surfaceLeft, Y + surfaceTop), so the source rect in surface
    // pixels is (bounds.left - surfaceLeft, bounds.top - surfaceTop).
    const surfaceLeft = surface.fabricImg.left ?? 0;
    const surfaceTop = surface.fabricImg.top ?? 0;
    const sx = Math.round(bounds.left - surfaceLeft);
    const sy = Math.round(bounds.top - surfaceTop);
    ctx.drawImage(surface.canvas, sx, sy, w, h, 0, 0, w, h);

    // Step 2: For non-rect marquees, mask out pixels outside the selection
    // shape. We render the marquee shape into a separate mask canvas in
    // out-local coords and apply it via destination-in compositing.
    if (m.shape !== 'rect') {
      const mask = document.createElement('canvas');
      mask.width = w;
      mask.height = h;
      const mCtx = mask.getContext('2d');
      if (mCtx) {
        mCtx.fillStyle = '#ffffff';
        if (m.shape === 'ellipse') {
          const e = m.obj as fabric.Ellipse;
          const rx = e.rx ?? 0;
          const ry = e.ry ?? 0;
          const cx = (e.left ?? 0) + rx - bounds.left;
          const cy = (e.top ?? 0) + ry - bounds.top;
          mCtx.beginPath();
          mCtx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
          mCtx.fill();
        } else if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 3) {
          mCtx.beginPath();
          mCtx.moveTo(m.points[0].x - bounds.left, m.points[0].y - bounds.top);
          for (let i = 1; i < m.points.length; i++) {
            mCtx.lineTo(m.points[i].x - bounds.left, m.points[i].y - bounds.top);
          }
          mCtx.closePath();
          mCtx.fill();
        } else if (m.shape === 'wand' && m.wandMaskCanvas) {
          // Crop the wand mask (full bg-sized) into the bbox region.
          mCtx.drawImage(
            m.wandMaskCanvas,
            Math.round(bounds.left),
            Math.round(bounds.top),
            w,
            h,
            0,
            0,
            w,
            h,
          );
        }
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(mask, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
      }
    }

    return { canvas: out, left: bounds.left, top: bounds.top };
  }, []);

  /** Copy the marqueed region of the active layer to the clipboard. Returns
   *  true on success. */
  const copySelection = useCallback((): boolean => {
    const result = renderMarqueeRegion();
    if (!result) return false;
    clipboardRef.current = result;
    setHasClipboard(true);
    return true;
  }, [renderMarqueeRegion]);

  /** Copy + clear: same as copySelection, then clip-clear the marqueed
   *  pixels from the active layer's paint surface. Returns true on success. */
  const cutSelection = useCallback((): boolean => {
    const result = renderMarqueeRegion();
    if (!result) return false;
    clipboardRef.current = result;
    setHasClipboard(true);

    const canvas = canvasRef.current;
    const m = marqueeRef.current;
    const layerId = activeLayerIdRef.current;
    if (!canvas || !layerId) return true;
    const surface = paintSurfacesRef.current.get(layerId);
    if (!surface) return true;
    const ctx = surface.canvas.getContext('2d');
    if (!ctx) return true;

    const offsetX = -(surface.fabricImg.left ?? 0);
    const offsetY = -(surface.fabricImg.top ?? 0);
    ctx.save();
    ctx.translate(offsetX, offsetY);
    if (m.shape === 'wand' && m.wandMaskCanvas) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.drawImage(m.wandMaskCanvas, 0, 0);
    } else {
      const clipped = applyMarqueeClipToContext(ctx, m);
      // The clearRect bounds are over-broad on purpose: after the
      // translate, this rect covers a shifted region that's NOT exactly
      // the surface's pixel bounds, but the active clip narrows the
      // actual cleared area to just the marquee shape. We just need any
      // rect that fully encloses the clip path.
      if (clipped) ctx.clearRect(0, 0, surface.canvas.width, surface.canvas.height);
    }
    ctx.restore();

    surface.fabricImg.setElement(surface.canvas);
    surface.fabricImg.dirty = true;
    surface.fabricImg.setCoords();
    canvas.renderAll();
    return true;
  }, [renderMarqueeRegion]);

  /** Read the clipboard payload — caller (EditorPage) is expected to
   *  create a new layer and place a fabric.FabricImage there. Returns
   *  null when the clipboard is empty. */
  const getClipboard = useCallback(() => clipboardRef.current, []);

  /** Bounding rect (scene coords) of the currently-finalized marquee, or
   *  null when there's no marquee in progress. Combines the primary
   *  marquee object with any extras so polygon / lasso multi-segment
   *  selections come back as a single rect. Used by Smart Create to know
   *  where to drop the generated image. */
  const getMarqueeBounds = useCallback(() => {
    const m = marqueeRef.current;
    if (!m.obj || m.isDrawing) return null;
    // Read intrinsic dimensions per shape type directly. Going through
    // `getBoundingRect()` was returning stroke-dominated values (the
    // marquee uses a `strokeUniform: true` border, and for shapes
    // where the intrinsic width somehow stayed near zero, the bbox
    // collapsed to roughly the stroke width — a 1.12px frame around
    // a 0×0 rect, not the user's actual selection). Reading the
    // stored geometry directly bypasses any stroke / transform
    // contamination.
    let bounds: { left: number; top: number; width: number; height: number } | null = null;
    if (m.shape === 'rect') {
      const r = m.obj as fabric.Rect;
      bounds = {
        left: r.left ?? 0,
        top: r.top ?? 0,
        width: (r.width ?? 0) * (r.scaleX ?? 1),
        height: (r.height ?? 0) * (r.scaleY ?? 1),
      };
    } else if (m.shape === 'ellipse') {
      const e = m.obj as fabric.Ellipse;
      const rx = (e.rx ?? 0) * (e.scaleX ?? 1);
      const ry = (e.ry ?? 0) * (e.scaleY ?? 1);
      bounds = {
        left: e.left ?? 0,
        top: e.top ?? 0,
        width: rx * 2,
        height: ry * 2,
      };
    } else if (m.shape === 'wand') {
      // Wand has no `m.points` (the shape lives in `m.wandMaskCanvas`
      // and the rendered Fabric Polygons). Union the polygon objects'
      // bboxes — each polygon's left/top/width/height is its
      // contour's bounding box in scene coords (originX/Y: left/top).
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const o of [m.obj, ...m.extraObjs]) {
        if (!o) continue;
        const left = o.left ?? 0;
        const top = o.top ?? 0;
        const w = (o.width ?? 0) * (o.scaleX ?? 1);
        const h = (o.height ?? 0) * (o.scaleY ?? 1);
        if (left < minX) minX = left;
        if (top < minY) minY = top;
        if (left + w > maxX) maxX = left + w;
        if (top + h > maxY) maxY = top + h;
      }
      if (Number.isFinite(minX) && Number.isFinite(minY)) {
        bounds = { left: minX, top: minY, width: maxX - minX, height: maxY - minY };
      }
    } else if (m.points.length >= 2) {
      // Polygon / lasso contour — derive from the stored points
      // directly (they're authored in scene coords).
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const p of m.points) {
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
      }
      if (Number.isFinite(minX) && Number.isFinite(minY)) {
        bounds = { left: minX, top: minY, width: maxX - minX, height: maxY - minY };
      }
    }
    if (!bounds || bounds.width <= 0 || bounds.height <= 0) return null;
    return bounds;
  }, []);

  /**
   * Run an inpaint pipeline (e.g. MI-GAN) over the active layer's marqueed
   * region and bake the result back onto the layer's paint surface.
   *
   *   • The injected `inpaint` function does the actual model inference —
   *     so the hook stays free of any onnxruntime / model-specific code.
   *     `lib/smart-delete.ts` ships the MI-GAN runtime; the AI tool's
   *     handler in EditorPage glues the two.
   *   • The result is composited only inside the marquee mask, so non-
   *     selected pixels stay byte-identical (avoids resize artifacts on
   *     pixels the user didn't ask us to touch).
   *   • Returns `true` on success, `false` when there's no active layer,
   *     no marquee, or the active layer doesn't have a paint surface yet.
   */
  const runSmartDeleteOnActiveLayer = useCallback(
    async (
      inpaint: (
        source: HTMLCanvasElement,
        mask: HTMLCanvasElement,
        /** Tight bounding rect of the masked region in mask-canvas
         *  coords (already clamped to mask bounds). Computed from
         *  shape geometry — no pixel scan — so callers can crop
         *  source / mask without an O(W·H) walk. */
        maskBbox: { left: number; top: number; width: number; height: number },
      ) => Promise<HTMLCanvasElement>,
    ): Promise<boolean> => {
      const m = marqueeRef.current;
      if (!m.obj || m.isDrawing) return false;
      const layerId = activeLayerIdRef.current;
      if (!layerId) return false;
      const surface = paintSurfacesRef.current.get(layerId);
      if (!surface) return false;
      const canvas = canvasRef.current;
      if (!canvas) return false;

      const w = surface.canvas.width;
      const h = surface.canvas.height;
      if (w <= 0 || h <= 0) return false;

      // Mask matches the surface's pixel grid. Marquee geometry is in
      // scene coords; for a paint surface positioned at scene (left, top)
      // we shift by -(left, top) so the mask lands on the right pixels.
      const mask = document.createElement('canvas');
      mask.width = w;
      mask.height = h;
      const mCtx = mask.getContext('2d');
      if (!mCtx) return false;
      const surfLeft = surface.fabricImg.left ?? 0;
      const surfTop = surface.fabricImg.top ?? 0;
      mCtx.save();
      mCtx.translate(-surfLeft, -surfTop);
      mCtx.fillStyle = '#ffffff';
      if (m.shape === 'wand' && m.wandMaskCanvas) {
        // Wand: wandMaskCanvas is already a binary alpha mask in scene
        // coords. Translate is already applied to the ctx.
        mCtx.drawImage(m.wandMaskCanvas, 0, 0);
      } else if (m.shape === 'rect') {
        const r = m.obj as fabric.Rect;
        mCtx.fillRect(r.left ?? 0, r.top ?? 0, r.width ?? 0, r.height ?? 0);
      } else if (m.shape === 'ellipse') {
        const e = m.obj as fabric.Ellipse;
        const rx = e.rx ?? 0;
        const ry = e.ry ?? 0;
        const cx = (e.left ?? 0) + rx;
        const cy = (e.top ?? 0) + ry;
        mCtx.beginPath();
        mCtx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        mCtx.fill();
      } else if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 3) {
        mCtx.beginPath();
        mCtx.moveTo(m.points[0].x, m.points[0].y);
        for (let i = 1; i < m.points.length; i++) {
          mCtx.lineTo(m.points[i].x, m.points[i].y);
        }
        mCtx.closePath();
        mCtx.fill();
      } else {
        mCtx.restore();
        return false;
      }
      mCtx.restore();

      // Source: a fresh copy of the surface so the model has full image
      // context (inpainting works better when the model can sample from
      // outside the marquee, not just within it).
      const source = document.createElement('canvas');
      source.width = w;
      source.height = h;
      const srcCtx = source.getContext('2d');
      if (!srcCtx) return false;
      srcCtx.drawImage(surface.canvas, 0, 0);

      // Tight bbox of the masked region, in mask-canvas coords, derived
      // from the marquee's stored shape geometry — no pixel scan. The
      // mask was filled with `mCtx.translate(-surfLeft, -surfTop)` so
      // we apply the same shift here, then clamp to mask bounds.
      let sceneLeft = 0;
      let sceneTop = 0;
      let sceneRight = w;
      let sceneBottom = h;
      if (m.shape === 'rect') {
        const r = m.obj as fabric.Rect;
        sceneLeft = r.left ?? 0;
        sceneTop = r.top ?? 0;
        sceneRight = sceneLeft + (r.width ?? 0);
        sceneBottom = sceneTop + (r.height ?? 0);
      } else if (m.shape === 'ellipse') {
        const e = m.obj as fabric.Ellipse;
        const rx = e.rx ?? 0;
        const ry = e.ry ?? 0;
        sceneLeft = e.left ?? 0;
        sceneTop = e.top ?? 0;
        sceneRight = sceneLeft + rx * 2;
        sceneBottom = sceneTop + ry * 2;
      } else if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 3) {
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (const p of m.points) {
          if (p.x < minX) minX = p.x;
          if (p.y < minY) minY = p.y;
          if (p.x > maxX) maxX = p.x;
          if (p.y > maxY) maxY = p.y;
        }
        sceneLeft = minX;
        sceneTop = minY;
        sceneRight = maxX;
        sceneBottom = maxY;
      }
      // Wand has no shape geometry to derive bbox from — its bounds
      // are the flood-filled pixels of `wandMaskCanvas`. Falling back
      // to the full mask is correct (the inpaint callback will still
      // get a valid rect, just larger than necessary). Accept the
      // looser crop for the rare wand+Blend case rather than scanning
      // a full-resolution alpha buffer.
      const maskBboxLeft = Math.max(0, Math.floor(sceneLeft - surfLeft));
      const maskBboxTop = Math.max(0, Math.floor(sceneTop - surfTop));
      const maskBboxRight = Math.min(w, Math.ceil(sceneRight - surfLeft));
      const maskBboxBottom = Math.min(h, Math.ceil(sceneBottom - surfTop));
      const maskBbox = {
        left: maskBboxLeft,
        top: maskBboxTop,
        width: Math.max(1, maskBboxRight - maskBboxLeft),
        height: Math.max(1, maskBboxBottom - maskBboxTop),
      };

      const result = await inpaint(source, mask, maskBbox);

      // Composite result onto surface, restricted to the mask region.
      const masked = document.createElement('canvas');
      masked.width = w;
      masked.height = h;
      const maskedCtx = masked.getContext('2d');
      if (!maskedCtx) return false;
      maskedCtx.drawImage(result, 0, 0, w, h);
      maskedCtx.globalCompositeOperation = 'destination-in';
      maskedCtx.drawImage(mask, 0, 0);

      const ctx = surface.canvas.getContext('2d');
      if (!ctx) return false;
      ctx.drawImage(masked, 0, 0);

      surface.fabricImg.setElement(surface.canvas);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.renderAll();
      onObjectModifiedRef.current?.();
      return true;
    },
    [],
  );

  /** Replace the active layer's full image with a transformed copy. The
   *  caller's `transform` receives a fresh copy of the surface canvas
   *  and returns a new canvas (typically from an AI service that
   *  rewrites the whole image, not a mask-localized edit). The return
   *  value is drawn back onto the surface, scaled to fit the surface's
   *  original dimensions — so the layer's place on the Fabric canvas,
   *  bounding box, and z-order are all preserved.
   *
   *  Used by Search & Replace, where Stability auto-segments the
   *  search target and returns the entire image with the replacement
   *  composited. Smart Delete + Inpaint use the mask-aware
   *  `runSmartDeleteOnActiveLayer` instead. */
  const runOnActiveLayerFullImage = useCallback(
    async (
      transform: (source: HTMLCanvasElement) => Promise<HTMLCanvasElement>,
    ): Promise<boolean> => {
      const layerId = activeLayerIdRef.current;
      if (!layerId) return false;
      const canvas = canvasRef.current;
      if (!canvas) return false;

      // Lazy-register a paint surface when one doesn't exist yet
      // for this layer. Image layers added via `addImageLayer` and
      // the background-before-first-paint have a FabricImage but
      // no `paintSurfacesRef` entry — brush handlers create the
      // entry on first stroke, but pixel-level bake ops (Liquify
      // Apply, future filters) need to operate without requiring
      // a precursor stroke. The helper mirrors the fallback in
      // `getActiveAdjustmentTargets` so the read-side and write-
      // side stay symmetric.
      const candidates: Array<{ id: string; image: fabric.FabricImage }> = [];
      for (const obj of canvas.getObjects()) {
        if (!(obj instanceof fabric.FabricImage)) continue;
        const id = getObjectLayerId(obj);
        if (!id) continue;
        candidates.push({ id, image: obj });
      }
      const surface: PaintSurface<fabric.FabricImage> | null = resolveOrRegisterPaintSurface(
        layerId,
        candidates,
        paintSurfacesRef.current,
      );
      if (!surface) return false;

      const w = surface.canvas.width;
      const h = surface.canvas.height;
      if (w <= 0 || h <= 0) return false;

      // Hand the transform a fresh canvas so it can't accidentally
      // mutate the live surface mid-flight.
      const source = document.createElement('canvas');
      source.width = w;
      source.height = h;
      const srcCtx = source.getContext('2d');
      if (!srcCtx) return false;
      srcCtx.drawImage(surface.canvas, 0, 0);

      const result = await transform(source);

      // Replace surface pixels in place, scaling the result to the
      // surface's native size. Clearing first prevents partial-alpha
      // result pixels from blending with stale surface content.
      const ctx = surface.canvas.getContext('2d');
      if (!ctx) return false;
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(result, 0, 0, result.width, result.height, 0, 0, w, h);

      surface.fabricImg.setElement(surface.canvas);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      canvas.renderAll();
      onObjectModifiedRef.current?.();
      return true;
    },
    [],
  );

  /** Live paint targets for an in-place pixel adjustment (Curves,
   *  Levels, etc.). Returns one entry per rasterizable layer the
   *  user is editing. Each entry exposes the live backing canvas
   *  (NOT a copy) so the caller can read+write pixels in place,
   *  plus the wrapping FabricImage so the caller can mark it
   *  `dirty` after writing.
   *
   *  Single layer → array of length 1. Group → array of one entry
   *  per rasterizable descendant (front-most first). Empty array
   *  when nothing in the active selection is rasterizable.
   *
   *  Per-layer resolution order:
   *    1. `paintSurfacesRef` — covers any layer that's been painted
   *       on (brush/eraser/retouch register entries here via
   *       `ensurePaintSurface`), and the background image once any
   *       paint has touched it.
   *    2. Direct FabricImage lookup by layerId. Catches image layers
   *       added via `addImageLayer` and the background before any
   *       paint stroke has triggered surface registration. Converts
   *       the element to a canvas (via `ensureCanvasElement`) and
   *       updates the FabricImage in place so subsequent reads stay
   *       coherent. */
  const getActiveAdjustmentTargets = useCallback(
    (doc: EditorDocument): Array<{ canvas: HTMLCanvasElement; fabricObj: fabric.FabricObject }> => {
      const fc = canvasRef.current;
      if (!fc) return [];
      const layerId = activeLayerIdRef.current;
      if (!layerId) return [];
      const node = doc.layers[layerId];
      if (!node) return [];

      const targets: Array<{ canvas: HTMLCanvasElement; fabricObj: fabric.FabricObject }> = [];
      const seen = new Set<string>();

      const tryAddLayer = (id: string) => {
        if (seen.has(id)) return;
        seen.add(id);
        const surface = paintSurfacesRef.current.get(id);
        if (surface && surface.canvas.width > 0 && surface.canvas.height > 0) {
          targets.push({ canvas: surface.canvas, fabricObj: surface.fabricImg });
          return;
        }
        // Direct FabricImage lookup.
        for (const obj of fc.getObjects()) {
          if (getObjectLayerId(obj) !== id) continue;
          if (!(obj instanceof fabric.FabricImage)) continue;
          const w = obj.width ?? 0;
          const h = obj.height ?? 0;
          if (w <= 0 || h <= 0) continue;
          const surfaceCanvas = ensureCanvasElement(
            obj.getElement() as HTMLImageElement | HTMLCanvasElement,
            w,
            h,
          );
          if (obj.getElement() !== surfaceCanvas) obj.setElement(surfaceCanvas);
          targets.push({ canvas: surfaceCanvas, fabricObj: obj });
          return;
        }
      };

      // Group: walk descendants depth-first, collect each rasterizable
      // child's target. `traverseDescendants` yields the group itself
      // first; skip non-rasterizable nodes (the group itself, nested
      // groups, text/shape/adjustment) — only image/paint can resolve.
      if (node.type === 'group') {
        for (const descendant of traverseDescendants(doc, layerId)) {
          if (descendant.type === 'image' || descendant.type === 'paint') {
            tryAddLayer(descendant.id);
          }
        }
        return targets;
      }

      // Single non-group layer. Only image/paint produce a target;
      // text/shape/adjustment/empty have no rasterizable surface.
      // Must stay aligned with `hasRasterizableTarget` in
      // `lib/layer-tree.ts` — that helper drives the Adjust
      // button's disabled state.
      if (node.type === 'image' || node.type === 'paint') {
        tryAddLayer(layerId);
      }
      return targets;
    },
    [],
  );

  /** Paint a single leaf layer's pixels onto an external target
   *  canvas at the layer's current transform. Used by the slow-path
   *  compositor in `render-pipeline.ts` to build the running
   *  composite below each adjustment layer. No-op when the layer
   *  has no FabricImage (e.g., text/shape — they render through
   *  Fabric in their normal z-order and aren't composited by the
   *  slow path in Phase 2).
   *
   *  Uses `fabricObj.render(ctx)` so the layer's full transform
   *  (position, scale, rotation, opacity) is applied. Skips
   *  invisible objects. */
  const paintLeafToCanvas = useCallback((target: HTMLCanvasElement, layerId: string): void => {
    const fc = canvasRef.current;
    if (!fc) return;
    const ctx = target.getContext('2d');
    if (!ctx) return;
    for (const obj of fc.getObjects()) {
      if (getObjectLayerId(obj) !== layerId) continue;
      if (!obj.visible) continue;
      // Fabric's `render` applies the object's full transform to
      // the target context. Save/restore so we don't leak the
      // object's matrix to subsequent paintLeaf calls.
      ctx.save();
      obj.render(ctx);
      ctx.restore();
    }
  }, []);

  /** Look up the backing canvas an adjustment layer's display
   *  FabricImage wraps. The slow-path compositor writes the
   *  post-adjustment pixels here; the caller marks the wrapping
   *  Fabric object dirty + `requestRenderAll` after the compositor
   *  returns.
   *
   *  Returns null when the layer isn't an adjustment, when no
   *  Fabric object exists yet for the layer, or when the Fabric
   *  object isn't wrapping a canvas element (shouldn't happen — the
   *  adjustment-create path in `EditorPage` allocates a canvas
   *  upfront). */
  const getAdjustmentBackingCanvas = useCallback((layerId: string): HTMLCanvasElement | null => {
    const fc = canvasRef.current;
    if (!fc) return null;
    for (const obj of fc.getObjects()) {
      if (getObjectLayerId(obj) !== layerId) continue;
      if (!(obj instanceof fabric.FabricImage)) continue;
      const el = obj.getElement();
      if (el instanceof HTMLCanvasElement) return el;
      return null;
    }
    return null;
  }, []);

  /** Non-mutating snapshot of the active layer for tools that produce a
   *  *new* layer rather than replacing the source pixels in place
   *  (e.g. Image to Image's "add as new layer" mode). Returns the
   *  layer's surface as a fresh canvas plus its scene-space bounds —
   *  width / height in scene units (i.e. already multiplied by the
   *  fabric image's scale) so callers can pass them straight into
   *  `addImageLayer`'s `fitTo`. The new image then lands exactly on
   *  top of the original, ready to move/hide/delete.
   *
   *  Returns null if there's no active layer or no surface for it. */
  /** Return a fresh CANVAS of the best sampling source for color-
   *  range / per-pixel selection ops, sized to the image rect.
   *  Prefers the active layer when it's an image; falls back to
   *  the background image. Returns null when no image is loaded.
   *
   *  IMPORTANT: returns a canvas rather than ImageData. For a 4K
   *  source ImageData is ~32MB and a 8K source is ~192MB; reading
   *  + passing that around forces expensive `putImageData` writes
   *  every time the consumer wants to render it. Consumers should
   *  use `drawImage` (GPU-accelerated) for any rendering and only
   *  call `getImageData` lazily at the points that genuinely need
   *  pixel data (sample-pick = 1×1; Apply = one-time full read).
   *
   *  Tier 1 Group D — Color Range and any future per-pixel
   *  selection tool funnel through here. */
  const getSourceCanvasForSampling = useCallback((): HTMLCanvasElement | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const imgW = imageDimsRef.current.width;
    const imgH = imageDimsRef.current.height;
    if (imgW <= 0 || imgH <= 0) return null;

    // Prefer the active image layer; otherwise the background.
    const bg = getBackgroundImage();
    const activeLayerId = activeLayerIdRef.current;
    let targetImg: fabric.FabricImage | null = null;
    if (activeLayerId) {
      for (const o of canvas.getObjects()) {
        if (getObjectLayerId(o) === activeLayerId && o instanceof fabric.FabricImage) {
          targetImg = o;
          break;
        }
      }
    }
    if (!targetImg) targetImg = bg;
    if (!targetImg) return null;

    const imgEl = targetImg.getElement() as HTMLImageElement | HTMLCanvasElement;
    const tmp = document.createElement('canvas');
    tmp.width = imgW;
    tmp.height = imgH;
    // `willReadFrequently: false` here — the typical consumer is
    // drawImage (GPU), not getImageData. Only the Apply path needs
    // pixel data and it allocates its own willReadFrequently canvas.
    const ctx = tmp.getContext('2d');
    if (!ctx) return null;
    // Anchor the source at its scene-space origin so sampling
    // coords line up with the canvas the user sees.
    const left = targetImg.left ?? 0;
    const top = targetImg.top ?? 0;
    const sX = targetImg.scaleX ?? 1;
    const sY = targetImg.scaleY ?? 1;
    const drawW = (targetImg.width ?? imgW) * sX;
    const drawH = (targetImg.height ?? imgH) * sY;
    ctx.drawImage(imgEl, left, top, drawW, drawH);
    return tmp;
  }, [getBackgroundImage]);

  const getActiveLayerSnapshot = useCallback((): {
    source: HTMLCanvasElement;
    bounds: { left: number; top: number; width: number; height: number };
  } | null => {
    const layerId = activeLayerIdRef.current;
    if (!layerId) return null;
    const surface = paintSurfacesRef.current.get(layerId);
    if (!surface) return null;
    const w = surface.canvas.width;
    const h = surface.canvas.height;
    if (w <= 0 || h <= 0) return null;

    const source = document.createElement('canvas');
    source.width = w;
    source.height = h;
    const ctx = source.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(surface.canvas, 0, 0);

    const fab = surface.fabricImg;
    const scaleX = fab.scaleX ?? 1;
    const scaleY = fab.scaleY ?? 1;
    return {
      source,
      bounds: {
        left: fab.left ?? 0,
        top: fab.top ?? 0,
        width: w * scaleX,
        height: h * scaleY,
      },
    };
  }, []);

  /** Upscale the active layer in place — replaces the layer's
   *  FabricImage with one wrapping the transform's result canvas at
   *  scale 1, so the displayed image actually grows. If the active
   *  layer is the background, the editor canvas dimensions also
   *  grow to match the result and the viewport re-fits. For non-bg
   *  layers, the layer overflows the canvas visually until the user
   *  zooms out — same behavior Photoshop gives you when you scale a
   *  layer up beyond the document.
   *
   *  Mirrors `runOutpaintOnBackground`'s replace-the-FabricImage
   *  pattern but without any object shifting (upscale anchors the
   *  layer's top-left in scene space and grows from there). */
  const runUpscaleOnActiveLayer = useCallback(
    async (
      transform: (source: HTMLCanvasElement) => Promise<HTMLCanvasElement>,
    ): Promise<boolean> => {
      const layerId = activeLayerIdRef.current;
      if (!layerId) return false;
      const surface = paintSurfacesRef.current.get(layerId);
      if (!surface) return false;
      const canvas = canvasRef.current;
      if (!canvas) return false;

      const oldFab = surface.fabricImg;
      const oldW = surface.canvas.width;
      const oldH = surface.canvas.height;
      if (oldW <= 0 || oldH <= 0) return false;

      // Hand the transform a fresh canvas so it can't race with stroke
      // writes in flight on the live surface.
      const source = document.createElement('canvas');
      source.width = oldW;
      source.height = oldH;
      const sCtx = source.getContext('2d');
      if (!sCtx) return false;
      sCtx.drawImage(surface.canvas, 0, 0);

      const result = await transform(source);
      const newW = result.width;
      const newH = result.height;
      if (newW <= 0 || newH <= 0) return false;

      // Normalize onto a fresh canvas so downstream code (paint
      // surface, fabricImg.width) sees stable inputs.
      const offscreen = document.createElement('canvas');
      offscreen.width = newW;
      offscreen.height = newH;
      const ctx = offscreen.getContext('2d');
      if (!ctx) return false;
      ctx.drawImage(result, 0, 0);

      const isBg = isBackgroundImage(oldFab);

      // Build the replacement FabricImage. scaleX/Y stays at 1 so the
      // larger pixel grid translates to a larger displayed image.
      // Position pins to the original's left/top + origin so the layer
      // anchors at the same scene point.
      const newImg = new fabric.FabricImage(offscreen, {
        left: oldFab.left ?? 0,
        top: oldFab.top ?? 0,
        originX: oldFab.originX ?? 'left',
        originY: oldFab.originY ?? 'top',
        scaleX: 1,
        scaleY: 1,
        selectable: oldFab.selectable,
        evented: oldFab.evented,
        angle: oldFab.angle ?? 0,
        flipX: oldFab.flipX ?? false,
        flipY: oldFab.flipY ?? false,
      });
      if (isBg) {
        tagAsBackground(newImg);
        (newImg as fabric.FabricImage & { erasable: boolean }).erasable = false;
      }
      newImg.set({ objectCaching: false } as Partial<fabric.FabricImage>);
      newImg.filters = oldFab.filters ? [...oldFab.filters] : [];
      newImg.applyFilters();

      setObjectLayerId(newImg, layerId);
      paintSurfacesRef.current.set(layerId, {
        canvas: offscreen,
        fabricImg: newImg,
        offsetX: newImg.left ?? 0,
        offsetY: newImg.top ?? 0,
      });

      canvas.remove(oldFab);
      canvas.add(newImg);
      if (isBg) {
        canvas.sendObjectToBack(newImg);
        backgroundImageRef.current = newImg;
        // Grow the canvas/document dims so the upscaled bg has room.
        // Without this the new larger pixel grid extends past the
        // editor canvas edges and the user sees the dark editor
        // background bleeding through (the "black border" symptom).
        imageDimsRef.current = { width: newW, height: newH };
        onCanvasDimensions?.(newW, newH);
      }

      zoomToFit();
      canvas.renderAll();
      onObjectModifiedRef.current?.();
      return true;
    },
    [onCanvasDimensions, zoomToFit],
  );

  /** Outpaint the background image by extending its bounds in any
   *  combination of directions. The transform receives the current
   *  background surface and the requested edge extensions, and is
   *  expected to return a new canvas at the extended dimensions
   *  (oldW + left + right, oldH + up + down) with the original content
   *  composited at offset (left, up).
   *
   *  After the transform, the helper:
   *    • replaces the background FabricImage with a new one wrapping
   *      the returned canvas (mirroring `applyActiveCrop`'s pattern)
   *    • shifts every non-background object by (+left, +up) so the
   *      original content stays visually anchored
   *    • updates `imageDimsRef` and notifies the parent via
   *      `onCanvasDimensions`
   *    • re-zooms to fit the new image
   *
   *  Returns false if there is no background to outpaint or the
   *  extensions are all zero. */
  const runOutpaintOnBackground = useCallback(
    async (
      extensions: { left: number; right: number; up: number; down: number },
      transform: (
        source: HTMLCanvasElement,
        ext: { left: number; right: number; up: number; down: number },
      ) => Promise<HTMLCanvasElement>,
    ): Promise<boolean> => {
      const canvas = canvasRef.current;
      const bg = getBackgroundImage();
      if (!canvas || !bg) return false;

      const leftPx = Math.max(0, Math.floor(extensions.left));
      const rightPx = Math.max(0, Math.floor(extensions.right));
      const upPx = Math.max(0, Math.floor(extensions.up));
      const downPx = Math.max(0, Math.floor(extensions.down));
      if (leftPx + rightPx + upPx + downPx === 0) return false;

      const oldW = bg.width ?? 0;
      const oldH = bg.height ?? 0;
      if (oldW <= 0 || oldH <= 0) return false;

      // Snapshot the current bg surface into a fresh canvas the
      // transform owns. The element under bg may be the live
      // paint-surface canvas (stroke target); we don't want the
      // network call to race with stroke writes.
      const source = document.createElement('canvas');
      source.width = oldW;
      source.height = oldH;
      const sCtx = source.getContext('2d');
      if (!sCtx) return false;
      sCtx.drawImage(bg.getElement() as CanvasImageSource, 0, 0);

      const result = await transform(source, {
        left: leftPx,
        right: rightPx,
        up: upPx,
        down: downPx,
      });

      const newW = oldW + leftPx + rightPx;
      const newH = oldH + upPx + downPx;

      // Normalize the result onto a canvas at exactly newW × newH so
      // downstream code (paint surface, fabricImg.width) doesn't have
      // to deal with provider rounding or unexpected scaling.
      const offscreen = document.createElement('canvas');
      offscreen.width = newW;
      offscreen.height = newH;
      const ctx = offscreen.getContext('2d');
      if (!ctx) return false;
      ctx.drawImage(result, 0, 0, result.width, result.height, 0, 0, newW, newH);

      // Mirror applyActiveCrop's overlay-shift, but in the opposite
      // direction: original content has shifted right by leftPx and
      // down by upPx within the new canvas, so non-bg objects must
      // shift the same amount to stay visually anchored.
      canvas.getObjects().forEach((obj) => {
        if (obj === bg) return;
        obj.set({
          left: (obj.left ?? 0) + leftPx,
          top: (obj.top ?? 0) + upPx,
        });
        obj.setCoords();
      });

      const newImg = new fabric.FabricImage(offscreen, {
        left: 0,
        top: 0,
        originX: 'left',
        originY: 'top',
        scaleX: 1,
        scaleY: 1,
        selectable: false,
        evented: false,
      });
      (newImg as fabric.FabricImage & { erasable: boolean }).erasable = false;
      tagAsBackground(newImg);
      newImg.set({ objectCaching: false } as Partial<fabric.FabricImage>);
      newImg.filters = bg.filters ? [...bg.filters] : [];
      newImg.applyFilters();

      const bgLayerId = getObjectLayerId(bg);
      if (bgLayerId) {
        setObjectLayerId(newImg, bgLayerId);
        paintSurfacesRef.current.set(bgLayerId, {
          canvas: offscreen,
          fabricImg: newImg,
          offsetX: newImg.left ?? 0,
          offsetY: newImg.top ?? 0,
        });
      }

      canvas.remove(bg);
      canvas.add(newImg);
      canvas.sendObjectToBack(newImg);
      backgroundImageRef.current = newImg;
      imageDimsRef.current = { width: newW, height: newH };
      onCanvasDimensions?.(newW, newH);

      zoomToFit();
      canvas.renderAll();
      onObjectModifiedRef.current?.();
      return true;
    },
    [getBackgroundImage, onCanvasDimensions, zoomToFit],
  );

  /** Switch the marquee shape, clearing any in-progress selection. No-op if unchanged. */
  const setMarqueeShape = useCallback(
    (shape: MarqueeShape) => {
      if (marqueeRef.current.shape === shape) return;
      clearMarquee();
      marqueeRef.current.shape = shape;
    },
    [clearMarquee],
  );

  /** Tag the current background image with a layer id (used to associate the
   *  bg image with the user-facing "Background" layer entry). No-op if there's
   *  no bg yet. */
  const setBackgroundLayerId = useCallback(
    (layerId: string) => {
      const bg = getBackgroundImage();
      if (!bg) return;
      setObjectLayerId(bg, layerId);
      // Register the bg's underlying canvas as the Background layer's paint
      // surface so subsequent strokes route there.
      const w = bg.width ?? 0;
      const h = bg.height ?? 0;
      const surfaceCanvas = ensureCanvasElement(
        bg.getElement() as HTMLImageElement | HTMLCanvasElement,
        w,
        h,
      );
      if (bg.getElement() !== surfaceCanvas) bg.setElement(surfaceCanvas);
      paintSurfacesRef.current.set(layerId, {
        canvas: surfaceCanvas,
        fabricImg: bg,
        offsetX: bg.left ?? 0,
        offsetY: bg.top ?? 0,
      });
    },
    [getBackgroundImage],
  );

  /** Toggle visibility of every Fabric object tagged with the given layerId. */
  const setLayerVisibility = useCallback((layerId: string, visible: boolean) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let changed = false;
    for (const o of canvas.getObjects()) {
      if (getObjectLayerId(o) === layerId) {
        o.visible = visible;
        changed = true;
      }
    }
    if (changed) canvas.renderAll();
  }, []);

  /** Apply tree-aware effective visibility to every layer-tagged
   *  Fabric object. A layer is rendered only when its own
   *  `visible` flag is true AND every ancestor in the tree is
   *  visible too — so hiding a group cascades to all its
   *  descendants. Idempotent: only renders when something actually
   *  changes, so the useEffect that calls this on every document
   *  update doesn't burn frames when visibility didn't move. */
  const applyEffectiveVisibility = useCallback((doc: EditorDocument) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const eff = new Map<string, boolean>();
    for (const id of Object.keys(doc.layers)) {
      let visible = true;
      let cur: string | null = id;
      // Walk the parent chain — first false flag wins.
      while (cur !== null) {
        const node: import('../types').LayerNode | undefined = doc.layers[cur];
        if (!node || !node.visible) {
          visible = false;
          break;
        }
        cur = node.parentId;
      }
      eff.set(id, visible);
    }
    let changed = false;
    for (const o of canvas.getObjects()) {
      const layerId = getObjectLayerId(o);
      if (!layerId) continue;
      const newVis = eff.get(layerId) ?? true;
      if (o.visible !== newVis) {
        o.visible = newVis;
        changed = true;
      }
    }
    // `requestRenderAll` (rAF-coalesced) rather than the sync
    // `renderAll` so multiple state.document-driven effects in the
    // same React commit (applyEffectiveVisibility + applyLayerOrder
    // + slow-path) flush in one frame paint instead of N sync
    // paints — the latter compound on top of the slow-path
    // compositor's own per-pixel work and turn a 200ms recomposite
    // into a perceptible UI freeze.
    if (changed) canvas.requestRenderAll();
  }, []);

  /** Set opacity (0..1) on every Fabric object tagged with the given
   *  layerId. Idempotent — re-renders only when something actually
   *  changed so dragging the slider doesn't burn frames. */
  const setLayerOpacity = useCallback((layerId: string, opacity: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const clamped = Math.max(0, Math.min(1, opacity));
    let changed = false;
    for (const o of canvas.getObjects()) {
      if (getObjectLayerId(o) === layerId && o.opacity !== clamped) {
        o.set('opacity', clamped);
        changed = true;
      }
    }
    if (changed) canvas.renderAll();
  }, []);

  /** Set globalCompositeOperation (a.k.a. blend mode) on every Fabric
   *  object tagged with the given layerId. The string values match
   *  Canvas2D's composite operations directly so no translation is
   *  needed. Already in SERIALIZED_PROPS, so changes survive
   *  toJSON/loadFromJSON without extra plumbing. */
  const setLayerBlendMode = useCallback((layerId: string, blendMode: string) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let changed = false;
    for (const o of canvas.getObjects()) {
      if (
        getObjectLayerId(o) === layerId &&
        (o as unknown as { globalCompositeOperation?: string }).globalCompositeOperation !==
          blendMode
      ) {
        (o as unknown as { set: (k: string, v: string) => void }).set(
          'globalCompositeOperation',
          blendMode,
        );
        changed = true;
      }
    }
    if (changed) canvas.renderAll();
  }, []);

  /** Apply a layer's effect bundle (drop shadow, stroke) to every
   *  Fabric object tagged with the given layerId. Stroke dispatches
   *  by object type so it follows the *visible content* on raster
   *  layers (paint / image) via PixelStrokeFilter, and uses Fabric's
   *  native stroke on shape / text / path / line objects (where the
   *  Fabric object IS the rendered shape). Drop shadow uses Canvas2D's
   *  native shadow context, which follows alpha automatically. All
   *  three properties (.shadow, .stroke, .strokeWidth) are part of
   *  Fabric's default serialization, so per-layer effect values
   *  round-trip through toJSON / loadFromJSON. */
  const setLayerEffects = useCallback(
    (
      layerId: string,
      effects:
        | {
            dropShadow?: { color: string; blur: number; offsetX: number; offsetY: number };
            stroke?: { color: string; width: number };
            glow?: { color: string; blur: number; intensity: number };
          }
        | undefined,
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ds = effects?.dropShadow;
      const st = effects?.stroke;
      const gl = effects?.glow;

      // Fabric only has one `shadow` slot per object. Drop shadow
      // gets first claim; glow falls back to that slot for non-image
      // layers (text/shape) when drop shadow isn't enabled. For
      // image layers we have a dedicated `PixelGlowFilter` that
      // composes alongside drop shadow + stroke.
      const dropShadowFabric = ds
        ? new fabric.Shadow({
            color: ds.color,
            blur: ds.blur,
            offsetX: ds.offsetX,
            offsetY: ds.offsetY,
          })
        : null;
      const glowAsShadowFabric = gl
        ? new fabric.Shadow({
            color: gl.color,
            blur: gl.blur,
            offsetX: 0,
            offsetY: 0,
          })
        : null;

      for (const o of canvas.getObjects()) {
        if (getObjectLayerId(o) !== layerId) continue;

        const isImage = (o as fabric.Object).type === 'image';
        if (isImage) {
          const img = o as fabric.FabricImage;
          // Image layers: shadow slot = drop shadow only; glow runs
          // through the pixel-aware filter so it tracks the alpha
          // silhouette (Fabric's native shadow on a transparent
          // canvas would draw a rectangle around the whole canvas).
          (img as unknown as { shadow: fabric.Shadow | null }).shadow = dropShadowFabric;

          const filters = img.filters as unknown as Array<{ type?: string }>;
          for (let i = filters.length - 1; i >= 0; i--) {
            const t = filters[i]?.type;
            if (t === PIXEL_STROKE_TYPE || t === PIXEL_GLOW_TYPE) filters.splice(i, 1);
          }
          // Order matters within the filter chain: glow first so it
          // sits behind the stroke ring in the composite.
          if (gl && gl.blur > 0 && gl.intensity > 0) {
            filters.push(
              new PixelGlowFilter({
                color: gl.color,
                blur: gl.blur,
                intensity: gl.intensity,
              }) as unknown as { type?: string },
            );
          }
          if (st && st.width > 0) {
            filters.push(
              new PixelStrokeFilter({
                color: st.color,
                width: st.width,
              }) as unknown as { type?: string },
            );
          }
          img.applyFilters();
          o.set({ strokeWidth: 0 });
        } else {
          // Non-image layers: drop shadow wins the shadow slot. If
          // only glow is enabled, glow gets the slot. If both are
          // enabled, drop shadow renders and glow is silently
          // dropped on this layer (text/shape layers can't host two
          // independent shadow passes without a bigger refactor).
          (o as unknown as { shadow: fabric.Shadow | null }).shadow =
            dropShadowFabric ?? glowAsShadowFabric;

          if (st && st.width > 0) {
            o.set({ stroke: st.color, strokeWidth: st.width });
          } else if (!(o instanceof fabric.Path)) {
            // Vector layers (fabric.Path) own their stroke via
            // `VectorPath.stroke` — leaving an effects-driven clear
            // to zero the strokeWidth would clobber the user's
            // chosen pen stroke. For shape/text the layer's stroke
            // is set elsewhere; clearing here is the historical
            // behavior we keep until a broader effects-vs-native
            // refactor.
            o.set({ strokeWidth: 0 });
          }
        }
      }
      canvas.renderAll();
    },
    [],
  );

  /** Rasterize the current marquee silhouette onto the given mask
   *  context, painting white where the selection covers. Returns
   *  true if the selection contributed any pixels. Used by the
   *  selection-to-mask path of `addLayerMask`. */
  const rasterizeMarqueeOntoMask = useCallback(
    (mCtx: CanvasRenderingContext2D, w: number, h: number): boolean => {
      const m = marqueeRef.current;
      if (!m.obj || m.isDrawing) return false;
      mCtx.fillStyle = '#ffffff';

      if (m.shape === 'rect') {
        const r = m.obj as fabric.Rect;
        const left = Math.max(0, r.left ?? 0);
        const top = Math.max(0, r.top ?? 0);
        const width = Math.min(w - left, (r.width ?? 0) * (r.scaleX ?? 1));
        const height = Math.min(h - top, (r.height ?? 0) * (r.scaleY ?? 1));
        if (width <= 0 || height <= 0) return false;
        mCtx.fillRect(left, top, width, height);
        return true;
      }
      if (m.shape === 'ellipse') {
        const e = m.obj as fabric.Ellipse;
        const rx = (e.rx ?? 0) * (e.scaleX ?? 1);
        const ry = (e.ry ?? 0) * (e.scaleY ?? 1);
        if (rx <= 0 || ry <= 0) return false;
        const cx = (e.left ?? 0) + rx;
        const cy = (e.top ?? 0) + ry;
        mCtx.beginPath();
        mCtx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        mCtx.fill();
        return true;
      }
      if ((m.shape === 'lasso' || m.shape === 'polygon') && m.points.length >= 3) {
        mCtx.beginPath();
        mCtx.moveTo(m.points[0].x, m.points[0].y);
        for (let i = 1; i < m.points.length; i++) {
          mCtx.lineTo(m.points[i].x, m.points[i].y);
        }
        mCtx.closePath();
        mCtx.fill();
        return true;
      }
      if (m.shape === 'wand' && m.wandMaskCanvas) {
        // Draw the cached wand-mask canvas directly — its alpha
        // channel is already 1-bit "selected = opaque".
        mCtx.drawImage(m.wandMaskCanvas, 0, 0);
        return true;
      }
      return false;
    },
    [],
  );

  /** Attach a raster mask to a layer. The mask is implemented via
   *  Fabric's native `clipPath` (destination-in compositing under
   *  the hood, which is exactly mask semantics) — this is why we
   *  don't need a custom render-pipeline path for basic masks.
   *  Feather / density / inverted / disabled require the slow path
   *  and ship in a later Tier 1 chunk.
   *
   *  When `fromMarquee: true` and a marquee is active, the mask
   *  alpha is filled from the selection silhouette (selected =
   *  visible, deselected = hidden — Photoshop's "Add Vector Mask
   *  From Selection"). Otherwise the mask is fully white (reveal-
   *  all) — does nothing visible until the user paints into it.
   *
   *  Returns the LayerMask config the caller should hand to
   *  `setLayerMask` in the reducer, or null if no target layer was
   *  found. */
  const addLayerMask = useCallback(
    (layerId: string, opts?: { fromMarquee?: boolean }): LayerMask | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const imgW = imageDimsRef.current.width;
      const imgH = imageDimsRef.current.height;
      if (imgW <= 0 || imgH <= 0) return null;

      // Find the layer's Fabric object. Empty layers don't have
      // one yet (the user hasn't painted into them) — for those we
      // lazily create a transparent paint surface, the same way
      // a brush stroke would. Without this fallback, "Add Mask"
      // on a fresh "+ Layer" silently no-ops, which is exactly
      // the bug reported during Step 4 QA.
      let targetObj: fabric.Object | null = null;
      for (const o of canvas.getObjects()) {
        if (getObjectLayerId(o) === layerId) {
          targetObj = o;
          break;
        }
      }
      if (!targetObj) {
        const surface = ensurePaintSurfaceRef.current?.(layerId);
        targetObj = surface?.fabricImg ?? null;
      }
      if (!targetObj) return null;

      // Build the RAW alpha-mask canvas (user paint target).
      // `bakeStrokeOntoMask` writes user strokes into this canvas;
      // `materializeMaskClipPath` derives the EFFECTIVE alpha (with
      // feather/invert/density baked in) into a separate canvas
      // wrapped by the Fabric clipPath. At creation time the mask
      // config is identity (enabled, !inverted, density=1, feather=0)
      // so effective ≡ raw — the first materialize is a single
      // drawImage. The split exists for subsequent config changes.
      const rawCanvas = document.createElement('canvas');
      rawCanvas.width = imgW;
      rawCanvas.height = imgH;
      const rCtx = rawCanvas.getContext('2d', { willReadFrequently: true });
      if (!rCtx) return null;

      const filledFromMarquee =
        opts?.fromMarquee === true && rasterizeMarqueeOntoMask(rCtx, imgW, imgH);
      if (!filledFromMarquee) {
        // Reveal-all default — entirely opaque white.
        rCtx.fillStyle = '#ffffff';
        rCtx.fillRect(0, 0, imgW, imgH);
      }

      // EFFECTIVE canvas — the one Fabric's clipPath wraps. Identity
      // materialize at creation is just a drawImage; the split costs
      // ~32MB extra per masked layer at 4K, which the soft mask
      // feather/invert/density UX needs (otherwise toggling feather
      // would have nowhere to recover the original raw alpha from).
      const effectiveCanvas = document.createElement('canvas');
      effectiveCanvas.width = imgW;
      effectiveCanvas.height = imgH;

      const initialMaskConfig: LayerMask = {
        id: `lmask_${layerId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        enabled: true,
        inverted: false,
        linked: true,
        density: 1,
        feather: 0,
        fabricObjectId: '',
      };
      materializeEffectiveMaskInto(
        effectiveCanvas,
        rawCanvas,
        maskMaterializationConfig(initialMaskConfig),
        imgW,
        imgH,
      );

      // objectCaching is left at Fabric's default (true). The mask
      // canvas is multi-megapixel; without caching, Fabric
      // rasterizes the mask FabricImage on every parent render,
      // which freezes the UI on full-size photos. We invalidate
      // the cache by setting `dirty = true` in `bakeStrokeOntoMask`
      // after each paint stroke, so caching is correct here.
      //
      // NOTE: do NOT set `excludeFromExport: true` on the mask.
      // Fabric's `toObject` checks `!clipPath.excludeFromExport`
      // before serializing it, so flagging the mask as
      // export-excluded silently drops it from undo snapshots and
      // the IndexedDB session JSON — which is what broke
      // mask persistence across refresh and undo. The mask is
      // attached via `clipPath` only (never `canvas.add`), so it
      // never appears in user PNG exports anyway, regardless of
      // this flag.
      const maskImg = new fabric.FabricImage(effectiveCanvas, {
        left: 0,
        top: 0,
        originX: 'left',
        originY: 'top',
        selectable: false,
        evented: false,
      });
      const maskFabricId = `fmask_${layerId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      (maskImg as fabric.FabricImage & { id: string }).id = maskFabricId;
      // absolutePositioned: clip in canvas coords, not the layer's
      // local coords. Without this the mask would translate with
      // the layer's transform, which is the opposite of what users
      // expect for a typical "fixed canvas mask."
      (maskImg as fabric.FabricObject & { absolutePositioned: boolean }).absolutePositioned = true;
      attachRawMaskCanvas(maskImg, rawCanvas);
      installMaskClipPathSerializer(maskImg);

      targetObj.clipPath = maskImg;
      canvas.requestRenderAll();

      initialMaskConfig.fabricObjectId = maskFabricId;
      maskConfigsRef.current.set(layerId, initialMaskConfig);
      return initialMaskConfig;
    },
    [rasterizeMarqueeOntoMask],
  );

  /** Detach the mask from a layer. Reverses `addLayerMask` —
   *  removes the clipPath, drops the Fabric mask object reference.
   *  No-op if the layer has no mask. */
  const removeLayerMask = useCallback((layerId: string): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    for (const o of canvas.getObjects()) {
      if (getObjectLayerId(o) === layerId && o.clipPath) {
        o.clipPath = undefined;
      }
    }
    maskConfigsRef.current.delete(layerId);
    canvas.requestRenderAll();
  }, []);

  /**
   * Look up (or lazily create) a paint surface for the given layer.
   *
   * Three cases:
   *
   * 1. **Cached.** Return the cached entry if its fabricImg is still
   *    on the canvas.
   *
   * 2. **FabricImage-backed layer** (bg image, pasted image, prior
   *    rasterize result). The surface canvas IS the FabricImage's
   *    own canvas — strokes write directly onto its pixels so the
   *    bbox stays tight to the image content, retouch results move
   *    with the layer in Move tool, and the per-dab destSample
   *    already contains the underlying image (no separate baseline
   *    composite needed). When the image is at non-identity scale,
   *    rotation, or skew, we auto-rasterize first via
   *    `rasterizeLayerToImage` so the resulting FabricImage is at
   *    identity-scale with no rotation/skew — simple translate from
   *    scene → canvas suffices, no inverse matrix.
   *
   *    `offsetX/Y` = the layer's scene (left, top). Bg is at (0, 0).
   *    A pasted layer typically lands at the marquee's scene
   *    position with scaleX=1, scaleY=1, no rotation, so no auto-
   *    rasterize triggers.
   *
   * 3. **Empty / paint layer** (e.g. one added via the LayersPanel
   *    "+ Add Layer" button) — no FabricImage yet. Falls back to a
   *    fresh doc-sized transparent canvas wrapped in a FabricImage
   *    tagged with this layerId. Surface offset is (0, 0) since the
   *    overlay spans the document.
   *
   * Returns null on a hard failure (no canvas, no doc dims, no
   * usable target).
   */
  const ensurePaintSurface = useCallback(
    (
      layerId: string,
    ): {
      canvas: HTMLCanvasElement;
      fabricImg: fabric.FabricImage;
      offsetX: number;
      offsetY: number;
    } | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const cached = paintSurfacesRef.current.get(layerId);
      if (cached && canvas.contains(cached.fabricImg)) return cached;

      // Case 2 — adopt a FabricImage tagged with this layerId as
      // the paint surface. Bg image AND non-bg image layers take
      // this path. Non-identity transforms trigger auto-rasterize
      // first.
      let imgObj: fabric.FabricImage | null = null;
      for (const obj of canvas.getObjects()) {
        if (!(obj instanceof fabric.FabricImage)) continue;
        if (getObjectLayerId(obj) !== layerId) continue;
        imgObj = obj;
        break;
      }
      if (imgObj) {
        const needsRasterize =
          (imgObj.scaleX ?? 1) !== 1 ||
          (imgObj.scaleY ?? 1) !== 1 ||
          (imgObj.angle ?? 0) !== 0 ||
          (imgObj.skewX ?? 0) !== 0 ||
          (imgObj.skewY ?? 0) !== 0 ||
          (imgObj.flipX ?? false) ||
          (imgObj.flipY ?? false);
        if (needsRasterize) {
          // Bail out entirely when the rasterize itself reports
          // failure. Otherwise the re-find below would return the
          // ORIGINAL non-identity FabricImage (still on canvas
          // because rasterize never swapped it), and the rest of
          // this function would build a paint surface with offset
          // = (left, top) on a layer that's actually rotated /
          // scaled — strokes would land at wrong canvas pixels.
          // Returning null mirrors the "no usable target" failure
          // mode and callers (ensurePaintSurfaceRef checkers in
          // every retouch handler) already abort cleanly on null.
          const ok = rasterizeLayerToImage(layerId);
          if (!ok) return null;
          // Re-find — rasterizeLayerToImage replaces the FabricImage
          // with a new instance.
          imgObj = null;
          for (const obj of canvas.getObjects()) {
            if (!(obj instanceof fabric.FabricImage)) continue;
            if (getObjectLayerId(obj) !== layerId) continue;
            imgObj = obj;
            break;
          }
        }
      }
      if (imgObj) {
        const w = imgObj.width ?? 0;
        const h = imgObj.height ?? 0;
        if (w > 0 && h > 0) {
          const surfaceCanvas = ensureCanvasElement(
            imgObj.getElement() as HTMLImageElement | HTMLCanvasElement,
            w,
            h,
          );
          if (imgObj.getElement() !== surfaceCanvas) imgObj.setElement(surfaceCanvas);
          const entry = {
            canvas: surfaceCanvas,
            fabricImg: imgObj,
            offsetX: imgObj.left ?? 0,
            offsetY: imgObj.top ?? 0,
          };
          paintSurfacesRef.current.set(layerId, entry);
          return entry;
        }
      }

      // Case 3 — empty layer with no FabricImage. Fresh doc-sized
      // transparent canvas + FabricImage.
      const dims = imageDimsRef.current;
      if (dims.width <= 0 || dims.height <= 0) return null;
      const surfaceCanvas = document.createElement('canvas');
      surfaceCanvas.width = dims.width;
      surfaceCanvas.height = dims.height;
      // Lock the canvas into CPU-readable mode at creation. The
      // Adjustments modal's source-capture path and the slow-path
      // compositor both call `getImageData` on paint surfaces; the
      // first getContext call decides the backing store policy, so
      // we set the flag here before anything else touches the
      // canvas. See the matching note in `ensureCanvasElement`.
      surfaceCanvas.getContext('2d', { willReadFrequently: true });
      const fabricImg = new fabric.FabricImage(surfaceCanvas, {
        left: 0,
        top: 0,
        originX: 'left',
        originY: 'top',
        scaleX: 1,
        scaleY: 1,
        selectable: false,
        evented: false,
        objectCaching: false,
      });
      (fabricImg as fabric.FabricImage & { erasable: boolean }).erasable = false;
      setObjectLayerId(fabricImg, layerId);
      canvas.add(fabricImg);

      const entry = { canvas: surfaceCanvas, fabricImg, offsetX: 0, offsetY: 0 };
      paintSurfacesRef.current.set(layerId, entry);
      return entry;
    },
    [rasterizeLayerToImage],
  );
  ensurePaintSurfaceRef.current = ensurePaintSurface;

  /**
   * Render a Fabric Path onto a paint surface's canvas, applying the active
   * marquee as a clip and the eraser's destination-out compositing if the
   * eraser tool was used.
   *
   * Implementation: render the path onto a *temporary* StaticCanvas to get a
   * clean Fabric rendering pipeline (transforms, brush settings, anti-
   * aliasing) with no shared state with the main canvas. Then `drawImage` the
   * resulting bitmap onto our surface with the appropriate compositing/clip.
   * This avoids cache contamination from rendering Path objects directly onto
   * the surface's 2D context.
   *
   * The path is removed from the main canvas before being added to the temp
   * canvas (Fabric objects belong to one canvas at a time).
   */
  const bakeStrokeOntoSurface = useCallback(
    (path: fabric.Object, layerId: string, isEraser: boolean) => {
      const surface = ensurePaintSurface(layerId);
      if (!surface) return false;
      const ctx = surface.canvas.getContext('2d');
      if (!ctx) return false;

      const w = surface.canvas.width;
      const h = surface.canvas.height;

      // Render the path onto a fresh temp canvas via Fabric's pipeline. The
      // path is expected to already be removed from the main canvas by the
      // caller.
      const tempEl = document.createElement('canvas');
      tempEl.width = w;
      tempEl.height = h;
      const tempCanvas = new fabric.StaticCanvas(tempEl, {
        width: w,
        height: h,
        enableRetinaScaling: false,
        renderOnAddRemove: false,
        backgroundColor: 'transparent',
      });
      tempCanvas.add(path);
      tempCanvas.renderAll();

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      // Strokes are authored in scene coordinates, but the surface canvas's
      // (0,0) corresponds to the paint surface's current world position. If
      // the layer has been moved (left/top != 0), shift the context so scene
      // coords map to the right surface pixels.
      const offsetX = -(surface.fabricImg.left ?? 0);
      const offsetY = -(surface.fabricImg.top ?? 0);

      // For eraser strokes, decouple the bake's alpha from the live preview
      // color (a fixed semi-transparent gray). Build a fresh mask where any
      // stroke pixel becomes opaque at the user's eraser-opacity setting,
      // so 1.0 fully erases and 0.5 reduces alpha by 50%, regardless of
      // the preview color's own alpha. For draw strokes we use tempEl
      // unchanged so opacity-aware blending keeps working.
      const stamp = isEraser ? buildEraserMask(tempEl, eraserOpacityRef.current) : tempEl;

      ctx.save();
      ctx.translate(offsetX, offsetY);
      if (hasMarquee && m.shape === 'wand' && m.wandMaskCanvas) {
        const masked = document.createElement('canvas');
        masked.width = w;
        masked.height = h;
        const mCtx = masked.getContext('2d');
        if (mCtx) {
          mCtx.drawImage(stamp, 0, 0);
          mCtx.globalCompositeOperation = 'destination-in';
          mCtx.drawImage(m.wandMaskCanvas, 0, 0);
          if (isEraser) ctx.globalCompositeOperation = 'destination-out';
          ctx.drawImage(masked, 0, 0);
        }
      } else {
        if (hasMarquee) applyMarqueeClipToContext(ctx, m);
        if (isEraser) ctx.globalCompositeOperation = 'destination-out';
        ctx.drawImage(stamp, 0, 0);
      }
      ctx.restore();

      tempCanvas.dispose();

      // Force Fabric to re-bind the FabricImage to its (now-modified) source
      // canvas. setElement clears internal texture handles and runs
      // `_initConfig`, which is what makes new pixels show up reliably across
      // multiple consecutive bakes — `dirty = true` alone isn't sufficient.
      surface.fabricImg.setElement(surface.canvas);
      surface.fabricImg.dirty = true;
      surface.fabricImg.setCoords();
      return true;
    },
    [ensurePaintSurface],
  );

  /** Render a brush stroke onto the active layer's mask alpha
   *  instead of its pixel surface. Brush strokes paint white onto
   *  the mask (revealing the layer); eraser strokes erase mask
   *  alpha (hiding the layer). The "color" of the user's brush is
   *  ignored in mask mode — only the alpha shape of the stroke
   *  matters, which is the natural Photoshop-like behavior.
   *
   *  Marquee clipping (when a finalized selection is active) applies
   *  here too, so users can constrain mask edits to a region. The
   *  layer's Fabric object is marked dirty afterwards so its
   *  cached render re-evaluates the clipPath. */
  const bakeStrokeOntoMask = useCallback(
    (path: fabric.Object, layerId: string, isEraser: boolean) => {
      const canvas = canvasRef.current;
      if (!canvas) return false;

      // Locate the leaf's clipPath FabricImage. We paint into its RAW
      // canvas (the user-paint target) and re-materialize the
      // EFFECTIVE alpha (the clipPath element) afterwards so feather/
      // invert/density show in-stroke. Pre-materialization sessions
      // have only the effective canvas saved — `ensureRawMaskCanvas`
      // lazily copies it into a raw slot on first stroke.
      let targetFabric: fabric.FabricObject | null = null;
      let clipPath: fabric.FabricImage | null = null;
      for (const o of canvas.getObjects()) {
        if (getObjectLayerId(o) !== layerId) continue;
        const cp = (o as fabric.FabricObject).clipPath;
        if (!(cp instanceof fabric.FabricImage)) continue;
        targetFabric = o as fabric.FabricObject;
        clipPath = cp;
        break;
      }
      if (!targetFabric || !clipPath) return false;
      const effectiveCanvas = clipPath.getElement();
      if (!(effectiveCanvas instanceof HTMLCanvasElement)) return false;
      const rawCanvas = ensureRawMaskCanvas(clipPath);
      if (!rawCanvas) return false;

      const w = rawCanvas.width;
      const h = rawCanvas.height;

      // Render path onto a temp canvas via Fabric. The user's brush
      // color is whatever they had set; we only care about the
      // stroke's alpha shape.
      const tempEl = document.createElement('canvas');
      tempEl.width = w;
      tempEl.height = h;
      const tempCanvas = new fabric.StaticCanvas(tempEl, {
        width: w,
        height: h,
        enableRetinaScaling: false,
        renderOnAddRemove: false,
        backgroundColor: 'transparent',
      });
      tempCanvas.add(path);
      tempCanvas.renderAll();

      const ctx = rawCanvas.getContext('2d');
      if (!ctx) {
        tempCanvas.dispose();
        return false;
      }

      const m = marqueeRef.current;
      const hasMarquee = !!m.obj && !m.isDrawing;

      ctx.save();
      if (hasMarquee) applyMarqueeClipToContext(ctx, m);

      if (isEraser) {
        // Eraser in mask mode subtracts mask alpha. Same gotcha as
        // bakeStrokeOntoSurface: the eraser's live preview color is
        // a fixed semi-transparent gray (for visibility against any
        // background), so the rendered tempEl's pixels have alpha
        // ≪ 1 even at 100% eraser opacity. buildEraserMask
        // re-normalizes the stroke to fully-opaque at the user's
        // eraser-opacity setting, so 1.0 fully clears mask alpha
        // and 0.5 halves it, independent of the preview color.
        const stamp = buildEraserMask(tempEl, eraserOpacityRef.current);
        ctx.globalCompositeOperation = 'destination-out';
        ctx.drawImage(stamp, 0, 0);
      } else {
        // Brush in mask mode paints white. Recolor the temp canvas's
        // RGB to white via source-in (preserves alpha shape, replaces
        // RGB with #ffffff), then composite source-over onto the
        // mask. Result: existing mask alpha is unioned with the
        // stroke's alpha, RGB stays white throughout.
        const tempCtx = tempEl.getContext('2d');
        if (tempCtx) {
          tempCtx.globalCompositeOperation = 'source-in';
          tempCtx.fillStyle = '#ffffff';
          tempCtx.fillRect(0, 0, w, h);
        }
        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(tempEl, 0, 0);
      }
      ctx.restore();
      tempCanvas.dispose();

      // Materialize raw → effective using the latest mask config
      // mirrored from React state. Missing entry = identity (the
      // addLayerMask defaults), so the materialize is a single
      // drawImage — same perf as the pre-split fast path.
      const maskConfig = maskConfigsRef.current.get(layerId);
      const matConfig = maskConfig
        ? maskMaterializationConfig(maskConfig)
        : { enabled: true, inverted: false, density: 1, feather: 0 };
      materializeEffectiveMaskInto(effectiveCanvas, rawCanvas, matConfig, w, h);

      clipPath.dirty = true;
      targetFabric.dirty = true;
      canvas.requestRenderAll();
      return true;
    },
    [],
  );

  /** Push the latest mask config for a layer into the canvas-side
   *  mirror. EditorPage calls this whenever `state.document`'s mask
   *  config changes — `bakeStrokeOntoMask` reads from the mirror to
   *  apply the right feather/invert/density during in-stroke
   *  materialization. `null` mask clears the entry. */
  const setMaskConfig = useCallback((layerId: string, mask: LayerMask | null): void => {
    if (mask) maskConfigsRef.current.set(layerId, mask);
    else maskConfigsRef.current.delete(layerId);
  }, []);

  /** Re-derive a layer's effective mask alpha (the clipPath element)
   *  from its raw mask canvas + supplied config. Called by EditorPage
   *  whenever a mask config field changes (Disable / Invert /
   *  Density / Feather) — feathered/inverted/density-attenuated alpha
   *  is baked into the effective canvas here, then Fabric's native
   *  destination-in clipPath compositing renders it correctly.
   *
   *  Returns true when a clipPath was found and materialized. */
  const materializeMaskClipPath = useCallback((layerId: string, mask: LayerMask): boolean => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    let didMaterialize = false;
    for (const obj of canvas.getObjects()) {
      if (getObjectLayerId(obj) !== layerId) continue;
      const cp = (obj as fabric.FabricObject).clipPath;
      if (!(cp instanceof fabric.FabricImage)) continue;
      const effective = cp.getElement();
      if (!(effective instanceof HTMLCanvasElement)) continue;
      const raw = ensureRawMaskCanvas(cp);
      if (!raw) continue;
      materializeEffectiveMaskInto(
        effective,
        raw,
        maskMaterializationConfig(mask),
        effective.width,
        effective.height,
      );
      cp.dirty = true;
      (obj as fabric.FabricObject).dirty = true;
      didMaterialize = true;
    }
    if (didMaterialize) {
      maskConfigsRef.current.set(layerId, mask);
      canvas.requestRenderAll();
    }
    return didMaterialize;
  }, []);

  /**
   * Reorder canvas objects to match the layer list. `orderedLayerIds[0]` is the
   * top-of-panel / front-of-canvas layer. Objects without a layerId keep their
   * relative positions; marquee and crop overlays are always brought to front.
   */
  const applyLayerOrder = useCallback((orderedLayerIds: readonly string[]) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Iterate from back-most to front-most. bringObjectToFront leaves the most
    // recently brought item on top, so the last id processed ends up at the top.
    for (let i = orderedLayerIds.length - 1; i >= 0; i--) {
      const layerId = orderedLayerIds[i];
      for (const obj of canvas.getObjects()) {
        if (getObjectLayerId(obj) === layerId) {
          canvas.bringObjectToFront(obj);
        }
      }
    }
    // Keep UI overlays above all layer content. The Clone Stamp
    // reticle bars belong here too — without this, every layers
    // re-render buries the reticle behind the active layer's
    // paint surface and the user only sees it for the brief
    // moment between renderCloneReticle and the next React tick.
    for (const obj of canvas.getObjects()) {
      const flags = obj as unknown as {
        _isMarquee?: boolean;
        _isCropRect?: boolean;
        _isCloneReticle?: boolean;
        _isQuickMaskOverlay?: boolean;
        _isPenPreview?: boolean;
        _isPathEditOverlay?: boolean;
      };
      if (
        flags._isMarquee ||
        flags._isCropRect ||
        flags._isCloneReticle ||
        flags._isQuickMaskOverlay ||
        flags._isPenPreview ||
        flags._isPathEditOverlay
      ) {
        canvas.bringObjectToFront(obj);
      }
    }
    // `requestRenderAll` rather than the sync `renderAll` — see the
    // matching note in `applyEffectiveVisibility`.
    canvas.requestRenderAll();
  }, []);

  /**
   * Select the canvas objects tagged with the given layerId. Single object →
   * setActiveObject; multiple → ActiveSelection. Forces target objects to be
   * selectable + evented so the transform handles render even if a previous
   * tool (marquee, crop) had locked them. The bg image is left non-selectable
   * to match Photoshop's locked-Background behavior.
   *
   * Exception: when the active tool needs layers LOCKED (marquee, retouch),
   * skip the unlock + setActiveObject entirely. Panel-side selection in those
   * tools is purely a state-level indicator (LayersPanel row highlight); the
   * tool's canvas-level mouse handlers don't want Fabric's hit-test running
   * Move-tool drag in parallel with their custom stamping/marquee handlers.
   */
  const selectLayerObjects = useCallback(
    (layerId: string) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const bg = getBackgroundImage();
      // Strokes are part of the layer but not individually selectable; skip them
      // so the active selection wraps only the user-movable objects. Paint
      // overlays (added on first brush/retouch touch by `ensurePaintSurface`)
      // are also filtered — they're doc-sized transparent UI artifacts, not
      // user-managed layer geometry, and wrapping them into an ActiveSelection
      // would inflate the bbox to the full canvas.
      const objs = canvas
        .getObjects()
        .filter((o) => getObjectLayerId(o) === layerId && !isPaintOverlay(o));

      if (TOOLS_REQUIRING_LOCKED_LAYERS.has(activeToolRef.current)) {
        // Leave Fabric state untouched. The tool's setTool branch
        // already locked every object; LayersPanel still highlights
        // the row via state.activeLayerId (React) so the user knows
        // which layer is targeted.
        return;
      }

      canvas.discardActiveObject();

      // Toggle the bg image's interactivity based on whether the target layer
      // *is* the Background. Selecting Background unlocks it; selecting any
      // other layer re-locks it so it's not accidentally clicked on the canvas.
      if (bg) {
        const targetingBg = objs.includes(bg);
        bg.set({ selectable: targetingBg, evented: targetingBg });
      }

      for (const o of objs) {
        o.set({ selectable: true, evented: true });
      }

      if (objs.length === 1) {
        // Text layers stay in non-editing mode on panel select —
        // the user wanted a single click to set up move-with-
        // transform-handles, with editing reserved for an explicit
        // canvas double-click. Fabric's IText default handles the
        // dblclick → enterEditing transition.
        canvas.setActiveObject(objs[0]);
      } else if (objs.length > 1) {
        const sel = new fabric.ActiveSelection(objs, { canvas });
        canvas.setActiveObject(sel);
      }
      canvas.requestRenderAll();
    },
    [getBackgroundImage],
  );

  /** Multi-layer selection: select every Fabric object tagged
   *  with any of the given layer ids and wrap them in a single
   *  Fabric ActiveSelection. Used by panel-side group clicks
   *  (click a group row → select every descendant's Fabric
   *  object) and by Shift+click range selections, so dragging
   *  any one of the selected objects moves them all together —
   *  Fabric's built-in ActiveSelection drag handles this
   *  uniformly. Empty input or no matching objects clears the
   *  active selection.
   *
   *  Same lock-mode exception as `selectLayerObjects`: tools that
   *  need every object non-evented (marquee, retouch) skip the
   *  Fabric-side selection entirely. */
  const selectLayersObjects = useCallback(
    (layerIds: readonly string[]) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      if (TOOLS_REQUIRING_LOCKED_LAYERS.has(activeToolRef.current)) return;
      const bg = getBackgroundImage();
      const idSet = new Set(layerIds);
      const objs = canvas.getObjects().filter((o) => {
        const lid = getObjectLayerId(o);
        // Same paint-overlay exclusion as the single-layer path —
        // don't let `ensurePaintSurface`'s doc-sized helper image
        // inflate the ActiveSelection's bbox.
        return typeof lid === 'string' && idSet.has(lid) && !isPaintOverlay(o);
      });
      canvas.discardActiveObject();

      // Bg interactivity follows whether the bg is in the
      // selection — same rule as the single-id path so the user
      // can grab + drag the Background as part of a group.
      if (bg) {
        const targetingBg = objs.includes(bg);
        bg.set({ selectable: targetingBg, evented: targetingBg });
      }

      for (const o of objs) {
        o.set({ selectable: true, evented: true });
      }

      if (objs.length === 1) {
        canvas.setActiveObject(objs[0]);
      } else if (objs.length > 1) {
        const sel = new fabric.ActiveSelection(objs, { canvas });
        canvas.setActiveObject(sel);
      }
      canvas.requestRenderAll();
    },
    [getBackgroundImage],
  );

  /** Remove every Fabric object tagged with the given layerId, including its
   *  paint surface entry. */
  const removeLayerObjects = useCallback(
    (layerId: string) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const toRemove = canvas.getObjects().filter((o) => getObjectLayerId(o) === layerId);
      paintSurfacesRef.current.delete(layerId);
      if (toRemove.length === 0) return;
      for (const o of toRemove) canvas.remove(o);
      canvas.discardActiveObject();
      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /** Add a FabricImage from a Blob (e.g. clipboard paste, AI Insert)
   *  tagged with the given layerId. Returns the created object or null.
   *
   *  Options:
   *    - `layerId` (required): the layer entry in `state.layers` that
   *      this fabric object should be associated with.
   *    - `fitTo` (optional): a scene-coord rect; the image is
   *      uniformly scaled (longest side fills the rect) and centered
   *      at the rect's center. Handy for the Smart Create Insert flow
   *      where the new layer should land inside the user's marquee.
   *      Without `fitTo`, the image lands at the photo's center at its
   *      natural size.
   *
   *  Either way, exactly one `renderAll` runs before the function
   *  returns, so the caller never needs to manually trigger a repaint
   *  after positioning. */
  const addImageLayer = useCallback(
    async (
      blob: Blob,
      options: {
        layerId: string;
        fitTo?: { left: number; top: number; width: number; height: number };
      },
    ): Promise<fabric.FabricImage | null> => {
      const { layerId, fitTo } = options;
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
      const img = await fabric.FabricImage.fromURL(dataUrl, { crossOrigin: 'anonymous' });

      // Read dimensions straight from the underlying `<img>` element
      // — `naturalWidth`/`naturalHeight` are guaranteed populated by
      // the time `FabricImage.fromURL`'s promise resolves. We've seen
      // Fabric's own `img.width` come back as 0 for blob-backed PNGs
      // (e.g. the bg-removal output), and even when it's non-zero
      // it's not always in sync with the element. So we *always*
      // overwrite Fabric's intrinsic width/height with what the
      // element reports — that's the source rect Fabric uses for the
      // actual drawImage call, so getting it right is critical.
      const el = img.getElement();
      const naturalW =
        (el instanceof HTMLImageElement
          ? el.naturalWidth || el.width
          : (el as HTMLCanvasElement).width) || 1;
      const naturalH =
        (el instanceof HTMLImageElement
          ? el.naturalHeight || el.height
          : (el as HTMLCanvasElement).height) || 1;
      img.set({ width: naturalW, height: naturalH });

      if (fitTo && fitTo.width > 0 && fitTo.height > 0) {
        const rawScale = Math.min(fitTo.width / naturalW, fitTo.height / naturalH);
        // Defensive: if naturalW/H were somehow NaN or the fit rect
        // was degenerate, `rawScale` could be NaN/Infinity/0 — any of
        // which would make the image disappear or render at native
        // size. Fall back to 1 so the user at least sees *something*
        // they can manually adjust, rather than a vertical sliver.
        const scale = Number.isFinite(rawScale) && rawScale > 0 ? rawScale : 1;
        img.set({
          left: fitTo.left + fitTo.width / 2,
          top: fitTo.top + fitTo.height / 2,
          originX: 'center',
          originY: 'center',
          scaleX: scale,
          scaleY: scale,
        });
        img.setCoords();
      } else {
        const photoW = imageDimsRef.current.width;
        const photoH = imageDimsRef.current.height;
        img.set({
          left: photoW / 2,
          top: photoH / 2,
          originX: 'center',
          originY: 'center',
        });
      }

      (img as fabric.FabricImage & { id: string }).id = `pasted_${Date.now()}`;
      setObjectLayerId(img, layerId);
      // Match the new image's hit-test policy to the current tool.
      // Otherwise a fresh AI / paste layer added while in marquee mode
      // covers the canvas with `evented: true` Fabric objects, which
      // trip `findTarget` on every click and silently abort the
      // marquee draw — symptom: "select tool stopped working after a
      // few Smart Creates." `setTool('marquee')` does this same
      // normalization for objects already on the canvas; this branch
      // covers objects added later.
      if (activeToolRef.current === 'marquee') {
        img.set({ selectable: false, evented: false });
      }
      canvas.add(img);
      // Don't auto-select the new image when the active tool is
      // marquee — the user is mid-selection workflow and a sudden
      // setActiveObject on a non-selectable image leaves Fabric in a
      // weird half-active state.
      if (activeToolRef.current !== 'marquee') {
        canvas.setActiveObject(img);
      }
      canvas.renderAll();
      // Snapshot is the caller's responsibility (see addText comment).
      return img;
    },
    [],
  );

  /** Create a FabricImage that wraps a canvas-sized backing
   *  canvas for an adjustment layer's display. The slow-path
   *  compositor in `render-pipeline.ts` writes the cumulative
   *  post-adjustment pixels into this backing canvas; Fabric
   *  paints it in its z-order alongside the source layers.
   *
   *  The display object is:
   *  - tagged with `layerId` so `applyLayerOrder` /
   *    `getAdjustmentBackingCanvas` / removal flows can find it
   *  - positioned at (0,0) with no scale — full canvas, identity
   *    transform — so the backing canvas's pixels paint 1:1 onto
   *    the document
   *  - non-selectable / non-evented — adjustment layers are
   *    edited through the LayersPanel modal, not by clicking on
   *    canvas (clicks pass through to the layers below)
   *  - rendered with `objectCaching: false` so a `dirty = true`
   *    flag after the compositor writes is enough to pick up
   *    fresh pixels on the next render
   *
   *  Returns the created FabricImage. Caller is responsible for
   *  the subsequent `applyLayerOrder` so the object lands at the
   *  right z-position. */
  const addAdjustmentLayer = useCallback(
    (options: { layerId: string }): fabric.FabricImage | null => {
      const fc = canvasRef.current;
      if (!fc) return null;
      const w = imageDimsRef.current.width;
      const h = imageDimsRef.current.height;
      if (w <= 0 || h <= 0) return null;
      const backing = document.createElement('canvas');
      backing.width = w;
      backing.height = h;
      // Pre-clear so the first render before the compositor runs
      // shows transparent (no flash of garbage). Allocating a fresh
      // canvas with `2d` gets transparent pixels by default but be
      // defensive — some browsers used to ship uninitialized backing
      // stores. The `willReadFrequently` hint locks the canvas into
      // CPU-readable mode at first-getContext time; the compositor's
      // adjustment-apply path writes here via `putImageData` and
      // composite-op blends drawImage it as a source — no
      // `getImageData` today, but downstream features (filters,
      // export, AI) may read it.
      const bctx = backing.getContext('2d', { willReadFrequently: true });
      if (bctx) bctx.clearRect(0, 0, w, h);
      const img = new fabric.FabricImage(backing, {
        left: 0,
        top: 0,
        originX: 'left',
        originY: 'top',
        scaleX: 1,
        scaleY: 1,
        selectable: false,
        evented: false,
        objectCaching: false,
      });
      img.set({ width: w, height: h });
      setObjectLayerId(img, options.layerId);
      fc.add(img);
      fc.requestRenderAll();
      return img;
    },
    [],
  );

  /** Whether to force rect/ellipse marquee drawing to a square aspect ratio. */
  const setForceSquare = useCallback((force: boolean) => {
    forceSquareRef.current = force;
  }, []);

  /** Update the marquee outline color. Re-tints any active marquee object live. */
  const setMarqueeColor = useCallback((color: string) => {
    marqueeColorRef.current = color;
    const canvas = canvasRef.current;
    const m = marqueeRef.current;
    if (canvas && (m.obj || m.extraObjs.length)) {
      if (m.obj) applyMarqueeColor(m.obj, color);
      for (const o of m.extraObjs) applyMarqueeColor(o, color);
      canvas.renderAll();
    }
  }, []);

  /** Update magic-wand tolerance and contiguous mode. */
  const setWandSettings = useCallback((tolerance: number, contiguous: boolean) => {
    wandToleranceRef.current = tolerance;
    wandContiguousRef.current = contiguous;
  }, []);

  /** Tell the canvas which layer is currently active. New paint strokes get
   *  tagged with this layerId so they're associated with that layer in the
   *  layers panel and follow its visibility / order / deletion. */
  const setActiveLayerId = useCallback((id: string | null) => {
    activeLayerIdRef.current = id;
  }, []);

  /** Push the React-side edit-target map into the canvas hook. The
   *  brush-stroke listener is a Fabric event handler and reads
   *  outside the React render lifecycle, so we mirror the state
   *  via a ref. EditorPage calls this in a useEffect on
   *  `state.editTargets`. */
  const setEditTargets = useCallback((map: Record<string, 'layer' | 'mask'>) => {
    editTargetsRef.current = map;
  }, []);

  /** Null every per-variant stroke ref. Called on tool-switch,
   *  variant-switch (Shift+R), window blur, and component
   *  unmount — anywhere a stroke might be silently abandoned
   *  before its `mouse:up` fires. Without this, the stroke ref
   *  + its lazily-allocated `appliedMask` Uint8Array (4–16 MB
   *  per variant on a typical 4MP layer) leaks until the next
   *  stroke of that variant is committed. If any ref was
   *  non-null we also fire `onObjectModified` so the
   *  in-progress paint is committed to undo history. */
  const clearAllRetouchStrokes = useCallback(() => {
    let didClean = false;
    if (cloneStrokeRef.current) {
      cloneStrokeRef.current = null;
      didClean = true;
    }
    if (smudgeStrokeRef.current) {
      smudgeStrokeRef.current = null;
      didClean = true;
    }
    if (blurStrokeRef.current) {
      blurStrokeRef.current = null;
      didClean = true;
    }
    if (sharpenStrokeRef.current) {
      sharpenStrokeRef.current = null;
      didClean = true;
    }
    if (dodgeStrokeRef.current) {
      dodgeStrokeRef.current = null;
      didClean = true;
    }
    if (burnStrokeRef.current) {
      burnStrokeRef.current = null;
      didClean = true;
    }
    if (spongeStrokeRef.current) {
      spongeStrokeRef.current = null;
      didClean = true;
    }
    if (didClean) onObjectModifiedRef.current?.();
  }, []);

  /** Subscribe to cursor scene-coords. Returns an unsubscribe.
   *  Lets a leaf component (StatusBar) track the cursor without
   *  routing through the reducer — `mouse:move` fires at 60+ Hz
   *  and was previously dispatching SET_CURSOR_POSITION on every
   *  tick, re-rendering the entire EditorPage subtree. */
  const subscribeToCursorMove = useCallback((cb: (x: number, y: number) => void) => {
    const listeners = cursorListenersRef.current;
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  }, []);

  /** Push the React-side RetouchSettings into the canvas hook so
   *  Fabric mouse handlers (which run outside the React render
   *  lifecycle) can read variant + per-variant config. EditorPage
   *  calls this in a useEffect on `state.retouchSettings`. If the
   *  variant changed mid-stroke (Shift+R cycle), abandon any in-
   *  flight stroke first — the new variant's mouse:up gate would
   *  otherwise miss the old ref and leak its `appliedMask`. */
  const setRetouchSettings = useCallback(
    (settings: RetouchSettings) => {
      const prev = retouchSettingsRef.current;
      retouchSettingsRef.current = settings;
      if (prev && prev.variant !== settings.variant) {
        clearAllRetouchStrokes();
      }
    },
    [clearAllRetouchStrokes],
  );

  /** Render or clear the Clone Stamp source reticle on the
   *  canvas. Reticle is a `+` (two crossed lines, no ring) drawn
   *  at the current sample position in scene coords — paired with
   *  the brush-circle SVG cursor in CanvasArea.tsx, which has a
   *  center `+` of its own at the destination. The two `+`s let
   *  the user see source ↔ destination as one gesture. The
   *  reticle is non-interactive and rides above all layers. The
   *  position passed in is OPTIONAL; if omitted, the reticle is
   *  placed at `cloneSourceRef` (default for fresh source-set or
   *  non-aligned post-stroke reset). The stamp loop and the
   *  hover-tracking mouse:move both call with explicit positions. */
  const renderCloneReticle = useCallback((overridePos?: { x: number; y: number }) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Tear down any existing bars before rebuilding.
    if (cloneReticleRef.current) {
      for (const bar of cloneReticleRef.current.bars) canvas.remove(bar);
      cloneReticleRef.current = null;
    }
    const src = overridePos ?? cloneSourceRef.current;
    if (!src) {
      canvas.requestRenderAll();
      return;
    }
    // Reticle: 4 fabric.Rect bars added directly to the canvas
    // (no Group wrapper). Two white halo bars sit underneath two
    // accent-colored bars to form a `+` with a white outline,
    // legible against both light and dark imagery. Sizing is in
    // scene units pre-divided by zoom so the on-screen size
    // stays stable across zoom levels.
    const zoom = canvas.getZoom() || 1;
    const crossLen = 18 / zoom;
    const haloThick = 5 / zoom;
    const accentThick = 2 / zoom;
    const buildBar = (width: number, height: number, color: string): fabric.Rect =>
      new fabric.Rect({
        left: src.x,
        top: src.y,
        width,
        height,
        fill: color,
        stroke: undefined,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        excludeFromExport: true,
        objectCaching: false,
      });
    const bars = [
      buildBar(crossLen, haloThick, '#ffffff'),
      buildBar(haloThick, crossLen, '#ffffff'),
      buildBar(crossLen, accentThick, '#0078d7'),
      buildBar(accentThick, crossLen, '#0078d7'),
    ];
    for (const bar of bars) {
      (bar as fabric.Rect & { _isCloneReticle: boolean })._isCloneReticle = true;
      canvas.add(bar);
      canvas.bringObjectToFront(bar);
    }
    cloneReticleRef.current = { bars, left: src.x, top: src.y };
    canvas.requestRenderAll();
  }, []);
  renderCloneReticleRef.current = renderCloneReticle;

  /** Arm the next canvas tap to set the Clone source point. UI
   *  flow: user taps "Set Source" button → setCloneArmed(true) →
   *  next mouse:down records the source and disarms. Tapping
   *  again (or pressing Esc, handled by EditorPage) cancels. */
  const setCloneArmed = useCallback((armed: boolean) => {
    cloneArmedRef.current = armed;
  }, []);

  /** Push a transient cursor override onto Fabric's defaultCursor.
   *  Used by EditorPage to flip the cursor to a crosshair while
   *  Clone Stamp source-set mode is active (button armed or Alt
   *  held). Pass `null` to clear and restore whatever cursor was
   *  set by the active tool's setTool branch.
   *
   *  We snapshot the cursor at the moment the override is applied
   *  (rather than re-deriving from the active tool) so that
   *  restoration is correct regardless of which tool is active —
   *  setTool has too many cursor branches (`text`, `shape`,
   *  `fill`, etc.) to mirror in a separate switch. */
  const cursorOverrideStashRef = useRef<{ cursor: string; tool: EditorTool } | null>(null);
  const setCursorOverride = useCallback((cursor: string | null) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (cursor) {
      if (cursorOverrideStashRef.current === null) {
        cursorOverrideStashRef.current = {
          cursor: canvas.defaultCursor || 'default',
          tool: activeToolRef.current,
        };
      }
      canvas.defaultCursor = cursor;
    } else if (cursorOverrideStashRef.current !== null) {
      // Only restore the snapshot if the user is still on the
      // same tool that was active when the override was applied.
      // If they've switched tools, setTool will have already
      // installed the right cursor for the new tool — restoring
      // the old one would clobber it.
      if (cursorOverrideStashRef.current.tool === activeToolRef.current) {
        canvas.defaultCursor = cursorOverrideStashRef.current.cursor;
      }
      cursorOverrideStashRef.current = null;
    }
    canvas.requestRenderAll();
  }, []);

  /** Programmatic source-point setter — used by long-press on
   *  touch and by undo/redo. Fires the change callback so the
   *  panel mirrors the new state. */
  const setCloneSource = useCallback(
    (point: { x: number; y: number } | null) => {
      cloneSourceRef.current = point;
      // A new (or cleared) source resets any persisted Aligned
      // offset — same as the mouse:down armed/alt-click path.
      cloneAlignedOffsetRef.current = null;
      renderCloneReticle();
      onCloneSourceChangedRef.current?.(point);
    },
    [renderCloneReticle],
  );

  /** Push the latest brush/fill color so the fill tool can pick it up. */
  const setBrushColor = useCallback((color: string) => {
    brushColorRef.current = color;
  }, []);

  /** Set the active tool on the canvas */
  const setTool = useCallback(
    (tool: EditorTool, brushSettings?: BrushSettings) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const prevTool = activeToolRef.current;
      activeToolRef.current = tool;
      // Abandon any in-flight retouch stroke when switching away
      // from the retouch tool — its `mouse:up` would otherwise
      // never fire, leaking the per-stroke ref + `appliedMask`.
      // Also drop the clone source state: source point, armed flag,
      // aligned offset, and the on-canvas reticle. Otherwise the
      // reticle stays visible after switching tools, and a return
      // to Clone/Heal would silently inherit the stale source.
      if (prevTool === 'retouch' && tool !== 'retouch') {
        clearAllRetouchStrokes();
        // Order-dependent: zero the refs FIRST, then render the
        // reticle. renderCloneReticle reads `cloneSourceRef.current`
        // — if it still held a position, the reticle would
        // re-render once at the old source before the next render
        // tear-down. Same for armed/aligned-offset which gate
        // future stroke behavior. Do not reorder.
        cloneSourceRef.current = null;
        cloneArmedRef.current = false;
        cloneAlignedOffsetRef.current = null;
        renderCloneReticleRef.current?.();
        // Mirror to React state via the callback so the
        // PropertiesPanel "Set Source" button leaves armed state.
        onCloneSourceChangedRef.current?.(null);
      }

      // Preserve the active marquee for tools that operate on the selection
      // (Move, Draw, Eraser, Fill) plus the marquee tool itself.
      if (!MARQUEE_PRESERVING_TOOLS.has(tool)) {
        clearMarquee();
      }

      // Tear down the crop rect if leaving crop mode without applying.
      if (tool !== 'crop' && cropRectRef.current) {
        canvas.discardActiveObject();
        canvas.remove(cropRectRef.current);
        cropRectRef.current = null;
      }

      canvas.isDrawingMode = false;
      canvas.selection = false;
      canvas.defaultCursor = 'default';

      switch (tool) {
        case 'move': {
          canvas.defaultCursor = 'grab';
          const bg = getBackgroundImage();
          canvas.getObjects().forEach((o) => {
            // Paint overlays must never become selectable —
            // they're doc-sized UI artifacts that would steal
            // every click (because they cover the whole canvas
            // and sit above the underlying layer) and would
            // inflate the Move-tool bbox to doc dimensions.
            if (o !== bg && !isMarqueeObject(o) && !isPaintOverlay(o)) {
              o.set({ selectable: true, evented: true });
            }
          });
          break;
        }
        case 'marquee': {
          canvas.defaultCursor = 'crosshair';
          const bg = getBackgroundImage();
          // Force the bg non-evented so its hit-test doesn't intercept
          // marquee mouse:down (otherwise selecting the Background layer
          // earlier would block subsequent marquee drawing).
          if (bg) bg.set({ selectable: false, evented: false });
          canvas.getObjects().forEach((o) => {
            if (o !== bg && !isMarqueeObject(o)) {
              o.set({ selectable: false, evented: false });
            }
          });
          break;
        }
        case 'draw':
          if (brushSettings) enableDrawMode(brushSettings);
          break;
        case 'retouch': {
          // Retouch variants each implement their own
          // mouse:down/move/up handlers gated on
          // `settings.variant`; none use Fabric's
          // freeDrawingBrush. Drawing mode stays off so a
          // brush-color line doesn't render on top of stamped
          // pixels.
          canvas.isDrawingMode = false;
          // Hide the OS arrow over the canvas — CanvasArea
          // renders an SVG brush-circle (with center cross for
          // the Clone variant) that fully replaces it. Source-set
          // mode flips this to 'crosshair' via setCursorOverride;
          // touch devices don't render the SVG cursor but also
          // don't show a system cursor, so 'none' is fine.
          canvas.defaultCursor = 'none';
          // Lock every object so Fabric's hit-test doesn't run
          // Move-tool drag in parallel with the custom retouch
          // mouse handlers. Without this, retouch on a
          // selectable image layer (e.g. a rasterized vector or
          // any non-bg image) drags the layer AND stamps pixels
          // simultaneously — the user sees the layer shift while
          // strokes accumulate on a lazy-mounted paint overlay
          // that stays put. Mirrors the marquee tool's lock
          // pattern. Switching to Move tool unlocks them again.
          const bg = getBackgroundImage();
          if (bg) bg.set({ selectable: false, evented: false });
          canvas.getObjects().forEach((o) => {
            if (o !== bg && !isMarqueeObject(o)) {
              o.set({ selectable: false, evented: false });
            }
          });
          break;
        }
        case 'eraser':
          canvas.isDrawingMode = true;
          canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
          // Semi-transparent neutral gray for the live preview — visible on
          // both light and dark backgrounds. The actual erase happens in
          // bakeStrokeOntoSurface via `globalCompositeOperation:
          // 'destination-out'`, so the preview color doesn't affect output.
          canvas.freeDrawingBrush.color = 'rgba(128, 128, 128, 0.5)';
          canvas.freeDrawingBrush.width = brushSettings?.width ?? 20;
          break;
        case 'crop': {
          canvas.defaultCursor = 'default';
          canvas.selection = true;
          const bg = getBackgroundImage();
          // Lock bg so its hit-test doesn't compete with the crop rect.
          if (bg) bg.set({ selectable: false, evented: false });
          canvas.getObjects().forEach((o) => {
            if (o !== bg && !isMarqueeObject(o)) {
              o.set({ selectable: false, evented: false });
            }
          });
          const imgW = imageDimsRef.current.width;
          const imgH = imageDimsRef.current.height;
          if (bg && imgW > 0 && imgH > 0) {
            const rect = buildCropRect(canvas, imgW, imgH);
            canvas.add(rect);
            canvas.bringObjectToFront(rect);
            canvas.setActiveObject(rect);
            cropRectRef.current = rect;
            canvas.requestRenderAll();
          }
          break;
        }
        case 'text':
          canvas.defaultCursor = 'text';
          break;
        case 'shape':
          canvas.defaultCursor = 'crosshair';
          break;
        case 'pen':
          // Crosshair matches Photoshop's pen cursor. Clearing any
          // stale pen preview defensively here lets a fresh tool
          // selection always start with a clean slate, even if the
          // activeTool mirror effect in EditorPage hasn't fired yet.
          canvas.defaultCursor = 'crosshair';
          clearPenState();
          break;
        case 'fill':
          canvas.defaultCursor = FILL_CURSOR;
          break;
        case 'ai':
          // AI tool drives a Properties-panel UI (Smart Delete / Smart
          // Create), not pointer interactions on the canvas. Keep the
          // marquee bg-lock behavior so a finalized marquee remains
          // selectable as the AI op's target region.
          canvas.defaultCursor = 'default';
          break;
        case 'quickmask': {
          // Quick Mask: paint into the working alpha mask. Brush
          // strokes are handled by the dedicated mouse:down/move/up
          // listeners (registered in the canvas-events effect),
          // NOT by Fabric's freeDrawingBrush. We keep `cursor:
          // crosshair` so the user sees a brush-tip target. The
          // overlay FabricImage was added by `enterQuickMask` and
          // is non-selectable / non-evented, so layer hit-tests
          // pass through to nothing — exactly what we want
          // (pointer events go to the canvas's mouse handlers
          // unmediated). Bg + layers stay non-evented per the
          // marquee/crop convention so the brush hits the canvas
          // not an underlying layer.
          canvas.defaultCursor = 'crosshair';
          const bg = getBackgroundImage();
          if (bg) bg.set({ selectable: false, evented: false });
          canvas.getObjects().forEach((o) => {
            if (o !== bg && !isMarqueeObject(o)) {
              o.set({ selectable: false, evented: false });
            }
          });
          break;
        }
      }

      canvas.renderAll();
    },
    [clearAllRetouchStrokes, enableDrawMode, getBackgroundImage],
  );

  /** Apply crop to the canvas */
  const applyCrop = useCallback(
    (rect: { left: number; top: number; width: number; height: number }) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const clipRect = new fabric.Rect({
        left: rect.left,
        top: rect.top,
        // v7 default origin is center; crop rect is anchored by its
        // top-left corner so the clip area matches the user's drag.
        originX: 'left',
        originY: 'top',
        width: rect.width,
        height: rect.height,
        absolutePositioned: true,
      });

      canvas.getObjects().forEach((obj) => {
        obj.clipPath = clipRect;
      });

      canvas.renderAll();
      onObjectModified?.();
    },
    [onObjectModified],
  );

  /**
   * Apply the active crop rect: extract the chosen region into a new background
   * image, translate overlay objects to the new origin, update canvas dimensions,
   * and reset the crop rect to the new full-image bounds so the user can iterate.
   */
  const applyActiveCrop = useCallback((): boolean => {
    const canvas = canvasRef.current;
    const bg = getBackgroundImage();
    const cropRect = cropRectRef.current;
    if (!canvas || !bg || !cropRect) return false;

    const left = cropRect.left ?? 0;
    const top = cropRect.top ?? 0;
    const width = (cropRect.width ?? 0) * (cropRect.scaleX ?? 1);
    const height = (cropRect.height ?? 0) * (cropRect.scaleY ?? 1);

    const imgW = bg.width ?? 0;
    const imgH = bg.height ?? 0;
    const cropX = Math.max(0, Math.round(left));
    const cropY = Math.max(0, Math.round(top));
    const cropW = Math.min(imgW - cropX, Math.round(width));
    const cropH = Math.min(imgH - cropY, Math.round(height));
    if (cropW <= 0 || cropH <= 0) return false;

    const imgEl = bg.getElement() as HTMLImageElement | HTMLCanvasElement;
    const offscreen = document.createElement('canvas');
    offscreen.width = cropW;
    offscreen.height = cropH;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return false;
    ctx.drawImage(imgEl, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    canvas.discardActiveObject();
    canvas.remove(cropRect);
    cropRectRef.current = null;

    canvas.getObjects().forEach((obj) => {
      if (obj === bg) return;
      obj.set({ left: (obj.left ?? 0) - cropX, top: (obj.top ?? 0) - cropY });
      obj.setCoords();
    });

    const newImg = new fabric.FabricImage(offscreen, {
      left: 0,
      top: 0,
      // v7 origin default flipped to center; the post-crop bg sits
      // at the canvas top-left.
      originX: 'left',
      originY: 'top',
      scaleX: 1,
      scaleY: 1,
      selectable: false,
      evented: false,
    });
    (newImg as fabric.FabricImage & { erasable: boolean }).erasable = false;
    tagAsBackground(newImg);
    newImg.set({ objectCaching: false } as Partial<fabric.FabricImage>);
    newImg.filters = bg.filters ? [...bg.filters] : [];
    newImg.applyFilters();

    // Carry the bg's layer id forward to the new image and re-register
    // its underlying canvas as the Background layer's paint surface.
    // Without this, draw / eraser / fill strokes would silently bake into
    // the orphaned old surface canvas and never reach the visible bg.
    const bgLayerId = getObjectLayerId(bg);
    if (bgLayerId) {
      setObjectLayerId(newImg, bgLayerId);
      paintSurfacesRef.current.set(bgLayerId, {
        canvas: offscreen,
        fabricImg: newImg,
        offsetX: newImg.left ?? 0,
        offsetY: newImg.top ?? 0,
      });
    }

    canvas.remove(bg);
    canvas.add(newImg);
    canvas.sendObjectToBack(newImg);
    backgroundImageRef.current = newImg;
    imageDimsRef.current = { width: cropW, height: cropH };
    onCanvasDimensions?.(cropW, cropH);

    zoomToFit();

    // Re-create crop rect at the new full bounds so the user can crop again.
    const fresh = buildCropRect(canvas, cropW, cropH);
    canvas.add(fresh);
    canvas.setActiveObject(fresh);
    cropRectRef.current = fresh;

    canvas.renderAll();
    onObjectModified?.();
    return true;
  }, [onCanvasDimensions, onObjectModified, zoomToFit, getBackgroundImage]);

  /**
   * Run `@imgly/background-removal` on the active layer (or bg fallback)
   * and return the alpha-thresholded mask as a same-sized canvas where
   * subject pixels are opaque white and bg pixels are fully transparent.
   * Returns null if no source image is loaded. Free — runs in-browser
   * via ONNX inference, no API credits charged. Used by AI Select
   * Subject to seed a wand-style marquee without modifying the image.
   */
  const extractSubjectMaskCanvas = useCallback(async (): Promise<HTMLCanvasElement | null> => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const bg = getBackgroundImage();
    const activeLayerId = activeLayerIdRef.current;
    let targetImg: fabric.FabricImage | null = null;
    if (activeLayerId) {
      for (const o of canvas.getObjects()) {
        if (getObjectLayerId(o) === activeLayerId && o instanceof fabric.FabricImage) {
          targetImg = o;
          break;
        }
      }
    }
    if (!targetImg) targetImg = bg;
    if (!targetImg) return null;

    const imgEl = targetImg.getElement() as HTMLImageElement | HTMLCanvasElement;
    const w = targetImg.width || 0;
    const h = targetImg.height || 0;
    if (!w || !h) return null;

    const offscreen = document.createElement('canvas');
    offscreen.width = w;
    offscreen.height = h;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(imgEl, 0, 0);

    const sourceBlob: Blob | null = await new Promise((resolve) =>
      offscreen.toBlob(resolve, 'image/png'),
    );
    if (!sourceBlob) return null;

    const { removeBackground: imglyRemoveBackground } = await import('@imgly/background-removal');
    const cutoutBlob = await imglyRemoveBackground(sourceBlob);

    const cutoutUrl = URL.createObjectURL(cutoutBlob);
    try {
      const cutoutImg = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to decode cutout'));
        img.src = cutoutUrl;
      });
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = cutoutImg.naturalWidth;
      maskCanvas.height = cutoutImg.naturalHeight;
      const mCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
      if (!mCtx) return null;
      mCtx.drawImage(cutoutImg, 0, 0);
      const id = mCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
      for (let i = 0; i < id.data.length; i += 4) {
        if (id.data[i + 3] > 64) {
          id.data[i] = 255;
          id.data[i + 1] = 255;
          id.data[i + 2] = 255;
          id.data[i + 3] = 255;
        } else {
          id.data[i + 3] = 0;
        }
      }
      mCtx.putImageData(id, 0, 0);
      return maskCanvas;
    } finally {
      URL.revokeObjectURL(cutoutUrl);
    }
  }, [getBackgroundImage]);

  /**
   * Remove the background of the current image. By default runs
   * `@imgly/background-removal` in the browser (ONNX inference, free). The
   * caller can inject a `processBlob` to substitute a server-side engine
   * (Stability or Recraft) — the layer-replacement logic stays here in
   * one place. The imgly library is loaded dynamically so it doesn't
   * bloat the editor's initial bundle for paid-engine users.
   */
  const removeBackground = useCallback(
    async (opts?: { processBlob?: (sourceBlob: Blob) => Promise<Blob> }) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Find the target image. If the active layer is an image layer,
      // operate on it; otherwise fall back to the bg image (the original
      // behavior). This is the fix for "Remove Background only worked on
      // the actual background, not on a selected layer image."
      const bg = getBackgroundImage();
      const activeLayerId = activeLayerIdRef.current;
      let targetImg: fabric.FabricImage | null = null;
      if (activeLayerId) {
        for (const o of canvas.getObjects()) {
          if (getObjectLayerId(o) === activeLayerId && o instanceof fabric.FabricImage) {
            targetImg = o;
            break;
          }
        }
      }
      if (!targetImg) targetImg = bg;
      if (!targetImg) return;

      const isBg = targetImg === bg;
      const imgEl = targetImg.getElement() as HTMLImageElement | HTMLCanvasElement;
      const w = targetImg.width || 0;
      const h = targetImg.height || 0;
      if (!w || !h) return;

      const offscreen = document.createElement('canvas');
      offscreen.width = w;
      offscreen.height = h;
      const ctx = offscreen.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(imgEl, 0, 0);

      const sourceBlob: Blob | null = await new Promise((resolve) =>
        offscreen.toBlob(resolve, 'image/png'),
      );
      if (!sourceBlob) return;

      let resultBlob: Blob;
      if (opts?.processBlob) {
        resultBlob = await opts.processBlob(sourceBlob);
      } else {
        const { removeBackground: imglyRemoveBackground } =
          await import('@imgly/background-removal');
        resultBlob = await imglyRemoveBackground(sourceBlob);
      }
      const resultUrl = URL.createObjectURL(resultBlob);

      try {
        const newImg = await fabric.FabricImage.fromURL(resultUrl, { crossOrigin: 'anonymous' });
        // Preserve placement of the layer we're replacing. For the bg
        // layer this is always (0,0) at scale 1; for a non-bg image layer
        // we copy across the user's existing position / scale / rotation
        // so the replacement lands exactly where the original was.
        if (isBg) {
          newImg.set({
            left: 0,
            top: 0,
            // v7 origin default flipped to center; the bg-removed
            // replacement should still anchor at the canvas top-left.
            originX: 'left',
            originY: 'top',
            scaleX: 1,
            scaleY: 1,
            selectable: false,
            evented: false,
            objectCaching: false,
          } as Partial<fabric.FabricImage>);
          (newImg as fabric.FabricImage & { erasable: boolean }).erasable = false;
          tagAsBackground(newImg);
        } else {
          newImg.set({
            left: targetImg.left,
            top: targetImg.top,
            scaleX: targetImg.scaleX,
            scaleY: targetImg.scaleY,
            angle: targetImg.angle,
            originX: targetImg.originX,
            originY: targetImg.originY,
            flipX: targetImg.flipX,
            flipY: targetImg.flipY,
          } as Partial<fabric.FabricImage>);
        }

        // Convert the loaded <img> element to an HTMLCanvasElement so the
        // new image is paintable. Subsequent draw / eraser / fill strokes
        // need a canvas surface to bake into; without this swap they'd
        // silently no-op (the rendering flips to a non-canvas element
        // after the background-removal step).
        const newW = newImg.width || 1;
        const newH = newImg.height || 1;
        const surfaceCanvas = ensureCanvasElement(
          newImg.getElement() as HTMLImageElement | HTMLCanvasElement,
          newW,
          newH,
        );
        if (newImg.getElement() !== surfaceCanvas) newImg.setElement(surfaceCanvas);

        newImg.filters = targetImg.filters ? [...targetImg.filters] : [];
        newImg.applyFilters();

        // Carry the layer id forward to the replacement and refresh the
        // paint-surface registry to point at the new canvas. The old
        // entry (pointing to targetImg's now-orphaned element) would
        // otherwise eat all future strokes silently.
        const layerId = getObjectLayerId(targetImg);
        if (layerId) {
          setObjectLayerId(newImg, layerId);
          paintSurfacesRef.current.set(layerId, {
            canvas: surfaceCanvas,
            fabricImg: newImg,
            offsetX: newImg.left ?? 0,
            offsetY: newImg.top ?? 0,
          });
        }

        canvas.remove(targetImg);
        canvas.add(newImg);
        if (isBg) {
          canvas.sendObjectToBack(newImg);
          backgroundImageRef.current = newImg;
        } else {
          canvas.setActiveObject(newImg);
        }
        canvas.renderAll();
        onObjectModified?.();
      } finally {
        URL.revokeObjectURL(resultUrl);
      }
    },
    [onObjectModified, getBackgroundImage],
  );

  /**
   * Clear every object on the canvas and reset our internal references
   * (background image, paint surfaces, image dimensions, marquee). Used by
   * the multi-tab flow when switching to a freshly-created tab that hasn't
   * had a file loaded into it yet.
   */
  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.clear();
    backgroundImageRef.current = null;
    paintSurfacesRef.current.clear();
    imageDimsRef.current = { width: 0, height: 0 };
    marqueeRef.current.obj = null;
    marqueeRef.current.extraObjs = [];
    marqueeRef.current.points = [];
    marqueeRef.current.wandMaskCanvas = null;
    marqueeRef.current.isDrawing = false;
    // Mirror into React state so the toolbar's Cut / Copy buttons disable
    // when switching to a freshly-emptied tab. Without this, they'd stay
    // enabled (matching the previous tab's marquee) and silently no-op.
    setHasMarqueeSelection(false);
    canvas.renderAll();
  }, []);

  /** Select all objects (excluding background) */
  const selectAll = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const bg = getBackgroundImage();
    const objects = canvas.getObjects().filter((o) => o !== bg);
    if (objects.length > 0) {
      const selection = new fabric.ActiveSelection(objects, { canvas });
      canvas.setActiveObject(selection);
      canvas.renderAll();
    }
  }, [getBackgroundImage]);

  // Memoize the returned API so its identity is stable across
  // renders that didn't change any of the underlying callbacks or
  // state. Consumers commonly use the whole object as a useEffect
  // dependency (`[..., canvas]`); without this memo the literal
  // is a fresh object every render, re-firing every dependent
  // effect and clobbering canvas-side state pushed by other paths.
  // The two regressions in `handleSelectSubject` and
  // `handleInvertSelection` (where the `setMarqueeShape` effect re-
  // fired and called `clearMarquee` after we'd just installed a
  // wand-style selection) were both caused by this churn.
  return useMemo(
    () => ({
      canvasRef,
      viewport,
      loadImage,
      enableDrawMode,
      disableDrawMode,
      addText,
      addShape,
      addVectorObject,
      replaceVectorObject,
      updateVectorStyle,
      getActiveVectorBounds,
      getBakedVectorPath,
      rasterizeLayerToImage,
      setPenSettings,
      cancelPenPath,
      finalizePenPath,
      enterPathEditMode,
      exitPathEditMode,
      setPathEditSelectedAnchor,
      setPathEditAnchorType,
      getPathEditSelectedAnchorType,
      refreshPathEditOverlay,
      updateActiveShape,
      getActiveShapeSettings,
      updateActiveText,
      getActiveTextSettings,
      setTextPathBinding,
      convertToGrayscale,
      deleteSelected,
      setZoom,
      fitToContainer,
      rotateCanvas,
      flipCanvas,
      exportToDataURL,
      exportToBlob,
      toJSON,
      loadFromJSON,
      setTool,
      applyCrop,
      applyActiveCrop,
      selectAll,
      clearMarquee,
      growMarqueeBy,
      canGrowMarquee,
      setSelectionFromMask,
      getSelectionAsMaskCanvas,
      enterQuickMask,
      exitQuickMask,
      getQuickMaskCanvas,
      discardQuickMask,
      applyLayerMaskFromCanvas,
      setQuickMaskMode,
      enterTransformMode,
      exitTransformMode,
      applyTransformControls,
      getTransformMatrix,
      setTransformMatrix,
      applyWarpPresetToOverlay,
      invertMarquee,
      setMarqueeShape,
      setForceSquare,
      setMarqueeColor,
      setWandSettings,
      setActiveLayerId,
      setBrushColor,
      updateBrush,
      setViewportTransform,
      setBackgroundLayerId,
      setLayerVisibility,
      applyEffectiveVisibility,
      setLayerOpacity,
      setLayerBlendMode,
      setLayerEffects,
      addLayerMask,
      removeLayerMask,
      setMaskConfig,
      materializeMaskClipPath,
      setEditTargets,
      setRetouchSettings,
      subscribeToCursorMove,
      setCloneArmed,
      setCloneSource,
      setCursorOverride,
      applyLayerOrder,
      selectLayerObjects,
      selectLayersObjects,
      removeLayerObjects,
      addImageLayer,
      addAdjustmentLayer,
      getViewportTransform,
      removeBackground,
      extractSubjectMaskCanvas,
      clearCanvas,
      copySelection,
      cutSelection,
      getClipboard,
      getMarqueeBounds,
      runSmartDeleteOnActiveLayer,
      runOnActiveLayerFullImage,
      runUpscaleOnActiveLayer,
      getActiveLayerSnapshot,
      getSourceCanvasForSampling,
      getActiveAdjustmentTargets,
      paintLeafToCanvas,
      getAdjustmentBackingCanvas,
      runOutpaintOnBackground,
      hasMarqueeSelection,
      hasClipboard,
    }),
    [
      viewport,
      hasMarqueeSelection,
      hasClipboard,
      loadImage,
      enableDrawMode,
      disableDrawMode,
      addText,
      addShape,
      addVectorObject,
      replaceVectorObject,
      updateVectorStyle,
      getActiveVectorBounds,
      getBakedVectorPath,
      rasterizeLayerToImage,
      setPenSettings,
      cancelPenPath,
      finalizePenPath,
      enterPathEditMode,
      exitPathEditMode,
      setPathEditSelectedAnchor,
      setPathEditAnchorType,
      getPathEditSelectedAnchorType,
      refreshPathEditOverlay,
      updateActiveShape,
      getActiveShapeSettings,
      updateActiveText,
      getActiveTextSettings,
      setTextPathBinding,
      convertToGrayscale,
      deleteSelected,
      setZoom,
      fitToContainer,
      rotateCanvas,
      flipCanvas,
      exportToDataURL,
      exportToBlob,
      toJSON,
      loadFromJSON,
      setTool,
      applyCrop,
      applyActiveCrop,
      selectAll,
      clearMarquee,
      growMarqueeBy,
      canGrowMarquee,
      setSelectionFromMask,
      getSelectionAsMaskCanvas,
      enterQuickMask,
      exitQuickMask,
      getQuickMaskCanvas,
      discardQuickMask,
      applyLayerMaskFromCanvas,
      setQuickMaskMode,
      enterTransformMode,
      exitTransformMode,
      applyTransformControls,
      getTransformMatrix,
      setTransformMatrix,
      applyWarpPresetToOverlay,
      invertMarquee,
      setMarqueeShape,
      setForceSquare,
      setMarqueeColor,
      setWandSettings,
      setActiveLayerId,
      setBrushColor,
      updateBrush,
      setViewportTransform,
      setBackgroundLayerId,
      setLayerVisibility,
      applyEffectiveVisibility,
      setLayerOpacity,
      setLayerBlendMode,
      setLayerEffects,
      addLayerMask,
      removeLayerMask,
      setMaskConfig,
      materializeMaskClipPath,
      setEditTargets,
      setRetouchSettings,
      subscribeToCursorMove,
      setCloneArmed,
      setCloneSource,
      setCursorOverride,
      applyLayerOrder,
      selectLayerObjects,
      selectLayersObjects,
      removeLayerObjects,
      addImageLayer,
      addAdjustmentLayer,
      getViewportTransform,
      removeBackground,
      extractSubjectMaskCanvas,
      clearCanvas,
      copySelection,
      cutSelection,
      getClipboard,
      getMarqueeBounds,
      runSmartDeleteOnActiveLayer,
      runOnActiveLayerFullImage,
      runUpscaleOnActiveLayer,
      getActiveLayerSnapshot,
      getSourceCanvasForSampling,
      getActiveAdjustmentTargets,
      paintLeafToCanvas,
      getAdjustmentBackingCanvas,
      runOutpaintOnBackground,
    ],
  );
}
